/**
 * APP.TSX - MAIN REACT APPLICATION COMPONENT FOR X(EPI)BLEPSIS MEDICAL MONITORING SYSTEM
 *
 * This is the main application component that orchestrates the entire medical monitoring system.
 * It handles authentication, routing, data visualization, and real-time monitoring of vital signs.
 *
 * Key Features:
 * - Firebase authentication integration (login, logout, user registration)
 * - Real-time vital signs monitoring with interactive charts
 * - Patient and doctor role-based access control
 * - Medical history forms and consent management
 * - Medication and threshold management
 * - Critical events detection and alerting
 * - Demo data generation for testing and development
 * - Responsive dashboard with multiple monitoring panels
 *
 * Architecture:
 * - Uses React hooks for state management (useState, useEffect, useRef)
 * - Firebase for authentication and user management
 * - Recharts library for data visualization and medical charts
 * - Date-fns for date/time manipulation and formatting
 * - Modular page components for different application sections
 * - API integration for backend data retrieval and storage
 *
 * User Roles:
 * - DOCTOR: Can view all patients, manage thresholds, access medical history
 * - PATIENT: Can view own vital signs, complete medical history forms
 * - PARENT: Can view child's data and complete forms on behalf of child
 *
 * Data Flow:
 * 1. User authentication via Firebase
 * 2. Role-based navigation and access control
 * 3. Real-time data fetching from backend API
 * 4. Data visualization using interactive charts
 * 5. Critical event detection and alerting
 * 6. Form submission and data persistence
 */

// React core imports for component functionality and state management
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Capacitor, CapacitorHttp } from '@capacitor/core';

// Firebase authentication and database imports for user management and data storage
import { auth } from './firebase';
import { onAuthStateChanged, signOut } from 'firebase/auth';

// Recharts library imports for medical data visualization and interactive charts
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceArea, ReferenceLine, Scatter, Customized  } from 'recharts';

// Date manipulation utilities for timestamp handling and time-based data processing
import { format, subHours, subDays, subMinutes, addMinutes, addHours, addDays, startOfHour, startOfDay } from 'date-fns';

// Page component imports for different sections of the medical monitoring application
import HistoryPage from './pages/HistoryPage';
import TimelinePage from './pages/TimelinePage';
import SettingsPage from './pages/SettingsPage';
import ConsentPage from './pages/ConsentPage';
import ThresholdsPage from './pages/ThresholdsPage';
import MedicationPage from './pages/MedicationPage';

// API imports for vital signs thresholds and medication management
import { loadThresholds, Thresholds, defaultThresholds } from './api/thresholds';
import { loadMedications, Medication } from './api/medication';
import {
  computeSleepScore,
  getSleepScoreColorClass,
  DEFAULT_SLEEP_GOAL_MINUTES,
  DURATION_MAX_POINTS,
  CONSISTENCY_MAX_POINTS,
  INTERRUPTION_MAX_POINTS,
  CONSISTENCY_HISTORY_NIGHTS,
  buildSleepScoreMetricDetails,
  deriveSleepSessionFromPoints,
  extractPreviousSleepOnsets,
  groupSleepPointsIntoSessions,
  getPreviousSleepSessionWindows,
  toSleepStagePoints,
  type SleepNightWindow,
} from './utils/sleepScore';
import SessionInvalidatedModal from './components/SessionInvalidatedModal';
import { isSessionInvalidated, onSessionInvalidated, resetSessionInvalidation } from './api/authSession';
import { authenticatedFetch } from './api/authenticatedFetch';

// Patient history (intake) panel - data from history form filled by patient
import PatientHistoryPanel from './components/PatientHistoryPanel';
import {
  SleepStageChart,
  formatSleepDuration,
  SLEEP_STAGE_NAMES,
  SLEEP_DAILY_Y_SPAN,
  sleepWindowYToMinutesOfDay,
  snapSleepWideZoomToNight,
  type SleepRun,
} from './components/SleepStageChart';

// Patient management page for doctors to view assigned patients
import PatientsPage from './pages/PatientsPage';
import AdminPhoneErrorsPage from './pages/AdminPhoneErrorsPage';
import AdminFetchRawPage from './pages/AdminFetchRawPage';
import AdminLoginAttemptsPage from './pages/AdminLoginAttemptsPage';

// Comprehensive API imports for backend data retrieval and medical monitoring
import { getVitalSignsRange, getVitalSigns, startHealthMonitor, onBackendHealthy, getServerHealth, getLatestEcgSamples, getLatestPpgGreenSamples, getLatestDataTimestamps, getLatestEcg2Samples, getLatestPpgYellowSamples, getTemperatureRange, getLatestTemperature, getDemoVitalsRange, getLatestDemoVitals, getLatestDemoRawSamples, getDemoRawSamplesByTimestamp, getRawSamplesByTimestamp, getDemoCriticalEventsRange, getCriticalEventsRange, getVitalsRange, getUserRole, getPatientByEmail, getPatientBedTime, getSleepConsistencyHistory, getPatientNotes, updatePatientNotes, getVitalsStatsWide, type VitalsStatsWideRow } from './api/database';
import { getPpgSampleInterval, getPpgWindowSampleCount } from './utils/ppgTiming';
import {
  ATRIAL_FIBRILLATION_EVENT_TYPE,
  ATRIAL_FIBRILLATION_X_EVENT_TYPE,
  getAtrialFibrillationDisplayLabel,
  isAtrialFibrillationEvent,
  normalizeAtrialFibrillationEventTypeForUi,
} from './utils/atrialFibrillationEvents';
import { FALL_EVENT_TYPE, getFallDisplayLabel, isFallEvent, normalizeFallEventTypeForUi } from './utils/fallEvents';
import {
  apiUrl,
  CAPTCHA_ENABLED,
  DEBUG_MODE,
  isAdminWebEmail,
  isFetchRawAdminEmail,
  isSecurityAdminEmail,
  sanitizeAuthError,
  shouldEnforceCaptcha,
} from './config/appConfig';

declare global {
  interface Window {
    grecaptcha?: {
      ready: (callback: () => void) => void;
      render: (container: HTMLElement, options: {
        sitekey: string;
        callback?: (token: string) => void;
        'expired-callback'?: () => void;
        'error-callback'?: () => void;
      }) => number;
      reset: (widgetId?: number) => void;
    };
  }
}

/**
 * CRITICAL EVENT INTERFACE - Medical Alert System Data Structure
 * 
 * Represents a critical medical event that requires attention, such as abnormal vital signs
 * that exceed predefined thresholds. Used for alerting medical staff to potential issues.
 */
interface CriticalEvent {
  id: string;                    // Unique identifier for the critical event
  timestamp: string;             // ISO timestamp when the event occurred
  type: string;                  // Type of critical event (e.g., 'heart_rate_high', 'spo2_low')
  value: string;                 // The actual measured value that triggered the alert
  position: 'above' | 'below';  // Whether the value was above or below the threshold
}

/**
 * EVENT CATEGORIES - Medical Event Classification System
 * 
 * Defines the main categories for organizing critical medical events by vital sign type.
 * Used for filtering and grouping events in the medical monitoring dashboard.
 */
const EVENT_CATEGORIES = ['Heart Rate', 'HRV', 'Blood Pressure', 'SpO2', 'RESP', 'Skin Temperature', 'Activity'] as const;
type EventCategory = typeof EVENT_CATEGORIES[number];

/**
 * AVAILABLE EVENT TYPES - Comprehensive Medical Alert Types
 * 
 * Defines all possible critical event types that can be detected by the monitoring system.
 * Each event type corresponds to a specific vital sign measurement that has exceeded
 * predefined medical thresholds, triggering an alert for medical staff attention.
 * 
 * Event Types by Category:
 * - Heart Rate: Low, high, and medium threshold violations
 * - HRV (Heart Rate Variability): Low, high, and medium variability alerts
 * - Blood Pressure: Systolic and diastolic pressure stage classifications
 * - SpO2 (Oxygen Saturation): Low and medium oxygen level alerts
 * - RESP (Respiratory Rate): Low, high, and medium breathing rate alerts
 * - Temperature: Low, high, and fever temperature alerts
 */
const AVAILABLE_EVENT_TYPES = [
  // Heart Rate - Cardiovascular monitoring alerts
  'heart_rate_low', 'heart_rate_high', 'heart_rate_medium',
  ATRIAL_FIBRILLATION_EVENT_TYPE,
  ATRIAL_FIBRILLATION_X_EVENT_TYPE,
  
  // HRV (Heart Rate Variability) - Cardiac rhythm analysis alerts
  'hrv_low', 'hrv_high', 'hrv_medium',
  
  // Blood Pressure - Systolic pressure monitoring with stage classifications
  'blood_pressure_systolic_low', 'blood_pressure_systolic_high', 
  'blood_pressure_systolic_stage1', 'blood_pressure_systolic_stage2', 
  
  // Blood Pressure - Diastolic pressure monitoring with stage classifications
  'blood_pressure_diastolic_low', 'blood_pressure_diastolic_high',
  'blood_pressure_diastolic_stage1', 'blood_pressure_diastolic_stage2', 
  
  // SpO2 (Oxygen Saturation) - Blood oxygen level monitoring
  'spo2_low', 'spo2_medium',
  
  // RESP (Respiratory Rate) - Breathing rate monitoring
  'resp_low', 'resp_high', 'resp_medium',
  
  // Temperature - Body temperature monitoring and fever detection
  'temperature_low', 'temperature_high',
  'temperature_fever', 

  // Activity
  FALL_EVENT_TYPE,

] as const;



/**
 * UTILITY FUNCTION: Get Event Category from Event Type
 * 
 * Categorizes critical medical events by analyzing the event type string and mapping
 * it to the appropriate medical category. This function uses keyword matching to
 * classify events into standard medical monitoring categories.
 * 
 * @param {string} eventType - The event type string to categorize
 * @returns {EventCategory | 'Other'} - The medical category or 'Other' if no match
 */
const getEventCategory = (eventType: string): EventCategory | 'Other' => {
  const t = (eventType || '').toLowerCase();
  if (isFallEvent(eventType)) return 'Activity';
  if (isAtrialFibrillationEvent(eventType)) return 'Heart Rate';
  if (t.includes('hrv') || t.includes('variability')) return 'HRV';
  if (t.includes('heart') || t.includes('hr_') || t.includes('heart_rate')) return 'Heart Rate';
  if (t.includes('blood') || t.includes('pressure') || t.includes('systolic') || t.includes('diastolic') || t.includes('bp')) return 'Blood Pressure';
  if (t.includes('spo2') || t.includes('oxygen')) return 'SpO2';
  if (t.includes('resp') || t.includes('breathing') || t.includes('breath')) return 'RESP';
  if (t.includes('temp') || t.includes('temperature') || t.includes('fever') || t.includes('hypothermia')) return 'Skin Temperature';
  return 'Other';
};

/** UI-only display labels for event types (Heart Rate, HRV, SpO2 level renames). */
const getEventTypeDisplayLabel = (eventType: string): string => {
  const t = (eventType || '').toLowerCase();
  // Heart Rate: low→Critical Low, medium→Resting High, high→Critical High
  if (t === 'heart_rate_low') return 'Critical Low';
  if (t === 'heart_rate_medium') return 'Resting High';
  if (t === 'heart_rate_high') return 'Critical High';
  // HRV: low→Critical Low, medium→Resting Low; high unchanged
  if (t === 'hrv_low') return 'Critical Low';
  if (t === 'hrv_medium') return 'Resting Low';
  if (t === 'hrv_high') return 'High';
  // SpO2: medium→Low, low→Critical Low
  if (t === 'spo2_medium') return 'Low';
  if (t === 'spo2_low') return 'Critical Low';
  if (isAtrialFibrillationEvent(eventType)) return getAtrialFibrillationDisplayLabel(eventType);
  if (isFallEvent(eventType)) return getFallDisplayLabel(eventType);
  // Default: keep existing formatting
  return (eventType || '').replace(/^blood_pressure_/, '').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

/** Short category label for graph and critical vents: HR, HRV, BPS, BPD, SpO2, RR, ST */
const getEventCategoryShort = (eventType: string): string => {
  const t = (eventType || '').toLowerCase();
  if (isFallEvent(eventType)) return 'Activity';
  if (isAtrialFibrillationEvent(eventType)) return 'AF';
  if (t.includes('hrv') || t.includes('variability')) return 'HRV';
  if (t.includes('heart') || t.includes('heart_rate')) return 'HR';
  if (t.includes('systolic')) return 'BPS';
  if (t.includes('diastolic')) return 'BPD';
  if (t.includes('blood') || t.includes('pressure') || t.includes('bp')) return 'BPS'; // fallback for generic BP
  if (t.includes('spo2') || t.includes('oxygen')) return 'SpO2';
  if (t.includes('resp') || t.includes('breathing') || t.includes('breath')) return 'RR';
  if (t.includes('temp') || t.includes('temperature') || t.includes('fever')) return 'ST';
  return '';
};

let parseDbTimestampDebugCount = 0;
const MAX_PARSE_DB_TIMESTAMP_DEBUG_LOGS = 120;

/**
 * Parse DB timestamps in a timezone-safe way.
 * If timezone is missing, treat the value as UTC (DB stores UTC values).
 */
const parseDbTimestamp = (value: string | number | Date | null | undefined): Date => {
  if (value instanceof Date) return new Date(value.getTime());
  if (value == null) return new Date(NaN);

  const raw = String(value).trim();
  let normalized = raw.includes('T') ? raw : raw.replace(' ', 'T');

  // Normalize timezone suffixes like +3 / +03 / +0300 / +03:00 to +HH:MM
  const tzMatch = normalized.match(/([+-])(\d{1,2})(?::?(\d{2}))?$/);
  const hasZulu = /Z$/i.test(normalized);
  if (tzMatch) {
    const sign = tzMatch[1];
    const hh = tzMatch[2].padStart(2, '0');
    const mm = (tzMatch[3] || '00').padStart(2, '0');
    normalized = normalized.replace(/([+-]\d{1,2}(?::?\d{2})?)$/, `${sign}${hh}:${mm}`);
  } else if (!hasZulu) {
    // DB values are UTC. If timezone is omitted, force UTC interpretation.
    normalized = `${normalized}Z`;
  }

  const parsed = new Date(normalized);
  if (parseDbTimestampDebugCount < MAX_PARSE_DB_TIMESTAMP_DEBUG_LOGS) {
    console.log('[TS-DEBUG][parseDbTimestamp]', {
      raw,
      normalized,
      parsedLocal: Number.isNaN(parsed.getTime()) ? null : parsed.toLocaleString('en-GB', { hour12: false }),
      parsedIso: Number.isNaN(parsed.getTime()) ? null : parsed.toISOString(),
    });
    parseDbTimestampDebugCount++;
  }
  return parsed;
};

const isValidDate = (d: Date): boolean => !Number.isNaN(d.getTime());
const parseValidTimestamp = (value: string | number | Date | null | undefined): Date | null => {
  const parsed = parseDbTimestamp(value);
  return isValidDate(parsed) ? parsed : null;
};

const DEFAULT_BED_TIME = '23:00:00';
const NIGHT_DURATION_MINUTES = 8.5 * 60;

const parsePatientBedTime = (bedTime: string | null | undefined): { hours: number; minutes: number; seconds: number } => {
  const raw = (bedTime && String(bedTime).trim()) || DEFAULT_BED_TIME;
  const parts = raw.split(':').map((p) => parseInt(p, 10) || 0);
  return { hours: parts[0] ?? 23, minutes: parts[1] ?? 0, seconds: parts[2] ?? 0 };
};

const getNightWindowContaining = (ts: Date, bedTime: string | null | undefined): { start: Date; end: Date } | null => {
  const { hours, minutes, seconds } = parsePatientBedTime(bedTime);
  const base = new Date(ts);
  base.setHours(hours, minutes, seconds, 0);
  for (const dayOffset of [0, -1, 1]) {
    const start = new Date(base);
    start.setDate(start.getDate() + dayOffset);
    const end = addMinutes(start, NIGHT_DURATION_MINUTES);
    if (ts.getTime() >= start.getTime() && ts.getTime() <= end.getTime()) {
      return { start, end };
    }
  }
  return null;
};

/** Most recent patient night window (bed_time → bed_time + 8.5h). Same as server/sleepHelpers.js. */
const getLastNightWindow = (
  referenceTime: Date,
  bedTime: string | null | undefined
): { start: Date; end: Date } | null => {
  const containing = getNightWindowContaining(referenceTime, bedTime);
  if (containing) return containing;

  const { hours, minutes, seconds } = parsePatientBedTime(bedTime);
  const nowMs = referenceTime.getTime();

  for (let dayOffset = 0; dayOffset >= -14; dayOffset -= 1) {
    const start = new Date(referenceTime);
    start.setHours(hours, minutes, seconds, 0);
    start.setDate(start.getDate() + dayOffset);
    const end = addMinutes(start, NIGHT_DURATION_MINUTES);
    if (end.getTime() <= nowMs) {
      return { start, end };
    }
  }

  return null;
};

const isInNightWindow = (ts: Date, bedTime: string | null | undefined): boolean =>
  getNightWindowContaining(ts, bedTime) != null;

type SleepStageStat = { minutes: number; percent: number };
type SleepStageSummary = {
  awake: SleepStageStat;
  rem: SleepStageStat;
  light: SleepStageStat;
  deep: SleepStageStat;
};

type ActivityState = 'still' | 'walking' | 'jogging' | 'sleep';

type ActivityPoint = {
  tsMs: number;
  activity: ActivityState | null;
  rawActivity: string | null;
  rawIntensity: string | number | null;
};

type ActivitySegment = {
  startMs: number;
  endMs: number;
  activity: ActivityState | null;
  rawActivity: string | null;
  rawIntensity: string | number | null;
};

type ActivityIntensityLevel = 'sedentary' | 'light' | 'moderate' | 'vigorous';

const buildSleepSeriesFromRows = (
  rows: any[],
  getSleepStageValue: (value: unknown) => number | null
): GraphData[] => {
  const out: GraphData[] = [];
  const byMinute = new Map<string, GraphData>();
  for (const r of rows) {
    const rawStage = r.activity ?? r.sleep_stage;
    const sleepValue = getSleepStageValue(rawStage);
    if (sleepValue == null) continue;
    const tsStr = r.timestamp ?? r.time ?? r.created_at ?? r.createdAt ?? r.ts;
    if (!tsStr) continue;
    const t = parseDbTimestamp(String(tsStr));
    const minuteKey = `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')} ${String(t.getHours()).padStart(2, '0')}:${String(t.getMinutes()).padStart(2, '0')}`;
    byMinute.set(minuteKey, {
      time: format(t, 'HH:mm'),
      value: sleepValue,
      tsMs: t.getTime(),
    });
  }
  for (const point of Array.from(byMinute.values())) {
    out.push(point);
  }
  return out.sort((a, b) => ((a as GraphData & { tsMs?: number }).tsMs ?? 0) - ((b as GraphData & { tsMs?: number }).tsMs ?? 0));
};

const computeSleepStageSummary = (points: Array<{ value: number }>): SleepStageSummary | null => {
  const counts = { awake: 0, rem: 0, light: 0, deep: 0 };
  for (const p of points) {
    if (p.value === 4) counts.awake += 1;
    else if (p.value === 3) counts.rem += 1;
    else if (p.value === 2) counts.light += 1;
    else if (p.value === 1) counts.deep += 1;
  }
  const total = counts.awake + counts.rem + counts.light + counts.deep;
  if (total === 0) return null;
  const pct = (n: number) => Math.round((n / total) * 100);
  return {
    awake: { minutes: counts.awake, percent: pct(counts.awake) },
    rem: { minutes: counts.rem, percent: pct(counts.rem) },
    light: { minutes: counts.light, percent: pct(counts.light) },
    deep: { minutes: counts.deep, percent: pct(counts.deep) },
  };
};

const SleepScoreMetric: React.FC<{ label: string; helpText: string }> = ({ label, helpText }) => (
  <div className="sleep-summary-metric sleep-score-help">
    <span>{label}</span>
    <span className="sleep-score-help-icon" aria-hidden="true">?</span>
    <span className="sleep-score-help-tooltip" role="tooltip">{helpText}</span>
  </div>
);

const mergeSleepPointsByMinute = (points: GraphData[]): GraphData[] => {
  const byMinute = new Map<string, GraphData>();
  for (const p of points) {
    const tsMs = (p as GraphData & { tsMs?: number }).tsMs;
    if (tsMs == null) continue;
    const t = new Date(tsMs);
    const minuteKey = `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')} ${String(t.getHours()).padStart(2, '0')}:${String(t.getMinutes()).padStart(2, '0')}`;
    byMinute.set(minuteKey, p);
  }
  return Array.from(byMinute.values()).sort(
    (a, b) => ((a as GraphData & { tsMs?: number }).tsMs ?? 0) - ((b as GraphData & { tsMs?: number }).tsMs ?? 0)
  );
};

const parseSleepStageFromActivity = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    if (value >= 0 && value <= 4) return value;
    return null;
  }
  if (typeof value === 'string') {
    const s = value.trim().toUpperCase();
    if (!s) return null;
    const stageMap: Record<string, number> = {
      MASK: 0,
      DEEP: 1,
      LIGHT: 2,
      REM: 3,
      WAKE: 4,
      AWAKE: 4,
    };
    if (stageMap[s] != null) return stageMap[s];
    const n = Number(value);
    if (Number.isFinite(n) && n >= 0 && n <= 4) return n;
  }
  return null;
};

const isSleepActivitySegment = (segment: Pick<ActivitySegment, 'activity' | 'rawActivity'>): boolean =>
  segment.activity === 'sleep' || parseSleepStageFromActivity(segment.rawActivity) != null;

const normalizeActivityState = (rawActivity: unknown, rawIntensity: unknown): ActivityState | null => {
  if (parseSleepStageFromActivity(rawActivity) != null) return 'sleep';

  if (typeof rawActivity === 'string') {
    const normalized = rawActivity.trim().toLowerCase();
    if (normalized.includes('jogg')) return 'jogging';
    if (normalized.includes('walk')) return 'walking';
    if (normalized.includes('still') || normalized.includes('rest') || normalized.includes('idle')) return 'still';
  }

  if (typeof rawIntensity === 'string') {
    const normalizedIntensity = rawIntensity.trim().toLowerCase();
    if (normalizedIntensity.includes('high') || normalizedIntensity.includes('vigorous') || normalizedIntensity.includes('jogg')) return 'jogging';
    if (normalizedIntensity.includes('medium') || normalizedIntensity.includes('walk') || normalizedIntensity.includes('moderate')) return 'walking';
    if (normalizedIntensity.includes('low') || normalizedIntensity.includes('still') || normalizedIntensity.includes('rest')) return 'still';
  }

  if (rawIntensity != null && rawIntensity !== '') {
    const numericIntensity = Number(rawIntensity);
    if (Number.isFinite(numericIntensity)) {
      if (numericIntensity >= 2) return 'jogging';
      if (numericIntensity >= 1) return 'walking';
      if (numericIntensity >= 0) return 'still';
    }
  }

  return null;
};

const ACTIVITY_COLORS: Record<ActivityState, string> = {
  still: '#0072B2',
  walking: '#009E73',
  jogging: '#D55E00',
  sleep: '#7B1FA2',
};
const ACTIVITY_INTENSITY_COLORS = {
  walkingLight: '#A1D99B',
  joggingSedentary: '#FDBE85',
  joggingModerate: '#F16913',
  joggingVigorous: '#A63603',
} as const;
const ACTIVITY_NO_DATA_COLOR = '#c4c9d1';
const ACTIVITY_TIMELINE_LEFT_GUTTER_PX = 75;
const ACTIVITY_TIMELINE_RIGHT_GUTTER_PX = 0;
/** Header space reserved when Reset Zoom is shown so the color legend stays visible */
const ACTIVITY_TIMELINE_LEGEND_SHIFT_WHEN_ZOOMED_PX = 100;

const normalizeActivityIntensityLevel = (rawIntensity: unknown): ActivityIntensityLevel | null => {
  if (typeof rawIntensity === 'string') {
    const normalized = rawIntensity.trim().toLowerCase();
    if (!normalized) return null;
    if (normalized.includes('sedentary') || normalized.includes('sedetary') || normalized === '0') return 'sedentary';
    if (normalized.includes('light') || normalized.includes('low')) return 'light';
    if (normalized.includes('moderate') || normalized.includes('medium')) return 'moderate';
    if (normalized.includes('vigorous') || normalized.includes('high')) return 'vigorous';
    const numericFromString = Number(normalized);
    if (Number.isFinite(numericFromString)) {
      if (numericFromString <= 0) return 'sedentary';
      if (numericFromString <= 1) return 'light';
      if (numericFromString <= 2) return 'moderate';
      return 'vigorous';
    }
    return null;
  }
  if (rawIntensity != null && rawIntensity !== '') {
    const numeric = Number(rawIntensity);
    if (Number.isFinite(numeric)) {
      if (numeric <= 0) return 'sedentary';
      if (numeric <= 1) return 'light';
      if (numeric <= 2) return 'moderate';
      return 'vigorous';
    }
  }
  return null;
};

const getActivitySegmentColor = (segment: ActivitySegment): string => {
  if (isSleepActivitySegment(segment)) {
    return ACTIVITY_COLORS.sleep;
  }
  if (!segment.activity) return ACTIVITY_NO_DATA_COLOR;
  if (segment.activity === 'still') return ACTIVITY_COLORS.still;

  const intensityLevel = normalizeActivityIntensityLevel(segment.rawIntensity);
  if (segment.activity === 'walking' && intensityLevel === 'light') {
    return ACTIVITY_INTENSITY_COLORS.walkingLight;
  }
  if (segment.activity === 'jogging' && intensityLevel === 'sedentary') {
    return ACTIVITY_INTENSITY_COLORS.joggingSedentary;
  }
  if (segment.activity === 'jogging' && intensityLevel === 'moderate') {
    return ACTIVITY_INTENSITY_COLORS.joggingModerate;
  }
  if (segment.activity === 'jogging' && intensityLevel === 'vigorous') {
    return ACTIVITY_INTENSITY_COLORS.joggingVigorous;
  }
  return ACTIVITY_COLORS[segment.activity];
};

/**
 * DEMO VITALS SNAPSHOT TYPE - Medical Data Point Structure
 * 
 * Represents a complete set of vital signs measurements at a specific timestamp.
 * Used for capturing the state of all vital signs when a critical event occurs,
 * allowing medical staff to see the full context of a patient's condition.
 */
type DemoVitalsSnapshot = {
  timestamp: string;                    // ISO timestamp of the measurement
  temperature?: number | null;          // Body temperature in Celsius
  breathing?: number | null;           // Respiratory rate in breaths per minute
  heart_rate?: number | null;          // Heart rate in beats per minute
  heart_rate_variability?: number | null; // HRV in milliseconds
  systolic?: number | null;            // Systolic blood pressure in mmHg
  diastolic?: number | null;           // Diastolic blood pressure in mmHg
  spo2?: number | null;                // Blood oxygen saturation percentage
};

/** HRV values above this are invalid and excluded from graph and stats. */
const MAX_HRV_MS = 200;

const normalizeHrvForDisplay = (raw: unknown): number | null => {
  if (raw == null || raw === '' || raw === -1) return null;
  const v = typeof raw === 'number' ? raw : Number(raw);
  if (!Number.isFinite(v) || v > MAX_HRV_MS) return null;
  return v;
};

const pickHrvFromRow = (r: any): number | null =>
  normalizeHrvForDisplay(r.hrv ?? r.hrv_ms ?? r.hrv_bpm ?? r.heart_rate_variability);

/**
 * GRAPH DATA INTERFACE - Chart Data Point Structure
 * 
 * Standard data structure for time-series data points used in medical charts.
 * Each point represents a measurement at a specific time for visualization.
 * 
 * `tsMs` is an optional absolute timestamp (milliseconds since epoch) that,
 * when present, is preferred for time alignment instead of parsing `time`.
 * This is especially important for long ranges such as "7 Days" and "30 Days" 
 * where `time` may be formatted as "dd MMM HH:mm" or "dd MMM".
 */
interface GraphData {
  time: string;        // Display label for the data point (e.g., "HH:mm", "dd MMM HH:mm")
  value: number;       // Numeric value of the measurement
  confidence?: number; // Optional confidence value (0-100)
  min?: number;
  max?: number;
  q1?:number;
  q3?:number;
  q_15?:number;
  q_85?:number;
  tsMs?: number;       // Optional absolute timestamp (ms since epoch) for precise alignment
  endTsMs?: number;    // Last source reading included in an aggregated point
}

/**
 * DENSE POINT TYPE - Interpolated Data Point Structure
 * 
 * Represents a data point in a densified time series, where additional points
 * are interpolated between actual measurements to create smoother visualizations.
 */
type DensePoint = { t: number; value: number | null; confidence?: number; min?: number; max?: number;q1?:number,q3?:number,q_15?:number, q_85?:number; endTsMs?: number };

// Like densifySeriesAligned but breaks the line when time gap between points exceeds maxGapMinutes
const densifyAlignedWithGaps = (
  base: GraphData[],
  pointsBetween: number,
  startLabel: string,
  maxGapMinutes: number,
  options: { startDate?: Date; rangeMinutes?: number } = {}
): DensePoint[] => {
  const toMinutes = (hm: string) => {
    const [h, m] = (hm || '00:00').split(':').map(x => parseInt(x, 10) || 0);
    return h * 60 + m;
  };
  const startMs = options.startDate?.getTime();
  const startMin = toMinutes(startLabel);
  const windowMinutes = options.rangeMinutes;
  const isOutsideWindow = (t: number) =>
    windowMinutes != null && (t < -0.0001 || t > windowMinutes + 0.0001);

  const offsetOf = (point: GraphData) => {
    const hm = point.time;
    const tsMs = (point as any).tsMs as number | undefined;

    // Prefer absolute timestamps when available to avoid wrapping artifacts
    if (startMs != null) {
      if (tsMs != null) {
        return (tsMs - startMs) / 60000;
      }
      const parsed = Date.parse(hm);
      if (!Number.isNaN(parsed)) {
        return (parsed - startMs) / 60000;
      }

      // Fall back to combining the provided time with the start date's day
      const baseDate = new Date(startMs);
      const [h, m] = (hm || '00:00').split(':').map(x => parseInt(x, 10) || 0);
      baseDate.setHours(h, m, 0, 0);
      let diff = (baseDate.getTime() - startMs) / 60000;

      // Only wrap across midnight when the wrapped position still fits in the current window
      // AND the point is actually within the window after wrapping
      if (diff < -0.5 && windowMinutes != null) {
        const wrappedDiff = diff + 24 * 60;
        // Only wrap if the wrapped position is within the window (0 to windowMinutes)
        if (wrappedDiff >= -0.0001 && wrappedDiff <= windowMinutes + 0.0001) {
          diff = wrappedDiff;
        }
        // If wrapped position is still outside, don't wrap (point will be filtered out)
      }
      return diff;
    }

    // Legacy fallback using just the HH:mm label
    const mm = toMinutes(hm);
    let diff = mm - startMin;
    if (diff < 0) diff += 24 * 60;
    return diff;
  };
  const out: DensePoint[] = [];
  if (!base || base.length === 0) return out;
  const pushNullBreaker = (t: number) => {
    out.push({ t: t + 0.0001, value: null });
  };
  for (let i = 0; i < base.length; i++) {
    const cur = base[i];
    const tCur = offsetOf(cur);
    if (tCur == null || isOutsideWindow(tCur)) continue;
    out.push({ t: tCur, value: cur.value, confidence: cur.confidence, min: cur.min, max: cur.max, q1:cur.q1, q3:cur.q3, q_15:cur.q_15, q_85:cur.q_85, endTsMs: cur.endTsMs });
    if (i < base.length - 1) {
      const next = base[i + 1];
      const tNext = offsetOf(next);
      if (tNext == null || isOutsideWindow(tNext)) {
        pushNullBreaker(tCur);
        continue;
      }
      const gap = tNext - tCur >= 0 ? (tNext - tCur) : (tNext - tCur + 24 * 60);
      if (gap > maxGapMinutes) {
        // break the line before next point
        pushNullBreaker(tCur);
      } else {
        // densify within the small gap
        const steps = pointsBetween + 1;
        for (let k = 1; k < steps; k++) {
          const ratio = k / steps;
          const t = tCur + (gap) * ratio;
          const value = cur.value + (next.value - cur.value) * ratio;
          out.push({ 
            t, 
            value, 
            confidence: cur.confidence,
            min: cur.min,     
            max: cur.max,
            q1:cur.q1,
            q3:cur.q3,
            q_15:cur.q_15,
            q_85:cur.q_85
          });
        }
      }
    }
  }
  return out;
};

const resolveVitalConfidence = (row: any, confidenceField?: string): number | undefined => {
  if (!confidenceField) return undefined;
  const raw =
    confidenceField === 'blood_oxygen_saturation_confidence'
      ? (row.spo2_confidence ?? row.blood_oxygen_saturation_confidence)
      : row[confidenceField];
  return typeof raw === 'number' ? raw : undefined;
};

const filterGraphDataByConfidence = <T extends { confidence?: number }>(data: T[], isDemo: boolean): T[] => {
  if (isDemo) return data;
  return data.filter(point => {
    if (point.confidence == null || point.confidence === undefined) return true;
    const conf = Number(point.confidence);
    if (isNaN(conf)) return true;
    return conf > 0 && conf !== -1;
  });
};

// Login Component
type UserProfile = { name: string; email: string; language: string; passwordLength?: number; role?: string };

const LoginPage: React.FC<{ onLogin: (user: UserProfile) => void; onGoToRegister: () => void; onGoToForgotPassword: () => void }> = ({ onLogin, onGoToRegister, onGoToForgotPassword }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [recaptchaToken, setRecaptchaToken] = useState('');
  const [captchaRequired, setCaptchaRequired] = useState(CAPTCHA_ENABLED);
  const [serverDebugMode, setServerDebugMode] = useState(DEBUG_MODE);
  const [captchaRenderVersion, setCaptchaRenderVersion] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const recaptchaContainerRef = useRef<HTMLDivElement | null>(null);
  const recaptchaWidgetIdRef = useRef<number | null>(null);
  const captchaActive = shouldEnforceCaptcha(captchaRequired, serverDebugMode);
  const recaptchaSiteKey = captchaActive
    ? String(process.env.REACT_APP_RECAPTCHA_SITE_KEY || '').trim()
    : '';

  useEffect(() => {
    if (DEBUG_MODE) {
      setServerDebugMode(true);
      setCaptchaRequired(false);
      return;
    }

    fetch(apiUrl('/config'))
      .then((response) => response.json())
      .then((cfg) => {
        if (cfg?.debugMode) {
          setServerDebugMode(true);
          setCaptchaRequired(false);
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const msg = localStorage.getItem('xv_verify_notice');
    if (msg) {
      setNotice(msg);
      localStorage.removeItem('xv_verify_notice');
    }
    
    const resetNotice = localStorage.getItem('xv_reset_notice');
    if (resetNotice) {
      setNotice(resetNotice);
      localStorage.removeItem('xv_reset_notice');
    }
  }, []);

  // Global cleanup for medication tooltips
  useEffect(() => {
    const handleGlobalClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      // If click is not on a medication element, remove all tooltips
      if (!target.closest('[data-medication-item]')) {
        const existingTooltips = document.querySelectorAll('[id^="medication-tooltip"]');
        existingTooltips.forEach(tooltip => {
          (tooltip as HTMLElement).style.opacity = '0';
          setTimeout(() => tooltip.remove(), 200);
        });
      }
    };

    const handleGlobalMouseLeave = () => {
      // Remove all tooltips when mouse leaves the window
      const existingTooltips = document.querySelectorAll('[id^="medication-tooltip"]');
      existingTooltips.forEach(tooltip => {
        (tooltip as HTMLElement).style.opacity = '0';
        setTimeout(() => tooltip.remove(), 200);
      });
    };

    document.addEventListener('click', handleGlobalClick);
    document.addEventListener('mouseleave', handleGlobalMouseLeave);

    return () => {
      document.removeEventListener('click', handleGlobalClick);
      document.removeEventListener('mouseleave', handleGlobalMouseLeave);
    };
  }, []);

  const resetRecaptcha = useCallback((hardReset = false) => {
    setRecaptchaToken('');
    if (hardReset) {
      recaptchaWidgetIdRef.current = null;
      if (recaptchaContainerRef.current) {
        recaptchaContainerRef.current.innerHTML = '';
      }
      return;
    }

    if (window.grecaptcha && recaptchaWidgetIdRef.current != null) {
      window.grecaptcha.reset(recaptchaWidgetIdRef.current);
    }
  }, []);

  useEffect(() => {
    if (!captchaActive) {
      return;
    }

    const renderRecaptcha = () => {
      if (!window.grecaptcha || !recaptchaContainerRef.current) {
        return;
      }

      if (!recaptchaSiteKey) {
        return;
      }

      // Always recreate widget for each required-challenge cycle.
      recaptchaWidgetIdRef.current = null;
      recaptchaContainerRef.current.innerHTML = '';
      setRecaptchaToken('');

      recaptchaWidgetIdRef.current = window.grecaptcha.render(recaptchaContainerRef.current, {
        sitekey: recaptchaSiteKey,
        callback: (token: string) => setRecaptchaToken(token),
        'expired-callback': () => setRecaptchaToken(''),
        'error-callback': () => setRecaptchaToken('')
      });
    };

    const waitForRecaptchaAndRender = () => {
      if (window.grecaptcha) {
        window.grecaptcha.ready(renderRecaptcha);
        return;
      }
      window.setTimeout(waitForRecaptchaAndRender, 100);
    };

    const existingScript = document.getElementById('google-recaptcha-script') as HTMLScriptElement | null;
    const requiredScriptSrc = 'https://www.google.com/recaptcha/api.js?render=explicit&hl=en';
    if (window.grecaptcha) {
      window.grecaptcha.ready(renderRecaptcha);
      return;
    }

    if (existingScript) {
      if (existingScript.src !== requiredScriptSrc) {
        existingScript.remove();
      } else {
        waitForRecaptchaAndRender();
        return;
      }
    }

    if (document.getElementById('google-recaptcha-script')) {
      waitForRecaptchaAndRender();
      return;
    }

    const script = document.createElement('script');
    script.id = 'google-recaptcha-script';
    script.src = requiredScriptSrc;
    script.async = true;
    script.defer = true;
    script.addEventListener('load', waitForRecaptchaAndRender, { once: true });
    document.body.appendChild(script);
  }, [captchaActive, recaptchaSiteKey, captchaRenderVersion]);

  const applyCaptchaFromResponse = (data: any) => {
    if (!shouldEnforceCaptcha(true, serverDebugMode)) {
      return;
    }
    if (data?.captchaRequired) {
      setCaptchaRequired(true);
      setCaptchaRenderVersion((v) => v + 1);
      return;
    }

    setCaptchaRequired(false);
    resetRecaptcha(true);
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const enforceCaptcha = shouldEnforceCaptcha(captchaRequired, serverDebugMode);
    if (enforceCaptcha && !recaptchaSiteKey) {
      setError('reCAPTCHA site key is missing. Please configure REACT_APP_RECAPTCHA_SITE_KEY.');
      return;
    }
    if (enforceCaptcha && !recaptchaToken) {
      setError('Please complete Google reCAPTCHA verification.');
      return;
    }
    try {
      console.log('[Login] Starting database login flow');

      const loginUrl = apiUrl('/users/login');
      const loginPayload = {
        email: email,
        password: password,
        recaptchaToken: enforceCaptcha ? recaptchaToken : undefined
      };

      let responseOk = false;
      let responseStatus = 0;
      let data: any = {};

      try {
        // Prefer standard fetch; fallback to native HTTP on iOS/Android when WebView networking fails.
        const response = await fetch(loginUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(loginPayload)
        });

        responseOk = response.ok;
        responseStatus = response.status;
        data = await response.json();
      } catch (fetchError) {
        if (!Capacitor.isNativePlatform()) {
          throw fetchError;
        }

        console.warn('[Login] fetch failed on native platform, retrying with CapacitorHttp');
        const nativeResponse = await CapacitorHttp.request({
          url: loginUrl,
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          data: loginPayload
        });

        responseStatus = nativeResponse.status;
        responseOk = nativeResponse.status >= 200 && nativeResponse.status < 300;
        if (typeof nativeResponse.data === 'string') {
          try {
            data = JSON.parse(nativeResponse.data);
          } catch {
            data = {};
          }
        } else {
          data = nativeResponse.data || {};
        }
      }

      applyCaptchaFromResponse(data);

      if (!responseOk) {
        if (responseStatus === 403) {
          setError(
            sanitizeAuthError(
              data.error || data.message,
              'Please verify your email before logging in',
              serverDebugMode
            )
          );
        } else {
          setError(sanitizeAuthError(data.error || data.message, 'Login failed', serverDebugMode));
        }
        return;
      }

      // Login successful
      const user = data.user;
      console.log('[Login] Database login success for:', user.email);
      
      // Create user profile
      const profile: UserProfile = {
        name: user.email.split('@')[0],
        email: user.email,
        language: 'English',
        passwordLength: password.length,
        role: user.role,
      };

      // Persist session/token before onLogin so follow-up API calls (e.g. role fetch) are authenticated.
      try {
        localStorage.setItem('xv_session', JSON.stringify(profile));
        localStorage.setItem('currentUserEmail', user.email);
        if (data?.token) {
          localStorage.setItem('xv_auth_token', data.token);
        }
        if (data?.pat_id != null) {
          localStorage.setItem('xv_pat_id', String(data.pat_id));
        } else {
          localStorage.removeItem('xv_pat_id');
        }
        if (data?.doc_id != null) {
          localStorage.setItem('xv_doc_id', String(data.doc_id));
        } else {
          localStorage.removeItem('xv_doc_id');
        }
      } catch (_err) {
        // ignore storage errors
      }
      setCaptchaRequired(false);
      resetRecaptcha(true);

      onLogin(profile);
    } catch (error: any) {
      console.error('[Login] Database login error:', error);
      setError('Network error. Check your connection');
    }
  };

  const handleRegister = () => {
    onGoToRegister();
  };

  const handleForgotPassword = () => {
    onGoToForgotPassword();
  };

  return (
    <div className="login-page">
      {/* Header */}
      <header className="login-header">
        <h1>Login</h1>
      </header>

      {/* Main Content */}
      <div className="login-container">
        <div className="login-card">
          <form onSubmit={handleLogin} className="login-form">
            {notice && (
              <div style={{ color: '#1976d2', fontSize: '0.95rem' }}>{notice}</div>
            )}
            {/* Email Field */}
            <div className="input-group">
              <div className="input-icon">✉</div>
              <div className="input-content">
                <label className="input-label">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                  }}
                  className="input-field"
                  placeholder=""
                  required
                />
              </div>
            </div>

            {/* Password Field */}
            <div className="input-group">
              <div className="input-icon">🔑</div>
              <div className="input-content">
                <label className="input-label">Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input-field"
                  placeholder=""
                  required
                />
              </div>
            </div>

            {captchaActive && recaptchaSiteKey && (
              <div style={{ display: 'flex', justifyContent: 'center', minHeight: '78px', margin: '8px 0' }}>
                <div key={captchaRenderVersion} ref={recaptchaContainerRef} />
              </div>
            )}

            {/* Action Buttons */}
            <div className="button-group">
              <button type="submit" className="btn btn-login">
                Login
              </button>
              <button type="button" className="btn btn-register" onClick={handleRegister}>
                Register
              </button>
            </div>

            {error && (
              <div style={{ color: '#d32f2f', fontSize: '0.9rem' }}>{error}</div>
            )}

            {/* Forgot Password Link */}
            <div className="forgot-password">
              <button type="button" className="forgot-link" onClick={handleForgotPassword}>
                Forgot Your Password?
              </button>
            </div>

            {/* Demo Use Button - Small and discrete */}
            <div className="demo-use" style={{ marginTop: '8px', textAlign: 'center' }}>
              <button 
                type="button" 
                className="demo-button" 
                onClick={() => {
                  const demoProfile: UserProfile = { 
                    name: 'Demo User', 
                    email: 'demo@example.com', 
                    language: 'English' 
                  };
                  try {
                    // Persist demo session so refresh keeps the user logged in (no auth token needed)
                    localStorage.setItem('xv_session', JSON.stringify(demoProfile));
                    localStorage.setItem('currentUserEmail', demoProfile.email);
                    localStorage.removeItem('xv_auth_token');
                    localStorage.removeItem('xv_pat_id');
                    localStorage.removeItem('xv_doc_id');
                  } catch (_err) {
                    // ignore storage errors
                  }
                  onLogin(demoProfile);
                }}
                style={{
                  padding: '6px 12px',
                  backgroundColor: 'transparent',
                  color: '#9C27B0',
                  border: '1px solid #9C27B0',
                  borderRadius: '4px',
                  fontSize: '12px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  display: 'inline-block'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor = '#9C27B0';
                  e.currentTarget.style.color = 'white';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = '#9C27B0';
                }}
              >
                🎯 Demo Use
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// Register Component
const RegisterPage: React.FC<{ onGoToLogin: () => void; onRegisterSuccess: () => void }> = ({ onGoToLogin, onRegisterSuccess }) => {
  const [name, setName] = useState('');
  const [surname, setSurname] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState('');
  const [captchaRequired, setCaptchaRequired] = useState(CAPTCHA_ENABLED);
  const [serverDebugMode, setServerDebugMode] = useState(DEBUG_MODE);
  const [captchaRenderVersion, setCaptchaRenderVersion] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const recaptchaContainerRef = useRef<HTMLDivElement | null>(null);
  const recaptchaWidgetIdRef = useRef<number | null>(null);
  const captchaActive = shouldEnforceCaptcha(captchaRequired, serverDebugMode);
  const recaptchaSiteKey = captchaActive
    ? String(process.env.REACT_APP_RECAPTCHA_SITE_KEY || '').trim()
    : '';

  const resetRecaptcha = useCallback((hardReset = false) => {
    setRecaptchaToken('');
    if (hardReset) {
      recaptchaWidgetIdRef.current = null;
      if (recaptchaContainerRef.current) {
        recaptchaContainerRef.current.innerHTML = '';
      }
      return;
    }

    if (window.grecaptcha && recaptchaWidgetIdRef.current != null) {
      window.grecaptcha.reset(recaptchaWidgetIdRef.current);
    }
  }, []);

  useEffect(() => {
    if (DEBUG_MODE) {
      setServerDebugMode(true);
      setCaptchaRequired(false);
      return;
    }

    fetch(apiUrl('/config'))
      .then((response) => response.json())
      .then((cfg) => {
        if (cfg?.debugMode) {
          setServerDebugMode(true);
          setCaptchaRequired(false);
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!captchaActive) {
      return;
    }

    const renderRecaptcha = () => {
      if (!window.grecaptcha || !recaptchaContainerRef.current) {
        return;
      }

      if (!recaptchaSiteKey) {
        return;
      }

      // Always recreate widget for each required-challenge cycle.
      recaptchaWidgetIdRef.current = null;
      recaptchaContainerRef.current.innerHTML = '';
      setRecaptchaToken('');

      recaptchaWidgetIdRef.current = window.grecaptcha.render(recaptchaContainerRef.current, {
        sitekey: recaptchaSiteKey,
        callback: (token: string) => setRecaptchaToken(token),
        'expired-callback': () => setRecaptchaToken(''),
        'error-callback': () => setRecaptchaToken('')
      });
    };

    const waitForRecaptchaAndRender = () => {
      if (window.grecaptcha) {
        window.grecaptcha.ready(renderRecaptcha);
        return;
      }
      window.setTimeout(waitForRecaptchaAndRender, 100);
    };

    const existingScript = document.getElementById('google-recaptcha-script') as HTMLScriptElement | null;
    const requiredScriptSrc = 'https://www.google.com/recaptcha/api.js?render=explicit&hl=en';
    if (window.grecaptcha) {
      window.grecaptcha.ready(renderRecaptcha);
      return;
    }

    if (existingScript) {
      if (existingScript.src !== requiredScriptSrc) {
        existingScript.remove();
      } else {
        waitForRecaptchaAndRender();
        return;
      }
    }

    if (document.getElementById('google-recaptcha-script')) {
      waitForRecaptchaAndRender();
      return;
    }

    const script = document.createElement('script');
    script.id = 'google-recaptcha-script';
    script.src = requiredScriptSrc;
    script.async = true;
    script.defer = true;
    script.addEventListener('load', waitForRecaptchaAndRender, { once: true });
    document.body.appendChild(script);
  }, [captchaActive, recaptchaSiteKey, captchaRenderVersion]);

  const requireCaptchaForNextAttempt = () => {
    if (!shouldEnforceCaptcha(true, serverDebugMode)) {
      return;
    }
    setCaptchaRequired(true);
    setCaptchaRenderVersion((v) => v + 1);
    resetRecaptcha(true);
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setInfo(null);
    if (!name || !surname || !email || !password || !confirmPassword || !agreeToTerms) {
      setError('Please fill in all fields and agree to terms');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    const enforceCaptcha = shouldEnforceCaptcha(captchaRequired, serverDebugMode);
    if (enforceCaptcha && !recaptchaSiteKey) {
      setError('reCAPTCHA site key is missing. Please configure REACT_APP_RECAPTCHA_SITE_KEY.');
      return;
    }
    if (enforceCaptcha && !recaptchaToken) {
      setError('Please complete Google reCAPTCHA verification.');
      return;
    }
    
    try {


      // Add doctor to PostgreSQL database
      const response = await fetch(apiUrl('/doctors'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          firstName: name,
          lastName: surname,
          email: email,
          password: password,
          recaptchaToken: enforceCaptcha ? recaptchaToken : undefined
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        setError(sanitizeAuthError(errorData.error, 'Registration failed', serverDebugMode));
        requireCaptchaForNextAttempt();
        return;
      }

      const doctorData = await response.json();
      console.log('Doctor added to database:', doctorData);
      
      // Store email for role checking after verification
      localStorage.setItem('currentUserEmail', email);
      localStorage.setItem('xv_verify_notice', 'Registration successful. Please check your email (and Spam folder) to verify your account, then log in.');
      setCaptchaRequired(false);
      resetRecaptcha(true);
      onGoToLogin();
      
    } catch (error: any) {
      console.error('Registration error:', error);
      setError('Network error. Check your connection');
      requireCaptchaForNextAttempt();
    }
  };

  const handleTermsClick = () => {
    // Handle terms and conditions click
    console.log('Terms and conditions clicked');
  };

  return (
    <div className="register-page">
      {/* Header */}
      <header className="register-header">
        <button className="back-button" onClick={onGoToLogin}>
          ←
        </button>
        <h1>Register</h1>
      </header>

      {/* Main Content */}
      <div className="register-container">
        <div className="register-card">
          <form onSubmit={handleRegister} className="register-form">
            {/* Name Field */}
            <div className="input-group">
              <div className="input-icon">👤</div>
              <div className="input-content">
                <label className="input-label">Name</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="input-field"
                  placeholder=""
                  required
                />
              </div>
            </div>

            {/* Surname Field */}
            <div className="input-group">
              <div className="input-icon">👤</div>
              <div className="input-content">
                <label className="input-label">Surname</label>
                <input
                  type="text"
                  value={surname}
                  onChange={(e) => setSurname(e.target.value)}
                  className="input-field"
                  placeholder=""
                  required
                />
              </div>
            </div>

            {/* Email Field */}
            <div className="input-group">
              <div className="input-icon">✉</div>
              <div className="input-content">
                <label className="input-label">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                  }}
                  className="input-field"
                  placeholder=""
                  required
                />
              </div>
            </div>

            {/* Password Field */}
            <div className="input-group">
              <div className="input-icon">🔑</div>
              <div className="input-content">
                <label className="input-label">Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input-field"
                  placeholder=""
                  required
                />
              </div>
            </div>

            {/* Confirm Password Field */}
            <div className="input-group">
              <div className="input-icon">🔑</div>
              <div className="input-content">
                <label className="input-label">Confirm Password</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="input-field"
                  placeholder=""
                  required
                />
              </div>
            </div>

            {/* Terms and Conditions */}
            <div className="terms-group">
              <input
                type="checkbox"
                id="terms"
                checked={agreeToTerms}
                onChange={(e) => setAgreeToTerms(e.target.checked)}
                className="terms-checkbox"
              />
              <label htmlFor="terms" className="terms-label">
                Check here to indicate that you have read and agree to the{' '}
                <button type="button" className="terms-link" onClick={handleTermsClick}>
                  terms and conditions
                </button>{' '}
                of the Traqbeat project.
              </label>
            </div>

            {captchaActive && recaptchaSiteKey && (
              <div style={{ display: 'flex', justifyContent: 'center', minHeight: '78px', margin: '8px 0' }}>
                <div key={captchaRenderVersion} ref={recaptchaContainerRef} />
              </div>
            )}

            {/* Register Button */}
            <div className="register-button-container">
              <button type="submit" className="btn btn-register-submit">
                Register
              </button>
            </div>
            {error && <div className="form-error">{error}</div>}
            {info && <div style={{ color: '#1976d2', fontSize: '0.95rem' }}>{info}</div>}
          </form>
        </div>
      </div>
    </div>
  );
};

// Email Verification Component
const ForgotPasswordPage: React.FC<{ onGoToLogin: () => void }> = ({ onGoToLogin }) => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    
    if (!email) {
      setError('Please enter your email address');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await fetch(apiUrl('/users/forgot-password'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email })
      });

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 404) {
          setError('Email not found');
        } else {
          setError(data.error || 'Failed to send reset email');
        }
        return;
      }

      // Store success message and go to login
      localStorage.setItem('xv_reset_notice', 'Password reset email sent! Please check your email (and Spam folder).');
      onGoToLogin();
    } catch (error) {
      console.error('Forgot password error:', error);
      setError('Network error. Check your connection');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ 
      minHeight: '100vh', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center',
      backgroundColor: '#f5f5f5',
      fontFamily: 'Arial, sans-serif'
    }}>
      <div style={{
        backgroundColor: 'white',
        padding: '40px',
        borderRadius: '8px',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
        width: '100%',
        maxWidth: '400px'
      }}>
        <h2 style={{ 
          textAlign: 'center', 
          marginBottom: '30px',
          color: '#1976d2'
        }}>
          Forgot Password
        </h2>
        
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '20px' }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '8px',
              fontWeight: 'bold',
              color: '#333'
            }}>
              Email Address
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              style={{
                width: '100%',
                padding: '12px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '16px',
                boxSizing: 'border-box'
              }}
              disabled={loading}
            />
          </div>
          
          {error && (
            <div style={{ 
              color: '#d32f2f', 
              marginBottom: '20px',
              padding: '10px',
              backgroundColor: '#ffebee',
              borderRadius: '4px',
              border: '1px solid #ffcdd2'
            }}>
              {error}
            </div>
          )}
          
          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%',
              padding: '12px',
              backgroundColor: loading ? '#ccc' : '#1976d2',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              fontSize: '16px',
              cursor: loading ? 'not-allowed' : 'pointer',
              marginBottom: '20px'
            }}
          >
            {loading ? 'Sending...' : 'Send Reset Email'}
          </button>
        </form>
        
        <div style={{ textAlign: 'center' }}>
          <button
            onClick={onGoToLogin}
            style={{
              background: 'none',
              border: 'none',
              color: '#1976d2',
              cursor: 'pointer',
              textDecoration: 'underline',
              fontSize: '14px'
            }}
          >
            Back to Login
          </button>
        </div>
      </div>
    </div>
  );
};

const ResetPasswordPage: React.FC<{ onGoToLogin: () => void }> = ({ onGoToLogin }) => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');

  useEffect(() => {
    // Get email from URL
    const urlParams = new URLSearchParams(window.location.search);
    const emailParam = urlParams.get('email');
    if (emailParam) {
      setEmail(emailParam);
    } else {
      setError('Invalid reset link');
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    
    if (!newPassword || !confirmPassword) {
      setError('Please fill in all fields');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await fetch(apiUrl('/users/reset-password'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          email: email,
          newPassword: newPassword
        })
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Failed to reset password');
        return;
      }

      setSuccess('Password updated successfully! You can now log in with your new password.');
    } catch (error) {
      console.error('Reset password error:', error);
      setError('Network error. Check your connection');
    } finally {
      setLoading(false);
    }
  };

  if (!email) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        backgroundColor: '#f5f5f5',
        fontFamily: 'Arial, sans-serif'
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: '40px',
          borderRadius: '8px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          textAlign: 'center'
        }}>
          <h2 style={{ color: '#d32f2f', marginBottom: '20px' }}>Invalid Reset Link</h2>
          <p style={{ marginBottom: '20px' }}>This password reset link is invalid or has expired.</p>
          <button
            onClick={onGoToLogin}
            style={{
              padding: '10px 20px',
              backgroundColor: '#1976d2',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ 
      minHeight: '100vh', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center',
      backgroundColor: '#f5f5f5',
      fontFamily: 'Arial, sans-serif'
    }}>
      <div style={{
        backgroundColor: 'white',
        padding: '40px',
        borderRadius: '8px',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
        width: '100%',
        maxWidth: '400px'
      }}>
        <h2 style={{ 
          textAlign: 'center', 
          marginBottom: '30px',
          color: '#1976d2'
        }}>
          Reset Password
        </h2>
        
        <p style={{ 
          textAlign: 'center', 
          marginBottom: '30px',
          color: '#666'
        }}>
          Enter your new password for: {email}
        </p>
        
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '20px' }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '8px',
              fontWeight: 'bold',
              color: '#333'
            }}>
              New Password
            </label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Enter new password"
              style={{
                width: '100%',
                padding: '12px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '16px',
                boxSizing: 'border-box'
              }}
              disabled={loading}
            />
          </div>
          
          <div style={{ marginBottom: '20px' }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '8px',
              fontWeight: 'bold',
              color: '#333'
            }}>
              Confirm New Password
            </label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm new password"
              style={{
                width: '100%',
                padding: '12px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '16px',
                boxSizing: 'border-box'
              }}
              disabled={loading}
            />
          </div>
          
          {error && (
            <div style={{ 
              color: '#d32f2f', 
              marginBottom: '20px',
              padding: '10px',
              backgroundColor: '#ffebee',
              borderRadius: '4px',
              border: '1px solid #ffcdd2'
            }}>
              {error}
            </div>
          )}
          
          {success && (
            <div style={{ 
              color: '#2e7d32', 
              marginBottom: '20px',
              padding: '10px',
              backgroundColor: '#e8f5e8',
              borderRadius: '4px',
              border: '1px solid #c8e6c9'
            }}>
              {success}
            </div>
          )}
          
          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%',
              padding: '12px',
              backgroundColor: loading ? '#ccc' : '#1976d2',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              fontSize: '16px',
              cursor: loading ? 'not-allowed' : 'pointer',
              marginBottom: '20px'
            }}
          >
            {loading ? 'Updating...' : 'Update Password'}
          </button>
        </form>
        
        <div style={{ textAlign: 'center' }}>
          <button
            onClick={onGoToLogin}
            style={{
              background: 'none',
              border: 'none',
              color: '#1976d2',
              cursor: 'pointer',
              textDecoration: 'underline',
              fontSize: '14px'
            }}
          >
            Back to Login
          </button>
        </div>
      </div>
    </div>
  );
};

const PatientResetPasswordPage: React.FC<{ onGoToLogin: () => void }> = ({ onGoToLogin }) => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');

  useEffect(() => {
    // Get email from URL
    const urlParams = new URLSearchParams(window.location.search);
    const emailParam = urlParams.get('email');
    if (emailParam) {
      setEmail(emailParam);
    } else {
      setError('Invalid reset link');
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    
    if (!newPassword || !confirmPassword) {
      setError('Please fill in all fields');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await fetch(apiUrl('/users/reset-password'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          email: email,
          newPassword: newPassword
        })
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Failed to reset password');
        return;
      }

      setSuccess('Password updated successfully! You can now log in with your new password.');
    } catch (error) {
      console.error('Reset password error:', error);
      setError('Network error. Check your connection');
    } finally {
      setLoading(false);
    }
  };

  if (!email) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        backgroundColor: '#f5f5f5',
        fontFamily: 'Arial, sans-serif'
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: '40px',
          borderRadius: '8px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          textAlign: 'center'
        }}>
          <h2 style={{ color: '#d32f2f', marginBottom: '20px' }}>Invalid Reset Link</h2>
          <p style={{ marginBottom: '20px' }}>This password reset link is invalid or has expired.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ 
      minHeight: '100vh', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center',
      backgroundColor: '#f5f5f5',
      fontFamily: 'Arial, sans-serif'
    }}>
      <div style={{
        backgroundColor: 'white',
        padding: '40px',
        borderRadius: '8px',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
        width: '100%',
        maxWidth: '400px'
      }}>
        <h2 style={{ 
          textAlign: 'center', 
          marginBottom: '30px',
          color: '#1976d2'
        }}>
          Reset Patient Password
        </h2>
        
        <p style={{ 
          textAlign: 'center', 
          marginBottom: '30px',
          color: '#666'
        }}>
          Enter your new password for: {email}
        </p>
        
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '20px' }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '8px',
              fontWeight: 'bold',
              color: '#333'
            }}>
              New Password
            </label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Enter new password"
              style={{
                width: '100%',
                padding: '12px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '16px',
                boxSizing: 'border-box'
              }}
              disabled={loading}
            />
          </div>
          
          <div style={{ marginBottom: '20px' }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '8px',
              fontWeight: 'bold',
              color: '#333'
            }}>
              Confirm New Password
            </label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm new password"
              style={{
                width: '100%',
                padding: '12px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '16px',
                boxSizing: 'border-box'
              }}
              disabled={loading}
            />
          </div>
          
          {error && (
            <div style={{ 
              color: '#d32f2f', 
              marginBottom: '20px',
              padding: '10px',
              backgroundColor: '#ffebee',
              borderRadius: '4px',
              border: '1px solid #ffcdd2'
            }}>
              {error}
            </div>
          )}
          
          {success && (
            <div style={{ 
              color: '#2e7d32', 
              marginBottom: '20px',
              padding: '10px',
              backgroundColor: '#e8f5e8',
              borderRadius: '4px',
              border: '1px solid #c8e6c9'
            }}>
              {success}
            </div>
          )}
          
          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%',
              padding: '12px',
              backgroundColor: loading ? '#ccc' : '#1976d2',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              fontSize: '16px',
              cursor: loading ? 'not-allowed' : 'pointer',
              marginBottom: '20px'
            }}
          >
            {loading ? 'Updating...' : 'Update Password'}
          </button>
        </form>
      </div>
    </div>
  );
};

const PatientVerificationPage: React.FC<{ onGoToLogin: () => void }> = ({ onGoToLogin }) => {
  const [verificationStatus, setVerificationStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState<string>('');

  useEffect(() => {
    const verifyEmail = async () => {
      try {
        // Get email from URL
        const urlParams = new URLSearchParams(window.location.search);
        const email = urlParams.get('email');
        
        if (!email) {
          setVerificationStatus('error');
          setMessage('No email provided in verification link');
          return;
        }

        // Call patient verification endpoint
        const response = await fetch(`${apiUrl('/users/verify_patients')}?email=${encodeURIComponent(email)}`);
        const data = await response.json();

        if (response.ok) {
          setVerificationStatus('success');
          setMessage(data.message);
        } else {
          setVerificationStatus('error');
          setMessage(data.error || 'Verification failed');
        }
      } catch (error) {
        setVerificationStatus('error');
        setMessage('Network error occurred during verification');
        console.error('Verification error:', error);
      }
    };

    verifyEmail();
  }, []);

  return (
    <div style={{ 
      minHeight: '100vh', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center',
      backgroundColor: '#f5f5f5',
      fontFamily: 'Arial, sans-serif'
    }}>
      <div style={{
        backgroundColor: 'white',
        padding: '40px',
        borderRadius: '12px',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
        width: '100%',
        maxWidth: '500px',
        textAlign: 'center'
      }}>
        {verificationStatus === 'loading' && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '20px' }}>
            <div style={{
              width: '60px',
              height: '60px',
              border: '4px solid #e3f2fd',
              borderTop: '4px solid #1976d2',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }}></div>
            <h2 style={{ color: '#1976d2', margin: 0 }}>Verifying Patient Email...</h2>
            <p style={{ color: '#666', margin: 0 }}>Please wait while we verify your email address.</p>
          </div>
        )}
        
        {verificationStatus === 'success' && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '20px' }}>
            <div style={{
              width: '80px',
              height: '80px',
              backgroundColor: '#4caf50',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '40px',
              color: 'white'
            }}>
              ✓
            </div>
            <h2 style={{ color: '#4caf50', margin: 0 }}>Email Verified Successfully!</h2>
            <p style={{ color: '#666', margin: 0, lineHeight: '1.5' }}>
              {message}
            </p>
            <p style={{ color: '#888', margin: 0, fontSize: '14px' }}>
              You can now log in to your patient account.
            </p>
          </div>
        )}
        
        {verificationStatus === 'error' && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '20px' }}>
            <div style={{
              width: '80px',
              height: '80px',
              backgroundColor: '#f44336',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '40px',
              color: 'white'
            }}>
              ✗
            </div>
            <h2 style={{ color: '#f44336', margin: 0 }}>Verification Failed</h2>
            <p style={{ color: '#666', margin: 0, lineHeight: '1.5' }}>
              {message}
            </p>
          </div>
        )}
      </div>
      
      <style>{`
        /* Custom range slider styling */
        input[type="range"] {
          -webkit-appearance: none;
          appearance: none;
          width: 100%;
          height: 8px;
          background: #ddd;
          border-radius: 4px;
          outline: none;
          cursor: pointer;
        }
        
        input[type="range"]::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 18px;
          height: 18px;
          background: #1976D2;
          border-radius: 50%;
          cursor: pointer;
          border: 2px solid white;
          box-shadow: 0 2px 4px rgba(0,0,0,0.2);
        }
        
        input[type="range"]::-moz-range-thumb {
          width: 18px;
          height: 18px;
          background: #1976D2;
          border-radius: 50%;
          cursor: pointer;
          border: 2px solid white;
          box-shadow: 0 2px 4px rgba(0,0,0,0.2);
        }
        
        input[type="range"]::-webkit-slider-runnable-track {
          width: 100%;
          height: 8px;
          background: #ddd;
          border-radius: 4px;
        }
        
        input[type="range"]::-moz-range-track {
          width: 100%;
          height: 8px;
          background: #ddd;
          border-radius: 4px;
        }
        
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

const VerificationPage: React.FC<{ onGoToLogin: () => void }> = ({ onGoToLogin }) => {
  const [verificationStatus, setVerificationStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState<string>('');

  useEffect(() => {
    const verifyEmail = async () => {
      try {
        // Get email from URL
        const urlParams = new URLSearchParams(window.location.search);
        const email = urlParams.get('email');
        
        if (!email) {
          setVerificationStatus('error');
          setMessage('No email provided in verification link');
          return;
        }

        // Call verification endpoint
        const response = await fetch(`${apiUrl('/users/verify')}?email=${encodeURIComponent(email)}`);
        const data = await response.json();

        if (response.ok) {
          setVerificationStatus('success');
          setMessage(data.message);
        } else {
          setVerificationStatus('error');
          setMessage(data.error || 'Verification failed');
        }
      } catch (error) {
        setVerificationStatus('error');
        setMessage('Network error occurred during verification');
        console.error('Verification error:', error);
      }
    };

    verifyEmail();
  }, []);

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px'
    }}>
      <div style={{ width: '100%', maxWidth: '500px' }}>
        <div style={{
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 10px 30px rgba(0, 0, 0, 0.2)',
          overflow: 'hidden'
        }}>
          <div style={{
            background: '#1976d2',
            color: 'white',
            padding: '30px',
            textAlign: 'center'
          }}>
            <h1 style={{ margin: 0, fontSize: '24px', fontWeight: 600 }}>Email Verification</h1>
          </div>
          
          <div style={{ padding: '40px 30px', textAlign: 'center' }}>
            {verificationStatus === 'loading' && (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '20px' }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  border: '4px solid #f3f3f3',
                  borderTop: '4px solid #1976d2',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }}></div>
                <p>Verifying your email address...</p>
              </div>
            )}
            
            {verificationStatus === 'success' && (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '20px' }}>
                <div style={{
                  width: '60px',
                  height: '60px',
                  background: '#4caf50',
                  color: 'white',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '30px',
                  fontWeight: 'bold'
                }}>✓</div>
                <h2 style={{ margin: 0, color: '#333', fontSize: '20px' }}>Email Verified Successfully!</h2>
                <p style={{ margin: '10px 0', color: '#666', lineHeight: 1.5 }}>{message}</p>
                <p style={{ margin: '10px 0', color: '#666', lineHeight: 1.5 }}>You can now log in to your account.</p>
                <button 
                  style={{
                    background: '#1976d2',
                    color: 'white',
                    border: 'none',
                    padding: '12px 24px',
                    borderRadius: '6px',
                    fontSize: '16px',
                    cursor: 'pointer'
                  }}
                  onClick={onGoToLogin}
                >
                  Go to Login
                </button>
              </div>
            )}
            
            {verificationStatus === 'error' && (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '20px' }}>
                <div style={{
                  width: '60px',
                  height: '60px',
                  background: '#f44336',
                  color: 'white',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '30px',
                  fontWeight: 'bold'
                }}>✗</div>
                <h2 style={{ margin: 0, color: '#333', fontSize: '20px' }}>Verification Failed</h2>
                <p style={{ margin: '10px 0', color: '#666', lineHeight: 1.5 }}>{message}</p>
                <button 
                  style={{
                    background: '#1976d2',
                    color: 'white',
                    border: 'none',
                    padding: '12px 24px',
                    borderRadius: '6px',
                    fontSize: '16px',
                    cursor: 'pointer'
                  }}
                  onClick={onGoToLogin}
                >
                  Go to Login
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const ALL_PAGES = [
  'login',
  'register',
  'dashboard',
  'history',
  'timeline',
  'history-debug',
  'settings',
  'consent',
  'thresholds',
  'patients',
  'medication',
  'verify',
  'verify-patient',
  'reset-password-patient',
  'forgot-password',
  'reset-password',
  'admin-errors',
  'admin-fetch-raw',
  'admin-login-attempts',
] as const;

type Page = typeof ALL_PAGES[number];

const isPageValue = (value: unknown): value is Page => {
  return typeof value === 'string' && (ALL_PAGES as readonly string[]).includes(value);
};

const determineInitialPage = (): Page => {
  if (typeof window === 'undefined') {
    return 'login';
  }

  const pathPage = getPageFromPath(window.location.pathname);
  if (pathPage) {
    return pathPage;
  }

  try {
    const stored = window.localStorage.getItem('lastPage');
    if (isPageValue(stored)) {
      return stored;
    }
  } catch (_err) {
    // ignore storage errors
  }

  return 'login';
};

const AUTH_ONLY_PAGES: Set<Page> = new Set<Page>([
  'login',
  'register',
  'forgot-password',
  'reset-password',
  'reset-password-patient',
  'verify',
  'verify-patient',
]);

const DOCTOR_ALLOWED_PAGES: Set<Page> = new Set<Page>([
  'dashboard',
  'patients',
  'history',
  'timeline',
  'history-debug',
  'settings',
  'consent',
  'thresholds',
  'medication',
]);

const PATIENT_ALLOWED_PAGES: Set<Page> = new Set<Page>([
  'dashboard',
  'history',
  'history-debug',
  'settings',
  'medication',
]);

const getPathForPage = (page: Page): string => {
  switch (page) {
    case 'login':
      return '/';
    case 'register':
      return '/register';
    case 'dashboard':
      return '/dashboard';
    case 'patients':
      return '/patients';
    case 'history':
      return '/history';
    case 'timeline':
      return '/timeline';
    case 'history-debug':
      return '/history';
    case 'settings':
      return '/settings';
    case 'consent':
      return '/consent';
    case 'thresholds':
      return '/thresholds';
    case 'medication':
      return '/medication';
    case 'admin-errors':
      return '/admin/errors';
    case 'admin-fetch-raw':
      return '/admin/fetch-raw';
    case 'admin-login-attempts':
      return '/admin/login-attempts';
    case 'forgot-password':
      return '/forgot-password';
    case 'reset-password':
      return '/reset-password';
    case 'reset-password-patient':
      return '/reset-password-patient';
    case 'verify':
      return '/verify';
    case 'verify-patient':
      return '/verify-patient';
    default:
      return '/';
  }
};

const getPageFromPath = (pathname: string): Page | null => {
  switch (pathname) {
    case '':
    case '/':
    case '/login':
      return 'login';
    case '/register':
      return 'register';
    case '/dashboard':
      return 'dashboard';
    case '/patients':
      return 'patients';
    case '/history':
      return 'history';
    case '/timeline':
      return 'timeline';
    case '/history-debug':
      return 'history';
    case '/settings':
      return 'settings';
    case '/consent':
      return 'consent';
    case '/thresholds':
      return 'thresholds';
    case '/medication':
      return 'medication';
    case '/admin/errors':
      return 'admin-errors';
    case '/admin/fetch-raw':
      return 'admin-fetch-raw';
    case '/admin/login-attempts':
      return 'admin-login-attempts';
    case '/forgot-password':
      return 'forgot-password';
    case '/reset-password':
      return 'reset-password';
    case '/reset-password-patient':
      return 'reset-password-patient';
    case '/verify':
      return 'verify';
    case '/verify-patient':
      return 'verify-patient';
    default:
      return null;
  }
};

const getFallbackPageForUser = (role: string, email?: string | null): Page => {
  if (isSecurityAdminEmail(email)) {
    return 'admin-login-attempts';
  }
  if (isFetchRawAdminEmail(email)) {
    return 'admin-fetch-raw';
  }
  if (isAdminWebEmail(email)) {
    return 'admin-errors';
  }
  return role === 'doctor' ? 'patients' : 'dashboard';
};

const isPageAllowedForUser = (role: string, email: string | null | undefined, page: Page | null): page is Page => {
  if (!page || AUTH_ONLY_PAGES.has(page)) {
    return false;
  }
  if (isSecurityAdminEmail(email)) {
    return page === 'admin-login-attempts';
  }
  if (isFetchRawAdminEmail(email)) {
    return page === 'admin-fetch-raw';
  }
  if (isAdminWebEmail(email)) {
    return page === 'admin-errors';
  }
  if (role === 'doctor') {
    return DOCTOR_ALLOWED_PAGES.has(page);
  }
  return PATIENT_ALLOWED_PAGES.has(page);
};

const selectPageForUser = (role: string, email: string | null | undefined, requestedPage: Page | null): Page => {
  return isPageAllowedForUser(role, email, requestedPage)
    ? (requestedPage as Page)
    : getFallbackPageForUser(role, email);
};

// Helper function to format numbers without .00 for integers
const formatThresholdValue = (value: number | string | null | undefined): string => {
  // Handle null, undefined, or empty values
  if (value === null || value === undefined || value === '') {
    return '0';
  }
  
  // Convert to number if it's a string
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  
  // Check if conversion was successful
  if (isNaN(numValue)) {
    return '0';
  }
  
  return Number.isInteger(numValue) ? numValue.toString() : numValue.toFixed(2);
};

const formatCriticalEventValue = (value: number | string | null | undefined): string | null => {
  if (value === null || value === undefined) return null;
  const raw = String(value).trim();
  if (!raw) return null;

  const numericValue = Number(raw);
  if (Number.isFinite(numericValue)) {
    return formatThresholdValue(numericValue);
  }
  return raw;
};

const pickNumericFromRow = (row: any, keys: string[]): number | null => {
  for (const key of keys) {
    if (row?.[key] == null) continue;
    const n = Number(row[key]);
    if (Number.isFinite(n)) return n;
  }
  return null;
};

const resolveCriticalEventValueFromRows = (
  event: CriticalEvent,
  vitalsRows: any[],
  tempRows: any[],
): string | null => {
  const direct = formatCriticalEventValue(event.value);
  if (direct !== null) return direct;

  const targetTs = parseDbTimestamp(event.timestamp).getTime();
  if (!Number.isFinite(targetTs)) return null;

  const type = (event.type || '').toLowerCase();
  const isTempEvent = type.includes('temp') || type.includes('temperature') || type.includes('fever') || type.includes('hypothermia');
  const rowsToSearch = isTempEvent && Array.isArray(tempRows) && tempRows.length > 0 ? tempRows : vitalsRows;
  if (!Array.isArray(rowsToSearch) || rowsToSearch.length === 0) return null;

  // Keep fallback strict enough to avoid showing unrelated values.
  const maxDiffMs = 15 * 60 * 1000;
  let bestRow: any = null;
  let bestDiff = Number.POSITIVE_INFINITY;
  for (const row of rowsToSearch) {
    const rowTsRaw = row?.timestamp ?? row?.timestamp_utc ?? row?.ts ?? row?.created_at;
    if (!rowTsRaw) continue;
    const rowTs = parseDbTimestamp(rowTsRaw).getTime();
    if (!Number.isFinite(rowTs)) continue;
    const diff = Math.abs(rowTs - targetTs);
    if (diff < bestDiff) {
      bestDiff = diff;
      bestRow = row;
    }
  }
  if (!bestRow || bestDiff > maxDiffMs) return null;

  let derived: number | null = null;
  if (type.includes('heart_rate') && !type.includes('hrv')) {
    derived = pickNumericFromRow(bestRow, ['heart_rate', 'heart_rate_bpm', 'pulse_rate', 'pulse_rate_bpm']);
  } else if (type.includes('hrv') || type.includes('variability')) {
    derived = pickNumericFromRow(bestRow, ['hrv', 'hrv_ms', 'hrv_bpm', 'heart_rate_variability']);
  } else if (type.includes('spo2') || type.includes('oxygen')) {
    derived = pickNumericFromRow(bestRow, ['spo2', 'blood_oxygen_saturation']);
  } else if (type.includes('resp') || type.includes('breath')) {
    derived = pickNumericFromRow(bestRow, ['respiratory_rate', 'respiratory_rate_bpm', 'breathing', 'breathing_rate', 'breaths_per_minute', 'resp_rate', 'rr']);
  } else if (type.includes('systolic')) {
    derived = pickNumericFromRow(bestRow, ['systolic_blood_pressure', 'systolic_blood_pressure_mmhg', 'systolic_blood_pressure_bpm', 'systolic_blood_pressure_bpm_mmhg', 'systolic']);
  } else if (type.includes('diastolic')) {
    derived = pickNumericFromRow(bestRow, ['diastolic_blood_pressure', 'diastolic_blood_pressure_mmhg', 'diastolic_blood_pressure_bpm', 'diastolic_blood_pressure_bpm_mmhg', 'diastolic']);
  } else if (isTempEvent) {
    derived = pickNumericFromRow(bestRow, ['temperature', 'temperature_value', 'temp']);
  }

  return formatCriticalEventValue(derived);
};

/** Event-based panel color: critical → red, only warnings → yellow, else green (like dashboard). */
const getEventRiskClass = (warnings: number, critical: number): 'risk-red' | 'risk-yellow' | 'risk-green' => {
  if (critical > 0) return 'risk-red';
  if (warnings > 0) return 'risk-yellow';
  return 'risk-green';
};

// Process medication data for timeline chart
const processMedicationData = (medications: Medication[], dateRange: { startDate: Date; endDate: Date }) => {
  if (!medications || medications.length === 0) return [];
  
  // Create a map of unique medications with their timeline data
  const medicationMap = new Map<string, {
    name: string;
    dosage: string;
    frequency: string;
    notes: string;
    startTime: number;
    endTime: number;
    color: string;
  }>();
  
  const colors = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7', '#DDA0DD', '#98D8C8', '#F7DC6F', '#BB8FCE', '#85C1E9'];
  let colorIndex = 0;
  
  medications.forEach((med, index) => {
    const startDate = new Date(med.starting_date);
    const endDate = med.ending_date ? new Date(med.ending_date) : dateRange.endDate;
    
    // Only include medications that overlap with our date range
    if (startDate <= dateRange.endDate && endDate >= dateRange.startDate) {
      const key = `${med.name}-${med.dosage || ''}`;
      if (!medicationMap.has(key)) {
        medicationMap.set(key, {
          name: med.name,
          dosage: med.dosage || '',
          frequency: med.frequency || 'Daily',
          notes: med.notes || '',
          startTime: Math.max(startDate.getTime(), dateRange.startDate.getTime()),
          endTime: Math.min(endDate.getTime(), dateRange.endDate.getTime()),
          color: colors[colorIndex % colors.length]
        });
        colorIndex++;
      }
    }
  });
  
  // Convert to array and sort by start time
  return Array.from(medicationMap.values()).sort((a, b) => a.startTime - b.startTime);
};

const TIME_RANGE_STORAGE_KEY = 'dashboardSelectedTimeRange';
const CUSTOM_RANGE_STORAGE_KEY = 'dashboardCustomRange';

const loadStoredCustomRange = () => {
  if (typeof window === 'undefined') {
    return { start: undefined, end: undefined };
  }
  try {
    const raw = window.localStorage.getItem(CUSTOM_RANGE_STORAGE_KEY);
    if (!raw) return { start: undefined, end: undefined };
    const parsed = JSON.parse(raw);
    const start = parsed?.start ? new Date(parsed.start) : undefined;
    const end = parsed?.end ? new Date(parsed.end) : undefined;
    return {
      start: start && !Number.isNaN(start.getTime()) ? start : undefined,
      end: end && !Number.isNaN(end.getTime()) ? end : undefined,
    };
  } catch {
    return { start: undefined, end: undefined };
  }
};

type ScreenOrientationPluginLike = {
  lock: (options: { orientation: string }) => Promise<void>;
  unlock: () => Promise<void>;
};

const getCapacitorScreenOrientationPlugin = (): ScreenOrientationPluginLike | null => {
  const capacitorBridge = (window as any)?.Capacitor;
  const plugin = capacitorBridge?.Plugins?.ScreenOrientation;
  if (!plugin || typeof plugin.lock !== 'function' || typeof plugin.unlock !== 'function') {
    return null;
  }
  return plugin as ScreenOrientationPluginLike;
};

const isNativeCapacitorApp = (): boolean => {
  const capacitorBridge = (window as any)?.Capacitor;
  if (!capacitorBridge) return false;
  if (typeof capacitorBridge.isNativePlatform === 'function') {
    return Boolean(capacitorBridge.isNativePlatform());
  }
  const platform = capacitorBridge.getPlatform?.();
  return platform === 'android' || platform === 'ios';
};

const isPhoneDevice = (): boolean => {
  if (typeof window === 'undefined') return false;
  const ua = window.navigator.userAgent || '';
  const isTablet = /iPad|Tablet|PlayBook|Silk/i.test(ua);
  const isMobileUa = /Android|webOS|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);
  const isSmallViewport = window.matchMedia?.('(max-width: 900px)').matches ?? false;
  return !isTablet && (isMobileUa || isSmallViewport);
};

const lockLandscapeForDashboard = async (): Promise<void> => {
  try {
    const nativeOrientationPlugin = getCapacitorScreenOrientationPlugin();
    if (isNativeCapacitorApp() && nativeOrientationPlugin) {
      await nativeOrientationPlugin.lock({ orientation: 'landscape' });
      return;
    }

    const orientationApi = window.screen?.orientation as { lock?: (orientation: string) => Promise<void> } | undefined;
    if (orientationApi?.lock) {
      await orientationApi.lock('landscape');
    }
  } catch (error) {
    console.warn('[FE] Unable to lock landscape orientation:', error);
  }
};

const lockPortraitForPhone = async (): Promise<void> => {
  if (!isPhoneDevice()) return;
  try {
    const nativeOrientationPlugin = getCapacitorScreenOrientationPlugin();
    if (isNativeCapacitorApp() && nativeOrientationPlugin) {
      await nativeOrientationPlugin.lock({ orientation: 'portrait' });
      return;
    }

    const orientationApi = window.screen?.orientation as { lock?: (orientation: string) => Promise<void> } | undefined;
    if (orientationApi?.lock) {
      await orientationApi.lock('portrait');
    }
  } catch (error) {
    console.warn('[FE] Unable to lock portrait orientation:', error);
  }
};

const unlockDashboardOrientation = async (): Promise<void> => {
  try {
    const nativeOrientationPlugin = getCapacitorScreenOrientationPlugin();
    if (isNativeCapacitorApp() && nativeOrientationPlugin) {
      await nativeOrientationPlugin.unlock();
      return;
    }

    const orientationApi = window.screen?.orientation as { unlock?: () => void } | undefined;
    if (orientationApi?.unlock) {
      orientationApi.unlock();
    }
  } catch (error) {
    console.warn('[FE] Unable to unlock screen orientation:', error);
  }
};

const RAW_SIGNAL_WINDOW_SECONDS = 10;
const RAW_SLIDE_HALF_SECONDS = 7.08;
const RAW_SLIDE_DURATION_MS = 300;

type RawBufferPoint = {
  time: number;
  value: number;
  sampleIndex: number;
  timestamp?: string | null;
};

type RawSlideAnim = {
  active: boolean;
  startMs: number;
  snapshot: RawBufferPoint[];
  newSecondHalf: RawBufferPoint[];
  onComplete: () => void;
};

function sliceRawSamplesByWindow<T extends { t: number }>(samples: T[], start: number, end: number): T[] {
  if (!samples.length) return [];
  let lo = 0;
  let hi = samples.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (samples[mid].t < start) lo = mid + 1;
    else hi = mid;
  }
  const startIdx = lo;
  lo = startIdx;
  hi = samples.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (samples[mid].t <= end) lo = mid + 1;
    else hi = mid;
  }
  return samples.slice(startIdx, lo);
}

const RAW_BATCH_EXPECTED_SAMPLES = 30000;
const RAW_BATCH_ECG_DT = 0.002;

function mapCriticalEventRawBatchRows(rows: any[]) {
  const validRows = rows.filter((r: any) => {
    const si = r.sample_index != null ? Number(r.sample_index) : null;
    return si !== null && si >= 0 && si < RAW_BATCH_EXPECTED_SAMPLES;
  });
  const sortBySampleIndex = (a: any, b: any) => Number(a.sample_index ?? 0) - Number(b.sample_index ?? 0);
  const getSeriesRows = (key: string, altKey?: string) => (
    validRows
      .filter((r: any) => {
        const val = r[key] != null ? r[key] : (altKey ? r[altKey] : null);
        return val != null && !isNaN(Number(val));
      })
      .sort(sortBySampleIndex)
      .slice(0, RAW_BATCH_EXPECTED_SAMPLES)
  );
  const inferPpgDt = (seriesRows: any[]) => {
    const seriesMaxSampleIdx = seriesRows.length > 0
      ? Math.max(...seriesRows.map((r: any) => Number(r.sample_index ?? 0)))
      : 0;
    return getPpgSampleInterval(seriesRows.length, seriesMaxSampleIdx);
  };
  const mapSeries = (seriesRows: any[], key: string, altKey?: string, sampleDt: number = RAW_BATCH_ECG_DT) => {
    return seriesRows
      .map((r: any) => {
        const si = Number(r.sample_index ?? 0);
        const val = r[key] != null ? r[key] : (altKey ? r[altKey] : null);
        return {
          t: si * sampleDt,
          value: Number(val),
          sampleIndex: si,
          timestamp: r.timestamp,
        };
      });
  };

  const ecgRows = getSeriesRows('ecg1');
  const ecg2Rows = getSeriesRows('ecg2');
  const greenRows = getSeriesRows('green_ppg', 'ppg_green');
  const yellowRows = getSeriesRows('yellow_ppg', 'ppg_yellow');

  return {
    ecgMapped: mapSeries(ecgRows, 'ecg1'),
    ecg2Mapped: mapSeries(ecg2Rows, 'ecg2'),
    greenMapped: mapSeries(greenRows, 'green_ppg', 'ppg_green', inferPpgDt(greenRows)),
    yellowMapped: mapSeries(yellowRows, 'yellow_ppg', 'ppg_yellow', inferPpgDt(yellowRows)),
    validRowCount: validRows.length,
  };
}

function finalizeRawBufferSlide(
  bufferRef: React.MutableRefObject<RawBufferPoint[]>,
  newDataSecondHalf: RawBufferPoint[],
  dt: number
) {
  const existingData = bufferRef.current
    .filter(p => p.time >= RAW_SLIDE_HALF_SECONDS && p.value !== 0)
    .map(p => ({ ...p, time: p.time - RAW_SLIDE_HALF_SECONDS }));

  const emptyFirstHalf = Array.from({ length: Math.floor(RAW_SLIDE_HALF_SECONDS / dt) }, (_, i) => ({
    time: i * dt,
    value: 0,
    sampleIndex: -1,
    timestamp: null as string | null,
  }));

  existingData.forEach(dataPoint => {
    const index = Math.floor(dataPoint.time / dt);
    if (index >= 0 && index < emptyFirstHalf.length) {
      emptyFirstHalf[index] = {
        ...dataPoint,
        timestamp: dataPoint.timestamp || null,
      };
    }
  });

  bufferRef.current = [...emptyFirstHalf, ...newDataSecondHalf];
}

function startRawBufferSlide(
  slideRef: React.MutableRefObject<RawSlideAnim | null>,
  bufferRef: React.MutableRefObject<RawBufferPoint[]>,
  newDataSecondHalf: RawBufferPoint[],
  dt: number
) {
  slideRef.current = {
    active: true,
    startMs: performance.now(),
    snapshot: bufferRef.current.map(p => ({ ...p })),
    newSecondHalf: newDataSecondHalf,
    onComplete: () => {
      finalizeRawBufferSlide(bufferRef, newDataSecondHalf, dt);
      slideRef.current = null;
    },
  };
}

function computeRawSlideDisplayPoints(
  slide: RawSlideAnim | null,
  buffer: RawBufferPoint[]
): RawBufferPoint[] {
  if (!slide?.active) {
    return buffer.filter(p => p.value !== 0 && p.time >= 0 && p.time <= 14.16);
  }

  const progress = Math.min(1, (performance.now() - slide.startMs) / RAW_SLIDE_DURATION_MS);
  const ease = 1 - Math.pow(1 - progress, 3);
  const offset = ease * RAW_SLIDE_HALF_SECONDS;
  const enterOffset = (1 - ease) * RAW_SLIDE_HALF_SECONDS;

  const oldPoints = slide.snapshot
    .filter(p => p.value !== 0)
    .map(p => ({ ...p, time: p.time - offset }));

  const newPoints = slide.newSecondHalf.map(p => ({
    ...p,
    time: p.time + enterOffset,
  }));

  const merged = [...oldPoints, ...newPoints].filter(
    p => p.value !== 0 && p.time >= 0 && p.time <= 14.16
  );

  if (progress >= 1) {
    slide.active = false;
    slide.onComplete();
    return buffer.filter(p => p.value !== 0 && p.time >= 0 && p.time <= 14.16);
  }

  return merged;
}

function mapRawBufferToChartPoints(points: RawBufferPoint[]) {
  return points.map(p => ({
    t: p.time,
    value: p.value,
    sampleIndex: p.sampleIndex,
    timestamp: p.timestamp ?? undefined,
  }));
}

// Dashboard Component
const Dashboard: React.FC<{ onLogout: () => void; onNavigate: (page: Page, options?: { keepPatient?: boolean }) => void; onPatIdChange?: (patId: string) => void; userRole: string; initialDemoMode?: boolean }> = ({ onLogout, onNavigate, onPatIdChange, userRole, initialDemoMode = false }) => {
  const [isPhoneLayout, setIsPhoneLayout] = useState<boolean>(() => isPhoneDevice());
  const useShortZoomHint = useMemo(() => {
    if (isNativeCapacitorApp()) return true;
    if (isPhoneLayout) return true;
    if (typeof window === 'undefined') return false;
    if (window.matchMedia?.('(pointer: coarse)').matches) return true;
    if (window.matchMedia?.('(max-width: 1024px)').matches) return true;
    return false;
  }, [isPhoneLayout]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedTimeRange, setSelectedTimeRange] = useState<string>(() => {
    if (typeof window === 'undefined') return '1 Hours';
    try {
      return localStorage.getItem(TIME_RANGE_STORAGE_KEY) || '1 Hours';
    } catch {
      return '1 Hours';
    }
  });
  const [patId, setPatId] = useState<string>(() => {
    try {
      return localStorage.getItem('selectedPatId') || '';
    } catch {
      return '';
    }
  });
  const [, setPatIdDraft] = useState<string>(() => {
    try {
      return localStorage.getItem('selectedPatId') || '';
    } catch {
      return '';
    }
  });
  const [patIdLoaded, setPatIdLoaded] = useState<boolean>(false);
  const [thresholds, setThresholds] = useState<Thresholds>(defaultThresholds);
  const [patientNotes, setPatientNotes] = useState<string>('');
  const [notesLoading, setNotesLoading] = useState<boolean>(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const storedCustomRange = useRef(loadStoredCustomRange());
  // Custom date range state
  const [customStartDate, setCustomStartDate] = useState<Date>(() => {
    const stored = storedCustomRange.current.start;
    if (stored) return stored;
    const date = new Date();
    date.setDate(date.getDate() - 30);
    date.setHours(0, 0, 0, 0);
    return date;
  });

  const [customEndDate, setCustomEndDate] = useState<Date>(() => {
    const stored = storedCustomRange.current.end;
    if (stored) return stored;
    const date = new Date();
    date.setHours(23, 59, 59, 999);
    return date;
  });
  // Track when custom date is applied to trigger fetch only on "Done" button
  const [customDateApplied, setCustomDateApplied] = useState<number>(0);
  const [showCustomInputs, setShowCustomInputs] = useState(false);
  // Temporary state for editing custom dates (only applied when "Apply" is clicked)
  const [tempCustomStartDate, setTempCustomStartDate] = useState<Date>(customStartDate);
  const [tempCustomEndDate, setTempCustomEndDate] = useState<Date>(customEndDate);
  // Apply pat_id coming from Patients page once on mount
  useEffect(() => {
    try {
      const sel = localStorage.getItem('selectedPatId');
      if (sel && sel !== patId) {
        console.log('[FE] applying selectedPatId from Patients page', sel);
        setPatId(sel);
        setPatIdDraft(sel);
        localStorage.removeItem('selectedPatId');
      }
    } catch {}
  }, [patId]);

  // Auto-set pat_id for patients on mount
  useEffect(() => {
    const loadPatientId = async () => {
      try {
        const userEmail = localStorage.getItem('currentUserEmail');
        if (userEmail && userRole === 'patient') {
          try {
            console.log('[FE] Loading patient ID for patient user:', userEmail);
            const patientData = await getPatientByEmail(userEmail);
            setPatId(patientData.pat_id);
            setPatIdDraft(patientData.pat_id);
            setPatIdLoaded(true);
            console.log('[FE] Patient ID auto-loaded:', patientData.pat_id);
          } catch (patientError) {
            console.error('[FE] Error loading patient ID:', patientError);
            setPatIdLoaded(true); // Mark as loaded even if failed
          }
        } else {
          setPatIdLoaded(true); // Mark as loaded for doctors
        }
      } catch (error) {
        console.error('[FE] Error loading patient ID:', error);
        setPatIdLoaded(true);
      }
    };
    
    if (userRole) {
      loadPatientId();
    }
  }, [userRole]);

  // Load thresholds when patId changes
  useEffect(() => {
    const loadPatientThresholds = async () => {
      if (patId) {
        try {
          console.log('[FE] Loading thresholds for pat_id:', patId);
          const patientThresholds = await loadThresholds(patId);
          setThresholds(patientThresholds);
          console.log('[FE] Thresholds loaded:', patientThresholds);
        } catch (error) {
          console.error('[FE] Error loading patient thresholds:', error);
          setThresholds(defaultThresholds);
        }
      } else {
        setThresholds(defaultThresholds);
      }
    };

    loadPatientThresholds();
  }, [patId]);

  // Keep dashboards in landscape when a patient is selected on mobile builds.
  useEffect(() => {
    const handleResize = () => setIsPhoneLayout(isPhoneDevice());
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!patId) {
      void unlockDashboardOrientation();
      return;
    }

    if (isPhoneLayout) {
      void lockLandscapeForDashboard();
    } else {
      void unlockDashboardOrientation();
    }
  }, [isPhoneLayout, patId]);

  // Clear all data when patId changes to prevent showing data from previous patient
  useEffect(() => {
    if (!patId) return;
    console.log('[FE] Patient changed, clearing all previous data for patId:', patId);
    // Clear all latest values
    setLatestVitalsTs(null);
    setLatestTemperature(null);
    setLatestBreathing(null);
    setLatestHeart(null);
    setLatestHeartRateVariability(null);
    setLatestSpo2(null);
    setLatestSystolicBloodPressure(null);
    setLatestDiastolicBloodPressure(null);
    // Clear all chart data
    setDbHeartRate([]);
    setDbHeartRateVariability([]);
    setDbBreathing([]);
    setDbSpo2([]);
    setDbSystolicBloodPressure([]);
    setDbDiastolicBloodPressure([]);
    setDbTemperature([]);
    setActivityTimelineRows([]);
  }, [patId]);


  // Notify parent component when patId changes
  useEffect(() => {
    if (onPatIdChange) {
      onPatIdChange(patId);
    }
  }, [patId, onPatIdChange]);
  // Operation mode switcher (real/demo) - start in demo if initialDemoMode is true
  const [operationMode, setOperationMode] = useState<'real' | 'demo'>(initialDemoMode ? 'demo' : 'real');
  
  // Force demo mode when initialDemoMode changes
  useEffect(() => {
    if (initialDemoMode) {
      setOperationMode('demo');
    }
  }, [initialDemoMode]);
  const isDemo = operationMode === 'demo';
  const isReal = operationMode === 'real';
  
  // Load patient notes when patId changes
  useEffect(() => {
    const loadPatientNotes = async () => {
      if (patId || operationMode === 'demo') {
        try {
          setNotesLoading(true);
          console.log('[FE] Loading patient notes for pat_id:', patId, 'mode:', operationMode);
          
          if (operationMode === 'demo') {
            // Generate random demo patient notes
            const demoNotes = `DEMO PATIENT NOTES - ${new Date().toLocaleDateString()}

Patient History:
- 65-year-old male with history of hypertension and diabetes
- Previous myocardial infarction in 2020
- Current medications: Aspirin, Metformin, Lisinopril, Atorvastatin
- Allergic to penicillin and sulfa drugs

Current Status:
- Blood pressure well controlled with current medication regimen
- Blood glucose levels within target range
- No recent episodes of chest pain or shortness of breath
- Patient reports good adherence to medication schedule

Monitoring Notes:
- Continue current vital signs monitoring
- Schedule follow-up appointment in 3 months
- Patient education provided on medication compliance
- Family history of cardiovascular disease noted

Recent Observations:
- Patient appears stable and comfortable
- No signs of acute distress
- Vital signs within normal limits for age group
- Patient reports good quality of life

Next Steps:
- Continue current treatment plan
- Monitor for any changes in symptoms
- Regular follow-up appointments scheduled
- Patient to report any new symptoms immediately`;
            
            setPatientNotes(demoNotes);
            console.log('[FE] Demo patient notes loaded');
          } else {
            const notesData = await getPatientNotes(patId);
            setPatientNotes(notesData.doctor_notes || '');
            console.log('[FE] Patient notes loaded:', notesData.doctor_notes);
          }
        } catch (error) {
          console.error('[FE] Error loading patient notes:', error);
          setPatientNotes('');
        } finally {
          setNotesLoading(false);
        }
      } else {
        setPatientNotes('');
      }
    };

    loadPatientNotes();
  }, [patId, operationMode]);
  
  // Live 'now' clock for header date range
  const [now, setNow] = useState<Date>(new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  // Sidebar (drawer)
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  // Force REAL on initial mount to avoid being stuck in demo after refresh
  useEffect(() => {
    try {
      if (typeof window !== 'undefined') {
        window.localStorage.setItem('operationMode', 'real');
      }
    } catch {}
  }, []);
  useEffect(() => {
    try {
      window.localStorage.setItem('operationMode', operationMode);
    } catch (e) {
      // ignore persistence errors
    }
  }, [operationMode]);

  const [showgraphs, setShowgraphs] = useState(true);
  const [clickedEventSnapshot, setClickedEventSnapshot] = useState<DemoVitalsSnapshot | null>(null);
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const [eventClickLocked, setEventClickLocked] = useState<boolean>(false);
  const [selectedEventTimestamp, setSelectedEventTimestamp] = useState<string | null>(null);
  
  // Shared zoom state for all charts - when one chart zooms, all zoom together
  // Store as timestamps (ISO strings) so all charts can use the same time range
  const [sharedZoomRange, setSharedZoomRange] = useState<{ startTime: string | null; endTime: string | null }>({
    startTime: null,
    endTime: null,
  });
  const sharedZoomRangeRef = useRef(sharedZoomRange);
  sharedZoomRangeRef.current = sharedZoomRange;
  const rawVitalsRowsRef = useRef<any[]>([]);
  const rawTempRowsRef = useRef<any[]>([]);
  const reaggregateVitalsRef = useRef<((rows: any[], tempRows?: any[]) => void) | null>(null);
  const refreshDemoDataRef = useRef<(() => void) | null>(null);
  const vitalsFetchGenerationRef = useRef(0);
  
  // Generic drag-to-zoom hook usable by any chart (numeric X axis over t)
  type ZoomState = {
    left: number | null;
    right: number | null;
    refAreaLeft: number | null;
    refAreaRight: number | null;
  };

  const useChartZoom = (
    baseData: GraphData[],
    getContainerWidth: () => number,
    sharedZoom?: { startTime: string | null; endTime: string | null },
    setSharedZoom?: (range: { startTime: string | null; endTime: string | null }) => void,
    rangeStartDate?: Date,
    rangeMinutes?: number,
    getPlotBounds?: () => PlotBounds | null,
    transformZoomSelection?: (
      from: number,
      to: number,
      ctx: { visibleLeft: number; visibleRight: number }
    ) => { from: number; to: number } | null
  ) => {
    // Each chart has its own refArea for drag selection, but shares the zoom range
    const [refArea, setRefArea] = useState<{ refAreaLeft: number | null; refAreaRight: number | null }>({
      refAreaLeft: null,
      refAreaRight: null,
    });
    const refAreaRef = useRef(refArea);
    refAreaRef.current = refArea;
    const dragCleanupRef = useRef<(() => void) | null>(null);

    const updateRefArea = (next: { refAreaLeft: number | null; refAreaRight: number | null }) => {
      refAreaRef.current = next;
      setRefArea(next);
    };

    useEffect(() => () => {
      dragCleanupRef.current?.();
      dragCleanupRef.current = null;
    }, []);
    
    // Convert shared timestamp zoom to t values (minutes from rangeStartDate) for this chart
    // Recalculate whenever sharedZoom changes or rangeStartDate changes
    const localZoom = useMemo((): { left: number | null; right: number | null } => {
      if (!sharedZoom || !sharedZoom.startTime || !sharedZoom.endTime || !rangeStartDate) {
        return { left: null, right: null };
      }
      
      // Convert timestamps to t values (minutes from rangeStartDate)
      const startTime = new Date(sharedZoom.startTime).getTime();
      const endTime = new Date(sharedZoom.endTime).getTime();
      const startDateMs = rangeStartDate.getTime();
      
      // Calculate t values in minutes from rangeStartDate
      const leftT = (startTime - startDateMs) / (60 * 1000);
      const rightT = (endTime - startDateMs) / (60 * 1000);
      
      // Clamp to valid range [0, rangeMinutes]
      const maxMinutes = rangeMinutes || 60;
      const clampedLeft = Math.max(0, Math.min(maxMinutes, leftT));
      const clampedRight = Math.max(0, Math.min(maxMinutes, rightT));
      
      return { left: clampedLeft, right: clampedRight };
    }, [sharedZoom, rangeStartDate, rangeMinutes]);
    
    // Combine local zoom with refArea for the complete zoom state
    const zoom = useMemo(() => ({ ...localZoom, ...refArea }), [localZoom, refArea]);

    // When zoomed in, map chartX to the visible domain so zoom sensitivity stays constant
    // (same pixel movement = same fraction of visible range, not of full range).
    const effectiveLeft = localZoom.left ?? 0;
    const effectiveRight = localZoom.right ?? (typeof rangeMinutes === 'number' && rangeMinutes > 0 ? rangeMinutes : (baseData?.length ? Math.max(1, baseData.length - 1) : 1));
    const visibleSpan = Math.max(0.001, effectiveRight - effectiveLeft);

    const resolveLabelFromEvent = (e: any): number | null => {
      if (!e) return null;

      const containerWidth = getContainerWidth();
      const maxT = typeof rangeMinutes === 'number' && rangeMinutes > 0
        ? rangeMinutes
        : (baseData?.length ? Math.max(1, baseData.length - 1) : 1);

      const bounds = getPlotBounds?.() ?? null;
      const plotLeft = bounds ? bounds.left : (containerWidth > 0 ? containerWidth * 0.10 : 0);
      const plotWidth = bounds && bounds.width > 0
        ? bounds.width
        : Math.max(1, containerWidth > 0 ? containerWidth * (1 - 0.10 - 0.04) : 1);

      const tFromPlotX = (plotX: number): number => {
        const normalizedX = Math.max(0, Math.min(plotWidth, plotX));
        const t = effectiveLeft + (normalizedX / plotWidth) * visibleSpan;
        return Math.max(0, Math.min(maxT, t));
      };

      // chartX is in container/wrapper coordinates — subtract plotLeft to get plot-relative x
      if (typeof e.chartX === 'number') {
        return tFromPlotX(e.chartX - plotLeft);
      }

      if (typeof e.activeCoordinate?.x === 'number') {
        return tFromPlotX(e.activeCoordinate.x - plotLeft);
      }

      const native = e.nativeEvent ?? e.event ?? null;
      if (native && typeof native.clientX === 'number') {
        const wrapper = (native.target as Element | null)?.closest?.('.recharts-wrapper')
          ?? (native.target as Element | null)?.closest?.('.recharts-surface')?.parentElement;
        if (wrapper) {
          const rect = wrapper.getBoundingClientRect();
          return tFromPlotX(native.clientX - rect.left - plotLeft);
        }
      }

      if (typeof e.activeLabel === 'number') {
        const labelValue = e.activeLabel as number;
        return Math.max(0, Math.min(maxT, labelValue));
      }

      return null;
    };

    const finishDragSelection = () => {
      dragCleanupRef.current?.();
      dragCleanupRef.current = null;

      const current = refAreaRef.current;
      if (current.refAreaLeft == null || current.refAreaRight == null) {
        updateRefArea({ refAreaLeft: null, refAreaRight: null });
        return;
      }
      let from = current.refAreaLeft as number;
      let to = current.refAreaRight as number;
      if (from === to) {
        updateRefArea({ refAreaLeft: null, refAreaRight: null });
        return;
      }
      if (from > to) {
        const tmp = from; from = to; to = tmp;
      }
      if (setSharedZoom && rangeStartDate && rangeMinutes) {
        let clampedFrom = Math.max(0, Math.min(rangeMinutes, from));
        let clampedTo = Math.max(0, Math.min(rangeMinutes, to));
        if (transformZoomSelection) {
          const transformed = transformZoomSelection(clampedFrom, clampedTo, {
            visibleLeft: effectiveLeft,
            visibleRight: effectiveRight,
          });
          if (transformed) {
            clampedFrom = transformed.from;
            clampedTo = transformed.to;
          }
        }
        const zoomDurationMinutes = clampedTo - clampedFrom;
        if (zoomDurationMinutes < 2) {
          updateRefArea({ refAreaLeft: null, refAreaRight: null });
          return;
        }
        const startTimestamp = new Date(rangeStartDate.getTime() + clampedFrom * 60 * 1000);
        const endTimestamp = new Date(rangeStartDate.getTime() + clampedTo * 60 * 1000);
        setSharedZoom({ startTime: startTimestamp.toISOString(), endTime: endTimestamp.toISOString() });
      }
      updateRefArea({ refAreaLeft: null, refAreaRight: null });
    };

    const onMouseDown = (e: any) => {
      const label = resolveLabelFromEvent(e);
      if (label == null) return;
      updateRefArea({ refAreaLeft: label, refAreaRight: label });

      dragCleanupRef.current?.();
      const onWindowMove = (ev: MouseEvent) => {
        if (refAreaRef.current.refAreaLeft == null) return;
        const moveLabel = resolveLabelFromEvent({ nativeEvent: ev });
        if (moveLabel == null) return;
        updateRefArea({ ...refAreaRef.current, refAreaRight: moveLabel });
      };
      const onWindowUp = () => {
        window.removeEventListener('mousemove', onWindowMove);
        window.removeEventListener('mouseup', onWindowUp);
        dragCleanupRef.current = null;
        finishDragSelection();
      };
      window.addEventListener('mousemove', onWindowMove);
      window.addEventListener('mouseup', onWindowUp);
      dragCleanupRef.current = () => {
        window.removeEventListener('mousemove', onWindowMove);
        window.removeEventListener('mouseup', onWindowUp);
      };
    };

    const onMouseMove = (e: any) => {
      if (refAreaRef.current.refAreaLeft == null) return;
      const label = resolveLabelFromEvent(e);
      if (label == null) return;
      updateRefArea({ ...refAreaRef.current, refAreaRight: label });
    };

    const onMouseUp = () => {
      finishDragSelection();
    };

    const reset = () => {
      dragCleanupRef.current?.();
      dragCleanupRef.current = null;
      if (setSharedZoom) {
        setSharedZoom({ startTime: null, endTime: null });
      }
      updateRefArea({ refAreaLeft: null, refAreaRight: null });
    };
    const setRange = (left: number | null, right: number | null) => {
      if (setSharedZoom && baseData && baseData.length > 0 && left !== null && right !== null) {
        const fromIdx = Math.max(0, Math.min(baseData.length - 1, Math.floor(left)));
        const toIdx = Math.max(0, Math.min(baseData.length - 1, Math.floor(right)));
        const fromData = baseData[fromIdx];
        const toData = baseData[toIdx];
        
        // Try to get timestamp from tsMs if available, otherwise parse from time field
        let startTime: string | null = null;
        let endTime: string | null = null;
        
        if (fromData) {
          if ((fromData as any).tsMs != null) {
            startTime = new Date((fromData as any).tsMs).toISOString();
          } else if (fromData.time) {
            const parsed = new Date(fromData.time);
            if (!Number.isNaN(parsed.getTime())) {
              startTime = parsed.toISOString();
            } else {
              const today = new Date();
              const [hours, minutes] = fromData.time.split(':');
              const date = new Date(today.getFullYear(), today.getMonth(), today.getDate(), parseInt(hours || '0'), parseInt(minutes || '0'));
              startTime = date.toISOString();
            }
          }
        }
        
        if (toData) {
          if ((toData as any).tsMs != null) {
            endTime = new Date((toData as any).tsMs).toISOString();
          } else if (toData.time) {
            const parsed = new Date(toData.time);
            if (!Number.isNaN(parsed.getTime())) {
              endTime = parsed.toISOString();
            } else {
              const today = new Date();
              const [hours, minutes] = toData.time.split(':');
              const date = new Date(today.getFullYear(), today.getMonth(), today.getDate(), parseInt(hours || '0'), parseInt(minutes || '0'));
              endTime = date.toISOString();
            }
          }
        }
        
        if (startTime && endTime) {
          setSharedZoom({ startTime, endTime });
        }
      } else if (setSharedZoom) {
        setSharedZoom({ startTime: null, endTime: null });
      }
      updateRefArea({ refAreaLeft: null, refAreaRight: null });
    };

    return { zoom, onMouseDown, onMouseMove, onMouseUp, reset, setRange };
  };

  // Map selected range to minutes
  const getRangeMinutes = (label: string): number => {
    switch (label) {
      case '1 Hours':
        return 60;
      case '6 Hours':
        return 6 * 60;
      case '12 Hours':
        return 12 * 60;
      case '24 Hours':
        return 24 * 60;
      case '7 Days':
        return 7 * 24 * 60;
      case '14 Days':
        return 14 * 24 * 60;
      case '30 Days':
        return 30 * 24 * 60;
      case 'Custom':
        // For custom range, calculate minutes from the actual date range
        return Math.max(1, Math.round((customEndDate.getTime() - customStartDate.getTime()) / (60 * 1000)));
      default:
        return 60;
    }
  };

  // Deterministic pseudo-random helper based on index
  const prand = (i: number) => {
    const x = Math.sin(i * 12.7898) * 43758.5453;
    return x - Math.floor(x);
  };

  // Helper function to get time format based on time range
  const getTimeFormat = (timeRange: string): string => {
    switch (timeRange) {
      case '7 Days':
      case '14 Days':
        return "dd MMM HH:mm"; // Show day and hour for 7/14 days
      case '30 Days':
        return 'dd MMM'; // Show only day for 30 days
      default:
        return 'HH:mm'; // Default format for hours
    }
  };

  // Generate a time series for the selected time range
  const generateSeries = (startDate: Date, minutes: number, base: number, amplitude: number, periodMinutes: number, timeRange?: string): GraphData[] => {
    const series: GraphData[] = [];
    const timeFormat = timeRange ? getTimeFormat(timeRange) : 'HH:mm';
    
    // For 7 Days: generate hourly slots (168 slots)
    if (timeRange === '7 Days') {
      const rangeStart = startOfHour(startDate);
      for (let i = 0; i < 168; i++) {
        const t = addHours(rangeStart, i);
        const time = format(t, timeFormat);
        const value = base + amplitude * Math.sin((2 * Math.PI * i) / periodMinutes) + (amplitude * 0.2) * (prand(i) - 0.5);
        series.push({ time, value: Number(value.toFixed(4)) });
      }
      return series;
    }

    // For 14 Days: generate hourly slots (336 slots)
    if (timeRange === '14 Days') {
      const rangeStart = startOfHour(startDate);
      for (let i = 0; i < 336; i++) {
        const t = addHours(rangeStart, i);
        const time = format(t, timeFormat);
        const value = base + amplitude * Math.sin((2 * Math.PI * i) / periodMinutes) + (amplitude * 0.2) * (prand(i) - 0.5);
        series.push({ time, value: Number(value.toFixed(4)) });
      }
      return series;
    }

    // For 30 Days: generate daily slots (30 slots)
    if (timeRange === '30 Days') {
      const rangeStart = startOfDay(startDate);
      for (let i = 0; i < 30; i++) {
        const t = addDays(rangeStart, i);
        const time = format(t, timeFormat);
        const value = base + amplitude * Math.sin((2 * Math.PI * i) / periodMinutes) + (amplitude * 0.2) * (prand(i) - 0.5);
        series.push({ time, value: Number(value.toFixed(4)) });
      }
      return series;
    }
    
    // Default: minute-resolution for other ranges
    for (let i = 0; i <= minutes; i++) {
      const t = addMinutes(startDate, i);
      const time = format(t, timeFormat);
      const value = base + amplitude * Math.sin((2 * Math.PI * i) / periodMinutes) + (amplitude * 0.2) * (prand(i) - 0.5);
      series.push({ time, value: Number(value.toFixed(4)) });
    }
    return series;
  };

  // Optionally override charts time window (e.g., when clicking a Critical Event)
  const [customChartWindow, setCustomChartWindow] = useState<{ startDate: Date; endDate: Date } | null>(null);
  const rangeMinutes = customChartWindow
    ? Math.max(1, Math.round((customChartWindow.endDate.getTime() - customChartWindow.startDate.getTime()) / (60 * 1000)))
    : getRangeMinutes(selectedTimeRange);
  const [serverNow, setServerNow] = useState<Date | null>(null);
  const GRAPH_REFRESH_LAG_MS = 5000; // graphs lag a few seconds after minute change
  const graphRefreshMinuteKey = useMemo(() => {
    const baseNow = serverNow || now;
    return Math.floor((baseNow.getTime() - GRAPH_REFRESH_LAG_MS) / 60000);
  }, [serverNow, now]);
  const minuteAlignedNow = useMemo(() => {
    const baseNow = serverNow || now;
    return new Date(Math.floor((baseNow.getTime() - GRAPH_REFRESH_LAG_MS) / 60000) * 60000);
  }, [serverNow, now]);
  const effectiveNow = customChartWindow ? customChartWindow.endDate : minuteAlignedNow;
  
  // Helper: given a window length (in minutes), return the expected aggregation interval in minutes
  // This mirrors the logic used in the aggregation functions (6/12/24/168/720 minutes)
  const getAggregationIntervalMinutesForWindow = (windowMinutes: number): number => {
    const hours = windowMinutes / 60;
    if (hours <= 1) return 1;      // ~1h window → per-minute
    if (hours <= 6) return 6;      // up to 6h → 6-minute interval
    if (hours <= 12) return 12;     // up to 12h → 12-minute interval
    if (hours <= 24) return 24;     // up to 24h → 24-minute interval
    if (hours <= 336) return 168;   // up to 14 days → 168-minute interval
    return 720;                     // >14 days → 720-minute interval
  };

  // Max allowed gap between consecutive points before we "break" the line.
  // Requirement: break if gap > 2× aggregation interval.
  const getMaxGapMinutesForWindow = (windowMinutes: number): number => {
    const interval = getAggregationIntervalMinutesForWindow(windowMinutes);
    return interval * 2;
  };

  // Effective window length for gap detection:
  // - if zoom is active, use zoom duration
  // - otherwise, use the full selected rangeMinutes
  const effectiveWindowMinutes = useMemo(() => {
    if (sharedZoomRange.startTime && sharedZoomRange.endTime) {
      const startMs = new Date(sharedZoomRange.startTime).getTime();
      const endMs = new Date(sharedZoomRange.endTime).getTime();
      const diffMinutes = (endMs - startMs) / 60000;
      if (diffMinutes > 0.1) return diffMinutes;
    }
    return rangeMinutes;
  }, [sharedZoomRange.startTime, sharedZoomRange.endTime, rangeMinutes]);

  const MAX_GAP_MINUTES_FOR_WINDOW = getMaxGapMinutesForWindow(effectiveWindowMinutes);
  
  // For custom range, use the actual custom dates instead of calculating from now
  const rangeStartDate = customChartWindow 
    ? customChartWindow.startDate 
    : (selectedTimeRange === 'Custom' ? customStartDate : subMinutes(effectiveNow, rangeMinutes));

  const resolveZoomAggregationParams = (rangeEndDate: Date) => {
    const zoom = sharedZoomRangeRef.current;
    let zoomDurationHours: number | null = null;
    let zoomStartDate: Date | null = null;
    let zoomStartMs: number | null = null;
    let zoomEndMs: number | null = null;

    if (zoom.startTime && zoom.endTime) {
      zoomStartDate = new Date(zoom.startTime);
      const zoomEnd = new Date(zoom.endTime);
      zoomStartMs = zoomStartDate.getTime();
      zoomEndMs = zoomEnd.getTime();
      zoomDurationHours = (zoomEndMs - zoomStartMs) / (60 * 1000) / 60;
    } else if (customChartWindow) {
      zoomDurationHours = (customChartWindow.endDate.getTime() - customChartWindow.startDate.getTime()) / (60 * 1000) / 60;
    } else if (selectedTimeRange === 'Custom') {
      zoomDurationHours = (customEndDate.getTime() - customStartDate.getTime()) / (60 * 1000) / 60;
    }

    const effectiveRangeStartMs = zoomStartMs ?? rangeStartDate.getTime();
    const effectiveRangeEndMs = zoomEndMs ?? rangeEndDate.getTime();
    const effectiveRangeStartDate = zoomStartDate ?? rangeStartDate;

    return {
      zoomDurationHours,
      zoomStartDate,
      zoomStartMs,
      zoomEndMs,
      effectiveRangeStartDate,
      effectiveRangeStartMs,
      effectiveRangeEndMs,
    };
  };

  // Summary cards should always represent the selected range, independent of chart zoom.
  const resolveSummaryRangeParams = (rangeEndDate: Date) => {
    const summaryRangeStartMs = rangeStartDate.getTime();
    const summaryRangeEndMs = rangeEndDate.getTime();
    return {
      summaryRangeStartMs,
      summaryRangeEndMs,
    };
  };

  const parseWristModeValue = (value: unknown): boolean | null => {
    if (value === true || value === false) return value;
    if (value == null) return null;
    if (typeof value === 'number') {
      if (value === 1) return true;
      if (value === 0) return false;
      return null;
    }
    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      if (['true', 't', '1', 'yes', 'y'].includes(normalized)) return true;
      if (['false', 'f', '0', 'no', 'n'].includes(normalized)) return false;
    }
    return null;
  };

  const getVitalRowTimestamp = (row: any): string | null => {
    const ts = row?.timestamp ?? row?.time ?? row?.created_at ?? row?.createdAt ?? row?.ts;
    return ts ? String(ts) : null;
  };

  const computeSummaryWristModeFromRows = (
    rows: any[],
    rangeEndDate: Date
  ): { hasTrue: boolean; hasFalse: boolean } => {
    const { summaryRangeStartMs, summaryRangeEndMs } = resolveSummaryRangeParams(rangeEndDate);
    let hasTrue = false;
    let hasFalse = false;
    for (const row of rows) {
      const rowTs = getVitalRowTimestamp(row);
      if (!rowTs) continue;
      const rowMs = parseDbTimestamp(rowTs).getTime();
      if (rowMs < summaryRangeStartMs || rowMs > summaryRangeEndMs) continue;
      const wristModeValue = parseWristModeValue(row.wrist_mode ?? row.wristMode);
      if (wristModeValue === true) hasTrue = true;
      if (wristModeValue === false) hasFalse = true;
      if (hasTrue && hasFalse) break;
    }
    return { hasTrue, hasFalse };
  };
  
  // console.log('Time range calculation:', {
  //   selectedTimeRange,
  //   effectiveNow: effectiveNow.toISOString(),
  //   rangeMinutes,
  //   rangeStartDate: rangeStartDate.toISOString(),
  //   customStartDate: customStartDate.toISOString(),
  //   customEndDate: customEndDate.toISOString()
  // });

  // Temperature from DB only (no demo fallback)
  const [dbTemperature, setDbTemperature] = useState<Array<GraphData & { tsMs?: number }>>([]);
  // Visible base data for the selected time window
  // Time base used only for x-axis labels/zoom (one label per minute/hour/day in range)
  const timeAxisBaseData = generateSeries(rangeStartDate, rangeMinutes, 0, 0, 1, selectedTimeRange);
  const temperatureBaseData = timeAxisBaseData;

  // Real data from DB for Breathing and Heart Rate
  const [dbBreathing, setDbBreathing] = useState<GraphData[]>([]);
  const [dbHeartRate, setDbHeartRate] = useState<GraphData[]>([]);
  const [dbHeartRateVariability, setDbHeartRateVariability] = useState<GraphData[]>([]);
  const [dbSpo2, setDbSpo2] = useState<GraphData[]>([]);
  const [dbRespirationRate, setDbRespirationRate] = useState<GraphData[]>([]);
  const [, setDbSystolic_diastolicBloodPressure] = useState<GraphData[]>([]);
  const [dbSystolicBloodPressure, setDbSystolicBloodPressure] = useState<GraphData[]>([]);
  const [dbDiastolicBloodPressure, setDbDiastolicBloodPressure] = useState<GraphData[]>([]);
  const [dbSleep, setDbSleep] = useState<GraphData[]>([]);
  const [activityTimelineRows, setActivityTimelineRows] = useState<any[]>([]);
  const [patientBedTime, setPatientBedTime] = useState<string | null>(null);
  const [summaryNightSleepData, setSummaryNightSleepData] = useState<GraphData[]>([]);
  const [historicalSleepData, setHistoricalSleepData] = useState<GraphData[]>([]);
  const [summaryWristMode, setSummaryWristMode] = useState<{ hasTrue: boolean; hasFalse: boolean }>({ hasTrue: false, hasFalse: false });

  /** Raw out-of-range counts for the selected period (from raw graph readings, not aggregated points). */
  const [rawOutOfRangeStats, setRawOutOfRangeStats] = useState<{
    heartRate: { out: number; total: number };
    hrv: { out: number; total: number };
    spo2: { out: number; total: number };
    breathing: { out: number; total: number };
    systolicBp: { out: number; total: number };
    diastolicBp: { out: number; total: number };
    temperature: { out: number; total: number };
  } | null>(null);

  /** Min, max, median, night median (patient bedtime window) from raw data for Current panels — overrides median-based stats when set. */
  type RawMetricStats = { min: number; max: number; median: number | null; nightMedian: number | null };
  // Y-axis bottom → top: Mask, Deep, Light, REM, Wake
  const SLEEP_STAGE_LABELS = ['Mask', 'Deep', 'Light', 'REM', 'Awake'];
  const SLEEP_STAGE_Y_MAX = 4.5;
  const getSleepStageValue = (value: unknown): number | null => {
    if (typeof value === 'number' && Number.isFinite(value)) {
      if (value >= 0 && value <= 4) return value;
      return null;
    }
    if (typeof value === 'string') {
      const s = value.trim().toUpperCase();
      if (!s) return null;
      const stageMap: Record<string, number> = {
        MASK: 0,
        DEEP: 1,
        LIGHT: 2,
        REM: 3,
        WAKE: 4,
        AWAKE: 4
      };
      if (stageMap[s] != null) return stageMap[s];
      const n = Number(value);
      if (Number.isFinite(n) && n >= 0 && n <= 4) return n;
    }
    return null;
  };
  const [rawCurrentStats, setRawCurrentStats] = useState<{
    heartRate?: RawMetricStats;
    hrv?: RawMetricStats;
    spo2?: RawMetricStats;
    breathing?: RawMetricStats;
    systolicBp?: RawMetricStats;
    diastolicBp?: RawMetricStats;
    temperature?: RawMetricStats;
  } | null>(null);
  
  const [latestVitalsTs, setLatestVitalsTs] = useState<Date | null>(null);
  const [, setLatestTempTs] = useState<Date | null>(null);
  const [, setLatestBreathing] = useState<number | null>(null);
  const [, setLatestHeart] = useState<number | null>(null);
  const [, setLatestHeartRateVariability] = useState<number | null>(null);
  const [, setLatestSpo2] = useState<number | null>(null);
  const [, setLatestTemperature] = useState<number | null>(null);
  
  const [, setLatestSystolicBloodPressure] = useState<number | null>(null);
  const [, setLatestDiastolicBloodPressure] = useState<number | null>(null);

  /** Stats from vitals_stats_wide for current patient (invalid%, Warnings, Critical events) — real mode only */
  const [, setCurrentVitalsStats] = useState<VitalsStatsWideRow | null>(null);

  // Start backend health monitor once on mount and sync server time
  useEffect(() => {
    startHealthMonitor();
    const sync = async () => {
      try {
        const h = await getServerHealth();
        if (h?.timestamp) setServerNow(new Date(h.timestamp));
      } catch {}
    };
    sync();
    const id = setInterval(sync, 30000);
    return () => clearInterval(id);
  }, []);

  // Fetch vitals stats (invalid%, Warnings, Critical events) for current patient — real mode only
  useEffect(() => {
    if (!patId || operationMode === 'demo') {
      setCurrentVitalsStats(null);
      return;
    }
    const range = selectedTimeRange === '24 Hours' ? '24h' : '7d';
    let cancelled = false;
    getVitalsStatsWide(patId, range).then((s) => {
      if (!cancelled) setCurrentVitalsStats(s ?? null);
    }).catch(() => {
      if (!cancelled) setCurrentVitalsStats(null);
    });
    return () => { cancelled = true; };
  }, [patId, operationMode, selectedTimeRange]);

  useEffect(() => {
    if (!patId || operationMode === 'demo') {
      setPatientBedTime(null);
      return;
    }
    let cancelled = false;
    getPatientBedTime(patId)
      .then((r) => {
        if (!cancelled) setPatientBedTime(r.bed_time ?? null);
      })
      .catch(() => {
        if (!cancelled) setPatientBedTime(null);
      });
    return () => { cancelled = true; };
  }, [patId, operationMode]);

  useEffect(() => {
    if (operationMode === 'demo') {
      setSummaryWristMode({ hasTrue: false, hasFalse: false });
    }
  }, [operationMode]);

  useEffect(() => {
    if (!patIdLoaded) return; // Wait for patId to be loaded from API
    // When in demo mode: clear real datasets and load demo data
    if (operationMode === 'demo') {
      // Clear all real datasets first
      setDbTemperature([]);
      setDbBreathing([]);
      setDbHeartRate([]);
      setDbHeartRateVariability([]);
      setDbSpo2([]);
      setDbRespirationRate([]);
      setDbSystolic_diastolicBloodPressure([]);
      setDbSystolicBloodPressure([]);
      setDbDiastolicBloodPressure([]);
      setSummaryWristMode({ hasTrue: false, hasFalse: false });
      setDbSleep([]);
      setActivityTimelineRows([]);
      setRawOutOfRangeStats(null);
      setRawCurrentStats(null);
      
      // Load demo data for heart rate, breathing, and temperature
      const loadDemoData = async () => {
        try {
          // Use the same date range logic as real mode (include critical-event ±12h via customChartWindow)
          const start = rangeStartDate;
          const end = customChartWindow ? customChartWindow.endDate : (selectedTimeRange === 'Custom' ? customEndDate : new Date());
          
          const rows = await getDemoVitalsRange(start.toISOString(), end.toISOString());
          setActivityTimelineRows(Array.isArray(rows) ? rows : []);
          console.log('[DEMO-FALLBACK] getDemoVitalsRange rows (no patId):', {
            selectedTimeRange,
            start: start.toISOString(),
            end: end.toISOString(),
            count: Array.isArray(rows) ? rows.length : 0,
          });
          console.log('[DEMO] getDemoVitalsRange rows:', {
            selectedTimeRange,
            start: start.toISOString(),
            end: end.toISOString(),
            count: Array.isArray(rows) ? rows.length : 0,
          });
          rawVitalsRowsRef.current = Array.isArray(rows) ? rows : [];
          
          // Helper function to calculate median
          const calculateMedian = (values: number[]): number => {
            if (values.length === 0) return 0;
            const sorted = [...values].sort((a, b) => a - b);
            const mid = Math.floor(sorted.length / 2);
            return sorted.length % 2 === 0 
              ? (sorted[mid - 1] + sorted[mid]) / 2 
              : sorted[mid];
          };
          
          
          const calculatePercentiles = (values: number[]): { q1: number; q3: number;} => {
            if (values.length === 0) {
              return { q1: NaN, q3: NaN};
            }
          
            const sorted = [...values].sort((a, b) => a - b);
          
            // Helper για percentile
            const percentile = (arr: number[], p: number): number => {
              const idx = (arr.length - 1) * p;
              const lower = Math.floor(idx);
              const upper = Math.ceil(idx);
              if (lower === upper) return arr[lower];
              const weight = idx - lower;
              return arr[lower] * (1 - weight) + arr[upper] * weight;
            };
            return {
              q1: percentile(sorted, 0.25),       // 25ο percentile
              q3: percentile(sorted, 0.75),       // 75ο percentile
            };
          };        


          const calculateCustomWhiskers = (values: number[]) => {
            if (values.length === 0) {
              return { q_15: NaN, q_85: NaN };
            }
          
            const sorted = [...values].sort((a, b) => a - b);
          
            const min = sorted[0];
            const max = sorted[sorted.length - 1];
          
            const { q1, q3 } = calculatePercentiles(values);
          
            const x = (q3 - q1) * 1.5;
          
            // ---- q_85 (upper whisker) ----
            const upperLimit = q3 + x;
            let q_85: number;
            const EPS = 1e-9;
            
            if (upperLimit >= max - EPS) {
              q_85 = max;
            } else {
                          // μεγαλύτερη τιμή <= upperLimit
              const found = [...sorted].reverse().find(v => v <= upperLimit);
              q_85 = found !== undefined ? found : max;
            }
          
            // ---- q_15 (lower whisker) ----
            const lowerLimit = q1 - x;
            let q_15: number;
          
            if (lowerLimit <= min + EPS) {
              q_15 = min;
            } else {
              // μικρότερη τιμή >= lowerLimit
              const found = sorted.find(v => v >= lowerLimit);
              q_15 = found !== undefined ? found : min;
            }
          
            return { q_15, q_85 };
          };
            

          const calculateStats = (values: number[]) => {


            if (values.length === 0) {
              return { median: NaN, min: NaN, max: NaN, q1: NaN, q3: NaN,q_15:NaN ,q_85:NaN};
            }
          
            const sorted = [...values].sort((a, b) => a - b);
          
            const median = calculateMedian (values);
            const {q1,q3}=calculatePercentiles(values);
            const {q_15,q_85} = calculateCustomWhiskers(values);
            return {
              median,
              min: sorted[0], 
              max: sorted[sorted.length - 1],
              q1:q1,
              q3:q3,
              q_15:q_15,
              q_85:q_85
            };
          };

          // Aggregation function based on time range
          const aggregateByTimeRange = (
            data: Array<{ timestamp: Date; value: number }>,
            timeRange: string,
            rangeStartDate: Date,
            zoomDurationHours?: number | null
          ): Array<{ timestamp: Date; endTimestamp?: Date; value: number; min: number; max: number,q1:number,q3:number,q_15:number, q_85:number}> => {
            if (data.length === 0) return [];
            
            // Determine aggregation interval based on zoom duration if zoom is active
            // (Critical-event ±12h window sets sharedZoomRange ≈ 24h → 24-min buckets by default;
            // further zoom uses the normal zoomDurationHours thresholds.)
            // Otherwise use the selected time range
            let useZoomInterval = false;
            let intervalMinutes: number = 1;
            
            if (zoomDurationHours != null && zoomDurationHours > 0) {
              useZoomInterval = true;
              if (zoomDurationHours <= 1) {
                // 1 h: Κανονικά 1/min (όλα τα points)
                console.log('[DEMO AGGREGATION] Zoom <= 1h: Returning all points without aggregation');
                return data.map(d => ({
                  timestamp: d.timestamp,
                  value: d.value, // median == raw value
                  min: d.value,
                  max: d.value,
                  q1:d.value,
                  q3:d.value,
                  q_15:d.value,
                  q_85:d.value
                }));
              } else if (zoomDurationHours <= 6) {
                // 6 h: 6-minute plotbox (60 time slots)
                intervalMinutes = 6;
                console.log(`[DEMO AGGREGATION] Zoom <= 6h: Using 6-minute plotbox, intervalMinutes=${intervalMinutes}`);
              } else if (zoomDurationHours <= 12) {
                // 12 h: 12-minute plotbox (60 time slots)
                intervalMinutes = 12;
                console.log(`[DEMO AGGREGATION] Zoom <= 12h: Using 12-minute plotbox, intervalMinutes=${intervalMinutes}`);
              } else if (zoomDurationHours <= 24) {
                // 24 h: 24-minute plotbox (60 time slots)
                intervalMinutes = 24;
                console.log(`[DEMO AGGREGATION] Zoom <= 24h: Using 24-minute plotbox, intervalMinutes=${intervalMinutes}`);
              } else if (zoomDurationHours <= 168) { // 7 days
                // 7 days: 168 minutes interval
                intervalMinutes = 168;
                console.log(`[DEMO AGGREGATION] Zoom <= 7 days: Using ${intervalMinutes} minutes interval`);
              } else {
                // > 7 days: 720 minutes interval
                intervalMinutes = 720;
                console.log(`[DEMO AGGREGATION] Zoom > 7 days: Using ${intervalMinutes} minutes interval`);
              }
            }
            
            // Determine aggregation interval based on time range (if not using zoom interval) initialization of intervalMinutes DEMO
            if (!useZoomInterval) {
              switch (timeRange) {
                case '1 Hours':
                  // 1 h: Κανονικά 1/min (όλα τα points)
                  return data.map(d => ({
                    timestamp: d.timestamp,
                    value: d.value, // median == raw value
                    min: d.value,
                    max: d.value,
                    q1: d.value,
                    q3: d.value,
                    q_15:d.value,
                    q_85:d.value
                  }));
                case '6 Hours':
                  // 6 h: 6-minute plotbox (60 time slots)
                  intervalMinutes = 6;
                  break;
                case '12 Hours':
                  // 12 h: 12-minute plotbox (60 time slots)
                  intervalMinutes = 12;
                  break;
                case '24 Hours':
                  // 24 h: 24-minute plotbox (60 time slots)
                  intervalMinutes = 24;
                  break;
                case '7 Days':
                case '14 Days':
                  // 7/14 days: 168 minutes interval
                  intervalMinutes = 168;
                  break;
                case '30 Days':
                  // 30 days: 720 minutes interval
                  intervalMinutes = 720;
                  break;
                default:
                  // For custom or unknown ranges, use 1-minute intervals
                  intervalMinutes = 1;
              }
            }
            
            console.log(`[DEMO AGGREGATION] Grouping ${data.length} points with intervalMinutes=${intervalMinutes}, rangeStartDate=${rangeStartDate.toISOString()}`);

            // Group data by time intervals
            const grouped = new Map<string, Array<{ timestamp: Date; value: number }>>();
            
            for (const point of data) {
              const timestamp = point.timestamp.getTime();
              const rangeStart = rangeStartDate.getTime();
              const minutesFromStart = Math.floor((timestamp - rangeStart) / (1000 * 60));
              const intervalIndex = Math.floor(minutesFromStart / intervalMinutes);
              const intervalKey = String(intervalIndex);
              
              if (!grouped.has(intervalKey)) {
                grouped.set(intervalKey, []);
              }
              grouped.get(intervalKey)!.push(point);
            }

            // Calculate median for each group
            const aggregated: Array<{
              timestamp: Date;
              endTimestamp?: Date;
              value: number;
              min: number;
              max: number;
              q1: number,
              q3: number,
              q_15:number,
              q_85:number,
            }> = [];
            const groupedEntries = Array.from(grouped.entries());
            for (let i = 0; i < groupedEntries.length; i++) {
              const [, points] = groupedEntries[i];
              const values = points.map((point) => point.value);
              const firstSampleMs = Math.min(...points.map((point) => point.timestamp.getTime()));
              const lastSampleMs = Math.max(...points.map((point) => point.timestamp.getTime()));

              const { median, min, max,q1,q3, q_15,q_85 } = calculateStats(values);

              aggregated.push({
                // Never position an aggregate before its first source reading.
                timestamp: new Date(firstSampleMs),
                endTimestamp: new Date(lastSampleMs),
                value: median,
                min:min,
                max:max,
                q1:q1,
                q3:q3,
                q_15:q_15,
                q_85:q_85
              });

            }

            // Sort by timestamp
            return aggregated.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
          };

          const applyDemoFromRows = (inputRows: any[]) => {
          setActivityTimelineRows(Array.isArray(inputRows) ? inputRows : []);
          // Calculate zoom duration from current zoom state (reads sharedZoomRangeRef for sync re-aggregation)
          const demoRangeEndDate = customChartWindow ? customChartWindow.endDate : (selectedTimeRange === 'Custom' ? customEndDate : now);
          const {
            zoomDurationHours,
            effectiveRangeStartMs,
            effectiveRangeEndMs,
            effectiveRangeStartDate,
          } = resolveZoomAggregationParams(demoRangeEndDate);

          if (zoomDurationHours != null && sharedZoomRangeRef.current.startTime && sharedZoomRangeRef.current.endTime) {
            console.log(`[DEMO ZOOM] Zoom active: ${zoomDurationHours.toFixed(2)} hours`);
          }
          
          // Collect all demo data points with timestamps
          const tempPoints: Array<{ timestamp: Date; value: number }> = [];
          const breathPoints: Array<{ timestamp: Date; value: number }> = [];
          const hrPoints: Array<{ timestamp: Date; value: number }> = [];
          const hrvPoints: Array<{ timestamp: Date; value: number }> = [];
          const spo2Points: Array<{ timestamp: Date; value: number }> = [];
          const sleepPoints: Array<{ timestamp: Date; value: number }> = [];
          const sysPoints: Array<{ timestamp: Date; value: number }> = [];
          const diaPoints: Array<{ timestamp: Date; value: number }> = [];

          let rawPointsCount = 0;
          for (const r of inputRows) {
            if (!r || !r.timestamp) continue;
            const ts = parseDbTimestamp(r.timestamp);
            const tMs = ts.getTime();
            
            // Filter data to be within the effective time range (zoom or selected range)
            if (tMs < effectiveRangeStartMs || tMs > effectiveRangeEndMs) {
              continue;
            }
            
            rawPointsCount++;
            
            const tempV = r.temperature != null ? Number(r.temperature) : null;
            const breathV = r.breathing != null ? Number(r.breathing) : null;
            const hrV = r.heart_rate != null ? Number(r.heart_rate) : null;
            const hrvV = r.heart_rate_variability != null ? Number(r.heart_rate_variability) : null;
            const spo2V = r.spo2 != null ? Number(r.spo2) : null;
            const sysV = r.systolic != null ? Number(r.systolic) : null;
            const diaV = r.diastolic != null ? Number(r.diastolic) : null;
            
            const sleepV = getSleepStageValue((r as any).sleep_stage);

            if (tempV != null && !isNaN(tempV)) tempPoints.push({ timestamp: ts, value: Number(tempV.toFixed(1)) });
            if (breathV != null && !isNaN(breathV)) breathPoints.push({ timestamp: ts, value: Math.round(breathV) });
            if (hrV != null && !isNaN(hrV)) hrPoints.push({ timestamp: ts, value: Math.round(hrV) });
            const hrvDisplay = normalizeHrvForDisplay(hrvV);
            if (hrvDisplay != null) hrvPoints.push({ timestamp: ts, value: Math.round(hrvDisplay) });
            if (spo2V != null && !isNaN(spo2V)) spo2Points.push({ timestamp: ts, value: Math.round(spo2V) });
            if (sleepV != null) sleepPoints.push({ timestamp: ts, value: sleepV });
            if (sysV != null && !isNaN(sysV)) sysPoints.push({ timestamp: ts, value: Math.round(sysV) });
            if (diaV != null && !isNaN(diaV)) diaPoints.push({ timestamp: ts, value: Math.round(diaV) });
          }

          if (zoomDurationHours != null) {
            console.log(`[DEMO ZOOM] Filtered ${rawPointsCount} raw points from ${inputRows.length} total rows for zoom range`);
          }

          // Demo mode: compute median/min/max/night median from raw points for Current panels (not from aggregated)
          const rawMedian = (arr: number[]) => {
            if (arr.length === 0) return null;
            const s = [...arr].sort((a, b) => a - b);
            const mid = Math.floor(s.length / 2);
            return s.length % 2 === 0 ? (s[mid - 1]! + s[mid]!) / 2 : s[mid]!;
          };
          const toRawStatsFromPoints = (points: Array<{ timestamp: Date; value: number }>): RawMetricStats | undefined => {
            // Match SQL: zeros are not valid readings
            const validPoints = points.filter((p) => p.value > 0);
            if (validPoints.length === 0) return undefined;
            const values = validPoints.map((p) => p.value);
            const nightValues = validPoints
              .filter((p) => isInNightWindow(p.timestamp, patientBedTime))
              .map((p) => p.value);
            return {
              min: Math.round(Math.min(...values)),
              max: Math.round(Math.max(...values)),
              median: rawMedian(values),
              nightMedian: nightValues.length > 0 ? rawMedian(nightValues) : null,
            };
          };
          /** HRV summary: period/night medians and min/max exclude values > 200 ms (invalid). */
          const toRawHrvStatsFromPoints = (points: Array<{ timestamp: Date; value: number }>): RawMetricStats | undefined => {
            const values = points.map((p) => p.value).filter((v) => v > 0 && v <= MAX_HRV_MS);
            if (values.length === 0) return undefined;
            const nightMedianValues = points
              .filter((p) => p.value > 0 && p.value <= MAX_HRV_MS && isInNightWindow(p.timestamp, patientBedTime))
              .map((p) => p.value);
            return {
              min: Math.round(Math.min(...values)),
              max: Math.round(Math.max(...values)),
              median: rawMedian(values),
              nightMedian: nightMedianValues.length > 0 ? rawMedian(nightMedianValues) : null,
            };
          };
          const toRawStatsTempFromPoints = (points: Array<{ timestamp: Date; value: number }>): RawMetricStats | undefined => {
            const validPoints = points.filter((p) => p.value > 0);
            if (validPoints.length === 0) return undefined;
            const values = validPoints.map((p) => p.value);
            const nightValues = validPoints
              .filter((p) => isInNightWindow(p.timestamp, patientBedTime))
              .map((p) => p.value);
            return {
              min: Math.round(Math.min(...values) * 10) / 10,
              max: Math.round(Math.max(...values) * 10) / 10,
              median: rawMedian(values),
              nightMedian: nightValues.length > 0 ? rawMedian(nightValues) : null,
            };
          };
          setRawCurrentStats({
            heartRate: toRawStatsFromPoints(hrPoints),
            hrv: toRawHrvStatsFromPoints(hrvPoints),
            spo2: toRawStatsFromPoints(spo2Points),
            breathing: toRawStatsFromPoints(breathPoints),
            temperature: toRawStatsTempFromPoints(tempPoints),
            systolicBp: toRawStatsFromPoints(sysPoints),
            diastolicBp: toRawStatsFromPoints(diaPoints),
          });

          // Demo: out-of-range from raw graph points (not aggregated). HRV uses hrvPoints (already excludes > 200 ms).
          const countOut = (points: Array<{ timestamp: Date; value: number }>, isOut: (v: number) => boolean) => {
            // Match SQL: zeros are not valid readings in the denominator
            const valid = points.filter((p) => p.value > 0);
            const total = valid.length;
            const out = valid.filter((p) => isOut(p.value)).length;
            return { out, total };
          };
          const isHrvOutOfRange = (v: number) =>
            v <= MAX_HRV_MS &&
            (v < thresholds.hrv.low || v > thresholds.hrv.high || (v >= thresholds.hrv.low && v <= thresholds.hrv.medium));
          const demoRawOutOfRange = {
            // Upper bounds inclusive of threshold (= in range), matching SQL view
            heartRate: countOut(hrPoints, (v) => v < thresholds.heartRate.low || v > thresholds.heartRate.medium),
            hrv: countOut(hrvPoints, isHrvOutOfRange),
            spo2: countOut(spo2Points, (v) => v <= thresholds.spo2.medium),
            breathing: countOut(breathPoints, (v) => v < thresholds.breathing.low || v > thresholds.breathing.medium),
            systolicBp: countOut(sysPoints, (v) => v < thresholds.bloodPressure.systolic.low || v > thresholds.bloodPressure.systolic.systolic_stage1),
            diastolicBp: countOut(diaPoints, (v) => v < thresholds.bloodPressure.diastolic.low || v > thresholds.bloodPressure.diastolic.diastolic_stage1),
            temperature: countOut(tempPoints, (v) => !(v >= thresholds.temperature.low && v <= thresholds.temperature.medium)),
          };
          setRawOutOfRangeStats(demoRawOutOfRange);

          // Apply aggregation based on time range with zoom duration
          // Use effectiveRangeStartDate (zoom start if zooming, otherwise original rangeStartDate)
          const aggregatedTemp = aggregateByTimeRange(tempPoints, selectedTimeRange, effectiveRangeStartDate, zoomDurationHours);
          const aggregatedBreath = aggregateByTimeRange(breathPoints, selectedTimeRange, effectiveRangeStartDate, zoomDurationHours);
          const aggregatedHr = aggregateByTimeRange(hrPoints, selectedTimeRange, effectiveRangeStartDate, zoomDurationHours);
          const aggregatedHrv = aggregateByTimeRange(hrvPoints, selectedTimeRange, effectiveRangeStartDate, zoomDurationHours);
          const aggregatedSpo2 = aggregateByTimeRange(spo2Points, selectedTimeRange, effectiveRangeStartDate, zoomDurationHours);
          const aggregatedSys = aggregateByTimeRange(sysPoints, selectedTimeRange, effectiveRangeStartDate, zoomDurationHours);
          const aggregatedDia = aggregateByTimeRange(diaPoints, selectedTimeRange, effectiveRangeStartDate, zoomDurationHours);
          
          if (zoomDurationHours != null) {
            console.log(`[DEMO ZOOM] After aggregation: temp=${aggregatedTemp.length}, hr=${aggregatedHr.length} points`);
          }

          // Convert to GraphData format with appropriate time format
          const timeFormat = (selectedTimeRange === "7 Days" || selectedTimeRange === "14 Days") ? "dd MMM HH:mm" 
  : selectedTimeRange === "30 Days" ? "dd MMM" 
  : "HH:mm";
          
          const demoTemperature = aggregatedTemp.map(d => ({
            time: format(d.timestamp, timeFormat),
            value: d.value,
            min: d.min,
            max: d.max,
            q1:d.q1,
            q3:d.q3,
            q_15:d.q_15,
            q_85:d.q_85,
            tsMs: d.timestamp.getTime(),
            endTsMs: d.endTimestamp?.getTime()
          }));
          
          const demoBreathing = aggregatedBreath.map(d => ({
            time: format(d.timestamp, timeFormat),
            value: d.value,
            min: d.min,
            max: d.max,
            q1:d.q1,
            q3:d.q3,
            q_15:d.q_15,
            q_85:d.q_85,
            tsMs: d.timestamp.getTime(),
            endTsMs: d.endTimestamp?.getTime()
          }));
          const demoHeartRate = aggregatedHr.map(d => ({
            time: format(d.timestamp, timeFormat),
            value: d.value,
            min: d.min,
            max: d.max,
            q1:d.q1,
            q3:d.q3,
            q_15:d.q_15,
            q_85:d.q_85,
            tsMs: d.timestamp.getTime(),
            endTsMs: d.endTimestamp?.getTime()
          }));
          const demoHeartRateVariability = aggregatedHrv.map(d => ({
            time: format(d.timestamp, timeFormat),
            value: d.value,
            min: d.min,
            max: d.max,
            q1:d.q1,
            q3:d.q3,
            q_15:d.q_15,
            q_85:d.q_85,
            tsMs: d.timestamp.getTime(),
            endTsMs: d.endTimestamp?.getTime()
          }));
          
          const demoSpo2 = aggregatedSpo2.map(d => ({
            time: format(d.timestamp, timeFormat),
            value: d.value,
            min: d.min,
            max: d.max,
            tsMs: d.timestamp.getTime(),
            endTsMs: d.endTimestamp?.getTime()
          }));
          
          // Sleep: 1 point per minute like real mode — never aggregate sleep stages
          const demoSleep = sleepPoints.map(p => ({
            time: format(p.timestamp, timeFormat),
            value: p.value,
            tsMs: p.timestamp.getTime(),
          }));
          
          const demoSystolicBloodPressure = aggregatedSys.map(d => ({
            time: format(d.timestamp, timeFormat),
            value: d.value,
            min: d.min,
            max: d.max,
            tsMs: d.timestamp.getTime(),
            endTsMs: d.endTimestamp?.getTime()
          }));
          
          const demoDiastolicBloodPressure = aggregatedDia.map(d => ({
            time: format(d.timestamp, timeFormat),
            value: d.value,
            min: d.min,
            max: d.max,
            tsMs: d.timestamp.getTime(),
            endTsMs: d.endTimestamp?.getTime()
          }));

          // Set demo data for all charts (except sleeping pattern)
          console.log('Demo data samples (aggregated):', { 
            selectedTimeRange,
            rangeStartDate: rangeStartDate.toISOString(),
            end: (selectedTimeRange === 'Custom' ? customEndDate : new Date()).toISOString(),
            rawDataCount: inputRows.length,
            tempPoints: tempPoints.length,
            breathPoints: breathPoints.length,
            hrPoints: hrPoints.length,
            hrvPoints: hrvPoints.length,
            spo2Points: spo2Points.length,
            sysPoints: sysPoints.length,
            diaPoints: diaPoints.length,
            aggregatedTemp: aggregatedTemp.length,
            aggregatedBreath: aggregatedBreath.length,
            aggregatedHr: aggregatedHr.length,
            aggregatedHrv: aggregatedHrv.length,
            aggregatedSpo2: aggregatedSpo2.length,
            aggregatedSys: aggregatedSys.length,
            aggregatedDia: aggregatedDia.length,
            temperature: demoTemperature.slice(0, 3), 
            heartRate: demoHeartRate.slice(0, 3), 
            spo2: demoSpo2.slice(0, 3)
          });
          setDbTemperature(demoTemperature);
          setDbBreathing(demoBreathing);
          setDbRespirationRate(demoBreathing);
          setDbHeartRate(demoHeartRate);
          setDbHeartRateVariability(demoHeartRateVariability);
          setDbSpo2(demoSpo2);
          setDbSystolicBloodPressure(demoSystolicBloodPressure);
          setDbDiastolicBloodPressure(demoDiastolicBloodPressure);
          setDbSystolic_diastolicBloodPressure(demoSystolicBloodPressure); // Combined chart
          setSummaryWristMode({ hasTrue: false, hasFalse: false });
          // Convert to final numeric series. If early minutes have no stage yet, default to Awake (0)
          const rawSleep: GraphData[] = demoSleep.map(s => ({
            time: s.time,
            value: s.value,
            tsMs: s.tsMs
          } as GraphData));
          setDbSleep(rawSleep);
          };

          reaggregateVitalsRef.current = applyDemoFromRows;
          applyDemoFromRows(rows);
          
        } catch (error) {
          console.warn('Failed to load demo data:', error);
          // Keep datasets empty if demo data fails to load
          setActivityTimelineRows([]);
        }
      };
      
      loadDemoData();
      refreshDemoDataRef.current = loadDemoData;
      // Refresh demo charts every 10s, but pause auto-refresh while a critical event snapshot is active
      if (!selectedEventTimestamp) {
        const demoIv = setInterval(loadDemoData, 10000);
        return () => clearInterval(demoIv);
      }
      return;
      
    }
    // Fetch real vital signs for current range every time range or clock changes
    const controller = new AbortController();
    const fetchRange = async () => {
      // Guard: only run real fetcher in REAL mode
      if (!isReal) return;
      // When a critical event is selected we still run the fetch so that temperature (and vitals)
      // for the ±12h window are loaded via getTemperatureRange/getVitalSignsRange; rangeStartDate/rangeEndDate
      // already come from customChartWindow, so we load the correct window. The 30s interval will re-fetch
      // the same window, which is correct.
      try {
        if (!patId) {
          // No patient selected, load demo data as fallback
          console.log('[FE] No patId selected, loading demo data as fallback');
          const start = rangeStartDate;
          const end = selectedTimeRange === 'Custom' ? customEndDate : new Date();
          
          const rows = await getDemoVitalsRange(start.toISOString(), end.toISOString());
          setActivityTimelineRows(Array.isArray(rows) ? rows : []);
          
          // Helper function to calculate median
          const calculateMedian = (values: number[]): number => {
            if (values.length === 0) return 0;
            const sorted = [...values].sort((a, b) => a - b);
            const mid = Math.floor(sorted.length / 2);
            return sorted.length % 2 === 0 
              ? (sorted[mid - 1] + sorted[mid]) / 2 
              : sorted[mid];
          };

          // Aggregation function based on time range
          const aggregateByTimeRange = (
            data: Array<{ timestamp: Date; value: number }>,
            timeRange: string,
            rangeStartDate: Date,
            zoomDurationHours?: number | null
          ): Array<{ timestamp: Date; endTimestamp?: Date; value: number }> => {
            if (data.length === 0) return [];
            
            // Determine aggregation interval based on zoom duration if zoom is active
            // Otherwise use the selected time range
            let useZoomInterval = false;
            let intervalMinutes: number = 1;
            
            if (zoomDurationHours != null && zoomDurationHours > 0) {
              useZoomInterval = true;
              if (zoomDurationHours <= 1) {
                // 1 h: Κανονικά 1/min (όλα τα points)
                return data.map(d => ({ timestamp: d.timestamp, value: d.value }));
                } else if (zoomDurationHours <= 6) {
                  // 6 h: 6-minute intervals
                  intervalMinutes = 6;
                } else if (zoomDurationHours <= 12) {
                  // 12 h: 12-minute intervals
                  intervalMinutes = 12;
                } else if (zoomDurationHours <= 24) {
                  // 24 h: 24-minute intervals
                  intervalMinutes = 24;
              } else if (zoomDurationHours <= 168) { // 7 days
                // 7 days: 168 minutes interval
                intervalMinutes = 168;
              } else {
                // > 7 days: 720 minutes interval
                intervalMinutes = 720;
              }
            }

            // Determine aggregation interval based on time range (if not using zoom interval) initialization of intervalMinutes
            if (!useZoomInterval) {
              switch (timeRange) {
                case '1 Hours':
                  // No aggregation for 1 hour - keep all points
                  return data.map(d => ({ timestamp: d.timestamp, value: d.value }));
                case '6 Hours':
                  intervalMinutes = 6;
                  break;
                case '12 Hours':
                  intervalMinutes = 12;
                  break;
                case '24 Hours':
                  intervalMinutes = 24;
                  break;
                case '7 Days':
                case '14 Days':
                  // 7/14 days: 168 minutes interval
                  intervalMinutes = 168;
                  break;
                case '30 Days':
                  // 30 days: 720 minutes interval
                  intervalMinutes = 720;
                  break;
                default:
                  // For custom or unknown ranges, use 1-minute intervals
                  intervalMinutes = 1;
              }
            }

            // Group data by time intervals
            const grouped = new Map<string, Array<{ timestamp: Date; value: number }>>();
            
            for (const point of data) {
              const timestamp = point.timestamp.getTime();
              const rangeStart = rangeStartDate.getTime();
              const minutesFromStart = Math.floor((timestamp - rangeStart) / (1000 * 60));
              const intervalIndex = Math.floor(minutesFromStart / intervalMinutes);
              const intervalKey = String(intervalIndex);
              
              if (!grouped.has(intervalKey)) {
                grouped.set(intervalKey, []);
              }
              grouped.get(intervalKey)!.push(point);
            }

            // Calculate median for each group
            const aggregated: Array<{ timestamp: Date; endTimestamp?: Date; value: number }> = [];
            const groupedEntries = Array.from(grouped.entries());
            for (let i = 0; i < groupedEntries.length; i++) {
              const [, points] = groupedEntries[i];
              const values = points.map((point) => point.value);
              const medianValue = calculateMedian(values);
              const firstSampleMs = Math.min(...points.map((point) => point.timestamp.getTime()));
              const lastSampleMs = Math.max(...points.map((point) => point.timestamp.getTime()));

              aggregated.push({
                timestamp: new Date(firstSampleMs),
                endTimestamp: new Date(lastSampleMs),
                value: medianValue
              });
            }

            // Sort by timestamp
            return aggregated.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
          };

          // Calculate zoom duration from sharedZoomRange if available
          // If no zoom but Custom range is selected, use custom date range duration as zoom duration
          let zoomDurationHoursFallback: number | null = null;
          let zoomStartDateFallback: Date | null = null;
          let zoomStartMsFallback: number | null = null;
          let zoomEndMsFallback: number | null = null;
          
          if (sharedZoomRange.startTime && sharedZoomRange.endTime) {
            // User has zoomed in the chart
            zoomStartDateFallback = new Date(sharedZoomRange.startTime);
            const zoomEnd = new Date(sharedZoomRange.endTime);
            zoomStartMsFallback = zoomStartDateFallback.getTime();
            zoomEndMsFallback = zoomEnd.getTime();
            const zoomDurationMinutes = (zoomEndMsFallback - zoomStartMsFallback) / (60 * 1000);
            zoomDurationHoursFallback = zoomDurationMinutes / 60;
          } else if (customChartWindow) {
            const customDurationMinutes = (customChartWindow.endDate.getTime() - customChartWindow.startDate.getTime()) / (60 * 1000);
            zoomDurationHoursFallback = customDurationMinutes / 60;
            console.log(`[REAL FALLBACK CUSTOM WINDOW] Using chart window duration: ${zoomDurationHoursFallback.toFixed(2)} hours for interval calculation`);
          } else if (selectedTimeRange === 'Custom') {
            // No zoom, but Custom range is selected - use custom date range duration
            const customDurationMinutes = (customEndDate.getTime() - customStartDate.getTime()) / (60 * 1000);
            zoomDurationHoursFallback = customDurationMinutes / 60;
            console.log(`[REAL FALLBACK CUSTOM RANGE] Using custom date range duration: ${zoomDurationHoursFallback.toFixed(2)} hours for interval calculation`);
          }
          
          // Use zoom range if available, otherwise use selected time range
          const effectiveRangeStartMsFallback = zoomStartMsFallback ?? rangeStartDate.getTime();
          const effectiveRangeEndMsFallback = zoomEndMsFallback ?? (selectedTimeRange === 'Custom' ? customEndDate : now).getTime();
          const effectiveRangeStartDateFallback = zoomStartDateFallback ?? rangeStartDate;
          
          // Collect all demo data points with timestamps
          const tempPoints: Array<{ timestamp: Date; value: number }> = [];
          const breathPoints: Array<{ timestamp: Date; value: number }> = [];
          const hrPoints: Array<{ timestamp: Date; value: number }> = [];
          const hrvPoints: Array<{ timestamp: Date; value: number }> = [];
          const spo2Points: Array<{ timestamp: Date; value: number }> = [];
          const sysPoints: Array<{ timestamp: Date; value: number }> = [];
          const diaPoints: Array<{ timestamp: Date; value: number }> = [];

          for (const r of rows) {
            if (!r || !r.timestamp) continue;
            const t = parseDbTimestamp(r.timestamp);
            const tMs = t.getTime();
            
            // Filter data to be within the effective time range (zoom or selected range)
            if (tMs < effectiveRangeStartMsFallback || tMs > effectiveRangeEndMsFallback) {
              continue;
            }
            
            const pickNum = (v: any): number | null => {
              if (v == null || v === '' || v === -1) return null;
              const n = Number(v);
              return isNaN(n) ? null : n;
            };
            
            const tempV = pickNum(r.temperature);
            const breathV = pickNum(r.breathing);
            const hrV = pickNum(r.heart_rate);
            const hrvV = r.heart_rate_variability != null ? Number(r.heart_rate_variability) : null;
            const spo2V = pickNum(r.spo2);
            const sysV = pickNum(r.systolic);
            const diaV = pickNum(r.diastolic);
            
            if (tempV != null) tempPoints.push({ timestamp: t, value: Number(Number(tempV).toFixed(1)) });
            if (breathV != null) breathPoints.push({ timestamp: t, value: Math.round(Number(breathV)) });
            if (hrV != null) hrPoints.push({ timestamp: t, value: Math.round(Number(hrV)) });
            const hrvDisplay = normalizeHrvForDisplay(hrvV);
            if (hrvDisplay != null) hrvPoints.push({ timestamp: t, value: Math.round(hrvDisplay) });
            if (spo2V != null) spo2Points.push({ timestamp: t, value: Math.round(Number(spo2V)) });
            if (sysV != null) sysPoints.push({ timestamp: t, value: Math.round(Number(sysV)) });
            if (diaV != null) diaPoints.push({ timestamp: t, value: Math.round(Number(diaV)) });
          }

          // Apply aggregation based on time range
          const aggregatedTemp = aggregateByTimeRange(tempPoints, selectedTimeRange, effectiveRangeStartDateFallback, zoomDurationHoursFallback);
          const aggregatedBreath = aggregateByTimeRange(breathPoints, selectedTimeRange, effectiveRangeStartDateFallback, zoomDurationHoursFallback);
          const aggregatedHr = aggregateByTimeRange(hrPoints, selectedTimeRange, effectiveRangeStartDateFallback, zoomDurationHoursFallback);
          const aggregatedHrv = aggregateByTimeRange(hrvPoints, selectedTimeRange, effectiveRangeStartDateFallback, zoomDurationHoursFallback);
          const aggregatedSpo2 = aggregateByTimeRange(spo2Points, selectedTimeRange, effectiveRangeStartDateFallback, zoomDurationHoursFallback);
          const aggregatedSys = aggregateByTimeRange(sysPoints, selectedTimeRange, effectiveRangeStartDateFallback, zoomDurationHoursFallback);
          const aggregatedDia = aggregateByTimeRange(diaPoints, selectedTimeRange, effectiveRangeStartDateFallback, zoomDurationHoursFallback);

          // Convert to GraphData format with appropriate time format
          const timeFormat = (selectedTimeRange === "7 Days" || selectedTimeRange === "14 Days") ? "dd MMM HH:mm" 
  : selectedTimeRange === "30 Days" ? "dd MMM" 
  : "HH:mm";

          const fallbackTemperature = aggregatedTemp.map(d => ({
            time: format(d.timestamp, timeFormat),
            value: d.value,
            tsMs: d.timestamp.getTime(),
            endTsMs: d.endTimestamp?.getTime()
          }));
          const fallbackBreathing = aggregatedBreath.map(d => ({
            time: format(d.timestamp, timeFormat),
            value: d.value,
            tsMs: d.timestamp.getTime()
          }));
          const fallbackHeartRate = aggregatedHr.map(d => ({
            time: format(d.timestamp, timeFormat),
            value: d.value,
            tsMs: d.timestamp.getTime()
          }));
          const fallbackHeartRateVariability = aggregatedHrv.map(d => ({
            time: format(d.timestamp, timeFormat),
            value: d.value,
            tsMs: d.timestamp.getTime()
          }));
          const fallbackSpo2 = aggregatedSpo2.map(d => ({
            time: format(d.timestamp, timeFormat),
            value: d.value,
            tsMs: d.timestamp.getTime()
          }));
          const fallbackSystolicBloodPressure = aggregatedSys.map(d => ({
            time: format(d.timestamp, timeFormat),
            value: d.value,
            tsMs: d.timestamp.getTime(),
            endTsMs: d.endTimestamp?.getTime()
          }));
          const fallbackDiastolicBloodPressure = aggregatedDia.map(d => ({
            time: format(d.timestamp, timeFormat),
            value: d.value,
            tsMs: d.timestamp.getTime(),
            endTsMs: d.endTimestamp?.getTime()
          }));

          setDbTemperature(fallbackTemperature);
          setDbBreathing(fallbackBreathing);
          setDbHeartRate(fallbackHeartRate);
          setDbHeartRateVariability(fallbackHeartRateVariability);
          setDbSpo2(fallbackSpo2);
          setDbSystolicBloodPressure(fallbackSystolicBloodPressure);
          setDbDiastolicBloodPressure(fallbackDiastolicBloodPressure);
          setSummaryWristMode({ hasTrue: false, hasFalse: false });
          setRawOutOfRangeStats(null);
          setRawCurrentStats(null);
          return;
        }
        
        // Prefer server-side window query (exact hours) for reliability
        const hoursMap: Record<string, number> = { '1 Hours': 1, '6 Hours': 6, '12 Hours': 12, '24 Hours': 24, '7 Days': 168, '14 Days': 336, '30 Days': 720 };
        const hours = hoursMap[selectedTimeRange] ?? 1;
        
        // For custom range, calculate hours from the actual range
        const actualHours = selectedTimeRange === 'Custom' 
          ? Math.ceil((customEndDate.getTime() - customStartDate.getTime()) / (1000 * 60 * 60))
          : hours;
        
        // When a critical event is selected, customChartWindow holds ±12h around the event — use it for API range.
        // For normal auto-refresh, always use the real current time (not a stale captured `now` from effect creation).
        const liveNow = new Date();
        const rangeEndDate = customChartWindow ? customChartWindow.endDate : (selectedTimeRange === 'Custom' ? customEndDate : liveNow);
        const startIso = rangeStartDate.toISOString();
        const endIso = rangeEndDate.toISOString();
        
        console.log('[FE] range-hours fetch', { 
          selectedTimeRange, 
          hours, 
          actualHours, 
          patId: patId || null,
          customStart: customStartDate.toISOString(),
          customEnd: customEndDate.toISOString(),
          rangeStartDate: rangeStartDate.toISOString(),
          rangeEndDate: endIso,
          rangeStartMs: rangeStartDate.getTime(),
          rangeEndMs: (selectedTimeRange === 'Custom' ? customEndDate : liveNow).getTime(),
          rangeMinutes: rangeMinutes
        });
        
        // Always use explicit UTC start/end from frontend to avoid backend NOW()/timezone drift.
        const data = await getVitalSignsRange(startIso, endIso, patId || undefined);
        console.log('[REAL] VitalSigns range response size:', {
          selectedTimeRange,
          startIso,
          endIso,
          hours,
          actualHours,
          rawCount: Array.isArray(data) ? data.length : 0,
        });
        // Normalize possible timestamp field names
        const getTs = (r: any): string | null => {
          const ts = r?.timestamp ?? r?.time ?? r?.created_at ?? r?.createdAt ?? r?.ts;
          return ts ? String(ts) : null;
        };
        // Sort ascending by timestamp
        let rows = (Array.isArray(data) ? data : [])
          .filter(r => r && getTs(r))
          .sort((a: any, b: any) => parseDbTimestamp(getTs(a) as string).getTime() - parseDbTimestamp(getTs(b) as string).getTime());
        console.log('[REAL] Range rows after mapping/sorting:', {
          selectedTimeRange,
          mappedCount: rows.length,
          startIso,
          endIso,
        });
        if (rows.length > 0) {
          const sampleRows = rows.slice(0, 3).map((r: any) => {
            const rawTs = getTs(r);
            const parsedTs = rawTs ? parseDbTimestamp(rawTs) : null;
            return {
              raw: rawTs,
              parsedLocal: parsedTs ? parsedTs.toLocaleString('en-GB', { hour12: false }) : null,
              parsedIso: parsedTs ? parsedTs.toISOString() : null,
            };
          });
          console.log('[TS-DEBUG][REAL rows sample]', sampleRows);
        }
        
        // Check SpO2 data in the rows

        // Fallback: if backend range returns 0, load recent and filter client-side (no out-of-range clamping)
        if (rows.length === 0) {
          try {
            console.log('[FE] fallback fetch recent', { limit: 500, patId: patId || null });
            const recent = await getVitalSigns(500, patId || undefined);
            const startMs = new Date(startIso).getTime();
            const endMs = new Date(endIso).getTime();
            const all = (Array.isArray(recent) ? recent : []).filter(r => getTs(r))
              .sort((a: any, b: any) => parseDbTimestamp(getTs(a) as string).getTime() - parseDbTimestamp(getTs(b) as string).getTime());
            const inWindow = all.filter(r => {
              const t = parseDbTimestamp(getTs(r) as string).getTime();
              return t >= startMs && t <= endMs;
            });

          if (inWindow.length > 0) rows = inWindow;
          console.log('[REAL] Fallback used (filtered only). Rows after fallback:', {
            selectedTimeRange,
            rowsAfterFallback: rows.length,
          });
          } catch (fallbackErr) {
            console.warn('[Range] Fallback failed:', fallbackErr);
          }
        }
        setActivityTimelineRows(rows);

        // Keep summary/raw stats bound to the selected range, not the current zoom window.
        const summaryRangeEndDate = customChartWindow ? customChartWindow.endDate : (selectedTimeRange === 'Custom' ? customEndDate : liveNow);
        const { summaryRangeStartMs: rawRangeStartMs, summaryRangeEndMs: rawRangeEndMs } =
          resolveSummaryRangeParams(summaryRangeEndDate);
        const pickNum = (v: any): number | null => (typeof v === 'number' ? v : (v != null ? Number(v) : null));
        const pick = (obj: any, keys: string[]): number | null => {
          for (const k of keys) {
            if (obj[k] != null) {
              const n = pickNum(obj[k]);
              if (typeof n === 'number' && !Number.isNaN(n) && n !== -1) return n;
            }
          }
          return null;
        };
        const rawCounts = {
          heartRate: { out: 0, total: 0 },
          hrv: { out: 0, total: 0 },
          spo2: { out: 0, total: 0 },
          breathing: { out: 0, total: 0 },
          systolicBp: { out: 0, total: 0 },
          diastolicBp: { out: 0, total: 0 },
        };
        const rawHeart: { v: number; tMs: number }[] = [];
        const rawHrv: { v: number; tMs: number }[] = [];
        const rawSpo2: { v: number; tMs: number }[] = [];
        const rawBreathing: { v: number; tMs: number }[] = [];
        const rawSystolic: { v: number; tMs: number }[] = [];
        const rawDiastolic: { v: number; tMs: number }[] = [];
        /** Same confidence gate as graph aggregation: only conf > 0 readings feed median/min/max. */
        const confOk = (row: any, field?: string) => {
          if (!isReal) return true;
          const c = resolveVitalConfidence(row, field);
          return c != null && Number(c) > 0;
        };
        for (const r of rows) {
          const tsStr = (r.__clampedTs as string) || getTs(r);
          if (!tsStr) continue;
          const tMs = parseDbTimestamp(tsStr).getTime();
          if (tMs < rawRangeStartMs || tMs > rawRangeEndMs) continue;
          const hr = pickNum(r.heart_rate ?? r.heart_rate_bpm ?? r.pulse_rate ?? r.pulse_rate_bpm);
          // Match SQL: value > 0 && confidence > 0; upper bound inclusive (medium is in-range)
          if (hr != null && (!isReal || Number(hr) > 0) && confOk(r, 'heart_rate_confidence')) {
            rawCounts.heartRate.total++;
            rawHeart.push({ v: Number(hr), tMs });
            if (hr < thresholds.heartRate.low || hr > thresholds.heartRate.medium) rawCounts.heartRate.out++;
          }
          const hrvVal = pickNum(r.hrv ?? r.hrv_ms ?? r.heart_rate_variability);
          // HRV > 200 ms are invalid for graph/stats — exclude from out-of-range entirely
          if (
            hrvVal != null &&
            Number.isFinite(hrvVal) &&
            (!isReal || Number(hrvVal) > 0) &&
            Number(hrvVal) <= MAX_HRV_MS &&
            confOk(r, 'heart_rate_variability_confidence')
          ) {
            rawCounts.hrv.total++;
            rawHrv.push({ v: Number(hrvVal), tMs });
            if (hrvVal < thresholds.hrv.low || hrvVal > thresholds.hrv.high || (hrvVal >= thresholds.hrv.low && hrvVal <= thresholds.hrv.medium)) {
              rawCounts.hrv.out++;
            }
          }
          const spo2Val = pickNum(r.spo2 ?? r.spo2_bpm ?? r.spo2_bpm_ms);
          if (spo2Val != null && (!isReal || Number(spo2Val) > 0) && confOk(r, 'blood_oxygen_saturation_confidence')) {
            rawCounts.spo2.total++;
            rawSpo2.push({ v: Number(spo2Val), tMs });
            if (spo2Val <= thresholds.spo2.medium) rawCounts.spo2.out++;
          }
          const breath = pickNum(r.respiratory_rate ?? r.respiratory_rate_bpm ?? r.breathing_rate ?? r.breaths_per_minute ?? r.resp_rate ?? r.rr);
          if (breath != null && (!isReal || Number(breath) > 0) && confOk(r, 'respiratory_rate_confidence')) {
            rawCounts.breathing.total++;
            rawBreathing.push({ v: Number(breath), tMs });
            if (breath < thresholds.breathing.low || breath > thresholds.breathing.medium) rawCounts.breathing.out++;
          }
          const sys = pick(r, ['systolic_blood_pressure', 'systolic_blood_pressure_mmhg', 'systolic_blood_pressure_bpm', 'systolic_blood_pressure_bpm_mmhg']);
          if (sys != null && (!isReal || Number(sys) > 0) && confOk(r, 'systolic_blood_pressure_confidence')) {
            rawCounts.systolicBp.total++;
            rawSystolic.push({ v: Number(sys), tMs });
            if (sys < thresholds.bloodPressure.systolic.low || sys > thresholds.bloodPressure.systolic.systolic_stage1) rawCounts.systolicBp.out++;
          }
          const dia = pick(r, ['diastolic_blood_pressure', 'diastolic_blood_pressure_mmhg', 'diastolic_blood_pressure_bpm', 'diastolic_blood_pressure_bpm_mmhg']);
          if (dia != null && (!isReal || Number(dia) > 0) && confOk(r, 'diastolic_blood_pressure_confidence')) {
            rawCounts.diastolicBp.total++;
            rawDiastolic.push({ v: Number(dia), tMs });
            if (dia < thresholds.bloodPressure.diastolic.low || dia > thresholds.bloodPressure.diastolic.diastolic_stage1) rawCounts.diastolicBp.out++;
          }
        }
        const rawMedian = (arr: number[]) => {
          if (arr.length === 0) return null;
          const s = [...arr].sort((a, b) => a - b);
          const mid = Math.floor(s.length / 2);
          return s.length % 2 === 0 ? (s[mid - 1]! + s[mid]!) / 2 : s[mid]!;
        };
        const rawNightValues = (arr: { v: number; tMs: number }[]) =>
          arr.filter((x) => isInNightWindow(new Date(x.tMs), patientBedTime)).map((x) => x.v);
        const toRawStats = (arr: { v: number; tMs: number }[]): RawMetricStats | undefined => {
          if (arr.length === 0) return undefined;
          const values = arr.map((x) => x.v);
          const nightV = rawNightValues(arr);
          return {
            min: Math.round(Math.min(...values)),
            max: Math.round(Math.max(...values)),
            median: rawMedian(values),
            nightMedian: nightV.length > 0 ? rawMedian(nightV) : null,
          };
        };
        /** HRV summary: period/night medians and min/max exclude values > 200 ms (invalid). */
        const toRawHrvStats = (arr: { v: number; tMs: number }[]): RawMetricStats | undefined => {
          if (arr.length === 0) return undefined;
          const values = arr.map((x) => x.v).filter((v) => v <= MAX_HRV_MS);
          if (values.length === 0) return undefined;
          const nightV = rawNightValues(arr).filter((v) => v <= MAX_HRV_MS);
          return {
            min: Math.round(Math.min(...values)),
            max: Math.round(Math.max(...values)),
            median: rawMedian(values),
            nightMedian: nightV.length > 0 ? rawMedian(nightV) : null,
          };
        };
        setRawCurrentStats({
          heartRate: toRawStats(rawHeart),
          hrv: toRawHrvStats(rawHrv),
          spo2: toRawStats(rawSpo2),
          breathing: toRawStats(rawBreathing),
          systolicBp: toRawStats(rawSystolic),
          diastolicBp: toRawStats(rawDiastolic),
        });

        // Helper function to calculate median
        const calculateMedian = (values: number[]): number => {
          if (values.length === 0) return 0;
          const sorted = [...values].sort((a, b) => a - b);
          const mid = Math.floor(sorted.length / 2);
          return sorted.length % 2 === 0 
            ? (sorted[mid - 1] + sorted[mid]) / 2 
            : sorted[mid];
        };
        
        const calculatePercentiles = (values: number[]): { q1: number; q3: number;} => {
          if (values.length === 0) {
            return { q1: NaN, q3: NaN};
          }
        
          const sorted = [...values].sort((a, b) => a - b);
        
          // Helper για percentile
          const percentile = (arr: number[], p: number): number => {
            const idx = (arr.length - 1) * p;
            const lower = Math.floor(idx);
            const upper = Math.ceil(idx);
            if (lower === upper) return arr[lower];
            const weight = idx - lower;
            return arr[lower] * (1 - weight) + arr[upper] * weight;
          };
          return {
            q1: percentile(sorted, 0.25),       // 25ο percentile
            q3: percentile(sorted, 0.75),       // 75ο percentile
          };
        };        


        const calculateCustomWhiskers = (values: number[]) => {
          if (values.length === 0) {
            return { q_15: NaN, q_85: NaN };
          }
        
          const sorted = [...values].sort((a, b) => a - b);
        
          const min = sorted[0];
          const max = sorted[sorted.length - 1];
        
          const { q1, q3 } = calculatePercentiles(values);
        
          const x = (q3 - q1) * 1.5;
        
          // ---- q_85 (upper whisker) ----
          const upperLimit = q3 + x;
          let q_85: number;
          const EPS = 1e-9;
          
          if (upperLimit >= max - EPS) {
            q_85 = max;
          } else {
                        // μεγαλύτερη τιμή <= upperLimit
            const found = [...sorted].reverse().find(v => v <= upperLimit);
            q_85 = found !== undefined ? found : max;
          }
        
          // ---- q_15 (lower whisker) ----
          const lowerLimit = q1 - x;
          let q_15: number;
        
          if (lowerLimit <= min + EPS) {
            q_15 = min;
          } else {
            // μικρότερη τιμή >= lowerLimit
            const found = sorted.find(v => v >= lowerLimit);
            q_15 = found !== undefined ? found : min;
          }
        
          return { q_15, q_85 };
        };
            

        const calculateStats = (values: number[]) => {


          if (values.length === 0) {
            return { median: NaN, min: NaN, max: NaN, q1: NaN, q3: NaN,q_15:NaN ,q_85:NaN};
          }
        
          const sorted = [...values].sort((a, b) => a - b);
        
          const median = calculateMedian (values);
          const {q1,q3}=calculatePercentiles(values);
          const {q_15,q_85} = calculateCustomWhiskers(values);
          return {
            median,
            min: sorted[0], 
            max: sorted[sorted.length - 1],
            q1:q1,
            q3:q3,
            q_15:q_15,
            q_85:q_85
          };
        };

        // Aggregation function based on time range
        const aggregateByTimeRange = (
          data: Array<{ timestamp: Date; value: number; confidence?: number }>,
          timeRange: string,
          rangeStartDate: Date,
          zoomDurationHours?: number | null
        ): Array<{ timestamp: Date; endTimestamp?: Date; value: number; min: number; max: number; q1: number; q3: number; q_15: number; q_85: number; confidence?: number }> => {
          if (data.length === 0) return [];
          
          // Determine aggregation interval based on zoom duration if zoom is active
          // (Critical-event ±12h window sets sharedZoomRange ≈ 24h → 24-min buckets by default;
          // further zoom uses the normal zoomDurationHours thresholds.)
          // Otherwise use the selected time range
          let useZoomInterval = false;
          let intervalMinutes: number = 1;
          
          if (zoomDurationHours != null && zoomDurationHours > 0) {
            useZoomInterval = true;
            if (zoomDurationHours <= 1) {
              // 1 h: Κανονικά 1/min (όλα τα points)
              console.log('[AGGREGATION] Zoom <= 1h: Returning all points without aggregation');
              return data.map(d => ({
                timestamp: d.timestamp,
                value: d.value, // median == raw value
                min: d.value,
                max: d.value,
                q1: d.value,
                q3: d.value,
                q_15: d.value,
                q_85: d.value,
                confidence: d.confidence
              }));
            } else if (zoomDurationHours <= 6) {
              // 6 h: 6-minute intervals
              intervalMinutes = 6;
              console.log(`[AGGREGATION] Zoom <= 6h: Using ${intervalMinutes}-minute interval`);
            } else if (zoomDurationHours <= 12) {
              // 12 h: 12-minute intervals
              intervalMinutes = 12;
              console.log(`[AGGREGATION] Zoom <= 12h: Using ${intervalMinutes}-minute interval`);
            } else if (zoomDurationHours <= 24) {
              // 24 h: 24-minute intervals
              intervalMinutes = 24;
              console.log(`[AGGREGATION] Zoom <= 24h: Using ${intervalMinutes}-minute interval`);
            } else if (zoomDurationHours <= 168) { // 7 days
              // 7 days: 168 minutes interval
              intervalMinutes = 168;
              console.log(`[AGGREGATION] Zoom <= 7 days: Using ${intervalMinutes}-minute interval`);
            } else {
              // > 7 days: 720 minutes interval
              intervalMinutes = 720;
              console.log(`[AGGREGATION] Zoom > 7 days: Using ${intervalMinutes}-minute interval`);
            }
          }

          // Determine aggregation interval based on time range (if not using zoom interval)
          if (!useZoomInterval) {
            switch (timeRange) {
              case '1 Hours':
                // 1 h: Κανονικά 1/min (όλα τα points)
                return data.map(d => ({
                  timestamp: d.timestamp,
                  value: d.value, // median == raw value
                  min: d.value,
                  max: d.value,
                  q1: d.value,
                  q3: d.value,
                  q_15: d.value,
                  q_85: d.value,
                  confidence: d.confidence
                }));
              case '6 Hours':
                // 6 h: 6-minute intervals
                intervalMinutes = 6;
                break;
              case '12 Hours':
                // 12 h: 12-minute intervals
                intervalMinutes = 12;
                break;
              case '24 Hours':
                // 24 h: 24-minute intervals
                intervalMinutes = 24;
                break;
              case '7 Days':
              case '14 Days':
                // 7/14 days: 168 minutes interval
                intervalMinutes = 168;
                break;
              case '30 Days':
                // 30 days: 720 minutes interval
                intervalMinutes = 720;
                break;
              default:
                // For custom or unknown ranges, use 1-minute intervals
                intervalMinutes = 1;
            }
          }

          // Group data by time intervals
          const grouped = new Map<string, Array<{ timestamp: Date; value: number; confidence?: number }>>();
          
          console.log(`[AGGREGATION] Grouping ${data.length} points with intervalMinutes=${intervalMinutes}, rangeStartDate=${rangeStartDate.toISOString()}`);
          
          for (const point of data) {
            const timestamp = point.timestamp.getTime();
            const rangeStart = rangeStartDate.getTime();
            const minutesFromStart = Math.floor((timestamp - rangeStart) / (1000 * 60));
            const intervalIndex = Math.floor(minutesFromStart / intervalMinutes);
            const intervalKey = String(intervalIndex);
            
            if (!grouped.has(intervalKey)) {
              grouped.set(intervalKey, []);
            }
            grouped.get(intervalKey)!.push(point);
          }

          // Calculate median for each group
          const aggregated: Array<{
            timestamp: Date;
            endTimestamp?: Date;
            value: number;
            min: number;
            max: number;
            q1: number;
            q3: number;
            q_15: number;
            q_85: number;
            confidence?: number;
          }> = [];
          const groupedEntries = Array.from(grouped.entries());
          for (let i = 0; i < groupedEntries.length; i++) {
            const [, values] = groupedEntries[i];
            // In real mode, only use values with confidence > 0 (i.e., confidence levels 1, 2, 3)
            // Also filter out negative values in real mode
            const validPoints = values
              .filter((v: { timestamp: Date; value: number; confidence?: number }) => {
                if (v.confidence == null || v.confidence === undefined) return false;
                if (!(Number(v.confidence) > 0)) return false; // Only include confidence levels 1, 2, 3
                // In real mode, filter out negative values
                if (isReal && Number(v.value) < 0) return false;
                return true;
              });
            const validValues = validPoints.map((v) => v.value);
            
            if (validValues.length === 0) continue; // Skip empty groups
            
            const { median, min, max, q1, q3, q_15, q_85 } = calculateStats(validValues);
            
            // Calculate average confidence if available
            const confidences = values.map((v: { timestamp: Date; value: number; confidence?: number }) => v.confidence).filter((c: number | undefined): c is number => c !== undefined);
            const avgConfidence = confidences.length > 0 
              ? confidences.reduce((sum: number, c: number) => sum + c, 0) / confidences.length 
              : undefined;

            aggregated.push({
              // Use the first valid reading rather than the artificial bucket boundary.
              timestamp: new Date(Math.min(...validPoints.map((point) => point.timestamp.getTime()))),
              endTimestamp: new Date(Math.max(...validPoints.map((point) => point.timestamp.getTime()))),
              value: median,
              min: min,
              max: max,
              q1: q1,
              q3: q3,
              q_15: q_15,
              q_85: q_85,
              confidence: avgConfidence
            });
          }

          // Sort by timestamp
          return aggregated.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
        };

        const realRangeEndDate = customChartWindow ? customChartWindow.endDate : (selectedTimeRange === 'Custom' ? customEndDate : liveNow);
        rawVitalsRowsRef.current = rows;
        let graphRowsForAggregation = rows;

        const toGraph = (getter: (r: any) => number | null, confidenceField?: string): GraphData[] => {
          const series: Array<{ timestamp: Date; value: number; confidence?: number }> = [];
          
          const {
            zoomDurationHours,
            effectiveRangeStartMs,
            effectiveRangeEndMs,
            effectiveRangeStartDate,
          } = resolveZoomAggregationParams(realRangeEndDate);
          
          if (zoomDurationHours != null && sharedZoomRangeRef.current.startTime && sharedZoomRangeRef.current.endTime) {
            console.log(`[ZOOM AGGREGATION] Zoom active: ${zoomDurationHours.toFixed(2)} hours, using interval based on zoom duration`);
            console.log(`[ZOOM AGGREGATION] Zoom range: ${new Date(effectiveRangeStartMs).toISOString()} to ${new Date(effectiveRangeEndMs).toISOString()}`);
          }
          
          let rawPointsCount = 0;
          for (const r of graphRowsForAggregation) {
            const v = getter(r);
            if (typeof v === 'number' && !Number.isNaN(v) && v !== -1) {
              // In real mode, filter out negative values
              if (isReal && Number(v) < 0) {
                continue;
              }
              const tsStr = (r.__clampedTs as string) || (getTs(r) as string);
              const t = parseDbTimestamp(tsStr);
              const tMs = t.getTime();
              
              // Filter data to be within the effective time range (zoom or selected range)
              if (tMs < effectiveRangeStartMs || tMs > effectiveRangeEndMs) {
                continue;
              }
              
              rawPointsCount++;
              const confValue = resolveVitalConfidence(r, confidenceField);
              series.push({ timestamp: t, value: Number(v), confidence: confValue });
            }
          }
          
          if (zoomDurationHours != null) {
            console.log(`[ZOOM AGGREGATION] Filtered ${rawPointsCount} raw points from ${graphRowsForAggregation.length} total rows for zoom range`);
          }
          
          // Apply aggregation based on time range with zoom duration
          // Use effectiveRangeStartDate (zoom start if zooming, otherwise original rangeStartDate)
          const aggregated = aggregateByTimeRange(series, selectedTimeRange, effectiveRangeStartDate, zoomDurationHours);
          
          if (zoomDurationHours != null) {
            console.log(`[ZOOM AGGREGATION] After aggregation: ${aggregated.length} points`);
          }
          
          // Filter aggregated results to be within effective range (in case aggregation created points outside)
          const filteredAggregated = aggregated.filter(d => {
            const dMs = d.timestamp.getTime();
            return dMs >= effectiveRangeStartMs && dMs <= effectiveRangeEndMs;
          });
          
          // Convert to GraphData format with appropriate time format
          const timeFormat = (selectedTimeRange === "7 Days" || selectedTimeRange === "14 Days") ? "dd MMM HH:mm" 
            : selectedTimeRange === "30 Days" ? "dd MMM" 
            : "HH:mm";
          
          return filteredAggregated.map(d => ({
            time: format(d.timestamp, timeFormat),
            value: d.value,
            min: d.min,
            max: d.max,
            q1: d.q1,
            q3: d.q3,
            q_15: d.q_15,
            q_85: d.q_85,
            confidence: d.confidence,
            tsMs: d.timestamp.getTime(),
            endTsMs: d.endTimestamp?.getTime()
          }));
        };
        const buildAndCommitRealVitalsGraphs = () => {
        const breathingSeries = toGraph((r) => {
          const raw = (
            r.respiratory_rate ??
            r.respiratory_rate_bpm ??
            r.breathing_rate ??
            r.breaths_per_minute ??
            r.resp_rate ??
            r.rr
          );
          return (typeof raw === 'number') ? raw : (raw != null ? Number(raw) : null);
        }, 'respiratory_rate_confidence');
        const heartSeries = toGraph((r) => {
          const raw = (r.heart_rate ?? r.heart_rate_bpm ?? r.pulse_rate ?? r.pulse_rate_bpm);
          console.log((typeof raw === 'number') ? raw : (raw != null ? Number(raw) : null));
          return (typeof raw === 'number') ? raw : (raw != null ? Number(raw) : null);
        }, 'heart_rate_confidence');
        const heartRateVariabilitySeries = toGraph((r) => pickHrvFromRow(r), 'heart_rate_variability_confidence');
        const spo2Series = toGraph((r) => {
          const raw = (r.spo2 ?? r.spo2_bpm ?? r.spo2_bpm_ms);
          const value = (typeof raw === 'number') ? raw : (raw != null ? Number(raw) : null);
          return value;
        }, 'blood_oxygen_saturation_confidence');
        const respirationRateSeries = toGraph((r) => {
          const raw = (r.respiratory_rate ?? r.respiratory_rate_bpm ?? r.breathing_rate ?? r.breaths_per_minute ?? r.resp_rate ?? r.rr);
          return (typeof raw === 'number') ? raw : (raw != null ? Number(raw) : null);
        }, 'respiratory_rate_confidence');
        const systolic_diastolicBloodPressureSeries = toGraph((r) => {
          const raw = (r.systolic_diastolic_blood_pressure ?? r.systolic_diastolic_blood_pressure_mmhg ?? r.systolic_diastolic_blood_pressure_bpm ?? r.systolic_diastolic_blood_pressure_bpm_mmhg);
          return (typeof raw === 'number') ? raw : (raw != null ? Number(raw) : null);
        });
        const systolicBloodPressureSeries = toGraph((r) => {
          const raw = (r.systolic_blood_pressure ?? r.systolic_blood_pressure_mmhg ?? r.systolic_blood_pressure_bpm ?? r.systolic_blood_pressure_bpm_mmhg);
          return (typeof raw === 'number') ? raw : (raw != null ? Number(raw) : null);
        }, 'systolic_blood_pressure_confidence');
        const diastolicBloodPressureSeries = toGraph((r) => {
          const raw = (r.diastolic_blood_pressure ?? r.diastolic_blood_pressure_mmhg ?? r.diastolic_blood_pressure_bpm ?? r.diastolic_blood_pressure_bpm_mmhg);
          return (typeof raw === 'number') ? raw : (raw != null ? Number(raw) : null);
        }, 'diastolic_blood_pressure_confidence');
        const sleepSeries = (() => {
          const timeFormat = (selectedTimeRange === "7 Days" || selectedTimeRange === "14 Days") ? "dd MMM HH:mm"
            : selectedTimeRange === "30 Days" ? "dd MMM"
            : "HH:mm";
          const out: GraphData[] = [];
          for (const r of graphRowsForAggregation) {
            const rawStage = r.activity ?? r.sleep_stage;
            const sleepValue = getSleepStageValue(rawStage);
            if (sleepValue == null) continue;
            const tsStr = (r.__clampedTs as string) || getTs(r);
            if (!tsStr) continue;
            const t = parseDbTimestamp(tsStr);
            out.push({
              time: format(t, timeFormat),
              value: sleepValue,
              tsMs: t.getTime()
            });
          }
          return out;
        })();

        setDbBreathing(breathingSeries.length >= 1 ? breathingSeries : []);
        setDbHeartRate(heartSeries.length >= 1 ? heartSeries : []);
        setDbHeartRateVariability(heartRateVariabilitySeries.length >= 1 ? heartRateVariabilitySeries : []);
        setDbSpo2(spo2Series.length >= 1 ? spo2Series : []);
        setDbRespirationRate(respirationRateSeries.length >= 1 ? respirationRateSeries : []);
        setDbSystolic_diastolicBloodPressure(systolic_diastolicBloodPressureSeries.length >= 1 ? systolic_diastolicBloodPressureSeries : []);
        setDbSystolicBloodPressure(systolicBloodPressureSeries.length >= 1 ? systolicBloodPressureSeries : []);
        setDbDiastolicBloodPressure(diastolicBloodPressureSeries.length >= 1 ? diastolicBloodPressureSeries : []);
        setDbSleep(sleepSeries.length >= 1 ? sleepSeries : []);
        setSummaryWristMode(computeSummaryWristModeFromRows(graphRowsForAggregation, realRangeEndDate));
        };

        const tempLow = thresholds.temperature.low;
        const tempMedium = thresholds.temperature.medium;
        let summaryTempOutOfRange = { out: 0, total: 0 };

        const buildAndCommitTemperatureGraph = (inputTempRows: any[]) => {
          summaryTempOutOfRange = { out: 0, total: 0 };
          const tempRangeEndDate = customChartWindow ? customChartWindow.endDate : (selectedTimeRange === 'Custom' ? customEndDate : liveNow);
          const { summaryRangeStartMs, summaryRangeEndMs } = resolveSummaryRangeParams(tempRangeEndDate);
          const {
            zoomDurationHours: tempZoomDurationHours,
            effectiveRangeStartMs,
            effectiveRangeEndMs,
            effectiveRangeStartDate,
          } = resolveZoomAggregationParams(tempRangeEndDate);
          const zoomDurationHours = tempZoomDurationHours;
          
          const getTempTs = (r: any): string | null => {
            const ts = r?.timestamp ?? r?.time ?? r?.created_at ?? r?.createdAt ?? r?.ts;
            return ts ? String(ts) : null;
          };
          
          let rawPointsCount = 0;
          // SQL temperature_invalid_pct base set: sample_index=0 AND temp>0 (API already filters both)
          const rawTempForStats: { v: number; tMs: number }[] = [];
          // Graph only: one point per minute
          const tempByMinute = new Map<string, { timestamp: Date; value: number; sampleIndex: number }>();
          for (const tr of Array.isArray(inputTempRows) ? inputTempRows : []) {
            if (!tr || !getTempTs(tr)) continue;
            const raw: any = (tr as any).temp ?? (tr as any).temperature;
            const v = (typeof raw === 'number') ? raw : (raw != null ? Number(raw) : null);
            if (typeof v !== 'number' || Number.isNaN(v)) continue;

            const sampleIndex = Number((tr as any).sample_index ?? -1);
            // Match SQL base filters: sample_index = 0 and temp > 0
            if (sampleIndex !== 0 || !(v > 0)) continue;
            
            const tsStr = getTempTs(tr);
            const t = parseDbTimestamp(tsStr as string);
            const tMs = t.getTime();

            if (tMs >= summaryRangeStartMs && tMs <= summaryRangeEndMs) {
              const roundedForSummary = Number(v.toFixed(1));
              rawTempForStats.push({ v: roundedForSummary, tMs });
            }
            
            if (tMs < effectiveRangeStartMs || tMs > effectiveRangeEndMs) {
              continue;
            }
            
            rawPointsCount++;
            const roundedV = Number(v.toFixed(1));

            const minuteKey = `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')} ${String(t.getHours()).padStart(2, '0')}:${String(t.getMinutes()).padStart(2, '0')}`;
            const existing = tempByMinute.get(minuteKey);
            if (!existing || sampleIndex >= existing.sampleIndex) {
              tempByMinute.set(minuteKey, {
                timestamp: t,
                value: roundedV,
                sampleIndex,
              });
            }
          }

          summaryTempOutOfRange = {
            total: rawTempForStats.length,
            out: rawTempForStats.filter(({ v }) => !(v >= tempLow && v <= tempMedium)).length,
          };
          
          // Graph aggregation input: one point per minute
          const tempSeries: Array<{ timestamp: Date; value: number }> = Array.from(tempByMinute.values())
            .map(({ timestamp, value }) => ({ timestamp, value }))
            .sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());

          if (rawTempForStats.length > 0) {
            const tempValues = rawTempForStats.map((x) => x.v);
            const tempNight = rawTempForStats
              .filter((x) => isInNightWindow(new Date(x.tMs), patientBedTime))
              .map((x) => x.v);
            const rawMedian = (arr: number[]) => {
              if (arr.length === 0) return null;
              const s = [...arr].sort((a, b) => a - b);
              const mid = Math.floor(s.length / 2);
              return s.length % 2 === 0 ? (s[mid - 1]! + s[mid]!) / 2 : s[mid]!;
            };
            setRawCurrentStats((prev) => ({
              ...prev ?? {},
              temperature: {
                min: Math.round(Math.min(...tempValues) * 10) / 10,
                max: Math.round(Math.max(...tempValues) * 10) / 10,
                median: rawMedian(tempValues),
                nightMedian: tempNight.length > 0 ? rawMedian(tempNight) : null,
              },
            }));
          } else {
            setRawCurrentStats((prev) => {
              if (!prev) return prev;
              const { temperature: _removed, ...rest } = prev;
              return rest;
            });
          }
          
          if (zoomDurationHours != null) {
            console.log(`[TEMPERATURE AGGREGATION] Filtered ${rawPointsCount} raw points from ${inputTempRows.length} total rows for zoom range`);
          }
          
          // Create a version of aggregateByTimeRange that doesn't require confidence
          // For temperature, we use all values without confidence filtering
          const aggregateTempByTimeRange = (
            data: Array<{ timestamp: Date; value: number }>,
            timeRange: string,
            rangeStartDate: Date,
            zoomDurationHours?: number | null
          ): Array<{ timestamp: Date; endTimestamp?: Date; value: number; min: number; max: number; q1: number; q3: number; q_15: number; q_85: number }> => {
            if (data.length === 0) return [];
            
            // Determine aggregation interval based on zoom duration if zoom is active
            // (Critical-event ±12h window sets sharedZoomRange ≈ 24h → 24-min buckets by default;
            // further zoom uses the normal zoomDurationHours thresholds.)
            // Otherwise use the selected time range
            let useZoomInterval = false;
            let intervalMinutes: number = 1;
            
            if (zoomDurationHours != null && zoomDurationHours > 0) {
              useZoomInterval = true;
              if (zoomDurationHours <= 1) {
                // 1 h: Κανονικά 1/min (όλα τα points)
                console.log('[TEMPERATURE AGGREGATION] Zoom <= 1h: Returning all points without aggregation');
                return data.map(d => ({
                  timestamp: d.timestamp,
                  value: d.value, // median == raw value
                  min: d.value,
                  max: d.value,
                  q1: d.value,
                  q3: d.value,
                  q_15: d.value,
                  q_85: d.value
                }));
              } else if (zoomDurationHours <= 6) {
                // 6 h: 6-minute intervals
                intervalMinutes = 6;
                console.log(`[TEMPERATURE AGGREGATION] Zoom <= 6h: Using ${intervalMinutes}-minute interval`);
              } else if (zoomDurationHours <= 12) {
                // 12 h: 12-minute intervals
                intervalMinutes = 12;
                console.log(`[TEMPERATURE AGGREGATION] Zoom <= 12h: Using ${intervalMinutes}-minute interval`);
              } else if (zoomDurationHours <= 24) {
                // 24 h: 24-minute intervals
                intervalMinutes = 24;
                console.log(`[TEMPERATURE AGGREGATION] Zoom <= 24h: Using ${intervalMinutes}-minute interval`);
              } else if (zoomDurationHours <= 168) { // 7 days
                // 7 days: 168 minutes interval
                intervalMinutes = 168;
                console.log(`[TEMPERATURE AGGREGATION] Zoom <= 7 days: Using ${intervalMinutes}-minute interval`);
              } else {
                // > 7 days: 720 minutes interval
                intervalMinutes = 720;
                console.log(`[TEMPERATURE AGGREGATION] Zoom > 7 days: Using ${intervalMinutes}-minute interval`);
              }
            }

            // Determine aggregation interval based on time range (if not using zoom interval) initialization of intervalMinutes
            if (!useZoomInterval) {
              switch (timeRange) {
                case '1 Hours':
                  // 1 h: Κανονικά 1/min (όλα τα points)
                  return data.map(d => ({
                    timestamp: d.timestamp,
                    value: d.value, // median == raw value
                    min: d.value,
                    max: d.value,
                    q1: d.value,
                    q3: d.value,
                    q_15: d.value,
                    q_85: d.value
                  }));
                case '6 Hours':
                  // 6 h: 6-minute intervals
                  intervalMinutes = 6;
                  break;
                case '12 Hours':
                  // 12 h: 12-minute intervals
                  intervalMinutes = 12;
                  break;
                case '24 Hours':
                  // 24 h: 24-minute intervals
                  intervalMinutes = 24;
                  break;
                case '7 Days':
                case '14 Days':
                  // 7/14 days: 168 minutes interval
                  intervalMinutes = 168;
                  break;
                case '30 Days':
                  // 30 days: 720 minutes interval
                  intervalMinutes = 720;
                  break;
                default:
                  // For custom or unknown ranges, use 1-minute intervals
                  intervalMinutes = 1;
              }
            }
            
            console.log(`[TEMPERATURE AGGREGATION] Grouping ${data.length} points with intervalMinutes=${intervalMinutes}, rangeStartDate=${rangeStartDate.toISOString()}`);

            // Group data by time intervals
            const grouped = new Map<string, Array<{ timestamp: Date; value: number }>>();
            
            for (const point of data) {
              const timestamp = point.timestamp.getTime();
              const rangeStart = rangeStartDate.getTime();
              const minutesFromStart = Math.floor((timestamp - rangeStart) / (1000 * 60));
              const intervalIndex = Math.floor(minutesFromStart / intervalMinutes);
              const intervalKey = String(intervalIndex);
              
              if (!grouped.has(intervalKey)) {
                grouped.set(intervalKey, []);
              }
              grouped.get(intervalKey)!.push(point);
            }

            // Calculate median for each group
            const aggregated: Array<{
              timestamp: Date;
              endTimestamp?: Date;
              value: number;
              min: number;
              max: number;
              q1: number,
              q3: number,
              q_15:number,
              q_85:number,
            }> = [];
            const groupedEntries = Array.from(grouped.entries());
            for (let i = 0; i < groupedEntries.length; i++) {
              const [, points] = groupedEntries[i];
              const values = points.map((point) => point.value);
              const firstSampleMs = Math.min(...points.map((point) => point.timestamp.getTime()));
              const lastSampleMs = Math.max(...points.map((point) => point.timestamp.getTime()));

              const { median, min, max, q1, q3, q_15, q_85 } = calculateStats(values);

              aggregated.push({
                timestamp: new Date(firstSampleMs),
                endTimestamp: new Date(lastSampleMs),
                value: median,
                min:min,
                max:max,
                q1:q1,
                q3:q3,
                q_15:q_15,
                q_85:q_85
              });

            }

            // Sort by timestamp
            return aggregated.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
          };
          
          // Apply aggregation based on time range with zoom duration
          const aggregated = aggregateTempByTimeRange(tempSeries, selectedTimeRange, effectiveRangeStartDate, zoomDurationHours);
          
          if (zoomDurationHours != null) {
            console.log(`[TEMPERATURE AGGREGATION] After aggregation: ${aggregated.length} points`);
          }
          
          // Filter aggregated results to be within effective range (in case aggregation created points outside)
          const filteredAggregated = aggregated.filter(d => {
            const dMs = d.timestamp.getTime();
            return dMs >= effectiveRangeStartMs && dMs <= effectiveRangeEndMs;
          });
          // Convert to GraphData format with appropriate time format
          const timeFormat = (selectedTimeRange === "7 Days" || selectedTimeRange === "14 Days") ? "dd MMM HH:mm" 
            : selectedTimeRange === "30 Days" ? "dd MMM" 
            : "HH:mm";
          
          const temperatureData = filteredAggregated.map(d => ({
            time: format(d.timestamp, timeFormat),
            value: d.value,
            min: d.min,
            max: d.max,
            q1: d.q1,
            q3: d.q3,
            q_15: d.q_15,
            q_85: d.q_85,
            tsMs: d.timestamp.getTime(),
            endTsMs: d.endTimestamp?.getTime()
          }));
          
          // Always set temperature from fetch so Current panel reflects current range (clear when no data after deselecting critical event)
          setDbTemperature(temperatureData);
        };

        buildAndCommitRealVitalsGraphs();
        reaggregateVitalsRef.current = (inputRows: any[], inputTempRows?: any[]) => {
          graphRowsForAggregation = inputRows;
          buildAndCommitRealVitalsGraphs();
          buildAndCommitTemperatureGraph(Array.isArray(inputTempRows) ? inputTempRows : rawTempRowsRef.current);
        };

        // Temperature: use same aggregation logic as other vitals (without confidence)
        // Raw summary (median/min/max/out-of-range) uses sample_index=0 & value>0 in the selected summary range.
        try {
          const tempRows = await getTemperatureRange(startIso, endIso, patId);
          rawTempRowsRef.current = Array.isArray(tempRows) ? tempRows : [];
          buildAndCommitTemperatureGraph(rawTempRowsRef.current);
        } catch (tempErr) {
          console.warn('Failed to load temperature range:', tempErr);
        }

        setRawOutOfRangeStats({ ...rawCounts, temperature: summaryTempOutOfRange });
      } catch (e) {
        console.warn('Failed to load vital signs range:', e);
        setDbBreathing([]);
        setDbHeartRate([]);
        setDbHeartRateVariability([]);
        setDbSpo2([]);
        setDbRespirationRate([]);
        setDbSystolic_diastolicBloodPressure([]);
        setDbSystolicBloodPressure([]);
        setDbDiastolicBloodPressure([]);
        setDbSleep([]);
        setActivityTimelineRows([]);
        setSummaryWristMode({ hasTrue: false, hasFalse: false });
        setRawOutOfRangeStats(null);
        setRawCurrentStats(null);
      }
    };
    fetchRange();
    const id = setInterval(() => {
      console.log('[Dashboard Auto Refresh] fetchRange triggered (60s interval)', {
        patId,
        at: new Date().toISOString()
      });
      fetchRange();
    }, 60000);
    return () => { clearInterval(id); controller.abort(); };
    // customStartDate/customEndDate omitted — fetch runs on customDateApplied only
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTimeRange, operationMode, patId, patIdLoaded, customDateApplied, selectedEventTimestamp, customChartWindow, graphRefreshMinuteKey, patientBedTime]);

  // Re-aggregate cached vitals immediately when zoom changes (no network round-trip)
  useEffect(() => {
    const apply = reaggregateVitalsRef.current;
    if (!apply || rawVitalsRowsRef.current.length === 0) return;
    apply(rawVitalsRowsRef.current, rawTempRowsRef.current);
  }, [sharedZoomRange.startTime, sharedZoomRange.endTime]);

  // Immediate demo chart reload after "Generate Demo Data"
  useEffect(() => {
    const handleRefreshDemoCharts = () => {
      refreshDemoDataRef.current?.();
    };
    window.addEventListener('refreshDemoCharts', handleRefreshDemoCharts);
    return () => window.removeEventListener('refreshDemoCharts', handleRefreshDemoCharts);
  }, []);

  // Fetch absolute latest values (independent from range) for Now panels
  useEffect(() => {
    if (!patIdLoaded) return; // Wait for patId to be loaded from API
    if (operationMode === 'demo') {
      // Load latest demo values
      const loadLatestDemoData = async () => {
        try {
          const latestDemo = await getLatestDemoVitals();
          if (latestDemo) {
            setLatestVitalsTs(new Date(latestDemo.timestamp));
            setLatestTempTs(new Date(latestDemo.timestamp));
            setLatestBreathing(latestDemo.breathing);
            setLatestHeart(latestDemo.heart_rate);
            setLatestTemperature(latestDemo.temperature);
            setLatestHeartRateVariability(normalizeHrvForDisplay(latestDemo.heart_rate_variability));
            setLatestSpo2(latestDemo.spo2);
            setLatestSystolicBloodPressure(latestDemo.systolic);
            setLatestDiastolicBloodPressure(latestDemo.diastolic);
          } else {
            // If no demo data available, clear all
            setLatestVitalsTs(null);
            setLatestTempTs(null);
            setLatestBreathing(null);
            setLatestHeart(null);
            setLatestHeartRateVariability(null);
            console.log('Clearing latestSpo2 - no demo data available');
            setLatestSpo2(null);
            setLatestTemperature(null);
            setLatestSystolicBloodPressure(null);
            setLatestDiastolicBloodPressure(null);
          }
        } catch (error) {
          console.warn('Failed to load latest demo data:', error);
          // Clear all on error
          setLatestVitalsTs(null);
          setLatestTempTs(null);
          setLatestBreathing(null);
          setLatestHeart(null);
          setLatestHeartRateVariability(null);
          setLatestSpo2(null);
          setLatestTemperature(null);
          setLatestSystolicBloodPressure(null);
          setLatestDiastolicBloodPressure(null);
        }
      };
      
      loadLatestDemoData();
      const demoLatestIv = setInterval(loadLatestDemoData, 10000);
      return () => clearInterval(demoLatestIv);
    }
    // Only clear values when operationMode actually changes, not on every render
    if (prevOperationModeRef.current !== operationMode) {
      console.log('Operation mode changed, clearing latest values - from:', prevOperationModeRef.current, 'to:', operationMode);
      setLatestVitalsTs(null);
      setLatestTempTs(null);
      setLatestBreathing(null);
      setLatestHeart(null);
      setLatestHeartRateVariability(null);
      console.log('Clearing latestSpo2 - operation mode changed');
      setLatestSpo2(null);
      setLatestTemperature(null);
      setLatestSystolicBloodPressure(null);
      setLatestDiastolicBloodPressure(null);
      prevOperationModeRef.current = operationMode;
    }

    const loadLatest = async () => {
      if (!isReal) return;
      try {
        if (!patId) {
          // No patient selected, load demo latest values as fallback
          console.log('[FE] No patId selected, loading demo latest values as fallback');
          const latestDemo = await getLatestDemoVitals();
          if (latestDemo) {
            setLatestVitalsTs(new Date(latestDemo.timestamp));
            setLatestTemperature(latestDemo.temperature || null);
            setLatestBreathing(latestDemo.breathing || null);
            setLatestHeart(latestDemo.heart_rate || null);
            setLatestHeartRateVariability(normalizeHrvForDisplay(latestDemo.heart_rate_variability));
            setLatestSpo2(latestDemo.spo2 || null);
            setLatestSystolicBloodPressure(latestDemo.systolic || null);
            setLatestDiastolicBloodPressure(latestDemo.diastolic || null);
          }
          return;
        }
        
        // Load latest real values for the specific patient
        console.log('[FE] Loading latest real values for patId:', patId);
        const latestReal = await getVitalSigns(10, patId);
        if (Array.isArray(latestReal) && latestReal.length > 0) {
          console.log('[FE] Found real data for patient:', latestReal.length, 'records');
          // Find the most recent record that has SpO2 data
          let r: any = latestReal[0]; // Default to first record
          const tsStr = r?.timestamp ?? r?.time ?? r?.created_at ?? r?.createdAt ?? r?.ts;
          const ts = parseDbTimestamp(tsStr);
          setLatestVitalsTs(ts);
          const hrRaw = (r.heart_rate ?? r.heart_rate_bpm ?? r.pulse_rate ?? r.pulse_rate_bpm);
          const hr = (typeof hrRaw === 'number') ? hrRaw : (hrRaw != null ? Number(hrRaw) : null);
          const heartConf = r.heart_rate_confidence;
          const heartConfValue = typeof heartConf === 'number' ? heartConf : null;
          const rr = (typeof r.respiratory_rate === 'number') ? r.respiratory_rate : (r.respiratory_rate != null ? Number(r.respiratory_rate) : null);
          const breathingConf = r.breathing_confidence ?? r.respiratory_rate_confidence;
          const breathingConfValue = typeof breathingConf === 'number' ? breathingConf : null;
          setLatestHeart(typeof hr === 'number' && !Number.isNaN(hr) && hr !== -1 && (heartConfValue == null || heartConfValue > 0) ? hr : null);

          setLatestBreathing(typeof rr === 'number' && !Number.isNaN(rr) && rr !== -1 && (breathingConfValue == null || breathingConfValue > 0) ? rr : null);
          const hrv = pickHrvFromRow(r);
          const hrvConf = r.heart_rate_variability_confidence;
          const hrvConfValue = typeof hrvConf === 'number' ? hrvConf : null;
          setLatestHeartRateVariability(hrv != null && (hrvConfValue == null || hrvConfValue > 0) ? hrv : null);
          const spo2Raw = (r.spo2 ?? r.blood_oxygen_saturation);
          const spo2 = (typeof spo2Raw === 'number') ? spo2Raw : (spo2Raw != null ? Number(spo2Raw) : null);
          const spo2Conf = r.spo2_confidence ?? r.blood_oxygen_saturation_confidence;
          const spo2ConfValue = typeof spo2Conf === 'number' ? spo2Conf : null;
          const spo2Value = typeof spo2 === 'number' && !Number.isNaN(spo2) && spo2 !== -1 && (spo2ConfValue == null || spo2ConfValue > 0) ? spo2 : null;
          setLatestSpo2(spo2Value);
          const sysRaw = (r.systolic_blood_pressure ?? r.systolic_blood_pressure_mmhg ?? r.systolic_blood_pressure_bpm ?? r.systolic_blood_pressure_bpm_mmhg ?? r.systolic);
          const sys = (typeof sysRaw === 'number') ? sysRaw : (sysRaw != null ? Number(sysRaw) : null);
          const sysConf = r.systolic_blood_pressure_confidence;
          const sysConfValue = typeof sysConf === 'number' ? sysConf : null;
          setLatestSystolicBloodPressure(typeof sys === 'number' && !Number.isNaN(sys) && sys !== -1 && (sysConfValue == null || sysConfValue > 0) ? sys : null);
          const diaRaw = (r.diastolic_blood_pressure ?? r.diastolic_blood_pressure_mmhg ?? r.diastolic_blood_pressure_bpm ?? r.diastolic_blood_pressure_bpm_mmhg ?? r.diastolic);
          const dia = (typeof diaRaw === 'number') ? diaRaw : (diaRaw != null ? Number(diaRaw) : null);
          const diaConf = r.diastolic_blood_pressure_confidence;
          const diaConfValue = typeof diaConf === 'number' ? diaConf : null;
          setLatestDiastolicBloodPressure(typeof dia === 'number' && !Number.isNaN(dia) && dia !== -1 && (diaConfValue == null || diaConfValue > 0) ? dia : null);
          const tempRaw = (r.temperature ?? r.temperature_value);
          const temp = (typeof tempRaw === 'number') ? tempRaw : (tempRaw != null ? Number(tempRaw) : null);
          setLatestTemperature(typeof temp === 'number' && !Number.isNaN(temp) && temp !== -1 ? temp : null);
          setLatestTempTs(ts);
          
          // Also fetch latest temperature from temperature table
          try {
            const latestTemp = await getLatestTemperature(patId);
            if (latestTemp && latestTemp.temp != null) {
              console.log('Latest temperature from temperature table:', latestTemp);
              setLatestTemperature(latestTemp.temp);
              setLatestTempTs(new Date(latestTemp.timestamp));
            }
          } catch (tempErr) {
            console.warn('Failed to fetch latest temperature:', tempErr);
          }
        } else {
          // No data found for this patient - clear all values
          console.log('[FE] No vital signs data found for patient:', patId);
          setLatestVitalsTs(null);
          setLatestTemperature(null);
          setLatestBreathing(null);
          setLatestHeart(null);
          setLatestHeartRateVariability(null);
          setLatestSpo2(null);
          setLatestSystolicBloodPressure(null);
          setLatestDiastolicBloodPressure(null);
        }
        return;
      } catch (e) {
        // keep previous
      }
      // Temperature is already loaded above with patId-specific data
    };
    loadLatest();
    const id = setInterval(() => {
      console.log('[Dashboard Auto Refresh] loadLatest triggered (60s interval)', {
        patId,
        at: new Date().toISOString()
      });
      loadLatest();
    }, 60000);
    return () => clearInterval(id);
  }, [operationMode, patId, patIdLoaded, isReal]);

  // Track current mode for callbacks that outlive renders
  const isRealRef = useRef<boolean>(isReal);
  const prevOperationModeRef = useRef<string>(operationMode);
  useEffect(() => { isRealRef.current = isReal; }, [isReal]);

  // Auto-refetch when backend becomes healthy again (after restart)
  useEffect(() => {
    const refetchAll = async () => {
      // Do nothing if not in real mode to avoid contaminating demo with real data
      if (!isRealRef.current) return;
      try {
        // Range refresh
        const startIso = rangeStartDate.toISOString();
        const endIso = now.toISOString();
        const data = await getVitalSignsRange(startIso, endIso, patId || undefined);
        const rows = (Array.isArray(data) ? data : []).filter(r => r && r.timestamp).sort((a: any, b: any) => parseDbTimestamp(a.timestamp).getTime() - parseDbTimestamp(b.timestamp).getTime());
        
        const getTs = (r: any): string | null => {
          const ts = r?.timestamp ?? r?.time ?? r?.created_at ?? r?.createdAt ?? r?.ts;
          return ts ? String(ts) : null;
        };
        
        // Helper functions for aggregation
        const calculateMedian = (values: number[]): number => {
          if (values.length === 0) return NaN;
          const sorted = [...values].sort((a, b) => a - b);
          const mid = Math.floor(sorted.length / 2);
          return sorted.length % 2 === 0 
            ? (sorted[mid - 1] + sorted[mid]) / 2 
            : sorted[mid];
        };
        
        const calculatePercentiles = (values: number[]): { q1: number; q3: number;} => {
          if (values.length === 0) {
            return { q1: NaN, q3: NaN};
          }
          const sorted = [...values].sort((a, b) => a - b);
          const percentile = (arr: number[], p: number): number => {
            const idx = (arr.length - 1) * p;
            const lower = Math.floor(idx);
            const upper = Math.ceil(idx);
            if (lower === upper) return arr[lower];
            const weight = idx - lower;
            return arr[lower] * (1 - weight) + arr[upper] * weight;
          };
          return {
            q1: percentile(sorted, 0.25),
            q3: percentile(sorted, 0.75),
          };
        };
        
        const calculateCustomWhiskers = (values: number[]) => {
          if (values.length === 0) {
            return { q_15: NaN, q_85: NaN };
          }
          const sorted = [...values].sort((a, b) => a - b);
          const min = sorted[0];
          const max = sorted[sorted.length - 1];
          const { q1, q3 } = calculatePercentiles(values);
          const x = (q3 - q1) * 1.5;
          const upperLimit = q3 + x;
          let q_85: number;
          const EPS = 1e-9;
          if (upperLimit >= max - EPS) {
            q_85 = max;
          } else {
            const found = [...sorted].reverse().find(v => v <= upperLimit);
            q_85 = found !== undefined ? found : max;
          }
          const lowerLimit = q1 - x;
          let q_15: number;
          if (lowerLimit <= min + EPS) {
            q_15 = min;
          } else {
            const found = sorted.find(v => v >= lowerLimit);
            q_15 = found !== undefined ? found : min;
          }
          return { q_15, q_85 };
        };
        
        const calculateStats = (values: number[]) => {
          if (values.length === 0) {
            return { median: NaN, min: NaN, max: NaN, q1: NaN, q3: NaN, q_15: NaN, q_85: NaN};
          }
          const sorted = [...values].sort((a, b) => a - b);
          const median = calculateMedian(values);
          const {q1, q3} = calculatePercentiles(values);
          const {q_15, q_85} = calculateCustomWhiskers(values);
          return {
            median,
            min: sorted[0], 
            max: sorted[sorted.length - 1],
            q1: q1,
            q3: q3,
            q_15: q_15,
            q_85: q_85
          };
        };
        
        // Aggregation function based on time range
        const aggregateByTimeRange = (
          data: Array<{ timestamp: Date; value: number; confidence?: number }>,
          timeRange: string,
          rangeStartDate: Date,
          zoomDurationHours?: number | null
        ): Array<{ timestamp: Date; value: number; min: number; max: number; q1: number; q3: number; q_15: number; q_85: number; confidence?: number }> => {
          if (data.length === 0) return [];
          
          let useZoomInterval = false;
          let intervalMinutes: number = 1;
          
          if (zoomDurationHours != null && zoomDurationHours > 0) {
            useZoomInterval = true;
            if (zoomDurationHours <= 1) {
              return data.map(d => ({
                timestamp: d.timestamp,
                value: d.value,
                min: d.value,
                max: d.value,
                q1: d.value,
                q3: d.value,
                q_15: d.value,
                q_85: d.value,
                confidence: d.confidence
              }));
            } else if (zoomDurationHours <= 6) {
              intervalMinutes = 6;
            } else if (zoomDurationHours <= 12) {
              intervalMinutes = 12;
            } else if (zoomDurationHours <= 24) {
              intervalMinutes = 24;
            } else if (zoomDurationHours <= 168) {
              intervalMinutes = 168;
            } else {
              intervalMinutes = 720;
            }
          }
          
          if (!useZoomInterval) {
            switch (timeRange) {
              case '1 Hours':
                return data.map(d => ({
                  timestamp: d.timestamp,
                  value: d.value,
                  min: d.value,
                  max: d.value,
                  q1: d.value,
                  q3: d.value,
                  q_15: d.value,
                  q_85: d.value,
                  confidence: d.confidence
                }));
              case '6 Hours':
                intervalMinutes = 6;
                break;
              case '12 Hours':
                intervalMinutes = 12;
                break;
              case '24 Hours':
                intervalMinutes = 24;
                break;
              case '7 Days':
              case '14 Days':
                intervalMinutes = 168;
                break;
              case '30 Days':
                intervalMinutes = 720;
                break;
              default:
                intervalMinutes = 1;
            }
          }
          
          const grouped = new Map<string, Array<{ timestamp: Date; value: number; confidence?: number }>>();
          for (const point of data) {
            const timestamp = point.timestamp.getTime();
            const rangeStart = rangeStartDate.getTime();
            const minutesFromStart = Math.floor((timestamp - rangeStart) / (1000 * 60));
            const intervalIndex = Math.floor(minutesFromStart / intervalMinutes);
            const intervalKey = String(intervalIndex);
            if (!grouped.has(intervalKey)) {
              grouped.set(intervalKey, []);
            }
            grouped.get(intervalKey)!.push(point);
          }
          
          const aggregated: Array<{
            timestamp: Date;
            value: number;
            min: number;
            max: number;
            q1: number;
            q3: number;
            q_15: number;
            q_85: number;
            confidence?: number;
          }> = [];
          const groupedEntries = Array.from(grouped.entries());
          for (let i = 0; i < groupedEntries.length; i++) {
            const [, values] = groupedEntries[i];
            const validPoints = values
              .filter((v: { timestamp: Date; value: number; confidence?: number }) => {
                if (v.confidence == null || v.confidence === undefined) return false;
                return Number(v.confidence) > 0;
              });
            const validValues = validPoints.map((v) => v.value);
            if (validValues.length === 0) continue;
            const { median, min, max, q1, q3, q_15, q_85 } = calculateStats(validValues);
            const confidences = values.map((v: { timestamp: Date; value: number; confidence?: number }) => v.confidence).filter((c: number | undefined): c is number => c !== undefined);
            const avgConfidence = confidences.length > 0 
              ? confidences.reduce((sum: number, c: number) => sum + c, 0) / confidences.length 
              : undefined;
            aggregated.push({
              timestamp: new Date(Math.min(...validPoints.map((point) => point.timestamp.getTime()))),
              value: median,
              min: min,
              max: max,
              q1: q1,
              q3: q3,
              q_15: q_15,
              q_85: q_85,
              confidence: avgConfidence
            });
          }
          return aggregated.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
        };
        
        // Calculate zoom duration from sharedZoomRange if available
        let zoomDurationHours: number | null = null;
        let zoomStartDate: Date | null = null;
        let zoomStartMs: number | null = null;
        let zoomEndMs: number | null = null;
        
        if (sharedZoomRange.startTime && sharedZoomRange.endTime) {
          zoomStartDate = new Date(sharedZoomRange.startTime);
          const zoomEnd = new Date(sharedZoomRange.endTime);
          zoomStartMs = zoomStartDate.getTime();
          zoomEndMs = zoomEnd.getTime();
          const zoomDurationMinutes = (zoomEndMs - zoomStartMs) / (1000 * 60);
          zoomDurationHours = zoomDurationMinutes / 60;
        } else if (customChartWindow) {
          const customDurationMinutes = (customChartWindow.endDate.getTime() - customChartWindow.startDate.getTime()) / (1000 * 60);
          zoomDurationHours = customDurationMinutes / 60;
        } else if (selectedTimeRange === 'Custom') {
          const customDurationMinutes = (customEndDate.getTime() - customStartDate.getTime()) / (1000 * 60);
          zoomDurationHours = customDurationMinutes / 60;
        }
        
        const effectiveRangeStartMs = zoomStartMs ?? rangeStartDate.getTime();
        const effectiveRangeEndMs = zoomEndMs ?? (selectedTimeRange === 'Custom' ? customEndDate : now).getTime();
        const effectiveRangeStartDate = zoomStartDate ?? rangeStartDate;
        
        const toGraph = (getter: (r: any) => number | null, confidenceField?: string): GraphData[] => {
          const series: Array<{ timestamp: Date; value: number; confidence?: number }> = [];
          
          for (const r of rows) {
            const v = getter(r);
            if (typeof v === 'number' && !Number.isNaN(v) && v !== -1) {
              const tsStr = (r.__clampedTs as string) || (getTs(r) as string);
              const t = parseDbTimestamp(tsStr);
              const tMs = t.getTime();
              
              if (tMs < effectiveRangeStartMs || tMs > effectiveRangeEndMs) {
                continue;
              }
              
              const confValue = resolveVitalConfidence(r, confidenceField);
              series.push({ timestamp: t, value: Number(v), confidence: confValue });
            }
          }
          
          // Apply aggregation based on time range with zoom duration
          const aggregated = aggregateByTimeRange(series, selectedTimeRange, effectiveRangeStartDate, zoomDurationHours);
          
          // Filter aggregated results to be within effective range
          const filteredAggregated = aggregated.filter(d => {
            const dMs = d.timestamp.getTime();
            return dMs >= effectiveRangeStartMs && dMs <= effectiveRangeEndMs;
          });
          
          // Convert to GraphData format with appropriate time format
          const timeFormat = (selectedTimeRange === "7 Days" || selectedTimeRange === "14 Days") ? "dd MMM HH:mm" 
            : selectedTimeRange === "30 Days" ? "dd MMM" 
            : "HH:mm";
          
          return filteredAggregated.map(d => ({
            time: format(d.timestamp, timeFormat),
            value: d.value,
            min: d.min,
            max: d.max,
            q1: d.q1,
            q3: d.q3,
            q_15: d.q_15,
            q_85: d.q_85,
            confidence: d.confidence,
            tsMs: d.timestamp.getTime()
          }));
        };
        
        const breathingSeries = toGraph((r) => {
          const v = r.respiratory_rate;
          return typeof v === 'number' ? v : (v != null ? Number(v) : null);
        }, 'respiratory_rate_confidence');
        const heartSeries = toGraph((r) => {
          const raw = (r.heart_rate ?? r.heart_rate_bpm ?? r.pulse_rate ?? r.pulse_rate_bpm);
          return (typeof raw === 'number') ? raw : (raw != null ? Number(raw) : null);
        }, 'heart_rate_confidence');
        const heartRateVariabilitySeries = toGraph((r) => pickHrvFromRow(r), 'heart_rate_variability_confidence');
        setDbBreathing(breathingSeries.length >= 1 ? breathingSeries : []);
        setDbHeartRate(heartSeries.length >= 1 ? heartSeries : []);
        setDbHeartRateVariability(heartRateVariabilitySeries.length >= 1 ? heartRateVariabilitySeries : []);

      } catch {}
      try {
        // Latest refresh
        const rows = await getVitalSigns(10, patId || undefined);
        if (Array.isArray(rows) && rows.length > 0) {
          // Find the most recent record that has SpO2 data
          let r: any = rows[0]; // Default to first record
          for (const record of rows) {
            const spo2Value = record.spo2 ?? record.blood_oxygen_saturation;
            if (spo2Value != null && spo2Value !== '') {
              r = record;
              // console.log('🔵 Range fetch found record with SpO2:', spo2Value, 'from record:', record.timestamp);
              break;
            }
          }
          const ts = parseDbTimestamp(r.timestamp);
          setLatestVitalsTs(ts);
          const hrRaw = (r.heart_rate ?? r.heart_rate_bpm ?? r.pulse_rate ?? r.pulse_rate_bpm);
          const hr = (typeof hrRaw === 'number') ? hrRaw : (hrRaw != null ? Number(hrRaw) : null);
          const heartConf = r.heart_rate_confidence;
          const heartConfValue = typeof heartConf === 'number' ? heartConf : null;
          const rr = (typeof r.respiratory_rate === 'number') ? r.respiratory_rate : (r.respiratory_rate != null ? Number(r.respiratory_rate) : null);
          const breathingConf = r.breathing_confidence ?? r.respiratory_rate_confidence;
          const breathingConfValue = typeof breathingConf === 'number' ? breathingConf : null;
          console.log('breathingConfValue', breathingConfValue);
          setLatestHeart(typeof hr === 'number' && !Number.isNaN(hr) && hr !== -1 && (heartConfValue == null || heartConfValue > 0) ? hr : null);
          setLatestBreathing(typeof rr === 'number' && !Number.isNaN(rr) && rr !== -1 && (breathingConfValue == null || breathingConfValue > 0) ? rr : null);
          const hrv = pickHrvFromRow(r);
          const hrvConf = r.heart_rate_variability_confidence;
          const hrvConfValue = typeof hrvConf === 'number' ? hrvConf : null;
          setLatestHeartRateVariability(hrv != null && (hrvConfValue == null || hrvConfValue > 0) ? hrv : null);
          const spo2Raw = (r.spo2 ?? r.spo2_bpm ?? r.spo2_bpm_ms ?? r.blood_oxygen_saturation);
          const spo2 = (typeof spo2Raw === 'number') ? spo2Raw : (spo2Raw != null ? Number(spo2Raw) : null);
          const spo2Conf = r.spo2_confidence ?? r.blood_oxygen_saturation_confidence;
          const spo2ConfValue = typeof spo2Conf === 'number' ? spo2Conf : null;
          setLatestSpo2(typeof spo2 === 'number' && !Number.isNaN(spo2) && spo2 !== -1 && (spo2ConfValue == null || spo2ConfValue > 0) ? spo2 : null);

          const systolicPressureRaw = (
            r.systolic_blood_pressure ??
            r.systolic_blood_pressure_mmhg ??
            r.systolic_blood_pressure_bpm ??
            r.systolic_blood_pressure_bpm_mmhg
          );
          const systolicPressure = (typeof systolicPressureRaw === 'number') ? systolicPressureRaw : (systolicPressureRaw != null ? Number(systolicPressureRaw) : null);
          const sysConf = r.systolic_blood_pressure_confidence;
          const sysConfValue = typeof sysConf === 'number' ? sysConf : null;
          setLatestSystolicBloodPressure(typeof systolicPressure === 'number' && !Number.isNaN(systolicPressure) && systolicPressure !== -1 && (sysConfValue == null || sysConfValue > 0) ? systolicPressure : null);

          const diastolicPressureRaw = (
            r.diastolic_blood_pressure ??
            r.diastolic_blood_pressure_mmhg ??
            r.diastolic_blood_pressure_bpm ??
            r.diastolic_blood_pressure_bpm_mmhg
          );
          const diastolicPressure = (typeof diastolicPressureRaw === 'number') ? diastolicPressureRaw : (diastolicPressureRaw != null ? Number(diastolicPressureRaw) : null);
          const diaConf = r.diastolic_blood_pressure_confidence;
          const diaConfValue = typeof diaConf === 'number' ? diaConf : null;
          setLatestDiastolicBloodPressure(typeof diastolicPressure === 'number' && !Number.isNaN(diastolicPressure) && diastolicPressure !== -1 && (diaConfValue == null || diaConfValue > 0) ? diastolicPressure : null);
        
        }
      } catch {}
    };
    onBackendHealthy(refetchAll);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Listen for custom events to trigger immediate dashboard refresh
  useEffect(() => {
    const handleForceRefreshDashboard = () => {
      console.log('Received forceRefreshDashboard event, triggering immediate dashboard refresh...');
      if (operationMode === 'demo') {
        refreshDemoDataRef.current?.();
        return;
      }
      // Trigger immediate refresh of all dashboard data
      const refetchAll = async () => {
        try {
          // Range refresh
          const startIso = rangeStartDate.toISOString();
          const endIso = now.toISOString();
          const data = await getVitalSignsRange(startIso, endIso, patId || undefined);
          const rows = (Array.isArray(data) ? data : []).filter(r => r && r.timestamp).sort((a: any, b: any) => parseDbTimestamp(a.timestamp).getTime() - parseDbTimestamp(b.timestamp).getTime());
          
          const getTs = (r: any): string | null => {
            const ts = r?.timestamp ?? r?.time ?? r?.created_at ?? r?.createdAt ?? r?.ts;
            return ts ? String(ts) : null;
          };
          
          // Helper functions for aggregation
          const calculateMedian = (values: number[]): number => {
            if (values.length === 0) return NaN;
            const sorted = [...values].sort((a, b) => a - b);
            const mid = Math.floor(sorted.length / 2);
            return sorted.length % 2 === 0 
              ? (sorted[mid - 1] + sorted[mid]) / 2 
              : sorted[mid];
          };
          
          const calculatePercentiles = (values: number[]): { q1: number; q3: number;} => {
            if (values.length === 0) {
              return { q1: NaN, q3: NaN};
            }
            const sorted = [...values].sort((a, b) => a - b);
            const percentile = (arr: number[], p: number): number => {
              const idx = (arr.length - 1) * p;
              const lower = Math.floor(idx);
              const upper = Math.ceil(idx);
              if (lower === upper) return arr[lower];
              const weight = idx - lower;
              return arr[lower] * (1 - weight) + arr[upper] * weight;
            };
            return {
              q1: percentile(sorted, 0.25),
              q3: percentile(sorted, 0.75),
            };
          };
          
          const calculateCustomWhiskers = (values: number[]) => {
            if (values.length === 0) {
              return { q_15: NaN, q_85: NaN };
            }
            const sorted = [...values].sort((a, b) => a - b);
            const min = sorted[0];
            const max = sorted[sorted.length - 1];
            const { q1, q3 } = calculatePercentiles(values);
            const x = (q3 - q1) * 1.5;
            const upperLimit = q3 + x;
            let q_85: number;
            const EPS = 1e-9;
            if (upperLimit >= max - EPS) {
              q_85 = max;
            } else {
              const found = [...sorted].reverse().find(v => v <= upperLimit);
              q_85 = found !== undefined ? found : max;
            }
            const lowerLimit = q1 - x;
            let q_15: number;
            if (lowerLimit <= min + EPS) {
              q_15 = min;
            } else {
              const found = sorted.find(v => v >= lowerLimit);
              q_15 = found !== undefined ? found : min;
            }
            return { q_15, q_85 };
          };
          
          const calculateStats = (values: number[]) => {
            if (values.length === 0) {
              return { median: NaN, min: NaN, max: NaN, q1: NaN, q3: NaN, q_15: NaN, q_85: NaN};
            }
            const sorted = [...values].sort((a, b) => a - b);
            const median = calculateMedian(values);
            const {q1, q3} = calculatePercentiles(values);
            const {q_15, q_85} = calculateCustomWhiskers(values);
            return {
              median,
              min: sorted[0], 
              max: sorted[sorted.length - 1],
              q1: q1,
              q3: q3,
              q_15: q_15,
              q_85: q_85
            };
          };
          
          // Aggregation function based on time range
          const aggregateByTimeRange = (
            data: Array<{ timestamp: Date; value: number; confidence?: number }>,
            timeRange: string,
            rangeStartDate: Date,
            zoomDurationHours?: number | null
          ): Array<{ timestamp: Date; value: number; min: number; max: number; q1: number; q3: number; q_15: number; q_85: number; confidence?: number }> => {
            if (data.length === 0) return [];
            
            let useZoomInterval = false;
            let intervalMinutes: number = 1;
            
            if (zoomDurationHours != null && zoomDurationHours > 0) {
              useZoomInterval = true;
              if (zoomDurationHours <= 1) {
                return data.map(d => ({
                  timestamp: d.timestamp,
                  value: d.value,
                  min: d.value,
                  max: d.value,
                  q1: d.value,
                  q3: d.value,
                  q_15: d.value,
                  q_85: d.value,
                  confidence: d.confidence
                }));
              } else if (zoomDurationHours <= 6) {
                intervalMinutes = 6;
              } else if (zoomDurationHours <= 12) {
                intervalMinutes = 12;
              } else if (zoomDurationHours <= 24) {
                intervalMinutes = 24;
              } else if (zoomDurationHours <= 168) {
                intervalMinutes = 168;
              } else {
                intervalMinutes = 720;
              }
            }
            
            if (!useZoomInterval) {
              switch (timeRange) {
                case '1 Hours':
                  return data.map(d => ({
                    timestamp: d.timestamp,
                    value: d.value,
                    min: d.value,
                    max: d.value,
                    q1: d.value,
                    q3: d.value,
                    q_15: d.value,
                    q_85: d.value,
                    confidence: d.confidence
                  }));
                case '6 Hours':
                  intervalMinutes = 6;
                  break;
                case '12 Hours':
                  intervalMinutes = 12;
                  break;
                case '24 Hours':
                  intervalMinutes = 24;
                  break;
                case '7 Days':
                case '14 Days':
                  intervalMinutes = 168;
                  break;
                case '30 Days':
                  intervalMinutes = 720;
                  break;
                default:
                  intervalMinutes = 1;
              }
            }
            
            const grouped = new Map<string, Array<{ timestamp: Date; value: number; confidence?: number }>>();
            for (const point of data) {
              const timestamp = point.timestamp.getTime();
              const rangeStart = rangeStartDate.getTime();
              const minutesFromStart = Math.floor((timestamp - rangeStart) / (1000 * 60));
              const intervalIndex = Math.floor(minutesFromStart / intervalMinutes);
              const intervalKey = String(intervalIndex);
              if (!grouped.has(intervalKey)) {
                grouped.set(intervalKey, []);
              }
              grouped.get(intervalKey)!.push(point);
            }
            
            const aggregated: Array<{
              timestamp: Date;
              value: number;
              min: number;
              max: number;
              q1: number;
              q3: number;
              q_15: number;
              q_85: number;
              confidence?: number;
            }> = [];
            const groupedEntries = Array.from(grouped.entries());
            for (let i = 0; i < groupedEntries.length; i++) {
              const [, values] = groupedEntries[i];
              const validPoints = values
                .filter((v: { timestamp: Date; value: number; confidence?: number }) => {
                  if (v.confidence == null || v.confidence === undefined) return false;
                  return Number(v.confidence) > 0;
                });
              const validValues = validPoints.map((v) => v.value);
              if (validValues.length === 0) continue;
              const { median, min, max, q1, q3, q_15, q_85 } = calculateStats(validValues);
              const confidences = values.map((v: { timestamp: Date; value: number; confidence?: number }) => v.confidence).filter((c: number | undefined): c is number => c !== undefined);
              const avgConfidence = confidences.length > 0 
                ? confidences.reduce((sum: number, c: number) => sum + c, 0) / confidences.length 
                : undefined;
              aggregated.push({
                timestamp: new Date(Math.min(...validPoints.map((point) => point.timestamp.getTime()))),
                value: median,
                min: min,
                max: max,
                q1: q1,
                q3: q3,
                q_15: q_15,
                q_85: q_85,
                confidence: avgConfidence
              });
            }
            return aggregated.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
          };
          
          // Calculate zoom duration from sharedZoomRange if available
          let zoomDurationHours: number | null = null;
          let zoomStartDate: Date | null = null;
          let zoomStartMs: number | null = null;
          let zoomEndMs: number | null = null;
          
          if (sharedZoomRange.startTime && sharedZoomRange.endTime) {
            zoomStartDate = new Date(sharedZoomRange.startTime);
            const zoomEnd = new Date(sharedZoomRange.endTime);
            zoomStartMs = zoomStartDate.getTime();
            zoomEndMs = zoomEnd.getTime();
            const zoomDurationMinutes = (zoomEndMs - zoomStartMs) / (1000 * 60);
            zoomDurationHours = zoomDurationMinutes / 60;
          } else if (customChartWindow) {
            const customDurationMinutes = (customChartWindow.endDate.getTime() - customChartWindow.startDate.getTime()) / (1000 * 60);
            zoomDurationHours = customDurationMinutes / 60;
          } else if (selectedTimeRange === 'Custom') {
            const customDurationMinutes = (customEndDate.getTime() - customStartDate.getTime()) / (1000 * 60);
            zoomDurationHours = customDurationMinutes / 60;
          }
          
          const effectiveRangeStartMs = zoomStartMs ?? rangeStartDate.getTime();
          const effectiveRangeEndMs = zoomEndMs ?? (selectedTimeRange === 'Custom' ? customEndDate : now).getTime();
          const effectiveRangeStartDate = zoomStartDate ?? rangeStartDate;
          
          const toGraph = (getter: (r: any) => number | null, confidenceField?: string): GraphData[] => {
            const series: Array<{ timestamp: Date; value: number; confidence?: number }> = [];
            
            for (const r of rows) {
              const v = getter(r);
              if (typeof v === 'number' && !Number.isNaN(v) && v !== -1) {
                const tsStr = (r.__clampedTs as string) || (getTs(r) as string);
                const t = parseDbTimestamp(tsStr);
                const tMs = t.getTime();
                
                if (tMs < effectiveRangeStartMs || tMs > effectiveRangeEndMs) {
                  continue;
                }
                
                const confValue = resolveVitalConfidence(r, confidenceField);
                series.push({ timestamp: t, value: Number(v), confidence: confValue });
              }
            }
            
            // Apply aggregation based on time range with zoom duration
            const aggregated = aggregateByTimeRange(series, selectedTimeRange, effectiveRangeStartDate, zoomDurationHours);
            
            // Filter aggregated results to be within effective range
            const filteredAggregated = aggregated.filter(d => {
              const dMs = d.timestamp.getTime();
              return dMs >= effectiveRangeStartMs && dMs <= effectiveRangeEndMs;
            });
            
            // Convert to GraphData format with appropriate time format
            const timeFormat = (selectedTimeRange === "7 Days" || selectedTimeRange === "14 Days") ? "dd MMM HH:mm" 
              : selectedTimeRange === "30 Days" ? "dd MMM" 
              : "HH:mm";
            
            return filteredAggregated.map(d => ({
              time: format(d.timestamp, timeFormat),
              value: d.value,
              min: d.min,
              max: d.max,
              q1: d.q1,
              q3: d.q3,
              q_15: d.q_15,
              q_85: d.q_85,
              confidence: d.confidence,
              tsMs: d.timestamp.getTime()
            }));
          };
          
          const breathingSeries = toGraph((r) => {
            const v = r.respiratory_rate;
            return typeof v === 'number' ? v : (v != null ? Number(v) : null);
          }, 'respiratory_rate_confidence');
          const heartSeries = toGraph((r) => {
            const raw = (r.heart_rate ?? r.heart_rate_bpm ?? r.pulse_rate ?? r.pulse_rate_bpm);
            return (typeof raw === 'number') ? raw : (raw != null ? Number(raw) : null);
          }, 'heart_rate_confidence');
          const heartRateVariabilitySeries = toGraph((r) => pickHrvFromRow(r), 'heart_rate_variability_confidence');
          setDbBreathing(breathingSeries.length >= 1 ? breathingSeries : []);
          setDbHeartRate(heartSeries.length >= 1 ? heartSeries : []);
          setDbHeartRateVariability(heartRateVariabilitySeries.length >= 1 ? heartRateVariabilitySeries : []);
          const spo2Series = toGraph((r) => {
            const v = r.spo2 ?? r.oxygen_saturation;
            return typeof v === 'number' ? v : (v != null ? Number(v) : null);
          }, 'blood_oxygen_saturation_confidence');
          const respirationRateSeries = toGraph((r) => {
            const v = r.respiratory_rate;
            return typeof v === 'number' ? v : (v != null ? Number(v) : null);
          });
          const systolic_diastolicBloodPressureSeries = toGraph((r) => {
            const v = r.systolic_blood_pressure;
            return typeof v === 'number' ? v : (v != null ? Number(v) : null);
          });
          const systolicBloodPressureSeries = toGraph((r) => {
            const v = r.systolic_blood_pressure;
            return typeof v === 'number' ? v : (v != null ? Number(v) : null);
          });
          const diastolicBloodPressureSeries = toGraph((r) => {
            const v = r.diastolic_blood_pressure;
            return typeof v === 'number' ? v : (v != null ? Number(v) : null);
          });
          setDbSpo2(spo2Series.length >= 1 ? spo2Series : []);
          setDbRespirationRate(respirationRateSeries.length >= 1 ? respirationRateSeries : []);
          setDbSystolic_diastolicBloodPressure(systolic_diastolicBloodPressureSeries.length >= 1 ? systolic_diastolicBloodPressureSeries : []);
          setDbSystolicBloodPressure(systolicBloodPressureSeries.length >= 1 ? systolicBloodPressureSeries : []);
          setDbDiastolicBloodPressure(diastolicBloodPressureSeries.length >= 1 ? diastolicBloodPressureSeries : []);
          console.log('Dashboard data refreshed successfully after demo data generation');
        } catch (e) {
          console.warn('Failed to refresh dashboard data:', e);
        }
      };
      refetchAll();
    };

    // Add event listener
    window.addEventListener('forceRefreshDashboard', handleForceRefreshDashboard);

    // Cleanup
    return () => {
      window.removeEventListener('forceRefreshDashboard', handleForceRefreshDashboard);
    };
  }, [rangeStartDate, now, patId, selectedTimeRange, sharedZoomRange.startTime, sharedZoomRange.endTime, customStartDate, customEndDate, selectedEventTimestamp, operationMode]);

  const breathingBaseData = (dbBreathing && dbBreathing.length >= 1) ? dbBreathing : [];
  const heartRateBaseData = (dbHeartRate && dbHeartRate.length >= 1) ? dbHeartRate : [];
  const heartRateVariabilityBaseData = (dbHeartRateVariability && dbHeartRateVariability.length >= 1)
    ? dbHeartRateVariability.filter((p) => normalizeHrvForDisplay(p.value) != null)
    : [];
  const spo2BaseData = (dbSpo2 && dbSpo2.length >= 1) ? dbSpo2 : [];
  const respirationRateBaseData = (dbRespirationRate && dbRespirationRate.length >= 1) ? dbRespirationRate : [];
  const systolic_BloodPressureBaseData = (dbSystolicBloodPressure && dbSystolicBloodPressure.length >= 1) ? dbSystolicBloodPressure : [];
  const diastolic_BloodPressureBaseData = (dbDiastolicBloodPressure && dbDiastolicBloodPressure.length >= 1) ? dbDiastolicBloodPressure : [];

  const activeSummaryNight = useMemo((): SleepNightWindow | null => {
    const lastNight = getLastNightWindow(new Date(), patientBedTime);

    if (sharedZoomRange.startTime && sharedZoomRange.endTime) {
      const sleepPoints = toSleepStagePoints(mergeSleepPointsByMinute(dbSleep));
      const zoomStartMs = new Date(sharedZoomRange.startTime).getTime();
      const zoomEndMs = new Date(sharedZoomRange.endTime).getTime();
      const nightByStartMs = new Map<number, SleepNightWindow>();

      for (const point of sleepPoints) {
        if (point.tsMs < zoomStartMs || point.tsMs > zoomEndMs) continue;
        const night = getNightWindowContaining(new Date(point.tsMs), patientBedTime);
        if (night) nightByStartMs.set(night.start.getTime(), night);
      }

      if (nightByStartMs.size === 1) {
        return Array.from(nightByStartMs.values())[0];
      }
    }

    return lastNight;
  }, [sharedZoomRange.startTime, sharedZoomRange.endTime, dbSleep, patientBedTime]);

  useEffect(() => {
    if (!patIdLoaded || !activeSummaryNight) {
      if (!activeSummaryNight) setSummaryNightSleepData([]);
      return;
    }
    const { start, end } = activeSummaryNight;
    const startIso = start.toISOString();
    const endIso = end.toISOString();

    const fetchSummaryNightSleep = async () => {
      try {
        let rows: any[] = [];
        if (operationMode === 'demo' || !patId) {
          rows = await getDemoVitalsRange(startIso, endIso);
        } else {
          rows = await getVitalSignsRange(startIso, endIso, patId);
        }
        const series = buildSleepSeriesFromRows(rows, getSleepStageValue);
        const nightStartMs = start.getTime();
        const nightEndMs = end.getTime();
        const filtered = series.filter((p) => {
          const tsMs = (p as GraphData & { tsMs?: number }).tsMs;
          return tsMs != null && tsMs >= nightStartMs && tsMs <= nightEndMs;
        });
        setSummaryNightSleepData(filtered);
      } catch (error) {
        console.warn('Failed to load summary night sleep data:', error);
        setSummaryNightSleepData([]);
      }
    };

    fetchSummaryNightSleep();
    const intervalId = setInterval(fetchSummaryNightSleep, 30000);
    return () => clearInterval(intervalId);
  }, [patId, patIdLoaded, operationMode, activeSummaryNight]);

  useEffect(() => {
    if (!patIdLoaded || !activeSummaryNight) {
      if (!activeSummaryNight) setHistoricalSleepData([]);
      return;
    }
    const beforeIso = activeSummaryNight.start.toISOString();

    const fetchHistoricalSleep = async () => {
      try {
        const response = await getSleepConsistencyHistory(beforeIso, {
          patId: patId || undefined,
          nights: CONSISTENCY_HISTORY_NIGHTS,
          demo: operationMode === 'demo' || !patId,
        });
        setHistoricalSleepData(
          buildSleepSeriesFromRows(response.rows, getSleepStageValue)
        );
      } catch (error) {
        console.warn('Failed to load historical sleep data:', error);
        setHistoricalSleepData([]);
      }
    };

    fetchHistoricalSleep();
    const intervalId = setInterval(fetchHistoricalSleep, 60000);
    return () => clearInterval(intervalId);
  }, [patId, patIdLoaded, operationMode, activeSummaryNight]);

  const sleepNightLabel = useMemo(() => {
    if (!activeSummaryNight) return 'No sleep data';
    const { start, end } = activeSummaryNight;
    return `${format(start, 'dd/MM HH:mm')} – ${format(end, 'dd/MM HH:mm')}`;
  }, [activeSummaryNight]);

  const sleepStageSummary = useMemo(() => {
    if (!activeSummaryNight) return null;
    const startMs = activeSummaryNight.start.getTime();
    const endMs = activeSummaryNight.end.getTime();
    const merged = mergeSleepPointsByMinute([...summaryNightSleepData, ...dbSleep]);
    const points = merged
      .filter((p) => {
        const tsMs = (p as GraphData & { tsMs?: number }).tsMs;
        return tsMs != null && tsMs >= startMs && tsMs <= endMs;
      })
      .filter((p) => typeof p.value === 'number' && p.value >= 1 && p.value <= 4)
      .map((p) => ({ value: p.value as number }));
    return computeSleepStageSummary(points);
  }, [activeSummaryNight, summaryNightSleepData, dbSleep]);

  const sleepScoreResult = useMemo(() => {
    if (!activeSummaryNight) return null;
    const startMs = activeSummaryNight.start.getTime();
    const endMs = activeSummaryNight.end.getTime();
    const merged = mergeSleepPointsByMinute([...summaryNightSleepData, ...dbSleep]);
    const currentPoints = toSleepStagePoints(
      merged.filter((p) => {
        const tsMs = (p as GraphData & { tsMs?: number }).tsMs;
        return tsMs != null && tsMs >= startMs && tsMs <= endMs;
      })
    );

    const currentSession = deriveSleepSessionFromPoints(currentPoints);
    if (!currentSession) return null;

    const historyPoints = toSleepStagePoints(
      mergeSleepPointsByMinute([...historicalSleepData, ...summaryNightSleepData, ...dbSleep])
    );
    const allSessions = groupSleepPointsIntoSessions(historyPoints);
    const previousWindows = getPreviousSleepSessionWindows(
      allSessions,
      activeSummaryNight,
      CONSISTENCY_HISTORY_NIGHTS
    );
    const previousOnsets = extractPreviousSleepOnsets(previousWindows, historyPoints);

    try {
      const score = computeSleepScore({
        currentSession,
        sleepGoalMinutes: DEFAULT_SLEEP_GOAL_MINUTES,
        previousSleepOnsets: previousOnsets,
      });
      const metricDetails = buildSleepScoreMetricDetails(
        {
          currentSession,
          sleepGoalMinutes: DEFAULT_SLEEP_GOAL_MINUTES,
          previousSleepOnsets: previousOnsets,
        },
        score
      );
      return { score, metricDetails };
    } catch (error) {
      console.warn('Failed to compute sleep score:', error);
      return null;
    }
  }, [activeSummaryNight, summaryNightSleepData, dbSleep, historicalSleepData]);

  const sleepingBaseData = (dbSleep && dbSleep.length >= 1) ? dbSleep : [];

  // Track chart sizes to map pointer X to nearest label when not over a point
  const tempContainerRef = useRef<HTMLDivElement>(null);
  const breathingContainerRef = useRef<HTMLDivElement>(null);
  const heartRateContainerRef = useRef<HTMLDivElement>(null);
  const heartRateVariabilityContainerRef = useRef<HTMLDivElement>(null);
  const systolic_diastolicBloodPressureContainerRef = useRef<HTMLDivElement>(null);
  const spo2ContainerRef = useRef<HTMLDivElement>(null);
  const respirationRateContainerRef = useRef<HTMLDivElement>(null);
  const sleepingContainerRef = useRef<HTMLDivElement>(null);
  type PlotBounds = { left: number; width: number };
  // Plot bounds from Recharts (for accurate zoom at edges)
  const heartRatePlotBoundsRef = useRef<PlotBounds | null>(null);
  const heartRateVariabilityPlotBoundsRef = useRef<PlotBounds | null>(null);
  const spo2PlotBoundsRef = useRef<PlotBounds | null>(null);
  const breathingPlotBoundsRef = useRef<PlotBounds | null>(null);
  const tempPlotBoundsRef = useRef<PlotBounds | null>(null);
  const respirationRatePlotBoundsRef = useRef<PlotBounds | null>(null);
  const systolic_diastolicBloodPressurePlotBoundsRef = useRef<PlotBounds | null>(null);
  const sleepingPlotBoundsRef = useRef<PlotBounds | null>(null);

  const useElementSize = (ref: React.RefObject<HTMLElement>) => {
    const [size, setSize] = useState<{ width: number; height: number }>({ width: 0, height: 0 });
    useEffect(() => {
      const update = () => {
        const rect = ref.current?.getBoundingClientRect();
        if (rect) setSize({ width: rect.width, height: rect.height });
      };
      update();
      window.addEventListener('resize', update);
      return () => window.removeEventListener('resize', update);
    }, [ref]);
    return size;
  };

  const tempChartSize = useElementSize(tempContainerRef);
  const breathingChartSize = useElementSize(breathingContainerRef);
  const heartRateChartSize = useElementSize(heartRateContainerRef);
  const heartRateVariabilityChartSize = useElementSize(heartRateVariabilityContainerRef);
  const spo2ChartSize = useElementSize(spo2ContainerRef);
  const respirationRateChartSize = useElementSize(respirationRateContainerRef);
  const systolic_diastolicBloodPressureChartSize = useElementSize(systolic_diastolicBloodPressureContainerRef);
  // Sleeping pattern container
  const sleepingChartSize = useElementSize(sleepingContainerRef);

  // Always show only real sample points (no interpolation) using 0 pointsBetween - same as demo mode
  // This prevents connecting points with interpolated values between them
  const displayedHeartRateData = densifyAlignedWithGaps(
    heartRateBaseData,
    0,
    format(rangeStartDate, 'HH:mm'),
    MAX_GAP_MINUTES_FOR_WINDOW,
    { startDate: rangeStartDate, rangeMinutes }
  );
  // Debug: Print only the final points that go to the chart
  
  const displayedHrvData = densifyAlignedWithGaps(
    heartRateVariabilityBaseData,
    0,
    format(rangeStartDate, 'HH:mm'),
    MAX_GAP_MINUTES_FOR_WINDOW,
    { startDate: rangeStartDate, rangeMinutes }
  );
  const displayedSpo2Data = densifyAlignedWithGaps(
    filterGraphDataByConfidence(spo2BaseData, isDemo),
    0,
    format(rangeStartDate, 'HH:mm'),
    MAX_GAP_MINUTES_FOR_WINDOW,
    { startDate: rangeStartDate, rangeMinutes }
  );
  const displayedRespirationData = densifyAlignedWithGaps(
    respirationRateBaseData,
    0,
    format(rangeStartDate, 'HH:mm'),
    MAX_GAP_MINUTES_FOR_WINDOW,
    { startDate: rangeStartDate, rangeMinutes }
  );
  const displayedTemperatureData = densifyAlignedWithGaps(
    dbTemperature,
    0,
    format(rangeStartDate, 'HH:mm'),
    MAX_GAP_MINUTES_FOR_WINDOW,
    { startDate: rangeStartDate, rangeMinutes }
  );
  const displayedSleepData = densifyAlignedWithGaps(
    sleepingBaseData,
    0,
    format(rangeStartDate, 'HH:mm'),
    Math.max(MAX_GAP_MINUTES_FOR_WINDOW, 2),
    { startDate: rangeStartDate, rangeMinutes }
  );
  // Use simple data without densification for blood pressure charts (like other charts)
  const systolicBloodPressureDense = densifyAlignedWithGaps(
    systolic_BloodPressureBaseData,
    0,
    format(rangeStartDate, 'HH:mm'),
    MAX_GAP_MINUTES_FOR_WINDOW,
    { startDate: rangeStartDate, rangeMinutes }
  );
  const diastolicBloodPressureDense = densifyAlignedWithGaps(
    diastolic_BloodPressureBaseData,
    0,
    format(rangeStartDate, 'HH:mm'),
    MAX_GAP_MINUTES_FOR_WINDOW,
    { startDate: rangeStartDate, rangeMinutes }
  );
  
  // Combine both blood pressure datasets for the chart
  // Match by time (t) instead of index to handle cases where arrays have different lengths
  // Create a map of diastolic points by time for efficient lookup
  const diastolicMap = new Map<number, DensePoint>();
  diastolicBloodPressureDense.forEach(point => {
    if (point.t != null && !isNaN(point.t)) {
      diastolicMap.set(point.t, point);
    }
  });
  
  
  const combinedBloodPressureData = systolicBloodPressureDense.map((systolicPoint) => {
    // Find matching diastolic point by time (with small tolerance for floating point differences)
    let diastolicPoint: DensePoint | undefined = undefined;
    if (systolicPoint.t != null && !isNaN(systolicPoint.t)) {
      // Try exact match first
      diastolicPoint = diastolicMap.get(systolicPoint.t);
      // If no exact match, try to find closest match within 0.001 minutes tolerance
      if (!diastolicPoint) {
        for (let i = 0; i < diastolicBloodPressureDense.length; i++) {
          const point = diastolicBloodPressureDense[i];
          if (point.t != null && !isNaN(point.t) && Math.abs(point.t - systolicPoint.t) < 0.001) {
            diastolicPoint = point;
            break;
          }
        }
      }
    }
    
    return {
      t: systolicPoint.t,
      systolic: systolicPoint.value,
      diastolic: diastolicPoint?.value ?? null,
      systolic_confidence: systolicPoint.confidence,
      diastolic_confidence: diastolicPoint?.confidence,
      endTsMs: systolicPoint.endTsMs,
      
      // 🔥 ΝΕΑ: 4 extra fields για min/max ΟΚΑΝΑ
      systolic_min: systolicPoint.min,
      systolic_max: systolicPoint.max,
      diastolic_min: diastolicPoint?.min,
      diastolic_max: diastolicPoint?.max,
      
      // Παλιά combined min/max (κράτα τα)
      min: Math.min(systolicPoint.value ?? Infinity, diastolicPoint?.value ?? Infinity),
      max: Math.max(systolicPoint.value ?? -Infinity, diastolicPoint?.value ?? -Infinity)
    };
  });
  
  // Add null breaker at the end to prevent horizontal line when data moves
  // if (combinedBloodPressureData.length > 0) {
  //   const lastPoint = combinedBloodPressureData[combinedBloodPressureData.length - 1];
  //   combinedBloodPressureData.push({
  //     t: lastPoint.t + 0.0001,
  //     systolic: null,
  //     diastolic: null,
  //     systolic_confidence: undefined,
  //     diastolic_confidence: undefined,
  //     min: -1,   
  //     max: -1   
  //   });
  // }
  
  // Debug logs
  // console.log('Systolic data length:', systolicBloodPressureDense.length);
  // console.log('Diastolic data length:', diastolicBloodPressureDense.length);
  // console.log('Combined data sample:', combinedBloodPressureData.slice(0, 3));

  // Debug: Track final points that go to heart rate chart

  // All charts share the same zoom range
  const tempZoomCtl = useChartZoom(
    timeAxisBaseData,
    () => tempChartSize.width,
    sharedZoomRange,
    setSharedZoomRange,
    rangeStartDate,
    rangeMinutes,
    () => tempPlotBoundsRef.current
  );
  const breathingZoomCtl = useChartZoom(
    timeAxisBaseData,
    () => breathingChartSize.width,
    sharedZoomRange,
    setSharedZoomRange,
    rangeStartDate,
    rangeMinutes,
    () => breathingPlotBoundsRef.current
  );
  const heartRateZoomCtl = useChartZoom(
    timeAxisBaseData,
    () => heartRateChartSize.width,
    sharedZoomRange,
    setSharedZoomRange,
    rangeStartDate,
    rangeMinutes,
    () => heartRatePlotBoundsRef.current
  );
  const heartRateVariabilityZoomCtl = useChartZoom(
    timeAxisBaseData,
    () => heartRateVariabilityChartSize.width,
    sharedZoomRange,
    setSharedZoomRange,
    rangeStartDate,
    rangeMinutes,
    () => heartRateVariabilityPlotBoundsRef.current
  );
  const spo2ZoomCtl = useChartZoom(
    timeAxisBaseData,
    () => spo2ChartSize.width,
    sharedZoomRange,
    setSharedZoomRange,
    rangeStartDate,
    rangeMinutes,
    () => spo2PlotBoundsRef.current
  );
  const respirationRateZoomCtl = useChartZoom(
    timeAxisBaseData,
    () => respirationRateChartSize.width,
    sharedZoomRange,
    setSharedZoomRange,
    rangeStartDate,
    rangeMinutes,
    () => respirationRatePlotBoundsRef.current
  );
  const systolic_diastolicBloodPressureZoomCtl = useChartZoom(
    timeAxisBaseData,
    () => systolic_diastolicBloodPressureChartSize.width,
    sharedZoomRange,
    setSharedZoomRange,
    rangeStartDate,
    rangeMinutes,
    () => systolic_diastolicBloodPressurePlotBoundsRef.current
  );
  const sleepZoomTransform = (
    from: number,
    to: number,
    ctx: { visibleLeft: number; visibleRight: number }
  ): { from: number; to: number } | null => {
    if (ctx.visibleRight - ctx.visibleLeft <= 24 * 60) return null;
    return snapSleepWideZoomToNight(rangeStartDate, rangeMinutes, from, to);
  };
  const sleepZoomCtl = useChartZoom(
    timeAxisBaseData,
    () => sleepingChartSize.width,
    sharedZoomRange,
    setSharedZoomRange,
    rangeStartDate,
    rangeMinutes,
    () => sleepingPlotBoundsRef.current,
    sleepZoomTransform
  );


  // Dynamic ticks based on current zoom range
  const getDomainAndTicks = (
    zoomCtl: { zoom: ZoomState },
    baseData: GraphData[],
    displayedData?: DensePoint[],
    windowMinutes: number = rangeMinutes
  ) => {
    // Fix left bound to always start from 0 (beginning of selected time range)
    const fullLeft = 0;
    // Fix right bound to whole selected window (minutes)
    const fullRight = windowMinutes;
    const hasZoom = zoomCtl.zoom.left != null && zoomCtl.zoom.right != null;
    let left = hasZoom ? Math.min(zoomCtl.zoom.left as number, zoomCtl.zoom.right as number) : fullLeft;
    let right = hasZoom ? Math.max(zoomCtl.zoom.left as number, zoomCtl.zoom.right as number) : fullRight;
    // Avoid identical domain (single-point series) which causes duplicate tick keys in Recharts
    if (right <= left) {
      right = left + 1;
    }
    
    // Generate exactly 10 ticks evenly distributed
    const ticks: number[] = [];
    for (let i = 0; i < 10; i++) {
      const t = left + (right - left) * (i / 9);
      ticks.push(t);
    }
    
    return { left, right, ticks };
  };

  const formatDynamicTime = (_baseData: GraphData[], t: number): string => {
    const clampT = Math.max(0, Math.min(rangeMinutes, Number(t) || 0));
    const currentDate = new Date(rangeStartDate.getTime() + clampT * 60 * 1000);
    const startDate = rangeStartDate;
    const endDate = new Date(rangeStartDate.getTime() + rangeMinutes * 60 * 1000);
    
    const pad = (n: number) => String(Math.floor(n)).padStart(2, '0');
    
    // Check if we're spanning multiple days or if the selected range is from a different day
    const isMultiDay = startDate.getDate() !== endDate.getDate() || 
                      startDate.getMonth() !== endDate.getMonth() || 
                      startDate.getFullYear() !== endDate.getFullYear();
    
    // Check if the current time point is on a different day than the start
    const isDifferentDay = currentDate.getDate() !== startDate.getDate() || 
                          currentDate.getMonth() !== startDate.getMonth() || 
                          currentDate.getFullYear() !== startDate.getFullYear();
    
    // Add date if we're spanning multiple days or if this is a historical date (not today)
    const today = new Date();
    const isHistoricalDate = startDate.getDate() !== today.getDate() || 
                           startDate.getMonth() !== today.getMonth() || 
                           startDate.getFullYear() !== today.getFullYear();
    
    const visibleSpanMinutes = Math.max(
      0.001,
      (sharedZoomRange.startTime && sharedZoomRange.endTime)
        ? (new Date(sharedZoomRange.endTime).getTime() - new Date(sharedZoomRange.startTime).getTime()) / 60000
        : rangeMinutes
    );
    const showSeconds = visibleSpanMinutes <= 60;

    // Format time from actual Date (local wall clock) to stay aligned with chart data
    let timeStr = '';
    if (isMultiDay || isDifferentDay || isHistoricalDate) {
      timeStr = format(currentDate, 'HH:mm');
      const dateStr = format(currentDate, 'dd/MM');
      return `${dateStr} ${timeStr}`;
    }

    if (showSeconds) {
      timeStr = format(currentDate, 'HH:mm:ss');
    } else {
      timeStr = format(currentDate, 'HH:mm');
    }
    
    return timeStr;
  };

  // Timeline formatting function for Critical Events and Medications Timeline
  const formatTimelineTime = (timestamp: number): string => {
    let date = parseValidTimestamp(timestamp);
    if (!date) {
      const rawNum = Number(timestamp);
      if (Number.isFinite(rawNum)) {
        const fallback = new Date(rawNum);
        if (isValidDate(fallback)) date = fallback;
      }
    }
    // Keep timeline labels readable even when a tick value is malformed.
    if (!date) return format(dateRange.startDate, 'HH:mm');
    const today = new Date();
    const startDate = dateRange.startDate;
    const endDate = dateRange.endDate;
    
    // Check if we're spanning multiple days or if the selected range is from a different day
    const isMultiDay = startDate.getDate() !== endDate.getDate() || 
                      startDate.getMonth() !== endDate.getMonth() || 
                      startDate.getFullYear() !== endDate.getFullYear();
    
    // Check if this is a historical date (not today)
    const isHistoricalDate = startDate.getDate() !== today.getDate() || 
                           startDate.getMonth() !== today.getMonth() || 
                           startDate.getFullYear() !== today.getFullYear();
    
    // Check if the current time point is on a different day than the start
    const isDifferentDay = date.getDate() !== startDate.getDate() || 
                          date.getMonth() !== startDate.getMonth() || 
                          date.getFullYear() !== startDate.getFullYear();
    
    // Format time (always without seconds for timelines)
    const timeStr = date.toLocaleTimeString('en-GB', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
    
    // Add date if we're spanning multiple days or if this is a historical date
    if (isMultiDay || isDifferentDay || isHistoricalDate) {
      const dateStr = date.toLocaleDateString('en-GB', { 
        day: '2-digit', 
        month: '2-digit' 
      });
      return `${dateStr} ${timeStr}`;
    }
    
    return timeStr;
  };
  
  const [selectedEventTypes, setSelectedEventTypes] = useState<Set<string>>(new Set([
    'heart_rate_low', 'heart_rate_high',
    ATRIAL_FIBRILLATION_EVENT_TYPE,
    ATRIAL_FIBRILLATION_X_EVENT_TYPE,
    'hrv_low', 
    'spo2_low', 'spo2_high',
    'breathing_low', 'breathing_high',
    'temperature_high'
  ]));
  const [showEventFilter, setShowEventFilter] = useState(false);
  
  // Close filter dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('[data-event-filter]')) {
        setShowEventFilter(false);
      }
    };

    if (showEventFilter) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showEventFilter]);
  
  // Collapse states per chart/panel
  const [hrCollapsed, setHrCollapsed] = useState(false);
  const [medicationCollapsed, setMedicationCollapsed] = useState(true);
  const [hrvCollapsed, setHrvCollapsed] = useState(false);
  const [bpCollapsed, setBpCollapsed] = useState(false);
  const [spo2Collapsed, setSpo2Collapsed] = useState(false);
  const [respCollapsed, setRespCollapsed] = useState(false);
  const [tempCollapsed, setTempCollapsed] = useState(false);
  const [sleepCollapsed, setSleepCollapsed] = useState(false);
  const [sleepSummaryView, setSleepSummaryView] = useState<'score' | 'breakdown'>('score');
  // Raw signals independent collapsers
  const [ecg1RawCollapsed, setEcg1RawCollapsed] = useState(false);
  const [ecg2RawCollapsed, setEcg2RawCollapsed] = useState(false);
  const [ppgGreenRawCollapsed, setPpgGreenRawCollapsed] = useState(false);
  const [ppgYellowRawCollapsed, setPpgYellowRawCollapsed] = useState(false);

  const tempAxis = getDomainAndTicks(tempZoomCtl as any, temperatureBaseData, displayedTemperatureData);
  const sleepAxis = getDomainAndTicks(sleepZoomCtl as any, sleepingBaseData, displayedSleepData);
  const breathingAxis = getDomainAndTicks(breathingZoomCtl as any, breathingBaseData);
  const heartRateAxis = getDomainAndTicks(heartRateZoomCtl as any, heartRateBaseData, displayedHeartRateData);
  const heartRateVariabilityAxis = getDomainAndTicks(heartRateVariabilityZoomCtl as any, heartRateVariabilityBaseData, displayedHrvData);
  const spo2Axis = getDomainAndTicks(spo2ZoomCtl as any, heartRateBaseData, displayedSpo2Data);
  const systolic_diastolicBloodPressureAxis = getDomainAndTicks(systolic_diastolicBloodPressureZoomCtl as any, systolic_BloodPressureBaseData, combinedBloodPressureData.map(p => ({ t: p.t, value: p.systolic })));

  // Helper function to filter data points based on zoom range
  // Filter out points with low confidence (for real mode only)
  // Filter out points with low confidence (for real mode only)
  // Filters out points where confidence <= 0 or confidence === -1
  const filterByConfidence = filterGraphDataByConfidence;

  const filterDataByZoom = <T extends { t: number }>(data: T[], zoomLeft: number | null, zoomRight: number | null): T[] => {
    if (zoomLeft == null || zoomRight == null) {
      return data;
    }
    const minT = Math.min(zoomLeft, zoomRight);
    const maxT = Math.max(zoomLeft, zoomRight);
    // Include points within the zoom range, with small tolerance for edge cases
    return data.filter(point => point.t >= minT - 0.001 && point.t <= maxT + 0.001);
  };

  // Filter displayed data based on zoom ranges (use axis domain which reflects current zoom)
  // First filter by confidence, then by zoom
  const filteredDisplayedTemperatureData = filterDataByZoom(displayedTemperatureData, tempZoomCtl.zoom.left, tempZoomCtl.zoom.right);
  const filteredDisplayedSleepData = filterDataByZoom(displayedSleepData, sleepZoomCtl.zoom.left, sleepZoomCtl.zoom.right);
  const sleepZoomSpanMinutes = sleepAxis.right - sleepAxis.left;
  const sleepWideView = sleepZoomSpanMinutes > 24 * 60;
  const sleepChartXAxisTicks = React.useMemo(() => {
    if (!sleepWideView) return sleepAxis.ticks;
    const ticks: number[] = [];
    const startMs = rangeStartDate.getTime() + sleepAxis.left * 60 * 1000;
    const endMs = rangeStartDate.getTime() + sleepAxis.right * 60 * 1000;
    const d = new Date(startMs);
    d.setHours(0, 0, 0, 0);
    while (d.getTime() <= endMs) {
      const dayStartT = (d.getTime() - rangeStartDate.getTime()) / (60 * 1000);
      const dayEndT = dayStartT + 24 * 60;
      const centerT = (Math.max(dayStartT, sleepAxis.left) + Math.min(dayEndT, sleepAxis.right)) / 2;
      ticks.push(centerT);
      d.setDate(d.getDate() + 1);
    }
    return ticks.length > 0 ? ticks : [sleepAxis.left];
  }, [sleepWideView, sleepAxis.left, sleepAxis.right, sleepAxis.ticks, rangeStartDate]);
  const formatSleepYTime = (minutes: number): string => {
    const h = Math.floor(minutes / 60) % 24;
    const m = Math.floor(minutes % 60);
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  };
  const sleepDailyYTicks = [
    0,
    Math.round(SLEEP_DAILY_Y_SPAN * 0.25),
    Math.round(SLEEP_DAILY_Y_SPAN * 0.5),
    Math.round(SLEEP_DAILY_Y_SPAN * 0.75),
    SLEEP_DAILY_Y_SPAN,
  ];
  const filteredDisplayedHeartRateData = filterDataByZoom(
    filterByConfidence(displayedHeartRateData, isDemo),
    heartRateZoomCtl.zoom.left,
    heartRateZoomCtl.zoom.right
  );
  const filteredDisplayedHrvData = filterDataByZoom(
    filterByConfidence(displayedHrvData, isDemo).filter(
      (point) => point.value == null || normalizeHrvForDisplay(point.value) != null
    ),
    heartRateVariabilityZoomCtl.zoom.left,
    heartRateVariabilityZoomCtl.zoom.right
  );
  const filteredDisplayedSpo2Data = filterDataByZoom(
    filterByConfidence(displayedSpo2Data, isDemo),
    spo2ZoomCtl.zoom.left,
    spo2ZoomCtl.zoom.right
  );
  const filteredDisplayedRespirationData = filterDataByZoom(
    filterByConfidence(displayedRespirationData, isDemo),
    breathingZoomCtl.zoom.left,
    breathingZoomCtl.zoom.right
  );
  // For blood pressure, filter by both systolic and diastolic confidence
  const filteredCombinedBloodPressureData = filterDataByZoom(
    isDemo ? combinedBloodPressureData : combinedBloodPressureData.filter(point => {
      const sysConf = point.systolic_confidence != null ? Number(point.systolic_confidence) : null;
      const diaConf = point.diastolic_confidence != null ? Number(point.diastolic_confidence) : null;
      // Filter out if both have invalid confidence (<= 0 or -1)
      const sysValid = sysConf == null || (isNaN(sysConf) || (sysConf > 0 && sysConf !== -1));
      const diaValid = diaConf == null || (isNaN(diaConf) || (diaConf > 0 && diaConf !== -1));
      // Keep point only if at least one has valid confidence, or if both are null/undefined
      if (sysConf == null && diaConf == null) return true; // Show if no confidence data
      return sysValid || diaValid;
    }),
    systolic_diastolicBloodPressureZoomCtl.zoom.left,
    systolic_diastolicBloodPressureZoomCtl.zoom.right
  );

  // const bloodPressureAxis = getDomainAndTicks(bloodPressureZoomCtl as any, bloodPressureBaseData);

  // Tooltip proximity control (in pixels)
  const PROXIMITY_PX = 10;
  const [showTempTooltip, setShowTempTooltip] = useState(false);
  const [showBreathingTooltip, setShowBreathingTooltip] = useState(false);
  const [showHeartRateTooltip, setShowHeartRateTooltip] = useState(false);
  const [showHeartRateVariabilityTooltip, setShowHeartRateVariabilityTooltip] = useState(false);
  const [showSpo2Tooltip, setShowSpo2Tooltip] = useState(false);
  const [showSystolic_diastolicBloodPressureTooltip, setShowSystolic_diastolicBloodPressureTooltip] = useState(false);
  const [activityTooltip, setActivityTooltip] = useState<{
    x: number;
    y: number;
    placement: 'above' | 'below';
    segment: ActivitySegment;
  } | null>(null);
  const [sleepRunTooltip, setSleepRunTooltip] = useState<{
    run: SleepRun;
    x: number;
    y: number;
  } | null>(null);

  const clampSleepTooltipPosition = (
    clientX: number,
    clientY: number,
    container: HTMLElement,
    tooltipSize = { width: 210, height: 72 }
  ) => {
    const rect = container.getBoundingClientRect();
    const padding = 8;
    const offset = 12;
    let x = clientX - rect.left + offset;
    let y = clientY - rect.top + offset;
    if (x + tooltipSize.width > rect.width - padding) {
      x = clientX - rect.left - tooltipSize.width - offset;
    }
    if (y + tooltipSize.height > rect.height - padding) {
      y = clientY - rect.top - tooltipSize.height - offset;
    }
    x = Math.max(padding, Math.min(x, rect.width - tooltipSize.width - padding));
    y = Math.max(padding, Math.min(y, rect.height - tooltipSize.height - padding));
    return { x, y };
  };

  const clampActivityTooltipFixedPosition = (
    clientX: number,
    clientY: number,
    container: HTMLElement,
    tooltipSize = { width: 230, height: 130 }
  ): { x: number; y: number; placement: 'above' | 'below' } => {
    const rect = container.getBoundingClientRect();
    const padding = 8;
    const offset = 10;
    const halfW = tooltipSize.width / 2;

    let x = clientX;
    x = Math.max(rect.left + padding + halfW, Math.min(x, rect.right - padding - halfW));

    const yAboveAnchor = clientY - offset;
    if (yAboveAnchor - tooltipSize.height >= rect.top + padding) {
      return { x, y: yAboveAnchor, placement: 'above' };
    }
    return { x, y: clientY + offset, placement: 'below' };
  };

  const showActivitySegmentTooltip = (e: React.MouseEvent, segment: ActivitySegment) => {
    const container = activityTimelineRef.current;
    if (!container) return;
    const { x, y, placement } = clampActivityTooltipFixedPosition(e.clientX, e.clientY, container);
    setActivityTooltip({ x, y, placement, segment });
  };

  // Helper: map raw real points onto numeric X domain (minutes from window start)
  const toAlignedPoints = (base: GraphData[]) => {
    const toMinutes = (hm: string) => {
      const [h, m] = (hm || '00:00').split(':').map(x => parseInt(x, 10) || 0);
      return h * 60 + m;
    };
    const startMs = rangeStartDate.getTime();
    const startLabel = format(rangeStartDate, 'HH:mm');
    const startMin = toMinutes(startLabel);
    const offsetOf = (point: GraphData) => {
      const hm = point.time;
      const tsMs = (point as any).tsMs as number | undefined;

      if (tsMs != null) {
        return (tsMs - startMs) / 60000;
      }
      const parsed = Date.parse(hm);
      if (!Number.isNaN(parsed)) {
        return (parsed - startMs) / 60000;
      }

      const mm = toMinutes(hm);
      let diff = mm - startMin;
      if (diff < 0) {
        const wrapped = diff + 24 * 60;
        if (wrapped >= -0.0001 && wrapped <= rangeMinutes + 0.0001) {
          diff = wrapped;
        }
      }
      return diff;
    };
    const normalizeSleep = (v: any) => {
      const normalized = getSleepStageValue(v);
      return normalized != null ? normalized : 0;
    };
    const pts = (base || []).map(p => ({
      t: offsetOf(p),
      value: normalizeSleep(p.value),
      tsMs: (p as any).tsMs as number | undefined,
    }));
    if (pts.length === 1) {
      // add a transparent dummy point so Recharts renders the single point
      return [...pts, { t: (pts[0] as any).t + 0.00001, value: null as unknown as number }];
    }
    return pts;
  };

  // Real measurement markers (not densified)
  const breathingRawPoints = toAlignedPoints(respirationRateBaseData);
  const temperatureRawPoints = toAlignedPoints(dbTemperature);
  const heartRateRawPoints = toAlignedPoints(heartRateBaseData);
  const hrvRawPoints = toAlignedPoints(
    heartRateVariabilityBaseData.filter(point => normalizeHrvForDisplay(point.value) != null)
  );
  const spo2RawPoints = toAlignedPoints(spo2BaseData);
  const systolicRawPoints = toAlignedPoints(systolic_BloodPressureBaseData);
  const diastolicRawPoints = toAlignedPoints(diastolic_BloodPressureBaseData);

  // Custom dot renderer for raw points (black markers)
  const RawDot = (props: any) => {
    const { cx, cy } = props;
    if (cx == null || cy == null) return null;
    const halo = 14;
    const core = 8;
    return (
      <g pointerEvents="none">
        <circle cx={cx} cy={cy} r={halo} fill="rgba(0,0,0,0.14)" />
        <circle cx={cx} cy={cy} r={core} fill="#000000" stroke="#ffffff" strokeWidth={2} />
      </g>
    );
  };


  // const [showBloodPressureTooltip, setShowBloodPressureTooltip] = useState(false);
  // ECG/PPG raw scrolling buffers and visible samples
  const [ecgRawSamples, setEcgRawSamples] = useState<Array<{ t: number; value: number; sampleIndex?: number; timestamp?: string }>>([]);
  const [, setEcgDemoRawSamples] = useState<Array<{ t: number; value: number }>>([]);
  const [ppgGreenRawSamples, setPpgGreenRawSamples] = useState<Array<{ t: number; value: number; sampleIndex?: number; timestamp?: string }>>([]);
  const [ppgYellowRawSamples, setPpgYellowRawSamples] = useState<Array<{ t: number; value: number; sampleIndex?: number; timestamp?: string }>>([]);
  const [, setPpgGreenDemoRawSamples] = useState<Array<{ t: number; value: number }>>([]);
  const [, setPpgYellowDemoRawSamples] = useState<Array<{ t: number; value: number }>>([]);
  const [, setPpgRedDemoRawSamples] = useState<Array<{ t: number; value: number }>>([]);
  const [, setPpgIrDemoRawSamples] = useState<Array<{ t: number; value: number }>>([]);
  const [ecg2RawSamples, setEcg2RawSamples] = useState<Array<{ t: number; value: number; sampleIndex?: number; timestamp?: string }>>([]);
  const [, setEcg2DemoRawSamples] = useState<Array<{ t: number; value: number }>>([]);
  // Zoom/scroll state for critical event raw signals (showing 10 seconds at a time)
  const [ecgRawZoom, setEcgRawZoom] = useState<{ start: number; end: number }>({ start: 0, end: 10 });
  const [ecg2RawZoom, setEcg2RawZoom] = useState<{ start: number; end: number }>({ start: 0, end: 10 });
  const [ppgGreenRawZoom, setPpgGreenRawZoom] = useState<{ start: number; end: number }>({ start: 0, end: 10 });
  const [ppgYellowRawZoom, setPpgYellowRawZoom] = useState<{ start: number; end: number }>({ start: 0, end: 10 });
  const setAllRawSignalZoom = useCallback((start: number) => {
    const end = start + RAW_SIGNAL_WINDOW_SECONDS;
    setEcgRawZoom({ start, end });
    setEcg2RawZoom({ start, end });
    setPpgGreenRawZoom({ start, end });
    setPpgYellowRawZoom({ start, end });
  }, []);

  const ecgSlideRef = useRef<RawSlideAnim | null>(null);
  const ecg2SlideRef = useRef<RawSlideAnim | null>(null);
  const ppgGreenSlideRef = useRef<RawSlideAnim | null>(null);
  const ppgYellowSlideRef = useRef<RawSlideAnim | null>(null);

  const filteredEcgRawChartData = useMemo(
    () => (selectedEventTimestamp ? sliceRawSamplesByWindow(ecgRawSamples, ecgRawZoom.start, ecgRawZoom.end) : []),
    [selectedEventTimestamp, ecgRawSamples, ecgRawZoom.start, ecgRawZoom.end]
  );
  const filteredEcg2RawChartData = useMemo(
    () => (selectedEventTimestamp ? sliceRawSamplesByWindow(ecg2RawSamples, ecg2RawZoom.start, ecg2RawZoom.end) : []),
    [selectedEventTimestamp, ecg2RawSamples, ecg2RawZoom.start, ecg2RawZoom.end]
  );
  const filteredPpgGreenRawChartData = useMemo(
    () => (selectedEventTimestamp ? sliceRawSamplesByWindow(ppgGreenRawSamples, ppgGreenRawZoom.start, ppgGreenRawZoom.end) : []),
    [selectedEventTimestamp, ppgGreenRawSamples, ppgGreenRawZoom.start, ppgGreenRawZoom.end]
  );
  const filteredPpgYellowRawChartData = useMemo(
    () => (selectedEventTimestamp ? sliceRawSamplesByWindow(ppgYellowRawSamples, ppgYellowRawZoom.start, ppgYellowRawZoom.end) : []),
    [selectedEventTimestamp, ppgYellowRawSamples, ppgYellowRawZoom.start, ppgYellowRawZoom.end]
  );
  const rawSignalMaxTime = useMemo(() => {
    const endTimes = [ecgRawSamples, ecg2RawSamples, ppgGreenRawSamples, ppgYellowRawSamples]
      .map(samples => (samples.length > 0 ? samples[samples.length - 1].t : 0));
    return Math.max(0, ...endTimes);
  }, [ecgRawSamples, ecg2RawSamples, ppgGreenRawSamples, ppgYellowRawSamples]);
  const rawSignalMaxSliderValue = Math.max(0, rawSignalMaxTime - RAW_SIGNAL_WINDOW_SECONDS);
  const handleRawSignalSlider = useCallback((value: number) => {
    setAllRawSignalZoom(value);
  }, [setAllRawSignalZoom]);
  
  const ecgBufferRef = useRef<Array<{ time: number; value: number; sampleIndex: number; timestamp?: string }>>([]);
  const ecgDemoBufferRef = useRef<Array<{ time: number; value: number; sampleIndex: number }>>([]);
  const ppgBufferRef = useRef<Array<{ time: number; value: number; sampleIndex: number; timestamp?: string }>>([]);
  const currentPpgDtRef = useRef(0.002);
  const ppgYellowBufferRef = useRef<Array<{ time: number; value: number; sampleIndex: number; timestamp?: string }>>([]);
  const ppgGreenDemoBufferRef = useRef<Array<{ time: number; value: number; sampleIndex: number }>>([]);
  const ppgYellowDemoBufferRef = useRef<Array<{ time: number; value: number; sampleIndex: number }>>([]);
  const ppgRedDemoBufferRef = useRef<Array<{ time: number; value: number; sampleIndex: number }>>([]);
  const ppgIrDemoBufferRef = useRef<Array<{ time: number; value: number; sampleIndex: number }>>([]);
  const ecg2BufferRef = useRef<Array<{ time: number; value: number; sampleIndex: number; timestamp?: string }>>([]);
  const ecg2DemoBufferRef = useRef<Array<{ time: number; value: number; sampleIndex: number }>>([]);
  const nowSeconds = () => Date.now() / 1000;
  const currentTimeRef = useRef<number>(nowSeconds());
  const [lastEcgTimestamp, setLastEcgTimestamp] = useState<string | null>(null);
  const [, setLastPpgTimestamp] = useState<string | null>(null);
  const serverTimeRef = useRef<string | null>(null);
  const [currentEcgBatchTs, setCurrentEcgBatchTs] = useState<string | null>(null);
  const [currentPpgBatchTs, setCurrentPpgBatchTs] = useState<string | null>(null);
  const [, setCurrentEcgBatchSize] = useState<number>(0);
  const [, setCurrentPpgBatchSize] = useState<number>(0);
  const lastAppendedEcgTsRef = useRef<string | null>(null);
  const lastAppendedEcgDemoTsRef = useRef<string | null>(null);
  const lastAppendedPpgTsRef = useRef<string | null>(null);
  const lastAppendedPpgYellowTsRef = useRef<string | null>(null);
  const lastAppendedEcg2TsRef = useRef<string | null>(null);
  const lastEcgSampleIndexRef = useRef<number>(-1);
  const lastEcgDemoSampleIndexRef = useRef<number>(-1);
  const lastPpgSampleIndexRef = useRef<number>(-1);
  const lastPpgYellowSampleIndexRef = useRef<number>(-1);
  const lastPpgGreenDemoSampleIndexRef = useRef<number>(-1);
  const lastPpgYellowDemoSampleIndexRef = useRef<number>(-1);
  const lastEcg2SampleIndexRef = useRef<number>(-1);
  const lastEcg2DemoSampleIndexRef = useRef<number>(-1);

  // Debug: log demo ECG samples when they update (demo mode only)
  // useEffect(() => {
  //   if (!isDemo) return;
  //   try {
  //     if (ecgDemoRawSamples.length) {
  //       console.table(ecgDemoRawSamples.slice(0, 10));
  //     }
  //   } catch {}
  // }, [ecgDemoRawSamples, isDemo]);

  // Reset last sample index when a new batch timestamp starts
  useEffect(() => { lastEcgSampleIndexRef.current = -1; }, [currentEcgBatchTs]);
  useEffect(() => { lastPpgSampleIndexRef.current = -1; }, [currentPpgBatchTs]);
  useEffect(() => { lastPpgYellowSampleIndexRef.current = -1; }, [currentPpgBatchTs]);
  useEffect(() => { lastEcg2SampleIndexRef.current = -1; }, [currentEcgBatchTs]);
  useEffect(() => {
    const fetchLatest = async () => {
      // DISABLED: No real data fetches in demo mode
      if (isDemo) return;
      // Don't update if a critical event is selected (let critical event data stay)
      if (selectedEventTimestamp) return;
      try {
        // Use demo raw source when in demo mode, otherwise real-time from data table
        let rows: any[];
        rows = await getLatestEcgSamples();
        console.log(isDemo ? '[DEMO] ECG1 latest rows:' : 'ECG1 latest rows:', rows?.length);
        // Console log για raw δεδομένα ECG1 με timestamp από server
        if (rows && rows.length > 0) {
          // console.log('[RAW DATA] ECG1 - Server Data Timestamp:', rows[0]?.timestamp, 'Samples:', rows.length, 'Last sample timestamp:', rows[rows.length - 1]?.timestamp);
        }
        if (isDemo) {
          // try { console.log('[DEMO] sample row:', rows?.[0]); } catch {}
        }
        // try { console.table(rows?.map((r: any) => ({ sample_index: r.sample_index, ecg1: r.ecg1 }))); } catch {}
        // Always reflect the size we are about to process
        setCurrentEcgBatchSize(rows.length || 0);
        // Skip duplicate appends for the same batch timestamp (real mode only)
        if (lastEcgTimestamp && lastEcgTimestamp === lastAppendedEcgTsRef.current) {
          // continue to PPG fetch below
        } else if (lastEcgTimestamp) {
          lastAppendedEcgTsRef.current = lastEcgTimestamp;
          setCurrentEcgBatchSize(rows.length);
        }
        // In demo mode, reset demo sample index when a new batch timestamp arrives
        if (isDemo) {
          const latestDemoTs = rows.length ? String(rows[0].timestamp) : null;
          if (latestDemoTs && latestDemoTs !== lastAppendedEcgDemoTsRef.current) {
            lastAppendedEcgDemoTsRef.current = latestDemoTs;
            lastEcgDemoSampleIndexRef.current = -1;
            // Keep buffer stable in demo mode - don't reset it
            // ecgDemoBufferRef.current = [];
          }
        }
        // Append after the last buffered time (or now) to avoid gaps
        // Display rate: 500 samples/second => 0.002s per sample
        const dt = 0.002;
        if (isDemo) {
          // Append new samples so that the LAST point aligns with current time
          const newRows = rows.filter((r: any) => Number(r.sample_index ?? 0) > lastEcgDemoSampleIndexRef.current);
          try {
            console.log('[DEMO] fetched rows:', rows.length, 'lastEcgDemoSampleIndexRef=', lastEcgDemoSampleIndexRef.current, 'newRows:', newRows.length);
            if (rows.length) console.log('[DEMO] first/last fetched:', rows[0], rows[rows.length - 1]);
          } catch {}
          const ecgAppendedDemo: Array<{ time: number; value: number; sampleIndex: number }> = [];
          const numNew = newRows.length;
          const startTime = currentTimeRef.current - dt * Math.max(1, numNew);
          for (let k = 0; k < newRows.length; k++) {
            const r = newRows[k];
            const si = Number(r?.sample_index ?? k);
            const t = startTime + dt * (k + 1);
            ecgAppendedDemo.push({ time: t, value: Number(r.ecg1), sampleIndex: si });
          }
          // Also set a direct demo preview series so the chart shows even if the animation window is empty
          if (rows.length > 0) {
            const preview = rows.map((r: any, i: number) => ({ t: i * dt, value: Number(r.ecg1) }));
            try { setEcgDemoRawSamples(preview.slice(-Math.min(preview.length, Math.ceil(10 / dt)))); } catch {}
          }
          if (ecgAppendedDemo.length > 0) {
            lastEcgDemoSampleIndexRef.current = Math.max(lastEcgDemoSampleIndexRef.current, ecgAppendedDemo[ecgAppendedDemo.length - 1].sampleIndex);
            ecgDemoBufferRef.current = [...ecgDemoBufferRef.current, ...ecgAppendedDemo].filter(p => p.time > (currentTimeRef.current - 14));
            try {
              console.log('[DEMO] appended demo points:', ecgAppendedDemo.length, 'buffer size:', ecgDemoBufferRef.current.length);
            } catch {}
            // Do not push currentTimeRef forward in demo; keep synced with real time so window shows appended samples immediately
          }
        } else {
          // ECG1 (REAL) — triple sliding mode (same as PPG Green)
          try {
            const slice = rows.slice(0, 3540);
            
            if (ecgBufferRef.current.length === 0) {
              // First batch: put data in second half (7.08-14.16s)
              const emptyFirstHalf = Array.from({ length: Math.floor(7.08 / dt) }, (_, i) => ({
                time: i * dt, // 0 to 7.08 seconds
                value: 0, // Empty data
                sampleIndex: -1,
                timestamp: null
              }));
              
              const latestDataSecondHalf = slice.map((r: any, i: number) => ({ 
                time: 7.08 + (i * dt), // 7.08 to 14.16 seconds
                value: Number(r.ecg1), 
                sampleIndex: Number(r.sample_index ?? i),
                timestamp: r.timestamp
              }));
              
              ecgBufferRef.current = [...emptyFirstHalf, ...latestDataSecondHalf];
            } else {
              // Controlled sliding mode: second buffer slides to first, new data goes to second
              const currentTimestamp = rows.length > 0 ? rows[0].timestamp : null;
              
              if (currentTimestamp && currentTimestamp !== lastAppendedEcgTsRef.current) {
                // New timestamp: do controlled sliding
                lastAppendedEcgTsRef.current = currentTimestamp;
                
                // Create new data for second half (7.08-14.16s) - NEEDED FOR ANIMATION
                const newDataSecondHalf = slice.map((r: any, i: number) => ({ 
                  time: 7.08 + (i * dt), // 7.08 to 14.16 seconds
                  value: Number(r.ecg1), 
                  sampleIndex: Number(r.sample_index ?? i),
                  timestamp: r.timestamp
                }));
                
                
                // Smooth sliding animation via requestAnimationFrame
                startRawBufferSlide(ecgSlideRef, ecgBufferRef, newDataSecondHalf, dt);
              } else {
                // Same timestamp: just update second half, no sliding
                const firstHalf = ecgBufferRef.current.filter(p => p.time < 7.08);
                const newDataSecondHalf = slice.map((r: any, i: number) => ({ 
                  time: 7.08 + (i * dt), // 7.08 to 14.16 seconds
                  value: Number(r.ecg1), 
                  sampleIndex: Number(r.sample_index ?? i),
                  timestamp: r.timestamp
                }));
                ecgBufferRef.current = [...firstHalf, ...newDataSecondHalf];
              }
            }
          } catch {}
        }
        // PPG handling (Green) – real vs demo
        if (isDemo) {
          const respDemo = await getLatestDemoRawSamples();
          const ppgGreenRows = respDemo.filter((r: any) => r.green_ppg != null).map((r: any, i: number) => ({ sample_index: Number(r.sample_index ?? i), value: Number(r.green_ppg) }));
          const ppgYellowRows = respDemo.filter((r: any) => r.yellow_ppg != null).map((r: any, i: number) => ({ sample_index: Number(r.sample_index ?? i), value: Number(r.yellow_ppg) }));
          const ppgDt = getPpgSampleInterval(Math.max(ppgGreenRows.length, ppgYellowRows.length));
          currentPpgDtRef.current = ppgDt;
          // green
          const newG = ppgGreenRows.filter(r => r.sample_index > lastPpgGreenDemoSampleIndexRef.current);
          let lastG = ppgGreenDemoBufferRef.current.length ? ppgGreenDemoBufferRef.current[ppgGreenDemoBufferRef.current.length - 1].time : currentTimeRef.current - ppgDt;
          const appG: Array<{ time: number; value: number; sampleIndex: number }> = [];
          newG.forEach((r, k) => { lastG += ppgDt; appG.push({ time: lastG, value: r.value, sampleIndex: r.sample_index }); });
          if (appG.length) { lastPpgGreenDemoSampleIndexRef.current = appG[appG.length-1].sampleIndex; ppgGreenDemoBufferRef.current = [...ppgGreenDemoBufferRef.current, ...appG].filter(p=>p.time>(currentTimeRef.current-14)); }
          // yellow
          const newY = ppgYellowRows.filter(r => r.sample_index > lastPpgYellowDemoSampleIndexRef.current);
          let lastY = ppgYellowDemoBufferRef.current.length ? ppgYellowDemoBufferRef.current[ppgYellowDemoBufferRef.current.length - 1].time : currentTimeRef.current - ppgDt;
          const appY: Array<{ time: number; value: number; sampleIndex: number }> = [];
          newY.forEach((r, k) => { lastY += ppgDt; appY.push({ time: lastY, value: r.value, sampleIndex: r.sample_index }); });
          if (appY.length) { lastPpgYellowDemoSampleIndexRef.current = appY[appY.length-1].sampleIndex; ppgYellowDemoBufferRef.current = [...ppgYellowDemoBufferRef.current, ...appY].filter(p=>p.time>(currentTimeRef.current-14)); }
        } else {
          const ppgRows = await getLatestPpgGreenSamples();
          console.log('PPG green latest rows:', ppgRows?.length);
          // Console log για raw δεδομένα PPG Green με timestamp από server
          if (ppgRows && ppgRows.length > 0) {
            console.log('[RAW DATA] PPG Green - Server Data Timestamp:', ppgRows[0]?.timestamp, 'Samples:', ppgRows.length, 'Last sample timestamp:', ppgRows[ppgRows.length - 1]?.timestamp);
          }
          // try { console.table(ppgRows?.map((r: any) => ({ sample_index: r.sample_index, ppg_green: r.ppg_green }))); } catch {}
          setCurrentPpgBatchSize(ppgRows.length || 0);
          // PPG Green (REAL) — sliding buffer mode (14.16s: sliding effect)
          try {
            const maxSampleIdx = ppgRows.length > 0
              ? Math.max(...ppgRows.map((r: any) => Number(r.sample_index ?? 0)))
              : 0;
            const ppgDt = getPpgSampleInterval(ppgRows.length, maxSampleIdx);
            currentPpgDtRef.current = ppgDt;
            const slice = ppgRows.slice(0, getPpgWindowSampleCount(ppgDt));
            
            if (ppgBufferRef.current.length === 0) {
              console.log('[PPG GREEN FIRST BATCH] First batch, putting data in second half (7.08-14.16s)');
              // First batch: put data in second half (7.08-14.16s)
              const emptyFirstHalf = Array.from({ length: Math.floor(7.08 / ppgDt) }, (_, i) => ({
                time: i * ppgDt, // 0 to 7.08 seconds
                value: 0, // Empty data
                sampleIndex: -1,
                timestamp: null
              }));
              
              const latestDataSecondHalf = slice.map((r: any, i: number) => ({ 
                time: 7.08 + (i * ppgDt), // 7.08 to 14.16 seconds
              value: Number(r.ppg_green), 
                sampleIndex: Number(r.sample_index ?? i),
                timestamp: r.timestamp
              }));
              
              ppgBufferRef.current = [...emptyFirstHalf, ...latestDataSecondHalf];
            } else {
              // Controlled sliding mode: second buffer slides to first, new data goes to second
              const currentTimestamp = ppgRows.length > 0 ? ppgRows[0].timestamp : null;
              
              if (currentTimestamp && currentTimestamp !== lastAppendedPpgTsRef.current) {
                // New timestamp: do controlled sliding
                lastAppendedPpgTsRef.current = currentTimestamp;
                setCurrentPpgBatchSize(ppgRows.length);
                
                // Create new data for second half (7.08-14.16s) - NEEDED FOR ANIMATION
                const newDataSecondHalf = slice.map((r: any, i: number) => ({ 
                  time: 7.08 + (i * ppgDt), // 7.08 to 14.16 seconds
                  value: Number(r.ppg_green), 
                  sampleIndex: Number(r.sample_index ?? i),
                  timestamp: r.timestamp
                }));
                
                
                // Combine filled first half + new data (second half)
                // ppgBufferRef.current = [...emptyFirstHalf, ...newDataSecondHalf];
                // console.log('[PPG ANIMATION SLIDING] newDataSecondHalf points:', newDataSecondHalf.length);
                
                // Smooth sliding animation via requestAnimationFrame
                startRawBufferSlide(ppgGreenSlideRef, ppgBufferRef, newDataSecondHalf, ppgDt);
                
                // Pulse animation temporarily disabled
                // const pulseAnimation = () => {
                //   setPpgGreenPulse(true);
                //   setTimeout(() => setPpgGreenPulse(false), 200);
                //   setTimeout(() => {
                //     setPpgGreenPulse(true);
                //     setTimeout(() => setPpgGreenPulse(false), 200);
                //   }, 400);
                //   setTimeout(() => {
                //     setPpgGreenPulse(true);
                //     setTimeout(() => setPpgGreenPulse(false), 200);
                //   }, 800);
                // };
                // pulseAnimation();
              } else {
                // const firstHalf = ppgBufferRef.current.filter(p => p.time < 7.08);
                // const newDataSecondHalf = slice.map((r: any, i: number) => ({ 
                //   time: 7.08 + (i * ppgDt), // 7.08 to 14.16 seconds
                //   value: Number(r.ppg_green), 
                //   sampleIndex: Number(r.sample_index ?? i),
                //   timestamp: r.timestamp
                // }));
                // ppgBufferRef.current = [...firstHalf, ...newDataSecondHalf];
                // console.log('[PPG GREEN UPDATE SECOND HALF] total points:', ppgBufferRef.current.length);
              }
            }
          } catch {}
        }

        // ECG2
        const ecg2Rows = isDemo ? (await getLatestDemoRawSamples()).filter((r: any) => r.ecg2 != null).map((r: any, i: number) => ({ sample_index: Number(r.sample_index ?? i), ecg2: Number(r.ecg2), timestamp: String(r.timestamp) })) : await getLatestEcg2Samples();
         console.log('ECG2 latest rows:', ecg2Rows?.length);
         // Console log για raw δεδομένα ECG2 με timestamp από server
         if (ecg2Rows && ecg2Rows.length > 0) {
           console.log('[RAW DATA] ECG2 - Server Data Timestamp:', ecg2Rows[0]?.timestamp, 'Samples:', ecg2Rows.length, 'Last sample timestamp:', ecg2Rows[ecg2Rows.length - 1]?.timestamp);
         }
        //  try { console.table(ecg2Rows?.map((r: any) => ({ sample_index: r.sample_index, ecg2: r.ecg2 }))); } catch {}
        if (lastEcgTimestamp && lastEcgTimestamp === lastAppendedEcg2TsRef.current) {
          // duplicate
        } else if (lastEcgTimestamp) {
          lastAppendedEcg2TsRef.current = lastEcgTimestamp;
        }
        const ecg2Dt = 0.002;
        if (isDemo) {
          // DEMO: append into ecg2Demo buffer anchored to now
          const newRows2 = ecg2Rows.filter((r: any) => Number(r.sample_index ?? 0) > lastEcg2DemoSampleIndexRef.current);
          const ecg2AppendedDemo: Array<{ time: number; value: number; sampleIndex: number }> = [];
          const numNew2 = newRows2.length;
          const startTime2 = currentTimeRef.current - ecg2Dt * Math.max(1, numNew2);
          for (let k = 0; k < newRows2.length; k++) {
            const r = newRows2[k];
            const si = Number(r?.sample_index ?? k);
            const t = startTime2 + ecg2Dt * (k + 1);
            ecg2AppendedDemo.push({ time: t, value: Number(r.ecg2), sampleIndex: si });
          }
          if (ecg2AppendedDemo.length > 0) {
            lastEcg2DemoSampleIndexRef.current = Math.max(lastEcg2DemoSampleIndexRef.current, ecg2AppendedDemo[ecg2AppendedDemo.length - 1].sampleIndex);
            ecg2DemoBufferRef.current = [...ecg2DemoBufferRef.current, ...ecg2AppendedDemo].filter(p => p.time > (currentTimeRef.current - 14));
          }
        } else {
          // ECG2 (REAL) — triple sliding mode (same as PPG Green)
          try {
            const slice = ecg2Rows.slice(0, 3540);
            
            if (ecg2BufferRef.current.length === 0) {
              // First batch: put data in second half (7.08-14.16s)
              const emptyFirstHalf = Array.from({ length: Math.floor(7.08 / ecg2Dt) }, (_, i) => ({
                time: i * ecg2Dt, // 0 to 7.08 seconds
                value: 0, // Empty data
                sampleIndex: -1,
                timestamp: null
              }));
              
              const latestDataSecondHalf = slice.map((r: any, i: number) => ({ 
                time: 7.08 + (i * ecg2Dt), // 7.08 to 14.16 seconds
                value: Number(r.ecg2), 
                sampleIndex: Number(r.sample_index ?? i),
                timestamp: r.timestamp
              }));
              
              ecg2BufferRef.current = [...emptyFirstHalf, ...latestDataSecondHalf];
            } else {
              // Controlled sliding mode: second buffer slides to first, new data goes to second
              const currentTimestamp = ecg2Rows.length > 0 ? ecg2Rows[0].timestamp : null;
              
              if (currentTimestamp && currentTimestamp !== lastAppendedEcg2TsRef.current) {
                // New timestamp: do controlled sliding
                lastAppendedEcg2TsRef.current = currentTimestamp;
                
                // Create new data for second half (7.08-14.16s) - NEEDED FOR ANIMATION
                const newDataSecondHalf = slice.map((r: any, i: number) => ({ 
                  time: 7.08 + (i * ecg2Dt), // 7.08 to 14.16 seconds
                  value: Number(r.ecg2), 
                  sampleIndex: Number(r.sample_index ?? i),
                  timestamp: r.timestamp
                }));
                
                
                // Smooth sliding animation via requestAnimationFrame
                startRawBufferSlide(ecg2SlideRef, ecg2BufferRef, newDataSecondHalf, ecg2Dt);
              } else {
                // Same timestamp: just update second half, no sliding
                const firstHalf = ecg2BufferRef.current.filter(p => p.time < 7.08);
                const newDataSecondHalf = slice.map((r: any, i: number) => ({ 
                  time: 7.08 + (i * ecg2Dt), // 7.08 to 14.16 seconds
                  value: Number(r.ecg2), 
                  sampleIndex: Number(r.sample_index ?? i),
                  timestamp: r.timestamp
                }));
                ecg2BufferRef.current = [...firstHalf, ...newDataSecondHalf];
              }
            }
          } catch {}
        }

        // PPG Yellow (REAL) — triple sliding mode (same as PPG Green)
        const ppgYellowRows = await getLatestPpgYellowSamples();
        
        if (ppgYellowRows && ppgYellowRows.length > 0) {
          const maxSampleIdx = Math.max(...ppgYellowRows.map((r: any) => Number(r.sample_index ?? 0)));
          const dt = getPpgSampleInterval(ppgYellowRows.length, maxSampleIdx);
          currentPpgDtRef.current = dt;
          const slice = ppgYellowRows.slice(0, getPpgWindowSampleCount(dt));
          
          if (ppgYellowBufferRef.current.length === 0) {
            // First batch: put data in second half (7.08-14.16s)
            const emptyFirstHalf = Array.from({ length: Math.floor(7.08 / dt) }, (_, i) => ({
              time: i * dt, // 0 to 7.08 seconds
              value: 0,
              sampleIndex: -1,
              timestamp: null as string | null
            }));
            
            const latestDataSecondHalf = slice.map((r: any, i: number) => ({ 
              time: 7.08 + (i * dt), // 7.08 to 14.16 seconds
              value: Number(r.ppg_yellow), 
              sampleIndex: Number(r.sample_index ?? i),
              timestamp: r.timestamp
            }));
            
              ppgYellowBufferRef.current = [...emptyFirstHalf, ...latestDataSecondHalf];
              
              // Update last sample index
              if (slice.length > 0) {
                lastPpgYellowSampleIndexRef.current = Number(slice[slice.length - 1].sample_index ?? slice.length - 1);
              }
          } else {
            // Controlled sliding mode: second buffer slides to first, new data goes to second
            const currentTimestamp = ppgYellowRows.length > 0 ? ppgYellowRows[0].timestamp : null;
            
            if (currentTimestamp && currentTimestamp !== lastAppendedPpgYellowTsRef.current) {
              // New timestamp: trigger sliding
              
              // BUFFER-LEVEL SLIDING COMMENTED OUT - ONLY ANIMATION SLIDING WILL BE USED
              // Get existing data from second half and move to first half
              // const existingData = ppgYellowBufferRef.current
              //   .filter(p => p.time >= 7.08 && p.value !== 0);
              
              // Create empty first half
              // const emptyFirstHalf = Array.from({ length: Math.floor(7.08 / dt) }, (_, i) => ({
              //   time: i * dt,
              //   value: 0,
              //   sampleIndex: -1,
              //   timestamp: null as string | null
              // }));
              
              // Replace empty points with existing data where available
              // existingData.forEach(dataPoint => {
              //   const index = Math.floor(dataPoint.time / dt);
              //   if (index >= 0 && index < emptyFirstHalf.length) {
              //     emptyFirstHalf[index] = {
              //       ...dataPoint,
              //       time: index * dt,
              //       timestamp: dataPoint.timestamp || null
              //     };
              //   }
              // });
              
              // Create new data for second half - NEEDED FOR ANIMATION
              const newDataSecondHalf = slice.map((r: any, i: number) => ({ 
                time: 7.08 + (i * dt), // 7.08 to 14.16 seconds
                value: Number(r.ppg_yellow), 
                sampleIndex: Number(r.sample_index ?? i),
                timestamp: r.timestamp
              }));
              
              // BUFFER UPDATE COMMENTED OUT - ANIMATION WILL HANDLE THE SLIDING
              // Combine filled first half + new data (second half)
              // ppgYellowBufferRef.current = [...emptyFirstHalf, ...newDataSecondHalf];
              
              // Smooth sliding animation via requestAnimationFrame
              startRawBufferSlide(ppgYellowSlideRef, ppgYellowBufferRef, newDataSecondHalf, dt);
              
              // Update timestamp reference and last sample index
              lastAppendedPpgYellowTsRef.current = currentTimestamp;
              if (slice.length > 0) {
                lastPpgYellowSampleIndexRef.current = Number(slice[slice.length - 1].sample_index ?? slice.length - 1);
              }
            } else {
              // Same timestamp: just update second half, no sliding
              const firstHalf = ppgYellowBufferRef.current.filter(p => p.time < 7.08);
              const newDataSecondHalf = slice.map((r: any, i: number) => ({ 
                time: 7.08 + (i * dt), // 7.08 to 14.16 seconds
                value: Number(r.ppg_yellow), 
                sampleIndex: Number(r.sample_index ?? i),
                timestamp: r.timestamp
              }));
              
              ppgYellowBufferRef.current = [...firstHalf, ...newDataSecondHalf];
              
              // Update last sample index
              if (slice.length > 0) {
                lastPpgYellowSampleIndexRef.current = Number(slice[slice.length - 1].sample_index ?? slice.length - 1);
              }
            }
          }
        }
      } catch (e) {
        console.error('Failed to fetch ECG samples', e);
        // Do not clear buffers on failure; keep previous
      }
    };
    let lastEcgTs: string | null = null;
    let lastPpgTs: string | null = null;
    let checkInFlight = false;
    const checkAndFetch = async () => {
      // DISABLED: No real data fetches in demo mode
      if (isDemo) return;
      if (isSessionInvalidated()) return;
      if (checkInFlight) return;
      // If a critical event is selected, don't fetch latest data continuously
      // The event-based fetching will handle it
      if (selectedEventTimestamp) return;
      checkInFlight = true;
      try {
        // Guard: only poll real latest timestamps in REAL mode
        if (!isReal) return;
        const ts = await getLatestDataTimestamps();
        setLastEcgTimestamp(ts.ecg_ts || null);
        setLastPpgTimestamp(ts.ppg_ts || null);
        serverTimeRef.current = ts.server_time || null;
        if (ts.ecg_ts && ts.ecg_ts !== lastEcgTs) {
          lastEcgTs = ts.ecg_ts;
          setCurrentEcgBatchTs(ts.ecg_ts);
          //await fetchLatest();
        } 
        if (ts.ppg_ts && ts.ppg_ts !== lastPpgTs) {
          lastPpgTs = ts.ppg_ts;
          setCurrentPpgBatchTs(ts.ppg_ts);
          await fetchLatest();
        }
        if(ts.ppg_ts === lastPpgTs){
        }

        // Temperature live append removed; range aggregation handles minute-collapsed series
      } catch (e) {
        // ignore transient errors
      } finally {
        checkInFlight = false;
      }
    };
    // DISABLED: No real data fetches in demo mode
    let iv: NodeJS.Timeout | null = null;
    if (!isDemo) {
      iv = setInterval(checkAndFetch, 1000);
    }
    let rafId: number;
    const animate = () => {
        // Time speed multiplier για πιο γρήγορο scrolling
        const timeSpeed = isDemo ? 1 : 1.11; // 2x ταχύτητα (για πιο αργό βάλε 0.5)
        currentTimeRef.current = nowSeconds() * timeSpeed;
        // Different window sizes for demo vs real data
        const windowSize = isDemo ? 10.0 : 14.16;
        const start = currentTimeRef.current - windowSize;
        // Trim buffers each frame to avoid growth and duplicates
        // ecgBufferRef.current = ecgBufferRef.current.filter(p => p.time >= (start - 1)); // ECG1 now uses sliding mode
        ecgDemoBufferRef.current = ecgDemoBufferRef.current.filter(p => p.time >= (start - 1));
        // Don't trim PPG buffers - they use sliding mode
        // ppgBufferRef.current = ppgBufferRef.current.filter(p => p.time >= (start - 1)); 
        // ecg2BufferRef.current = ecg2BufferRef.current.filter(p => p.time >= (start - 1)); // ECG2 now uses sliding mode
        ecg2DemoBufferRef.current = ecg2DemoBufferRef.current.filter(p => p.time >= (start - 1));
        // ppgYellowBufferRef.current = ppgYellowBufferRef.current.filter(p => p.time >= (start - 1));
        ppgGreenDemoBufferRef.current = ppgGreenDemoBufferRef.current.filter(p => p.time >= (start - 1));
        ppgYellowDemoBufferRef.current = ppgYellowDemoBufferRef.current.filter(p => p.time >= (start - 1));
        ppgRedDemoBufferRef.current = ppgRedDemoBufferRef.current.filter(p => p.time >= (start - 1));
        ppgIrDemoBufferRef.current = ppgIrDemoBufferRef.current.filter(p => p.time >= (start - 1));
        
        // Define time intervals for consistent mapping
        const dt = 0.002;
        const ppgDt = currentPpgDtRef.current;
        const ecg2Dt = 0.002;
        
        // ECG1 real data — now uses sliding buffer like PPG Green
        if (!isDemo) {
          // Don't update if a critical event is selected
          if (!selectedEventTimestamp) {
            const ecgVisible = mapRawBufferToChartPoints(
              computeRawSlideDisplayPoints(ecgSlideRef.current, ecgBufferRef.current)
            );
            setEcgRawSamples(ecgVisible);
          }
        } else {
          // Demo mode
          if (!selectedEventTimestamp) {
            const ecgVisible = ecgBufferRef.current.filter(p => p.time >= start && p.time <= currentTimeRef.current)
              .map((p, i) => ({ t: i * dt, value: p.value, sampleIndex: p.sampleIndex }));
            setEcgRawSamples(ecgVisible);
          }
        }
        const ecgDemoVisible = ecgDemoBufferRef.current
          .filter(p => p.time >= start && p.time <= currentTimeRef.current)
          .map(p => ({ t: p.time - start, value: p.value, sampleIndex: p.sampleIndex }));
        if (!selectedEventTimestamp && ecgDemoVisible.length > 0) {
          setEcgDemoRawSamples(ecgDemoVisible);
        }
        // PPG Green real data — now uses sliding window like demo data
        if (isDemo) {
          if (!selectedEventTimestamp) {
            const ppgVisible = ppgBufferRef.current.filter(p => p.time >= start && p.time <= currentTimeRef.current)
              .map((p, i) => ({ t: i * ppgDt, value: p.value, sampleIndex: p.sampleIndex }));
            setPpgGreenRawSamples(ppgVisible);
          }
        } else {
          // Don't update if a critical event is selected
          if (!selectedEventTimestamp) {
            const ppgVisible = mapRawBufferToChartPoints(
              computeRawSlideDisplayPoints(ppgGreenSlideRef.current, ppgBufferRef.current)
            );
            setPpgGreenRawSamples(ppgVisible);
          }
        }
        const ppgGreenDemoVisible = ppgGreenDemoBufferRef.current.filter(p => p.time >= start && p.time <= currentTimeRef.current)
          .map(p => ({ t: p.time - start, value: p.value, sampleIndex: p.sampleIndex }));
        if (!selectedEventTimestamp && ppgGreenDemoVisible.length) setPpgGreenDemoRawSamples(ppgGreenDemoVisible);
        // ECG2 real data — now uses sliding buffer like PPG Green
        if (!isDemo) {
          // Don't update if a critical event is selected
          if (!selectedEventTimestamp) {
            const ecg2Visible = mapRawBufferToChartPoints(
              computeRawSlideDisplayPoints(ecg2SlideRef.current, ecg2BufferRef.current)
            );
            setEcg2RawSamples(ecg2Visible);
          }
        } else {
          // Demo mode
          if (!selectedEventTimestamp) {
            const ecg2Visible = ecg2BufferRef.current.filter(p => p.time >= start && p.time <= currentTimeRef.current)
              .map((p, i) => ({ t: i * ecg2Dt, value: p.value, sampleIndex: p.sampleIndex }));
            setEcg2RawSamples(ecg2Visible);
          }
        }
        const ecg2DemoVisible = ecg2DemoBufferRef.current
          .filter(p => p.time >= start && p.time <= currentTimeRef.current)
          .map(p => ({ t: p.time - start, value: p.value, sampleIndex: p.sampleIndex }));
        if (!selectedEventTimestamp && ecg2DemoVisible.length > 0) {
          setEcg2DemoRawSamples(ecg2DemoVisible);
        }
        // PPG Yellow real data — now uses sliding buffer like PPG Green
        if (!isDemo) {
          // Don't update if a critical event is selected
          if (!selectedEventTimestamp) {
            const ppgYellowVisible = mapRawBufferToChartPoints(
              computeRawSlideDisplayPoints(ppgYellowSlideRef.current, ppgYellowBufferRef.current)
            );
            setPpgYellowRawSamples(ppgYellowVisible);
          }
        } else {
          // Demo mode
          if (!selectedEventTimestamp) {
            const ppgYellowVisible = ppgYellowBufferRef.current.filter(p => p.time >= start && p.time <= currentTimeRef.current)
              .map((p, i) => ({ t: i * ppgDt, value: p.value, sampleIndex: p.sampleIndex }));
            setPpgYellowRawSamples(ppgYellowVisible);
          }
        }
        const ppgYellowDemoVisible = ppgYellowDemoBufferRef.current.filter(p => p.time >= start && p.time <= currentTimeRef.current)
          .map(p => ({ t: p.time - start, value: p.value, sampleIndex: p.sampleIndex }));
        if (!selectedEventTimestamp && ppgYellowDemoVisible.length) setPpgYellowDemoRawSamples(ppgYellowDemoVisible);
        const ppgRedDemoVisible = ppgRedDemoBufferRef.current.filter(p => p.time >= start && p.time <= currentTimeRef.current)
          .map(p => ({ t: p.time - start, value: p.value, sampleIndex: p.sampleIndex }));
        if (!selectedEventTimestamp && ppgRedDemoVisible.length) setPpgRedDemoRawSamples(ppgRedDemoVisible);
        const ppgIrDemoVisible = ppgIrDemoBufferRef.current.filter(p => p.time >= start && p.time <= currentTimeRef.current)
          .map(p => ({ t: p.time - start, value: p.value, sampleIndex: p.sampleIndex }));
        if (!selectedEventTimestamp && ppgIrDemoVisible.length) setPpgIrDemoRawSamples(ppgIrDemoVisible);
      rafId = requestAnimationFrame(animate);
    };
    rafId = requestAnimationFrame(animate);
    return () => {
      if (iv) clearInterval(iv);
      cancelAnimationFrame(rafId);
    };
    // isDemo/isReal/lastEcgTimestamp read via refs inside polling to avoid restarting RAF loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEventTimestamp]);

  // Fetch raw signals based on selected critical event (full 60s batch by timestamp).
  useEffect(() => {
    if (!selectedEventTimestamp) {
      // Clear raw signals when no event is selected
      setEcgRawSamples([]);
      setEcg2RawSamples([]);
      setPpgGreenRawSamples([]);
      setPpgYellowRawSamples([]);
      setEcgDemoRawSamples([]);
      setEcg2DemoRawSamples([]);
      setPpgGreenDemoRawSamples([]);
      setPpgYellowDemoRawSamples([]);
      // Reset zoom to initial state
      setAllRawSignalZoom(0);
      return;
    }

    const fetchEventRawData = async () => {
      try {
        const eventTime = parseValidTimestamp(selectedEventTimestamp);
        if (!eventTime) {
          console.warn('[Critical events] Invalid selected event timestamp, skipping raw fetch:', selectedEventTimestamp);
          return;
        }
        if (isDemo) {
          console.log('[Demo raw] Fetching raw by timestamp:', selectedEventTimestamp);
          const demoRows = await getDemoRawSamplesByTimestamp(selectedEventTimestamp);
          console.log('[Demo raw] Received rows:', demoRows?.length ?? 0, demoRows?.[0] ? 'sample keys: ' + Object.keys(demoRows[0]).join(', ') : '');

          const { ecgMapped, ecg2Mapped, greenMapped, yellowMapped, validRowCount } =
            mapCriticalEventRawBatchRows(demoRows);

          console.log('[Demo raw] Total rows received:', demoRows.length, 'Valid rows (0-29999):', validRowCount);
          console.log('[Demo raw] Mapped samples:', {
            ecg1: ecgMapped.length,
            ecg2: ecg2Mapped.length,
            green_ppg: greenMapped.length,
            yellow_ppg: yellowMapped.length,
          });

          setEcgDemoRawSamples(ecgMapped);
          setEcg2DemoRawSamples(ecg2Mapped);
          setPpgGreenDemoRawSamples(greenMapped);
          setPpgYellowDemoRawSamples(yellowMapped);
          setEcgRawSamples(ecgMapped);
          setEcg2RawSamples(ecg2Mapped);
          setPpgGreenRawSamples(greenMapped);
          setPpgYellowRawSamples(yellowMapped);
          return;
        }

        console.log('[Critical Event] Fetching 60s raw batch by timestamp:', selectedEventTimestamp);
        const rawRows = await getRawSamplesByTimestamp(selectedEventTimestamp, patId || undefined);
        console.log('[Critical Event] Received raw batch rows:', rawRows?.length ?? 0);

        const { ecgMapped, ecg2Mapped, greenMapped, yellowMapped, validRowCount } =
          mapCriticalEventRawBatchRows(rawRows);

        console.log('[Critical Event] Valid rows (0-29999):', validRowCount);
        console.log('[Critical Event] Mapped samples:', {
          ecg: ecgMapped.length,
          ecg2: ecg2Mapped.length,
          ppgGreen: greenMapped.length,
          ppgYellow: yellowMapped.length,
          ecgTimeRange: ecgMapped.length > 0
            ? `${ecgMapped[0].t.toFixed(2)}s to ${ecgMapped[ecgMapped.length - 1].t.toFixed(2)}s`
            : 'N/A',
        });

        setEcgRawSamples(ecgMapped);
        setEcg2RawSamples(ecg2Mapped);
        setPpgGreenRawSamples(greenMapped);
        setPpgYellowRawSamples(yellowMapped);
      } catch (error) {
        console.error('Error fetching event raw data:', error);
        // On error, clear the samples
        setEcgRawSamples([]);
        setEcg2RawSamples([]);
        setPpgGreenRawSamples([]);
        setPpgYellowRawSamples([]);
        setEcgDemoRawSamples([]);
        setEcg2DemoRawSamples([]);
        setPpgGreenDemoRawSamples([]);
        setPpgYellowDemoRawSamples([]);
      }
    };

    fetchEventRawData();
  }, [selectedEventTimestamp, isDemo, patId]);

  // When user toggles to Raw Signals in DEMO mode (without selected event), prime charts from latest demo raw.
  useEffect(() => {
    if (!showgraphs && isDemo && !selectedEventTimestamp) {
      (async () => {
        try {
          const resp = await getLatestDemoRawSamples();
          console.log('[DEMO] Raw signals response:', resp?.length || 0);
          const rows = (resp || []).filter((r: any) => r && r.ecg1 != null);
          console.log('[DEMO] ECG1 rows after filter:', rows.length);
          const dt = 0.002;
          // Use all available data instead of limiting to maxPoints
          const slice = rows; // Use all available data
          const preview = slice.map((r: any, i: number) => ({ t: i * dt, value: Number(r.ecg1) }));
          console.log('[DEMO] ECG1 preview length:', preview.length);
          // Always seed the chart with stable demo data
          setEcgDemoRawSamples(preview);
          // Seed ECG2 demo preview as well
          const rows2 = (resp || []).filter((r: any) => r && r.ecg2 != null);
          const slice2 = rows2; // Use all available data
          const preview2 = slice2.map((r: any, i: number) => ({ t: i * dt, value: Number(r.ecg2) }));
          // Always seed ECG2 with stable demo data
          setEcg2DemoRawSamples(preview2);
          // Seed PPG demo previews (green, yellow only)
          const greenRows = (resp || []).filter((r: any) => r && r.green_ppg != null);
          const yellowRows = (resp || []).filter((r: any) => r && r.yellow_ppg != null);
          const ppgDt = getPpgSampleInterval(Math.max(greenRows.length, yellowRows.length));
          currentPpgDtRef.current = ppgDt;
          const gPrev = greenRows.map((r: any, i: number) => ({ t: Number(r.sample_index ?? i) * ppgDt, value: Number(r.green_ppg) }));
          const yPrev = yellowRows.map((r: any, i: number) => ({ t: Number(r.sample_index ?? i) * ppgDt, value: Number(r.yellow_ppg) }));
          if (ppgGreenDemoBufferRef.current.length === 0) setPpgGreenDemoRawSamples(gPrev);
          if (ppgYellowDemoBufferRef.current.length === 0) setPpgYellowDemoRawSamples(yPrev);
          console.log('[DEMO button] primed demo raw preview points:', preview.length);
        } catch (e) {
          console.warn('Failed to fetch demo raw on toggle:', e);
        }
      })();
    }
  }, [showgraphs, isDemo, selectedEventTimestamp]);
 
  // Keep DEMO raw charts in sync with DEMO vitals: whenever latest demo timestamp updates,
  // fetch the most recent raw batch and seed preview so charts update together
  // DISABLED: Auto-refresh removed to prevent continuous chart changes
  // useEffect(() => {
  //   const syncDemoRawWithVitals = async () => {
  //     if (!isDemo || showgraphs) return;
  //     try {
  //       const resp = await getLatestDemoRawSamples();
  //       const dt = 0.002;
  //       // ECG1 - use all available data
  //       const e1 = (resp || []).filter((r: any) => r && r.ecg1 != null)
  //         .map((r: any, i: number) => ({ t: i * dt, value: Number(r.ecg1) }));
  //       if (e1.length) setEcgDemoRawSamples(e1);
  //       // ECG2 - use all available data
  //       const e2 = (resp || []).filter((r: any) => r && r.ecg2 != null)
  //         .map((r: any, i: number) => ({ t: i * dt, value: Number(r.ecg2) }));
  //       if (e2.length) setEcg2DemoRawSamples(e2);
  //       // PPGs - use all available data
  //       const g = (resp || []).filter((r: any) => r && r.green_ppg != null)
  //         .map((r: any, i: number) => ({ t: i * dt, value: Number(r.green_ppg) }));
  //       if (g.length) setPpgGreenDemoRawSamples(g);
  //       const y = (resp || []).filter((r: any) => r && r.yellow_ppg != null)
  //         .map((r: any, i: number) => ({ t: i * dt, value: Number(r.yellow_ppg) }));
  //       if (y.length) setPpgYellowDemoRawSamples(y);
  //       const r = (resp || []).filter((r: any) => r && r.red_ppg != null)
  //         .map((r: any, i: number) => ({ t: i * dt, value: Number(r.red_ppg) }));
  //       if (r.length) setPpgRedDemoRawSamples(r);
  //       const ir = (resp || []).filter((r: any) => r && r.ir_ppg != null)
  //         .map((r: any, i: number) => ({ t: i * dt, value: Number(r.ir_ppg) }));
  //       if (ir.length) setPpgIrDemoRawSamples(ir);
  //     } catch (_e) {
  //       // ignore transient errors
  //     }
  //   };
  //   syncDemoRawWithVitals();
  // }, [latestVitalsTs, isDemo, showgraphs]);

  /** Get timestamp from a GraphData point for night median (patient bedtime window). */
  const getTimestampFromPoint = (p: GraphData): Date | null => {
    const g = p as GraphData & { tsMs?: number };
    if (g.tsMs != null) return new Date(g.tsMs);
    const t = (g.time || '').trim();
    const iso = Date.parse(t);
    if (!Number.isNaN(iso)) return new Date(iso);
    return null;
  };

  /** Compute median, night median (patient bedtime window), min, max from a series. Filter by confidence when present.
   *  When medianMaxInclusive is set, values above it are excluded from median/nightMedian only (min/max still use all valid points). */
  const computeSummaryStats = (
    series: GraphData[],
    filterConfidence = true,
    medianMaxInclusive?: number
  ): { median: number | null; nightMedian: number | null; min: number; max: number } => {
    const valid = (series || []).filter((d) => {
      const v = d.value;
      if (v == null || Number.isNaN(v) || !Number.isFinite(v)) return false;
      if (filterConfidence && d.confidence != null && d.confidence <= 0) return false;
      return true;
    });
    const values = valid.map((d) => Number(d.value));
    if (!values.length) return { median: null, nightMedian: null, min: 0, max: 0 };
    const medianValues =
      medianMaxInclusive != null ? values.filter((v) => v <= medianMaxInclusive) : values;
    let median: number | null = null;
    if (medianValues.length > 0) {
      const sorted = [...medianValues].sort((a, b) => a - b);
      const mid = Math.floor(sorted.length / 2);
      median = sorted.length % 2 === 0 ? (sorted[mid - 1]! + sorted[mid]!) / 2 : sorted[mid]!;
    }
    const nightValues = valid
      .filter((d) => {
        const ts = getTimestampFromPoint(d);
        if (ts == null || !isInNightWindow(ts, patientBedTime)) return false;
        if (medianMaxInclusive != null && Number(d.value) > medianMaxInclusive) return false;
        return true;
      })
      .map((d) => Number(d.value));
    let nightMedian: number | null = null;
    if (nightValues.length > 0) {
      const nightSorted = [...nightValues].sort((a, b) => a - b);
      const nMid = Math.floor(nightSorted.length / 2);
      nightMedian = nightSorted.length % 2 === 0 ? (nightSorted[nMid - 1]! + nightSorted[nMid]!) / 2 : nightSorted[nMid]!;
    }
    return {
      median,
      nightMedian,
      min: Math.round(Math.min(...values)),
      max: Math.round(Math.max(...values)),
    };
  };

  /** Summary stats (median, night median, min, max) from current range base series — for Current panels. */
  const heartRateSummaryStats = computeSummaryStats(heartRateBaseData);
  // HRV medians exclude values > 200 ms (invalid); min/max still reflect the series used for the panel.
  const hrvSummaryStats = computeSummaryStats(heartRateVariabilityBaseData, true, MAX_HRV_MS);
  const spo2SummaryStats = computeSummaryStats(spo2BaseData);
  const breathingSummaryStats = computeSummaryStats(respirationRateBaseData);
  const tempSummaryStats = computeSummaryStats(dbTemperature as GraphData[]);
  const systolicSummaryStats = computeSummaryStats(systolic_BloodPressureBaseData);
  const diastolicSummaryStats = computeSummaryStats(diastolic_BloodPressureBaseData);

  /** Display stats: median/min/max/night median from raw graph readings when available (not from aggregated plotbox points). */
  const withRawStats = (
    base: { median: number | null; nightMedian: number | null; min: number; max: number },
    raw?: RawMetricStats
  ) => ({
    ...base,
    median: raw?.median ?? base.median,
    min: raw?.min ?? base.min,
    max: raw?.max ?? base.max,
    nightMedian: raw?.nightMedian ?? base.nightMedian,
  });
  const withEventSnapshotMedian = (
    display: { median: number | null; nightMedian: number | null; min: number; max: number },
    snapshotValue: number | null | undefined
  ) => {
    if (!selectedEventTimestamp || snapshotValue == null || !Number.isFinite(snapshotValue)) return display;
    return { ...display, median: snapshotValue };
  };
  const hrDisplay = withEventSnapshotMedian(
    withRawStats(heartRateSummaryStats, rawCurrentStats?.heartRate),
    clickedEventSnapshot?.heart_rate
  );
  const hrvDisplay = withEventSnapshotMedian(
    withRawStats(hrvSummaryStats, rawCurrentStats?.hrv),
    normalizeHrvForDisplay(clickedEventSnapshot?.heart_rate_variability)
  );
  const spo2Display = withEventSnapshotMedian(
    withRawStats(spo2SummaryStats, rawCurrentStats?.spo2),
    clickedEventSnapshot?.spo2
  );
  const breathingDisplay = withEventSnapshotMedian(
    withRawStats(breathingSummaryStats, rawCurrentStats?.breathing),
    clickedEventSnapshot?.breathing
  );
  const tempDisplay = withEventSnapshotMedian(
    withRawStats(tempSummaryStats, rawCurrentStats?.temperature),
    clickedEventSnapshot?.temperature
  );
  const systolicDisplay = withEventSnapshotMedian(
    withRawStats(systolicSummaryStats, rawCurrentStats?.systolicBp),
    clickedEventSnapshot?.systolic
  );
  const diastolicDisplay = withEventSnapshotMedian(
    withRawStats(diastolicSummaryStats, rawCurrentStats?.diastolicBp),
    clickedEventSnapshot?.diastolic
  );

  /** Helper: valid points from series (confidence ok, value > 0 to match SQL), then % where predicate is true. */
  const pctOutOfRange = (series: GraphData[], isOut: (v: number) => boolean): number | null => {
    const valid = (series || []).filter((d) => {
      const v = d.value;
      return v != null && !Number.isNaN(v) && Number.isFinite(v) && Number(v) > 0 && (d.confidence == null || d.confidence > 0);
    });
    if (!valid.length) return null;
    const count = valid.filter((d) => isOut(Number(d.value))).length;
    return (count / valid.length) * 100;
  };
  /** Out-of-range % fallback from base series (aggregated) when raw counts unavailable. HRV excludes > 200 ms.
   *  Upper bounds match SQL: medium/stage1 are in-range (use `>` not `>=`). */
  const heartRateInvalidPctFromRange = pctOutOfRange(heartRateBaseData, (v) => v < thresholds.heartRate.low || v > thresholds.heartRate.medium);
  const hrvInvalidPctFromRange = pctOutOfRange(
    (heartRateVariabilityBaseData || []).filter((d) => d.value != null && Number(d.value) <= MAX_HRV_MS),
    (v) => v < thresholds.hrv.low || v > thresholds.hrv.high || (v >= thresholds.hrv.low && v <= thresholds.hrv.medium)
  );
  const spo2InvalidPctFromRange = pctOutOfRange(spo2BaseData, (v) => v <= thresholds.spo2.medium);
  const respiratoryRateInvalidPctFromRange = pctOutOfRange(respirationRateBaseData, (v) => v < thresholds.breathing.low || v > thresholds.breathing.medium);
  const systolicBpInvalidPctFromRange = pctOutOfRange(systolic_BloodPressureBaseData, (v) => v < thresholds.bloodPressure.systolic.low || v > thresholds.bloodPressure.systolic.systolic_stage1);
  const diastolicBpInvalidPctFromRange = pctOutOfRange(diastolic_BloodPressureBaseData, (v) => v < thresholds.bloodPressure.diastolic.low || v > thresholds.bloodPressure.diastolic.diastolic_stage1);
  const tempInvalidPctFromRange = pctOutOfRange(temperatureBaseData, (v) => v < thresholds.temperature.low || v > thresholds.temperature.medium);

  /** Out-of-range % from raw data in the period when available; otherwise fallback to median-based %. */
  const displayHeartRateInvalidPct = (rawOutOfRangeStats?.heartRate?.total != null && rawOutOfRangeStats.heartRate.total > 0)
    ? (rawOutOfRangeStats.heartRate.out / rawOutOfRangeStats.heartRate.total * 100) : heartRateInvalidPctFromRange;
  const displayHrvInvalidPct = (rawOutOfRangeStats?.hrv?.total != null && rawOutOfRangeStats.hrv.total > 0)
    ? (rawOutOfRangeStats.hrv.out / rawOutOfRangeStats.hrv.total * 100) : hrvInvalidPctFromRange;
  const displaySpo2InvalidPct = (rawOutOfRangeStats?.spo2?.total != null && rawOutOfRangeStats.spo2.total > 0)
    ? (rawOutOfRangeStats.spo2.out / rawOutOfRangeStats.spo2.total * 100) : spo2InvalidPctFromRange;
  const displayRespiratoryInvalidPct = (rawOutOfRangeStats?.breathing?.total != null && rawOutOfRangeStats.breathing.total > 0)
    ? (rawOutOfRangeStats.breathing.out / rawOutOfRangeStats.breathing.total * 100) : respiratoryRateInvalidPctFromRange;
  const displaySystolicBpInvalidPct = (rawOutOfRangeStats?.systolicBp?.total != null && rawOutOfRangeStats.systolicBp.total > 0)
    ? (rawOutOfRangeStats.systolicBp.out / rawOutOfRangeStats.systolicBp.total * 100) : systolicBpInvalidPctFromRange;
  const displayDiastolicBpInvalidPct = (rawOutOfRangeStats?.diastolicBp?.total != null && rawOutOfRangeStats.diastolicBp.total > 0)
    ? (rawOutOfRangeStats.diastolicBp.out / rawOutOfRangeStats.diastolicBp.total * 100) : diastolicBpInvalidPctFromRange;
  const displayTempInvalidPct = (rawOutOfRangeStats?.temperature?.total != null && rawOutOfRangeStats.temperature.total > 0)
    ? (rawOutOfRangeStats.temperature.out / rawOutOfRangeStats.temperature.total * 100)
    : tempInvalidPctFromRange;

  // If a critical event was clicked, use its timestamp in the right-side "Current" panels
  const selectedEventDate = selectedEventTimestamp ? parseValidTimestamp(selectedEventTimestamp) : null;
  // Update titles to use critical event timestamp when available
  const effectiveTimestamp = selectedEventDate || latestVitalsTs || new Date();
  const isEventSnapshot = !!selectedEventTimestamp;
  
  // Get time range label for graph titles (left column)
  const getTimeRangeLabel = (): string => {
    if (isEventSnapshot) {
      return format(effectiveTimestamp, 'dd MMM yyyy HH:mm:ss');
    }
    switch (selectedTimeRange) {
      case '1 Hours':
        return '1 Hour';
      case '6 Hours':
        return '6 Hours';
      case '12 Hours':
        return '12 Hours';
      case '24 Hours':
        return '24 Hours';
      case '7 Days':
        return '7 Days';
      case '14 Days':
        return '14 Days';
      case '30 Days':
        return '30 Days';
      case 'Custom':
        return `${format(customStartDate, 'dd MMM HH:mm')} - ${format(customEndDate, 'dd MMM HH:mm')}`;
      default:
        return '1 Hour';
    }
  };
  
  const timeRangeLabel = getTimeRangeLabel();
  
  // Short time range label for min/max display (e.g., "last hour" instead of "Last 1 Hour")
  const getShortTimeRangeLabel = (): string => {
    if (isEventSnapshot) {
      return format(effectiveTimestamp, 'HH:mm');
    }
    switch (selectedTimeRange) {
      case '1 Hours':
        return '1 hour';
      case '6 Hours':
        return '6 hours';
      case '12 Hours':
        return '12 hours';
      case '24 Hours':
        return '24 hours';
      case '7 Days':
        return '7 days';
      case '14 Days':
        return '14 days';
      case '30 Days':
        return '30 days';
      case 'Custom':
        return `${format(customStartDate, 'HH:mm')} - ${format(customEndDate, 'HH:mm')}`;
      default:
        return '1 hour';
    }
  };
  
  const shortTimeRangeLabel = getShortTimeRangeLabel();
  /** Label above Median in Current panels: "Last 1 hour", "Last 7 days", or "At HH:mm" when snapshot */
  const summaryRangeLabel = isEventSnapshot ? `At ${shortTimeRangeLabel}` : `Last ${shortTimeRangeLabel}`;
  const renderSummaryValueWithWristMode = (value: React.ReactNode) => (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <span style={{ fontSize: 28, fontWeight: 700, color: '#222' }}>{value}</span>
      {summaryWristMode.hasTrue && (
        <img
          src="/wrist-mode-icon.png"
          title="Wrist mode readings found in selected period"
          aria-label="Wrist mode"
          alt="Wrist mode"
          style={{
            position: 'absolute',
            top: '50%',
            left: -78,
            transform: 'translateY(-50%)',
            width: 68,
            height: 92,
            objectFit: 'contain',
            borderRadius: 4,
            pointerEvents: 'none'
          }}
        />
      )}
      {summaryWristMode.hasFalse && (
        <img
          src="/patch-mode-icon.png"
          title="Patch mode readings found in selected period"
          aria-label="Patch mode"
          alt="Patch mode"
          style={{
            position: 'absolute',
            top: '50%',
            right: -82,
            transform: 'translateY(-50%)',
            width: 72,
            height: 96,
            objectFit: 'contain',
            borderRadius: 4,
            pointerEvents: 'none'
          }}
        />
      )}
    </div>
  );
  
  // Graph titles (left column) - use time range
  const tempTitle = isEventSnapshot ? `Skin Temperature (${format(effectiveTimestamp, 'dd MMM yyyy HH:mm:ss')})` : `Skin Temperature (Last ${timeRangeLabel})`;
  const breathingTitle = isEventSnapshot ? `Breathing Rate (${format(effectiveTimestamp, 'dd MMM yyyy HH:mm:ss')})` : `Breathing Rate (Last ${timeRangeLabel})`;
  const heartTitle = isEventSnapshot ? `Heart Rate(${format(effectiveTimestamp, 'dd MMM yyyy HH:mm:ss')})` : `Heart Rate (Last ${timeRangeLabel})`;
  const heartVariabilityTitle = isEventSnapshot ? `Heart Rate Variability (${format(effectiveTimestamp, 'dd MMM yyyy HH:mm:ss')})` : `Heart Rate Variability (Last ${timeRangeLabel})`;
  const spo2Title = isEventSnapshot ? `SpO2 (${format(effectiveTimestamp, 'dd MMM yyyy HH:mm:ss')})` : `SpO2 (Last ${timeRangeLabel})`;
  const systolic_diastolicBloodPressureTitle = isEventSnapshot ? `Systolic/Diastolic Blood Pressure (${format(effectiveTimestamp, 'dd MMM yyyy HH:mm:ss')})` : `Systolic/Diastolic Blood Pressure (Last ${timeRangeLabel})`;
  
  // Current panel titles (right column) — Summary style (no "Now" / latest timestamp in title)
  const breathingTitleCurrent = 'Breathing Rate Summary';
  const heartTitleCurrent = 'Heart Rate Summary';
  const heartVariabilityTitleCurrent = 'Heart Rate Variability Summary';
  const spo2TitleCurrent = 'SpO2 Summary';
  const systolic_diastolicBloodPressureTitleCurrent = 'Blood Pressure Summary';
  const activityTitleCurrent = 'Activity Summary';

  const isNearActivePoint = (e: any, thresholdPx: number) => {
    if (!e) return false;
    // For dense data we do not force proximity; allow tooltip if mouse is inside chart
    if (e && typeof e.chartX === 'number' && typeof e.chartY === 'number') return true;
    if (!e.activeCoordinate) return false;
    const dx = Math.abs((e.chartX ?? 0) - (e.activeCoordinate.x ?? 0));
    const dy = Math.abs((e.chartY ?? 0) - (e.activeCoordinate.y ?? 0));
    return dx <= thresholdPx && dy <= thresholdPx;
  };

  const handleTempMouseMove = (e: any) => {
    tempZoomCtl.onMouseMove(e);
    // Hide tooltip while selecting or if far from a point
    if (tempZoomCtl.zoom.refAreaLeft != null) {
      setShowTempTooltip(false);
      return;
    }
    setShowTempTooltip(isNearActivePoint(e, PROXIMITY_PX));
  };

  const handleTempMouseLeave = (_e: any) => {
    tempZoomCtl.onMouseUp();
    setShowTempTooltip(false);
  };

  const handleBreathingMouseMove = (e: any) => {
    breathingZoomCtl.onMouseMove(e);
    if (breathingZoomCtl.zoom.refAreaLeft != null) {
      setShowBreathingTooltip(false);
      return;
    }
    setShowBreathingTooltip(isNearActivePoint(e, PROXIMITY_PX));
  };

  const handleBreathingMouseLeave = (_e: any) => {
    breathingZoomCtl.onMouseUp();
    setShowBreathingTooltip(false);
  };

  const handleSleepMouseMove = (e: any) => {
    sleepZoomCtl.onMouseMove(e);
  };

  const handleSleepRunHover = (run: SleepRun | null, event: React.MouseEvent<Element>) => {
    if (sleepZoomCtl.zoom.refAreaLeft != null) {
      setSleepRunTooltip(null);
      return;
    }
    if (!run || !sleepingContainerRef.current) {
      setSleepRunTooltip(null);
      return;
    }
    const { x, y } = clampSleepTooltipPosition(
      event.clientX,
      event.clientY,
      sleepingContainerRef.current
    );
    setSleepRunTooltip({ run, x, y });
  };

  const handleSleepMouseLeave = (_e: any) => {
    sleepZoomCtl.onMouseUp();
    setSleepRunTooltip(null);
  };

  const handleHeartRateMouseMove = (e: any) => {
    heartRateZoomCtl.onMouseMove(e);
    if (heartRateZoomCtl.zoom.refAreaLeft != null) {
      setShowHeartRateTooltip(false);
      return;
    }
    setShowHeartRateTooltip(isNearActivePoint(e, PROXIMITY_PX));
  };

  const handleHeartRateMouseLeave = (_e: any) => {
    heartRateZoomCtl.onMouseUp();
    setShowHeartRateTooltip(false);
  };

  const handleHeartRateVariabilityMouseMove = (e: any) => {

    heartRateVariabilityZoomCtl.onMouseMove(e);
    if (heartRateVariabilityZoomCtl.zoom.refAreaLeft != null) {
      setShowHeartRateVariabilityTooltip(false);
      return;
    }
    setShowHeartRateVariabilityTooltip(isNearActivePoint(e, PROXIMITY_PX));
  };

  const handleHeartRateVariabilityMouseLeave = (_e: any) => {
    heartRateVariabilityZoomCtl.onMouseUp();
    setShowHeartRateVariabilityTooltip(false);
  };

  const handleSystolicBloodPressureMouseMove = (e: any) => {
    systolic_diastolicBloodPressureZoomCtl.onMouseMove(e);
    if (systolic_diastolicBloodPressureZoomCtl.zoom.refAreaLeft != null) {
      setShowSystolic_diastolicBloodPressureTooltip(false);
      return;
    }
    setShowSystolic_diastolicBloodPressureTooltip(isNearActivePoint(e, PROXIMITY_PX));
  };

  const handleSystolicBloodPressureMouseLeave = (_e: any) => {
    systolic_diastolicBloodPressureZoomCtl.onMouseUp();
    setShowSystolic_diastolicBloodPressureTooltip(false);
  };

  const handleSpo2MouseMove = (e: any) => {
    spo2ZoomCtl.onMouseMove(e);
    if (spo2ZoomCtl.zoom.refAreaLeft != null) {
      setShowSpo2Tooltip(false);
      return;
    }
    setShowSpo2Tooltip(isNearActivePoint(e, PROXIMITY_PX));
  };

  const handleSpo2MouseLeave = (_e: any) => {
    spo2ZoomCtl.onMouseUp();
    setShowSpo2Tooltip(false);
  };


  const formatPointTimeRange = (point: DensePoint | undefined, startLabel: string): string => {
    const endTsMs = point?.endTsMs;
    if (endTsMs == null) return startLabel;
    const endOffset = (endTsMs - rangeStartDate.getTime()) / 60000;
    const endLabel = formatDynamicTime([] as any, endOffset);
    return endLabel === startLabel ? startLabel : `${startLabel} – ${endLabel}`;
  };

  const formatActivitySegmentTimeRange = (startMs: number, endMs: number): string => {
    const startOffset = (startMs - rangeStartDate.getTime()) / 60000;
    const endOffset = (endMs - rangeStartDate.getTime()) / 60000;
    const startLabel = formatDynamicTime([] as any, startOffset);
    const endLabel = formatDynamicTime([] as any, endOffset);
    return endLabel === startLabel ? startLabel : `${startLabel} – ${endLabel}`;
  };

  const formatActivityTooltipTitle = (segment: ActivitySegment): string => {
    if (isSleepActivitySegment(segment)) return 'Sleep';
    if (!segment.activity) return 'No data';
    if (
      segment.activity === 'still' &&
      normalizeActivityIntensityLevel(segment.rawIntensity) === 'sedentary'
    ) {
      return 'Sedentary';
    }
    const name = `${segment.activity.charAt(0).toUpperCase()}${segment.activity.slice(1)}`;
    const intensity = segment.rawIntensity;
    if (intensity == null || intensity === '') return name;
    return `${name} (${String(intensity)})`;
  };

  const formatTemp1 = (v: number | null | undefined) => (v != null && Number.isFinite(v) ? Number(v).toFixed(1) : '-');
  const TemperatureTooltip = ({ active, payload, label }: any) => {
    if (!active || !showTempTooltip || !payload || !payload.length) return null;
  
    const item = payload[0];
    const point = item.payload;
  
    const value = typeof item.value === 'number' ? item.value : point?.value;
    const min = point?.min;
    const max = point?.max;
  
    const t = point?.t ?? label;
    const timeLabel = formatDynamicTime([] as any, Number(t));
  
    const hasRange =
      typeof min === 'number' &&
      typeof max === 'number' &&
      min !== max;


    
    return (
      <div style={{ background: 'rgba(255,255,255,0.95)', border: '1px solid #ddd', padding: '6px 8px', borderRadius: 4, fontSize: 12 }}>
        {!hasRange && (
          <>
            <div>{`time : ${formatPointTimeRange(point, timeLabel)}`}</div>
            <div>{`value : ${formatTemp1(value)}`}</div>
          </>
        )}
  
        {hasRange && (
          <>
            <div>{`time : ${formatPointTimeRange(point, timeLabel)}`}</div>
            <div>{`median : ${formatTemp1(value)}`}</div>
            <div>{`min : ${formatTemp1(min)}`}</div>
            <div>{`max : ${formatTemp1(max)}`}</div>
          </>
        )}
      </div>
    );
  };
  

  const BreathingTooltip = ({ active, payload, label }: any) => {
    if (!active || !showBreathingTooltip || !payload || !payload.length) return null;
  
    const item = payload[0];
    const point = item.payload;
  
    const median =
      typeof item.value === 'number'
        ? item.value
        : point?.value;
  
    const min = point?.min;
    const max = point?.max;
  
    const t = point?.t ?? label;
    const confidence = point?.confidence;
    const timeLabel = formatDynamicTime([] as any, Number(t));


    const hasRange =
      typeof min === 'number' &&
      typeof max === 'number' &&
      min !== max;

  
    return (
      <div style={{ background: 'rgba(255,255,255,0.95)', border: '1px solid #ddd', padding: '6px 8px', borderRadius: 4, fontSize: 12 }}>
        {!hasRange && (
          <>
            <div>{`time : ${formatPointTimeRange(point, timeLabel)}`}</div>
            <div>{`value : ${Math.round(median)}`}</div>
          </>
        )}
  
        {hasRange && (
          <>
            <div>{`time : ${formatPointTimeRange(point, timeLabel)}`}</div>
            <div>{`median : ${Math.round(median)}`}</div>
            <div>{`min : ${Math.round(min)}`}</div>
            <div>{`max : ${Math.round(max)}`}</div>
          </>
        )}
        {confidence != null && typeof confidence === 'number' && (
          <div>{`level of confidence: ${Number(confidence).toFixed(1)}`}</div>
        )}
      </div>
    );
  };
  

  const HeartRateTooltip = ({ active, payload, label }: any) => {

    if (!active || !showHeartRateTooltip || !payload || !payload.length) return null;
  
    const item = payload[0];
    const point = item.payload;
  
    // ΠΑΡΕ ΤΟ MEDIAN ΑΠΟ ΤΟ dataKey ΤΟΥ LINE
    const median =
      typeof item.value === 'number'
        ? item.value
        : point?.value;
  
    const min = point?.min;
    const max = point?.max;
    const q1 = point?.q1;
    const q3 = point?.q3;
    const q_15=point?.q_15;
    const q_85=point?.q_85;


    const t = point?.t ?? label;
  
    const timeLabel = formatDynamicTime([] as any, Number(t));
  
    const hasRange =
      typeof min === 'number' &&
      typeof max === 'number' &&
      min !== max;


    return (
      <div
        style={{
          background: 'rgba(255,255,255,0.95)',
          border: '1px solid #ddd',
          padding: '6px 8px',
          borderRadius: 4,
          fontSize: 12,
        }}
      >
        {!hasRange && (
          <>
            <div>{`time : ${formatPointTimeRange(point, timeLabel)}`}</div>
            <div>{`value : ${Math.round(median)}`}</div>
          </>
        )}
  
        {hasRange && (
          <>
            <div>{`time : ${formatPointTimeRange(point, timeLabel)}`}</div>
            <div>{`median : ${Math.round(median)}`}</div>
            <div>{`min : ${Math.round(min)}`}</div>
            <div>{`max : ${Math.round(max)}`}</div>
            <div>{`q1 : ${Math.round(q1)}`}</div>
            <div>{`q3 : ${Math.round(q3)}`}</div>
            <div>{`q85 : ${Math.round(q_85)}`}</div>
            <div>{`q15 : ${Math.round(q_15)}`}</div>
          </>
        )}
  
      </div>
    );
  };
  
  
  const HeartRateVariabilityTooltip = ({ active, payload, label }: any) => {
    if (!active || !showHeartRateVariabilityTooltip || !payload || !payload.length) return null;
  
    const item = payload[0];
    const point = item.payload;
  
    // ΠΑΡΕ ΤΟ MEDIAN ΑΠΟ ΤΟ dataKey ΤΟΥ LINE
    const median =
      typeof item.value === 'number'
        ? item.value
        : point?.value;
  
    const min = point?.min;
    const max = point?.max;
    const q1 = point?.q1;
    const q3 = point?.q3;
    const q_15=point?.q_15;
    const q_85=point?.q_85;


    const t = point?.t ?? label;
  
    const timeLabel = formatDynamicTime([] as any, Number(t));
  
    const hasRange =
      typeof min === 'number' &&
      typeof max === 'number' &&
      min !== max;
      

    return (
      <div
        style={{
          background: 'rgba(255,255,255,0.95)',
          border: '1px solid #ddd',
          padding: '6px 8px',
          borderRadius: 4,
          fontSize: 12,
        }}
      >
        {!hasRange && (
          <>
            <div>{`time : ${formatPointTimeRange(point, timeLabel)}`}</div>
            <div>{`value : ${Math.round(median)}`}</div>
          </>
        )}
  
        {hasRange && (
          <>
            <div>{`time : ${formatPointTimeRange(point, timeLabel)}`}</div>
            <div>{`median : ${Math.round(median)}`}</div>
            <div>{`min : ${Math.round(min)}`}</div>
            <div>{`max : ${Math.round(max)}`}</div>
            <div>{`q1 : ${Math.round(q1)}`}</div>
            <div>{`q3 : ${Math.round(q3)}`}</div>
            <div>{`q85 : ${Math.round(q_85)}`}</div>
            <div>{`q15 : ${Math.round(q_15)}`}</div>
          </>
        )}
  
      </div>
    );
  };
  
  const Spo2Tooltip = ({ active, payload, label }: any) => {
    if (!active || !showSpo2Tooltip || !payload || !payload.length) return null;
  
    const item = payload[0];
    const point = item.payload;
  
    const median =
      typeof item.value === 'number'
        ? item.value
        : point?.value;
  
    const min = point?.min;
    const max = point?.max;
  
    const t = point?.t ?? label;
    const confidence = point?.confidence;
    const timeLabel = formatDynamicTime([] as any, Number(t));
  
    const hasRange =
      typeof min === 'number' &&
      typeof max === 'number' &&
      min !== max;

    return (
      <div style={{ background: 'rgba(255,255,255,0.95)', border: '1px solid #ddd', padding: '6px 8px', borderRadius: 4, fontSize: 12 }}>
        {!hasRange && (
          <>
            <div>{`time : ${formatPointTimeRange(point, timeLabel)}`}</div>
            <div>{`value : ${median}%`}</div>
          </>
        )}
  
        {hasRange && (
          <>
            <div>{`time : ${formatPointTimeRange(point, timeLabel)}`}</div>
            <div>{`median : ${median}%`}</div>
            <div>{`min : ${min}%`}</div>
            <div>{`max : ${max}%`}</div>
          </>
        )}
        {confidence != null && typeof confidence === 'number' && (
          <div>{`level of confidence: ${Number(confidence).toFixed(1)}`}</div>
        )}
      </div>
    );
  };
  

  const renderSleepRunTooltip = () => {
    if (!sleepRunTooltip) return null;
    const { run, x, y } = sleepRunTooltip;
    const stage = SLEEP_STAGE_NAMES[run.value] ?? SLEEP_STAGE_LABELS[run.value] ?? String(run.value);
    const startLabel = formatDynamicTime(sleepingBaseData, run.startT);
    const displayEndT = run.endT > run.startT ? run.endT : run.startT + run.minuteCount;
    const endLabel = formatDynamicTime(sleepingBaseData, displayEndT);
    const durationLabel = formatSleepDuration(run.minuteCount);
    return (
      <div
        style={{
          position: 'absolute',
          left: x,
          top: y,
          background: 'rgba(255,255,255,0.95)',
          border: '1px solid #ddd',
          padding: '6px 8px',
          borderRadius: 4,
          fontSize: 12,
          color: '#000',
          pointerEvents: 'none',
          zIndex: 20,
          whiteSpace: 'nowrap',
        }}
      >
        <div>{`stage : ${stage}`}</div>
        <div>{`duration : ${durationLabel}`}</div>
        <div>{`time : ${startLabel} – ${endLabel}`}</div>
      </div>
    );
  };

  const SystolicDiastolicBloodPressureTooltip = ({ active, payload, label }: any) => {
    if (!active || !showSystolic_diastolicBloodPressureTooltip || !payload?.length) return null;
    
    const point = payload[0].payload;
    const t = point?.t ?? label;
    const timeLabel = formatDynamicTime([] as any, Number(t));

    const hasRangeS =
      typeof point.systolic_min === 'number' &&
      typeof point.systolic_max === 'number' &&
      point.systolic_min !== point.systolic_max;

      const hasRangeD =
      typeof point.diastolic_min === 'number' &&
      typeof point.diastolic_max === 'number' &&
      point.diastolic_min !== point.diastolic_max;


    return (
      <div style={{
        background: 'rgba(255,255,255,0.95)', 
        border: '1px solid #ddd', 
        padding: '8px 10px', 
        borderRadius: '6px', 
        fontSize: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}>
        {!hasRangeS && !hasRangeD &&(
          
            (point.systolic !== undefined || point.diastolic !== undefined) && (
              <div>
                <div>
                  {`time : ${formatPointTimeRange(point, timeLabel)}`}
                </div>
              <div>
                <span >Systolic:</span> 
                <span> {point.systolic?.toFixed(0)} mmHg</span>
              </div>
            <div >
              {'-----------------------'}
            </div>
            
            <div>
              <span>Diastolic:</span> 
              <span> {point.diastolic?.toFixed(0)} mmHg</span>
            </div>
          
          </div>)
 
        )}
        {(hasRangeS || hasRangeD) &&(
            (point.systolic !== undefined || point.diastolic !== undefined) && (
              <div>
                 <div>{`time : ${formatPointTimeRange(point, timeLabel)}`}</div>
              <div>
                <span >Systolic:</span> 
                <span> {point.systolic?.toFixed(0)} mmHg</span>
                {point.systolic_min !== undefined && point.systolic_max !== undefined && (
                  <div>
                      Min: <strong>{point.systolic_min.toFixed(0)}</strong> | Max: <strong>{point.systolic_max.toFixed(0)}</strong>
                  </div>
                )}
              </div>
            <div >
              {'-----------------------'}
            </div>
            
            <div>
              <span>Diastolic:</span> 
              <span> {point.diastolic?.toFixed(0)} mmHg</span>
              {point.diastolic_min !== undefined && point.diastolic_max !== undefined && (
                <div>
                  Min: <strong>{point.diastolic_min.toFixed(0)}</strong> | Max: <strong>{point.diastolic_max.toFixed(0)}</strong>
                </div>
              )}
            </div>
          
          </div>)
 
        )}
        </div>
    );
  };

  // Capture Recharts plot area (x-axis scale range) for accurate zoom click mapping at edges
  const zoomBoundsCapture = (boundsRef: React.MutableRefObject<PlotBounds | null>) => (props: any) => {
    const xAxis = Object.values(props.xAxisMap || {})[0] as any;
    if (xAxis?.scale?.range) {
      const r = xAxis.scale.range();
      if (Array.isArray(r) && r.length >= 2) boundsRef.current = { left: r[0], width: r[1] - r[0] };
    }
    return null;
  };

  const MinMaxMarkers = (props: any) => {
    if (getAggregationIntervalMinutesForWindow(effectiveWindowMinutes) === 1) return <g></g>;
    const { data, xAxisMap, yAxisMap } = props;
  
    const xAxis = Object.values(xAxisMap)[0] as any;
    const yAxis = Object.values(yAxisMap)[0] as any;
  
    if (!xAxis || !yAxis) return null;
  
    const xScale = xAxis.scale;
    const yScale = yAxis.scale;
  
    return (
      <g>
        {data.map((d: any, index: number) => {
          if (d.t == null) return null;
  
          const cx = xScale(d.t);
  
          const hasMax = d.max != null;
          const hasMin = d.min != null;
  
          if (!hasMax && !hasMin) return null;
  
          const yMax = hasMax ? yScale(d.max) : null;
          const yMin = hasMin ? yScale(d.min) : null;
  
          return (
            <g key={`hr-mm-${index}`}>
              {/* MAX marker (X) */}
              {(yMax !== null && d.q_85 !== null && d.q_85 !== d.max) && (
                <>
                  <line
                    x1={cx - 3}
                    y1={yMax - 3}
                    x2={cx + 3}
                    y2={yMax + 3}
                    stroke="#000"
                    strokeWidth={1.5}
                    strokeLinecap="round"
                  />
                  <line
                    x1={cx - 3}
                    y1={yMax + 3}
                    x2={cx + 3}
                    y2={yMax - 3}
                    stroke="#000"
                    strokeWidth={1.5}
                    strokeLinecap="round"
                  />
                </>
              )}
  
              {/* MIN marker (—) */}
              {(yMin !== null && d.q_15 !== null && d.q_15 !== d.min)&& (
                <>
                <line
                  x1={cx - 3}
                  y1={yMin - 3}
                  x2={cx + 3}
                  y2={yMin + 3}
                  stroke="#000"
                  strokeWidth={1.5}
                  strokeLinecap="round"
                />
                <line
                  x1={cx - 3}
                  y1={yMin + 3}
                  x2={cx + 3}
                  y2={yMin - 3}
                  stroke="#000"
                  strokeWidth={1.5}
                  strokeLinecap="round"
                />
              </>
              )}
            </g>
          );
        })}
      </g>
    );
  };
  
   const BloodPressureLines = (props: any) => {
    if (getAggregationIntervalMinutesForWindow(effectiveWindowMinutes) === 1) return <g></g>;
    const { data, xAxisMap, yAxisMap } = props;

    const xAxis = Object.values(xAxisMap)[0] as any;
    const yAxis = Object.values(yAxisMap)[0] as any;

    if (!xAxis || !yAxis) return null;

    const xScale = xAxis.scale;
    const yScale = yAxis.scale;

    return (
      <g>
        {data.map((d: any, index: number) => {
          if (d.t == null) return null;

          const systolic = d.systolic != null && !isNaN(Number(d.systolic)) ? Number(d.systolic) : null;
          const diastolic = d.diastolic != null && !isNaN(Number(d.diastolic)) ? Number(d.diastolic) : null;

          if (systolic == null || diastolic == null) return null;

          const cx = xScale(d.t);
          const ySystolic = yScale(systolic);
          const yDiastolic = yScale(diastolic);

          return (
            <line
              key={`bp-vertical-${index}`}
              x1={cx}
              y1={ySystolic}
              x2={cx}
              y2={yDiastolic}
              stroke="#000000"
              strokeWidth={1}
              strokeDasharray="0"
            />
          );
        })}
      </g>
    );
  };


  const MinMaxMarkersforSPO2 = (props: any) => {
    if (getAggregationIntervalMinutesForWindow(effectiveWindowMinutes) === 1) return <g></g>;
    const { data, xAxisMap, yAxisMap } = props;
  
    const xAxis = Object.values(xAxisMap)[0] as any;
    const yAxis = Object.values(yAxisMap)[0] as any;
  
    if (!xAxis || !yAxis) return null;
  
    const xScale = xAxis.scale;
    const yScale = yAxis.scale;
  
    return (
      <g>
        {data.map((d: any, index: number) => {
          if (d.t == null) return null;
  
          const cx = xScale(d.t);
  
          const hasMax = d.max != null;
          const hasMin = d.min != null;
  
          if (!hasMax && !hasMin) return null;
  
          const yMax = hasMax ? yScale(d.max) : null;
          const yMin = hasMin ? yScale(d.min) : null;
  
          return (
            <g key={`hr-mm-${index}`}>
              {/* MAX marker (X) */}
              {(yMax != null) && (
                <>
                  <line
                    x1={cx - 3}
                    y1={yMax - 3}
                    x2={cx + 3}
                    y2={yMax + 3}
                    stroke="#000"
                    strokeWidth={1.5}
                    strokeLinecap="round"
                  />
                  <line
                    x1={cx - 3}
                    y1={yMax + 3}
                    x2={cx + 3}
                    y2={yMax - 3}
                    stroke="#000"
                    strokeWidth={1.5}
                    strokeLinecap="round"
                  />
                </>
              )}
  
              {/* MIN marker (—) */}
              {(yMin != null)&& (
                <>
                <line
                  x1={cx - 3}
                  y1={yMin - 3}
                  x2={cx + 3}
                  y2={yMin + 3}
                  stroke="#000"
                  strokeWidth={1.5}
                  strokeLinecap="round"
                />
                <line
                  x1={cx - 3}
                  y1={yMin + 3}
                  x2={cx + 3}
                  y2={yMin - 3}
                  stroke="#000"
                  strokeWidth={1.5}
                  strokeLinecap="round"
                />
              </>
              )}
            </g>
          );
        })}
      </g>
    );
  };

  const MinMaxMarkersforTEMP = (props: any) => {
    if (getAggregationIntervalMinutesForWindow(effectiveWindowMinutes) === 1) return <g></g>;
    const { data, xAxisMap, yAxisMap } = props;
  
    const xAxis = Object.values(xAxisMap)[0] as any;
    const yAxis = Object.values(yAxisMap)[0] as any;
  
    if (!xAxis || !yAxis) return null;
  
    const xScale = xAxis.scale;
    const yScale = yAxis.scale;
  
    return (
      <g>
        {data.map((d: any, index: number) => {
          if (d.t == null) return null;
  
          const cx = xScale(d.t);
  
          const hasMax = d.max != null;
          const hasMin = d.min != null;
  
          if (hasMax && hasMin && d.max === d.min) return null;
  
          const yMax = hasMax ? yScale(d.max) : null;
          const yMin = hasMin ? yScale(d.min) : null;
          const Xvalue = d.value != null ? yScale(d.value) : null;
          return (
            <g key={`hr-mm-${index}`}>
              {/* MAX marker (X) */}
              {(yMax != null) && (
                <>
                  <line
                    x1={cx - 3}
                    y1={yMax }
                    x2={cx + 3}
                    y2={yMax }
                    stroke="#000"
                    strokeWidth={1.5}
                    strokeLinecap="round"
                  />
                  <line
                    x1={cx }
                    y1={yMax }
                    x2={cx }
                    y2={Xvalue}
                    stroke="#000"
                    strokeWidth={1.5}
                    strokeLinecap="round"
                  />
                </>
              )}
  
              {/* MIN marker (—) */}
              {(yMin != null)&& (
                <>
                <line
                  x1={cx - 3}
                  y1={yMin }
                  x2={cx + 3}
                  y2={yMin }
                  stroke="#000"
                  strokeWidth={1.5}
                  strokeLinecap="round"
                />
                <line
                  x1={cx }
                  y1={yMin }
                  x2={cx }
                  y2={Xvalue }
                  stroke="#000"
                  strokeWidth={1.5}
                  strokeLinecap="round"
                />
              </>
              )}
            </g>
          );
        })}
      </g>
    );
  };

  const QuartileMarkers = (props: any) => {
    if (getAggregationIntervalMinutesForWindow(effectiveWindowMinutes) === 1) return <g></g>;
    const { data, xAxisMap, yAxisMap } = props;
  
    const xAxis = Object.values(xAxisMap)[0] as any;
    const yAxis = Object.values(yAxisMap)[0] as any;
  
    if (!xAxis || !yAxis) return null;
  
    const xScale = xAxis.scale;
    const yScale = yAxis.scale;

    return (
      <g>
        {data.map((d: any, index: number) => {
          if (d.t == null) return null;

          const cx = xScale(d.t);
  
          const yQ1 = d.q1 != null ? yScale(d.q1) : null;
          const yQ3 = d.q3 != null ? yScale(d.q3) : null;
          const yQ15 = d.q_15 != null ? yScale(d.q_15) : null;
          const yQ85 = d.q_85!= null ? yScale(d.q_85) : null;
  
          // Αν δεν έχουμε ούτε q1 ούτε q3, δεν κάνουμε τίποτα
          if (yQ1 == null && yQ3 == null) return null;
  
          return (
            <g key={`quartile-${index}`}>
              {/* Q1 marker */}
              {yQ1 != null && (
                <line
                  x1={cx - 4}
                  y1={yQ1}
                  x2={cx + 4}
                  y2={yQ1}
                  stroke="#000"
                  strokeWidth={1.5}
                  strokeLinecap="round"
                />
              )}
  
              {/* Q3 marker */}
              {yQ3 != null && (
                <line
                  x1={cx - 4}
                  y1={yQ3}
                  x2={cx + 4}
                  y2={yQ3}
                  stroke="#000"
                  strokeWidth={1.5}
                  strokeLinecap="round"
                />
              )}
             {(yQ3 != null && yQ1 != null) && (
              <>
                <line
                  x1={cx - 4}
                  y1={yQ1}
                  x2={cx - 4}
                  y2={yQ3}
                  stroke="#000"
                  strokeWidth={1.5}
                  strokeLinecap="round"
                />

                <line
                  x1={cx + 4}
                  y1={yQ1}
                  x2={cx + 4}
                  y2={yQ3}
                  stroke="#000"
                  strokeWidth={1.5}
                  strokeLinecap="round"
                />
              </>
            )}
            
            {(yQ15 != null && yQ85 != null) && (
              <>
                <line
                  x1={cx}
                  y1={yQ15}
                  x2={cx}
                  y2={yQ1}
                  stroke="#000"
                  strokeWidth={1.5}
                  strokeLinecap="round"
                />
                 <line
                  x1={cx}
                  y1={yQ3}
                  x2={cx}
                  y2={yQ85}
                  stroke="#000"
                  strokeWidth={1.5}
                  strokeLinecap="round"
                />
                <line
                  x1={cx - 4}
                  y1={yQ15}
                  x2={cx + 4}
                  y2={yQ15}
                  stroke="#000"
                  strokeWidth={1.5}
                  strokeLinecap="round"
                />
                <line
                  x1={cx - 4}
                  y1={yQ85}
                  x2={cx + 4}
                  y2={yQ85}
                  stroke="#000"
                  strokeWidth={1.5}
                  strokeLinecap="round"
                />
              </>
            )}
            </g>
          );
        })}
      </g>
    );
  };
  



  const formatDate = (timestamp: string) => {
    const parsed = parseDbTimestamp(timestamp);
    if (!isValidDate(parsed)) return 'Unknown time';
    return format(parsed, 'dd MMM yyyy HH:mm');
  };

  const getTimePosition = (timestamp: string, startTime: Date, endTime: Date) => {
    const eventTime = parseDbTimestamp(timestamp);
    if (!isValidDate(eventTime)) return 0;
    const totalDuration = endTime.getTime() - startTime.getTime();
    if (!Number.isFinite(totalDuration) || totalDuration <= 0) return 0;
    const eventPosition = eventTime.getTime() - startTime.getTime();
    return Math.max(0, Math.min(100, (eventPosition / totalDuration) * 100));
  };

  const clearCriticalEventSelection = useCallback(() => {
    setSelectedEventId(null);
    setClickedEventSnapshot(null);
    setCustomChartWindow(null);
    setSelectedEventTimestamp(null);
    setEventClickLocked(false);
  }, []);

  // Reset zoom functions
  const resetTempZoom = () => {
    clearCriticalEventSelection();
    tempZoomCtl.reset();
  };

  const resetBreathingZoom = () => {
    clearCriticalEventSelection();
    breathingZoomCtl.reset();
  };

  const resetHeartRateZoom = () => {
    clearCriticalEventSelection();
    heartRateZoomCtl.reset();
  };
  const resetHeartRateVariabilityZoom = () => {
    clearCriticalEventSelection();
    heartRateVariabilityZoomCtl.reset();
  };
  const resetSpo2Zoom = () => {
    clearCriticalEventSelection();
    spo2ZoomCtl.reset();
  };
  const resetSystolic_diastolicBloodPressureZoom = () => {
    clearCriticalEventSelection();
    systolic_diastolicBloodPressureZoomCtl.reset();
  };

  const resetSleepZoom = () => {
    clearCriticalEventSelection();
    sleepZoomCtl.reset();
  };



  // const resetBloodPressureZoom = () => {
  //   bloodPressureZoomCtl.reset();
  // };

  // Small lag so meds/critical events follow graph refresh cadence

  // Calculate date range based on selected time range (live updating)
  const dateRange = React.useMemo(() => {
    const baseNow = serverNow || now;
    const current = new Date(Math.floor((baseNow.getTime() - GRAPH_REFRESH_LAG_MS) / 60000) * 60000);
    let startTime: Date;
    let endTime: Date;

    switch (selectedTimeRange) {
      case '1 Hours':
        startTime = subHours(current, 1);
        endTime = current;
        break;
      case '6 Hours':
        startTime = subHours(current, 6);
        endTime = current;
        break;
      case '12 Hours':
        startTime = subHours(current, 12);
        endTime = current;
        break;
      case '24 Hours':
        startTime = subHours(current, 24);
        endTime = current;
        break;
      case '7 Days':
        startTime = subDays(current, 7);
        endTime = current;
        break;
      case '14 Days':
        startTime = subDays(current, 14);
        endTime = current;
        break;
      case '30 Days':
        startTime = subDays(current, 30);
        endTime = current;
        break;
      case 'Custom':
        startTime = customStartDate;
        endTime = customEndDate;
        break;
      default:
        startTime = subHours(current, 1);
        endTime = current;
    }

    return {
      start: format(startTime, 'dd MMM yyyy HH:mm'),
      end: format(endTime, 'dd MMM yyyy HH:mm'),
      startDate: startTime,
      endDate: endTime,
    } as { start: string; end: string; startDate: Date; endDate: Date };
  }, [selectedTimeRange, customStartDate, customEndDate, serverNow, now]);

  const handleTimeRangeSelect = (range: string) => {
    if (range === 'Edit') {
      // When opening the modal, copy current values to temporary state
      setTempCustomStartDate(new Date(customStartDate));
      setTempCustomEndDate(new Date(customEndDate));
      setShowCustomInputs(true);
      setIsDropdownOpen(false);
      return;
    }
    // Full reset as on second click on critical event: clear event selection and zoom so aggregation uses new range
    setSelectedEventId(null);
    setClickedEventSnapshot(null);
    setCustomChartWindow(null);
    setSelectedEventTimestamp(null);
    setSharedZoomRange({ startTime: null, endTime: null });
    setSelectedTimeRange(range);
    setIsDropdownOpen(false);
  };

  const handleCustomDateApply = () => {
    // Copy temporary values to actual state
    setCustomStartDate(new Date(tempCustomStartDate));
    setCustomEndDate(new Date(tempCustomEndDate));
    setSelectedTimeRange('Custom');
    setShowCustomInputs(false);
    // Full reset as on second click on critical event: clear event selection and zoom so aggregation uses new range
    setSelectedEventId(null);
    setClickedEventSnapshot(null);
    setCustomChartWindow(null);
    setSelectedEventTimestamp(null);
    setSharedZoomRange({ startTime: null, endTime: null });
    // Trigger a re-fetch by updating a timestamp that's in the dependencies
    // This will cause the useEffect to run and fetch data with the new custom dates
    setCustomDateApplied(Date.now());
  };

  const handleCustomDateCancel = () => {
    // Reset temporary values to current actual values
    setTempCustomStartDate(new Date(customStartDate));
    setTempCustomEndDate(new Date(customEndDate));
    setShowCustomInputs(false);
  };

  // Handle clicking outside the dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  // (optional) debug hooks could go here

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      localStorage.setItem(TIME_RANGE_STORAGE_KEY, selectedTimeRange);
      if (selectedTimeRange === 'Custom') {
        localStorage.setItem(
          CUSTOM_RANGE_STORAGE_KEY,
          JSON.stringify({
            start: customStartDate.toISOString(),
            end: customEndDate.toISOString(),
          })
        );
      } else {
        localStorage.removeItem(CUSTOM_RANGE_STORAGE_KEY);
      }
    } catch {
      // ignore storage errors
    }
  }, [selectedTimeRange, customStartDate, customEndDate]);

  const timeRangeOptions = [
    { value: '1 Hours', label: '1 Hour', icon: '📅', color: '#f44336' },
    { value: '6 Hours', label: '6 Hours', icon: '📅', color: '#f44336' },
    { value: '12 Hours', label: '12 Hours', icon: '📅', color: '#f44336' },
    { value: '24 Hours', label: '24 Hours', icon: '📅', color: '#f44336' },
    { value: '7 Days', label: '7 Days', icon: '📅', color: '#f44336' },
    { value: '14 Days', label: '14 Days', icon: '📅', color: '#f44336' },
    // { value: '30 Days', label: '30 Days', icon: '📅', color: '#f44336' },
    { value: 'Custom', label: `${format(customStartDate, 'dd MMM HH:mm')} - ${format(customEndDate, 'dd MMM HH:mm')}`, icon: '📅', color: '#f44336' },
    { value: 'Edit', label: 'Edit', icon: '✏️', color: '#ff9800' },
  ];

  // Load critical events (demo/real)
  const [allCriticalEvents, setAllCriticalEvents] = useState<CriticalEvent[]>([]);

  /** Normalize event type aliases so RESP/breathing names are treated the same in UI filters/counters. */
  const normalizeEventTypeForUi = (eventType: string): string => {
    const t = (eventType || '').toLowerCase().trim();
    if (isFallEvent(eventType)) return normalizeFallEventTypeForUi(eventType);
    if (isAtrialFibrillationEvent(eventType)) return normalizeAtrialFibrillationEventTypeForUi(eventType);
    if (t.startsWith('respiratory_rate_')) return t.replace('respiratory_rate_', 'breathing_');
    if (t.startsWith('resp_')) return t.replace('resp_', 'breathing_');
    if (t.startsWith('breath_')) return t.replace('breath_', 'breathing_');
    return t;
  };

  /** Count Warnings and Critical events per vital category from all loaded events (independent of graph filter). */
  const criticalCountsByCategory = (() => {
    const yellowTypes = /medium|stage1|stage2/;
    const categories: Record<string, { warnings: number; critical: number }> = {
      'Heart Rate': { warnings: 0, critical: 0 },
      'HRV': { warnings: 0, critical: 0 },
      'Blood Pressure': { warnings: 0, critical: 0 },
      'SpO2': { warnings: 0, critical: 0 },
      'RESP': { warnings: 0, critical: 0 },
      'Skin Temperature': { warnings: 0, critical: 0 },
      'Activity': { warnings: 0, critical: 0 },
    };
    (allCriticalEvents || []).forEach((e) => {
      const cat = getEventCategory(e.type);
      const rec = categories[cat];
      if (!rec) return;
      const t = (e.type || '').toLowerCase();
      if (yellowTypes.test(t)) rec.warnings++;
      else rec.critical++;
    });
    return categories;
  })();
  
  // Load medications
  const [allMedications, setAllMedications] = useState<Medication[]>([]);
  useEffect(() => {
    if (isSessionInvalidated()) return;
    // In demo mode, don't require patId - load demo critical events
    if (operationMode !== 'demo' && !patId) { setAllCriticalEvents([]); return; }
    let cancelled = false;
    const load = async () => {
      try {
        console.log('[FE] critical events fetch', { start: dateRange.startDate.toISOString(), end: dateRange.endDate.toISOString(), patId: patId || null, mode: operationMode });
        const rows = operationMode === 'demo'
          ? await getDemoCriticalEventsRange(dateRange.startDate.toISOString(), dateRange.endDate.toISOString(), patId || undefined)
          : await getCriticalEventsRange(dateRange.startDate.toISOString(), dateRange.endDate.toISOString(), patId || undefined);
        if (cancelled) return;
        const mapped: CriticalEvent[] = (rows || []).flatMap((r: any) => {
          if (!r?.timestamp || !r?.type) return [];
          const parsed = parseValidTimestamp(r.timestamp);
          if (!parsed) return [];
          const timestampIso = parsed.toISOString();
          const type = String(r.type).trim();
          if (!type) return [];
          return [{ id: `${timestampIso}-${type}`, timestamp: timestampIso, type, value: r.value != null ? String(r.value) : '', position: 'above' }];
        });
        setAllCriticalEvents(mapped);
      } catch (_) {
        // Preserve previous events on error to avoid empty flashes
      }
    };
    load();
    return () => { cancelled = true; };
  }, [operationMode, dateRange.startDate, dateRange.endDate, patId]);

  // Load medications
  useEffect(() => {
    if (isSessionInvalidated()) return;
    if (!patId && operationMode !== 'demo') { setAllMedications([]); return; }
    let cancelled = false;
    const load = async () => {
      try {
        console.log('[FE] medications fetch for patId:', patId, 'mode:', operationMode);
        
        if (operationMode === 'demo') {
          // Generate random demo medications
          const demoMedications: Medication[] = [
            {
              med_id: 1,
              pat_id: patId ? parseInt(patId) : 999,
              name: 'Aspirin',
              starting_date: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
              ending_date: new Date(Date.now() + 15 * 24 * 60 * 60 * 1000).toISOString(),
              dosage: '100mg',
              frequency: 'Once daily',
              notes: 'Take with food to prevent stomach irritation'
            },
            {
              med_id: 2,
              pat_id: patId ? parseInt(patId) : 999,
              name: 'Metformin',
              starting_date: new Date(Date.now() - 45 * 24 * 60 * 60 * 1000).toISOString(),
              ending_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
              dosage: '500mg',
              frequency: 'Twice daily',
              notes: 'Monitor blood glucose levels regularly'
            },
            {
              med_id: 3,
              pat_id: patId ? parseInt(patId) : 999,
              name: 'Lisinopril',
              starting_date: new Date(Date.now() - 20 * 24 * 60 * 60 * 1000).toISOString(),
              ending_date: new Date(Date.now() + 60 * 24 * 60 * 60 * 1000).toISOString(),
              dosage: '10mg',
              frequency: 'Once daily',
              notes: 'Monitor blood pressure and kidney function'
            },
            {
              med_id: 4,
              pat_id: patId ? parseInt(patId) : 999,
              name: 'Atorvastatin',
              starting_date: new Date(Date.now() - 60 * 24 * 60 * 60 * 1000).toISOString(),
              ending_date: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString(),
              dosage: '20mg',
              frequency: 'Once daily',
              notes: 'Take in the evening, monitor liver function'
            },
            {
              med_id: 5,
              pat_id: patId ? parseInt(patId) : 999,
              name: 'Omeprazole',
              starting_date: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString(),
              ending_date: new Date(Date.now() + 20 * 24 * 60 * 60 * 1000).toISOString(),
              dosage: '20mg',
              frequency: 'Once daily',
              notes: 'Take 30 minutes before breakfast'
            }
          ];
          
          if (cancelled) return;
          setAllMedications(demoMedications);
        } else {
          const medications = await loadMedications(patId);
          if (cancelled) return;
          setAllMedications(medications);
        }
      } catch (error) {
        console.error('Error loading medications:', error);
        if (cancelled) return;
        setAllMedications([]);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [patId, dateRange.startDate, dateRange.endDate, operationMode]);

  // Listen for custom events to trigger immediate critical events refresh
  useEffect(() => {
    const handleRefreshCriticalEvents = () => {
      if (isSessionInvalidated()) return;
      console.log('Received refreshCriticalEvents event, triggering immediate critical events fetch...');
      // Trigger immediate fetch of critical events
      const loadCriticalEvents = async () => {
        try {
          console.log('[FE] immediate critical events fetch', { start: dateRange.startDate.toISOString(), end: dateRange.endDate.toISOString(), patId: patId || null, mode: operationMode });
          const rows = operationMode === 'demo'
            ? await getDemoCriticalEventsRange(dateRange.startDate.toISOString(), dateRange.endDate.toISOString(), patId || undefined)
            : await getCriticalEventsRange(dateRange.startDate.toISOString(), dateRange.endDate.toISOString(), patId || undefined);
          const mapped: CriticalEvent[] = (rows || []).flatMap((r: any) => {
            if (!r?.timestamp || !r?.type) return [];
            const parsed = parseValidTimestamp(r.timestamp);
            if (!parsed) return [];
            const timestampIso = parsed.toISOString();
            const type = String(r.type).trim();
            if (!type) return [];
            return [{ id: `${timestampIso}-${type}`, timestamp: timestampIso, type, value: r.value != null ? String(r.value) : '', position: 'above' }];
          });
          setAllCriticalEvents(mapped);
          console.log('[FE] critical events refreshed successfully:', mapped.length, 'events');
        } catch (error) {
          console.error('Error refreshing critical events:', error);
        }
      };
      loadCriticalEvents();
    };

    // Add event listener
    window.addEventListener('refreshCriticalEvents', handleRefreshCriticalEvents);

    // Cleanup
    return () => {
      window.removeEventListener('refreshCriticalEvents', handleRefreshCriticalEvents);
    };
  }, [dateRange.startDate, dateRange.endDate, operationMode, patId]);

  // Critical Events Timeline Zoom State
  type TimelineZoomState = {
    startTime: number | null; // timestamp in ms
    endTime: number | null; // timestamp in ms
    refAreaStart: number | null; // percentage (0-100)
    refAreaEnd: number | null; // percentage (0-100)
  };
  type ActivityZoomState = {
    startTime: number | null;
    endTime: number | null;
    refAreaStart: number | null;
    refAreaEnd: number | null;
  };
  const [timelineZoom, setTimelineZoom] = useState<TimelineZoomState>({
    startTime: null,
    endTime: null,
    refAreaStart: null,
    refAreaEnd: null,
  });
  const [activityZoom, setActivityZoom] = useState<ActivityZoomState>({
    startTime: null,
    endTime: null,
    refAreaStart: null,
    refAreaEnd: null,
  });

  // Filter events based on zoom
  const visibleCriticalEvents: CriticalEvent[] = React.useMemo(() => {
    if (timelineZoom.startTime === null || timelineZoom.endTime === null) {
      return allCriticalEvents;
    }
    return allCriticalEvents.filter(ev => {
      const parsed = parseDbTimestamp(ev.timestamp);
      if (!isValidDate(parsed)) return false;
      const evTime = parsed.getTime();
      return evTime >= timelineZoom.startTime! && evTime <= timelineZoom.endTime!;
    });
  }, [allCriticalEvents, timelineZoom.startTime, timelineZoom.endTime]);

  const [hoveredEventId, setHoveredEventId] = useState<string | null>(null);
  
  // Timeline zoom handlers
  const timelinePanelRef = React.useRef<HTMLDivElement>(null);
  const medicationsTimelineRef = React.useRef<HTMLDivElement>(null);
  const activityTimelineRef = React.useRef<HTMLDivElement>(null);
  
  const TIMELINE_LEFT_GUTTER_PX = 25;
  const TIMELINE_RIGHT_GUTTER_PX = 25;

  const getTimelineLeftCss = (pct: number): string => {
    const clamped = Math.max(0, Math.min(100, pct));
    return `calc(${TIMELINE_LEFT_GUTTER_PX}px + (100% - ${TIMELINE_LEFT_GUTTER_PX + TIMELINE_RIGHT_GUTTER_PX}px) * ${clamped / 100})`;
  };

  const getTimelineWidthCss = (startPct: number, endPct: number): string => {
    const start = Math.max(0, Math.min(100, startPct));
    const end = Math.max(0, Math.min(100, endPct));
    const span = Math.max(0, end - start);
    return `calc((100% - ${TIMELINE_LEFT_GUTTER_PX + TIMELINE_RIGHT_GUTTER_PX}px) * ${span / 100})`;
  };

  const getActivityTimelineLeftCss = (pct: number): string => {
    const clamped = Math.max(0, Math.min(100, pct));
    return `calc(${ACTIVITY_TIMELINE_LEFT_GUTTER_PX}px + (100% - ${ACTIVITY_TIMELINE_LEFT_GUTTER_PX + ACTIVITY_TIMELINE_RIGHT_GUTTER_PX}px) * ${clamped / 100})`;
  };

  const getActivityTimelineWidthCss = (startPct: number, endPct: number): string => {
    const start = Math.max(0, Math.min(100, startPct));
    const end = Math.max(0, Math.min(100, endPct));
    const span = Math.max(0, end - start);
    return `calc((100% - ${ACTIVITY_TIMELINE_LEFT_GUTTER_PX + ACTIVITY_TIMELINE_RIGHT_GUTTER_PX}px) * ${span / 100})`;
  };

  const getTimelinePositionFromMouse = (
    e: React.MouseEvent<HTMLElement>,
    ref: React.RefObject<HTMLElement>,
    paddingLeft: number = TIMELINE_LEFT_GUTTER_PX,
    paddingRight: number = TIMELINE_RIGHT_GUTTER_PX
  ): number | null => {
    if (!ref.current) return null;
    const rect = ref.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const width = rect.width;
    const contentWidth = Math.max(1, width - (paddingLeft + paddingRight));
    const relativeX = x - paddingLeft;
    if (relativeX < -paddingLeft || relativeX > contentWidth + paddingRight) return null;
    const clampedX = Math.max(0, Math.min(contentWidth, relativeX));
    return (clampedX / contentWidth) * 100;
  };

  const getTimestampFromPercentage = (pct: number, startMs: number, endMs: number): number => {
    const start = startMs;
    const end = endMs;
    return start + (pct / 100) * (end - start);
  };

  const handleTimelineMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    // Don't start zoom if clicking on interactive elements
    const target = e.target as HTMLElement;
    if (target.closest('.timeline-event, .timeline-label, button, [data-event-filter], .panel-title')) return;
    
    const pct = getTimelinePositionFromMouse(e, timelinePanelRef);
    if (pct === null) return;
    setTimelineZoom(prev => ({ ...prev, refAreaStart: pct, refAreaEnd: pct }));
  };

  const handleTimelineMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (timelineZoom.refAreaStart === null) return;
    const pct = getTimelinePositionFromMouse(e, timelinePanelRef);
    if (pct === null) return;
    setTimelineZoom(prev => ({ ...prev, refAreaEnd: pct }));
  };
  
  const handleMedicationsTimelineMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    const pct = getTimelinePositionFromMouse(e, medicationsTimelineRef, 0, 0);
    if (pct === null) return;
    setTimelineZoom(prev => ({ ...prev, refAreaStart: pct, refAreaEnd: pct }));
  };

  const handleMedicationsTimelineMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (timelineZoom.refAreaStart === null) return;
    const pct = getTimelinePositionFromMouse(e, medicationsTimelineRef, 0, 0);
    if (pct === null) return;
    setTimelineZoom(prev => ({ ...prev, refAreaEnd: pct }));
  };

  const handleActivityTimelineMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    const pct = getTimelinePositionFromMouse(e, activityTimelineRef, ACTIVITY_TIMELINE_LEFT_GUTTER_PX, ACTIVITY_TIMELINE_RIGHT_GUTTER_PX);
    if (pct === null) return;
    setActivityZoom(prev => ({ ...prev, refAreaStart: pct, refAreaEnd: pct }));
  };

  const handleActivityTimelineMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (activityZoom.refAreaStart === null) return;
    const pct = getTimelinePositionFromMouse(e, activityTimelineRef, ACTIVITY_TIMELINE_LEFT_GUTTER_PX, ACTIVITY_TIMELINE_RIGHT_GUTTER_PX);
    if (pct === null) return;
    setActivityZoom(prev => ({ ...prev, refAreaEnd: pct }));
  };

  const handleActivityTimelineMouseUp = () => {
    if (activityZoom.refAreaStart === null || activityZoom.refAreaEnd === null) {
      setActivityZoom(prev => ({ ...prev, refAreaStart: null, refAreaEnd: null }));
      return;
    }

    let startPct = activityZoom.refAreaStart;
    let endPct = activityZoom.refAreaEnd;
    if (startPct === endPct) {
      setActivityZoom(prev => ({ ...prev, refAreaStart: null, refAreaEnd: null }));
      return;
    }
    if (startPct > endPct) {
      const tmp = startPct;
      startPct = endPct;
      endPct = tmp;
    }

    const visibleStartMs = activityZoom.startTime ?? dateRange.startDate.getTime();
    const visibleEndMs = activityZoom.endTime ?? dateRange.endDate.getTime();
    const startTime = getTimestampFromPercentage(startPct, visibleStartMs, visibleEndMs);
    const endTime = getTimestampFromPercentage(endPct, visibleStartMs, visibleEndMs);

    setSharedZoomRange({
      startTime: new Date(startTime).toISOString(),
      endTime: new Date(endTime).toISOString(),
    });

    setActivityZoom({
      startTime,
      endTime,
      refAreaStart: null,
      refAreaEnd: null,
    });
  };

  const handleTimelineMouseUp = () => {
    if (timelineZoom.refAreaStart === null || timelineZoom.refAreaEnd === null) {
      setTimelineZoom(prev => ({ ...prev, refAreaStart: null, refAreaEnd: null }));
      return;
    }
    
    let startPct = timelineZoom.refAreaStart;
    let endPct = timelineZoom.refAreaEnd;
    
    if (startPct === endPct) {
      // Clear selection if single point
      setTimelineZoom(prev => ({ ...prev, refAreaStart: null, refAreaEnd: null }));
      return;
    }
    
    if (startPct > endPct) {
      const tmp = startPct;
      startPct = endPct;
      endPct = tmp;
    }
    
    // Map drag percentages to the currently visible timeline window
    // so repeated zooms keep event positions aligned with each zoom level.
    const visibleStartMs = timelineZoom.startTime ?? dateRange.startDate.getTime();
    const visibleEndMs = timelineZoom.endTime ?? dateRange.endDate.getTime();
    const startTime = getTimestampFromPercentage(startPct, visibleStartMs, visibleEndMs);
    const endTime = getTimestampFromPercentage(endPct, visibleStartMs, visibleEndMs);
    
    // Update shared zoom range when timeline zoom changes
    setSharedZoomRange({
      startTime: new Date(startTime).toISOString(),
      endTime: new Date(endTime).toISOString(),
    });
    
    setTimelineZoom({
      startTime,
      endTime,
      refAreaStart: null,
      refAreaEnd: null,
    });
  };

  const resetTimelineZoom = () => {
    clearCriticalEventSelection();
    // Reset shared zoom range when timeline zoom is reset
    setSharedZoomRange({
      startTime: null,
      endTime: null,
    });
    
    setTimelineZoom({
      startTime: null,
      endTime: null,
      refAreaStart: null,
      refAreaEnd: null,
    });
  };

  const resetActivityTimelineZoom = () => {
    clearCriticalEventSelection();
    setSharedZoomRange({
      startTime: null,
      endTime: null,
    });
    setActivityZoom({
      startTime: null,
      endTime: null,
      refAreaStart: null,
      refAreaEnd: null,
    });
  };
  
  // Sync timeline zoom with shared zoom range (when graphs zoom changes)
  useEffect(() => {
    if (sharedZoomRange.startTime && sharedZoomRange.endTime) {
      const startTime = new Date(sharedZoomRange.startTime).getTime();
      const endTime = new Date(sharedZoomRange.endTime).getTime();

      setTimelineZoom(prev => {
        if (prev.startTime === startTime && prev.endTime === endTime) return prev;
        return { ...prev, startTime, endTime };
      });
    } else if (sharedZoomRange.startTime === null && sharedZoomRange.endTime === null) {
      setTimelineZoom(prev => {
        if (prev.startTime === null && prev.endTime === null) return prev;
        return { ...prev, startTime: null, endTime: null };
      });
    }
  }, [sharedZoomRange.startTime, sharedZoomRange.endTime]);

  useEffect(() => {
    if (sharedZoomRange.startTime && sharedZoomRange.endTime) {
      const startTime = new Date(sharedZoomRange.startTime).getTime();
      const endTime = new Date(sharedZoomRange.endTime).getTime();

      setActivityZoom(prev => {
        if (prev.startTime === startTime && prev.endTime === endTime) return prev;
        return { ...prev, startTime, endTime };
      });
    } else if (sharedZoomRange.startTime === null && sharedZoomRange.endTime === null) {
      setActivityZoom(prev => {
        if (prev.startTime === null && prev.endTime === null) return prev;
        return { ...prev, startTime: null, endTime: null };
      });
    }
  }, [sharedZoomRange.startTime, sharedZoomRange.endTime]);

  const activityTimelineData = useMemo(() => {
    const startMs = (activityZoom.startTime ?? dateRange.startDate.getTime());
    const endMs = (activityZoom.endTime ?? dateRange.endDate.getTime());
    if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs <= startMs) {
      return {
        startMs: dateRange.startDate.getTime(),
        endMs: dateRange.endDate.getTime(),
        segments: [] as ActivitySegment[],
        hasAnyActivityData: false,
        summaryMinutes: { still: 0, walking: 0, jogging: 0, sleep: 0 },
      };
    }

    const points: ActivityPoint[] = (Array.isArray(activityTimelineRows) ? activityTimelineRows : [])
      .map((row) => {
        const tsRaw = row?.timestamp ?? row?.time ?? row?.created_at ?? row?.createdAt ?? row?.ts;
        if (!tsRaw) return null;
        const tsMs = parseDbTimestamp(String(tsRaw)).getTime();
        if (!Number.isFinite(tsMs)) return null;
        const rawActivity = row?.activity == null ? null : String(row.activity);
        const rawIntensity = row?.activ_intensity ?? row?.activity_intensity ?? null;
        const activity = normalizeActivityState(rawActivity, rawIntensity);
        return { tsMs, activity, rawActivity, rawIntensity };
      })
      .filter((point): point is ActivityPoint => point != null)
      .sort((a, b) => a.tsMs - b.tsMs);

    if (points.length === 0) {
      return {
        startMs,
        endMs,
        segments: [] as ActivitySegment[],
        hasAnyActivityData: false,
        summaryMinutes: { still: 0, walking: 0, jogging: 0, sleep: 0 },
      };
    }

    const floorToMinuteMs = (tsMs: number): number => {
      const d = new Date(tsMs);
      d.setSeconds(0, 0);
      return d.getTime();
    };

    const ACTIVITY_MINUTE_MS = 60_000;
    const pointsInWindow = points.filter((p) => p.tsMs >= startMs && p.tsMs <= endMs);
    const minuteBuckets = new Map<number, ActivityPoint>();
    for (const point of pointsInWindow) {
      const minuteStart = floorToMinuteMs(point.tsMs);
      minuteBuckets.set(minuteStart, {
        tsMs: minuteStart,
        activity: point.activity,
        rawActivity: point.rawActivity,
        rawIntensity: point.rawIntensity,
      });
    }

    const activityMinuteSegments: ActivitySegment[] = [];
    for (const minuteStart of Array.from(minuteBuckets.keys()).sort((a, b) => a - b)) {
      const point = minuteBuckets.get(minuteStart)!;
      const segStart = Math.max(startMs, minuteStart);
      const segEnd = Math.min(endMs, minuteStart + ACTIVITY_MINUTE_MS);
      if (segEnd <= segStart) continue;
      activityMinuteSegments.push({
        startMs: segStart,
        endMs: segEnd,
        activity: point.activity,
        rawActivity: point.rawActivity,
        rawIntensity: point.rawIntensity,
      });
    }

    const segments: ActivitySegment[] = [];
    let gapCursor = startMs;
    for (const seg of activityMinuteSegments) {
      if (seg.startMs > gapCursor) {
        segments.push({
          startMs: gapCursor,
          endMs: seg.startMs,
          activity: null,
          rawActivity: null,
          rawIntensity: null,
        });
      }
      segments.push(seg);
      gapCursor = Math.max(gapCursor, seg.endMs);
    }
    if (gapCursor < endMs) {
      segments.push({
        startMs: gapCursor,
        endMs: endMs,
        activity: null,
        rawActivity: null,
        rawIntensity: null,
      });
    }

    const merged: ActivitySegment[] = [];
    for (let i = 0; i < segments.length; i++) {
      const current = segments[i];
      const previous = merged[merged.length - 1];
      const canMerge =
        previous &&
        previous.endMs === current.startMs &&
        (isSleepActivitySegment(previous) && isSleepActivitySegment(current)
          ? true
          : previous.activity === current.activity &&
            previous.rawActivity === current.rawActivity &&
            previous.rawIntensity === current.rawIntensity);
      if (canMerge) {
        previous!.endMs = current.endMs;
      } else {
        merged.push({ ...current });
      }
    }

    const summaryMinutes = { still: 0, walking: 0, jogging: 0, sleep: 0 };
    let hasAnyActivityData = false;
    for (const point of pointsInWindow) {
      if (!point.activity) continue;
      summaryMinutes[point.activity] += 1;
      hasAnyActivityData = true;
    }

    return { startMs, endMs, segments: merged, hasAnyActivityData, summaryMinutes };
  }, [activityTimelineRows, activityZoom.startTime, activityZoom.endTime, dateRange.startDate, dateRange.endDate]);

  // Global zoom reset (Escape key + double-click on charts)
  const isZoomActive = !!(sharedZoomRange.startTime && sharedZoomRange.endTime);
  useEffect(() => {
    if (!isZoomActive) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !(e.target as HTMLElement)?.closest('input, textarea, [contenteditable="true"]')) {
        e.preventDefault();
        resetTimelineZoom();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isZoomActive]);
  
  // State for patient information display
  const [patientName, setPatientName] = useState<string>('');

  // Fetch patient information when patId changes
  useEffect(() => {
    const fetchPatientInfo = async () => {
      if (operationMode === 'demo') {
        // In demo mode, always show "Demo User"
        setPatientName('Demo User');
        return;
      }
      
      if (patId && String(patId).trim()) {
        try {
          const response = await authenticatedFetch(`${apiUrl('/patient-info')}?pat_id=${patId}`);
          if (response.ok) {
            const raw = await response.text();
            let patientData: any = null;
            try {
              patientData = raw ? JSON.parse(raw) : null;
            } catch {
              patientData = null;
            }
            if (patientData && patientData.first_name && patientData.last_name) {
              setPatientName(`${patientData.first_name} ${patientData.last_name}`);
            } else {
              setPatientName(`Patient ID: ${patId}`);
            }
          } else {
            setPatientName(`Patient ID: ${patId}`);
          }
        } catch (error) {
          console.error('Error fetching patient info:', error);
          setPatientName(`Patient ID: ${patId}`);
        }
      } else {
        setPatientName('');
      }
    };

    fetchPatientInfo();
  }, [patId, operationMode]);

  return (
    <div className="dashboard">
      {/* Header */}
      <header className="header">
        <div className="header-left" style={{ display: 'flex', alignItems: 'center' }}>
          <button className="hamburger-btn" onClick={() => setIsSidebarOpen(true)} aria-label="Open menu">☰</button>
        </div>
        <div className="header-title-wrap" style={{ flex: 1, display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}>
          <h1 style={{ marginLeft: '12px' }}>
            {userRole === 'doctor' && !isDemo ? patientName : 'Demo User'}
          </h1>
        </div>

        <div className="header-actions" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        {isDemo && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <button
              className="demo-generate-btn"
              onClick={async () => {
                try {
                  console.log('Generating demo data...');
                  // Clear in-memory chart state immediately so stale vitals disappear
                  setDbTemperature([]);
                  setDbBreathing([]);
                  setDbHeartRate([]);
                  setDbHeartRateVariability([]);
                  setDbSpo2([]);
                  setDbRespirationRate([]);
                  setDbSystolic_diastolicBloodPressure([]);
                  setDbSystolicBloodPressure([]);
                  setDbDiastolicBloodPressure([]);
                  setDbSleep([]);
                  setActivityTimelineRows([]);
                  setRawOutOfRangeStats(null);
                  setRawCurrentStats(null);
                  rawVitalsRowsRef.current = [];
                  reaggregateVitalsRef.current = null;
                  setSharedZoomRange({ startTime: null, endTime: null });
                  setEcgRawSamples([]);
                  setEcg2RawSamples([]);
                  setSelectedEventTimestamp(null);
                  setSelectedEventId(null);
                  setClickedEventSnapshot(null);
                  setCustomChartWindow(null);

                  const response = await fetch(apiUrl('/generate-demo-data'), {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ hours: 24 })
                  });
                  const data = await response.json();
                  if (data.success) {
                    console.log('Demo data generated successfully!', data.purged);
                    window.dispatchEvent(new CustomEvent('refreshDemoCharts'));
                    window.dispatchEvent(new CustomEvent('refreshCriticalEvents'));
                  } else {
                    console.error('Error generating demo data:', data.error);
                  }
                } catch (error) {
                  console.error('Network error:', error);
                }
              }}
              style={{
                background: 'rgba(156, 39, 176, 0.8)',
                border: '2px solid rgba(156, 39, 176, 1)',
                borderRadius: '8px',
                padding: '8px 12px',
                color: 'white',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                textShadow: '1px 1px 2px rgba(0,0,0,0.3)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(156, 39, 176, 1)';
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.2)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(156, 39, 176, 0.8)';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <span>🎯</span>
              <span>Generate Demo Data</span>
            </button>
          </div>
        )}
        <div className="header-actions-inner" style={{ display: 'flex', alignItems: 'center', gap: '35px', marginRight: '20px' }}>
          {/* Thresholds button (right) - for doctors (demo + real) */}
          {userRole === 'doctor' && (
            <button
              className="thresholds-btn"
              onClick={() => onNavigate('thresholds')}
              style={{
                background: 'rgba(255, 255, 255, 0.2)',
                border: '2px solid rgba(255, 255, 255, 0.4)',
                borderRadius: '8px',
                padding: '8px 12px',
                color: 'white',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                textShadow: '1px 1px 2px rgba(0,0,0,0.3)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)';
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.2)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <span>⚙️</span>
              <span>Thresholds</span>
            </button>
          )}
          <div className="date-range-container" ref={dropdownRef}>
            <div 
              className="date-range" 
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            >
              <span className="date-range-label">{dateRange.start} - {dateRange.end}</span>
              <span className={`dropdown-arrow ${isDropdownOpen ? 'open' : ''}`}>▼</span>
            </div>
            {isDropdownOpen && (
              <div className="date-dropdown">
                {timeRangeOptions.map((option) => (
                  <div
                    key={option.value}
                    className={`dropdown-option ${selectedTimeRange === option.value ? 'selected' : ''}`}
                    onClick={() => handleTimeRangeSelect(option.value)}
                  >
                    <span className="option-icon" style={{ color: option.color }}>{option.icon}</span>
                    <span className="option-label">{option.label}</span>
                    {selectedTimeRange === option.value && <div className="selected-indicator"></div>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        </div>
        
        {/* Custom Date Range Modal */}
        {showCustomInputs && (
          <div className="modal-backdrop" onClick={handleCustomDateCancel}>
            <div className="modal-card" onClick={(e) => e.stopPropagation()}>
              <div className="section-title">Custom Date Range</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, color: '#333' }}>
                    Start Date & Time:
                  </label>
                  <input
                    type="datetime-local"
                    value={(() => {
                      const date = new Date(tempCustomStartDate);
                      const year = date.getFullYear();
                      const month = String(date.getMonth() + 1).padStart(2, '0');
                      const day = String(date.getDate()).padStart(2, '0');
                      const hours = String(date.getHours()).padStart(2, '0');
                      const minutes = String(date.getMinutes()).padStart(2, '0');
                      return `${year}-${month}-${day}T${hours}:${minutes}`;
                    })()}
                    onChange={(e) => {
                      const newDate = new Date(e.target.value);
                      setTempCustomStartDate(newDate);
                    }}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #ddd',
                      borderRadius: '6px',
                      fontSize: '14px'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, color: '#333' }}>
                    End Date & Time:
                  </label>
                  <input
                    type="datetime-local"
                    value={(() => {
                      const date = new Date(tempCustomEndDate);
                      const year = date.getFullYear();
                      const month = String(date.getMonth() + 1).padStart(2, '0');
                      const day = String(date.getDate()).padStart(2, '0');
                      const hours = String(date.getHours()).padStart(2, '0');
                      const minutes = String(date.getMinutes()).padStart(2, '0');
                      return `${year}-${month}-${day}T${hours}:${minutes}`;
                    })()}
                    onChange={(e) => {
                      const newDate = new Date(e.target.value);
                      setTempCustomEndDate(newDate);
                    }}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #ddd',
                      borderRadius: '6px',
                      fontSize: '14px'
                    }}
                  />
                </div>
                <div className="modal-actions">
                  <button className="btn-secondary" onClick={handleCustomDateCancel}>
                    Cancel
                  </button>
                  <button className="btn-primary" onClick={handleCustomDateApply}>
                    Apply
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </header>

      {/* Sidebar Drawer */}
      <div className={`sidebar ${isSidebarOpen ? 'open' : ''}`} role="navigation" aria-label="Main menu">
        <ul className="sidebar-menu">
          <li
            onClick={() => {
              setIsSidebarOpen(false);
              if (userRole === 'doctor' && !isDemo) {
                onNavigate('patients');
              } else {
                onNavigate('dashboard');
              }
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
          >
            <span className="mi">🏠</span><span>Overview</span>
          </li>
          <li onClick={() => { setIsSidebarOpen(false); onNavigate('history'); }}><span className="mi">🕒</span><span>History</span></li>
          {userRole === 'doctor' && !isDemo && (
            <li onClick={() => { setIsSidebarOpen(false); onNavigate('timeline'); }}><span className="mi">📈</span><span>Timeline</span></li>
          )}
          <li onClick={() => { setIsSidebarOpen(false); onNavigate('settings'); }}><span className="mi">⚙️</span><span>Settings</span></li>
          {userRole === 'doctor' && (
            <li onClick={() => { setIsSidebarOpen(false); onNavigate('consent'); }}><span className="mi">📝</span><span>Consent</span></li>
          )}
          <li onClick={() => { setIsSidebarOpen(false); onLogout(); }}><span className="mi">↪</span><span>Logout</span></li>
        </ul>
        <div className="sidebar-footer">
          <div className="sidebar-brand"></div>
        </div>
      </div>
      <div className={`sidebar-backdrop ${isSidebarOpen ? 'show' : ''}`} onClick={() => setIsSidebarOpen(false)}></div>

      {/* Main Content */}
      {!patId && userRole === 'doctor' && !isDemo ? (
        <div className="main-content" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '80vh' }}>
          <button
            onClick={() => onNavigate('patients')}
            style={{
              background: '#1976d2',
              color: 'white',
              fontSize: 32,
              fontWeight: 700,
              padding: '20px 40px',
              border: 'none',
              borderRadius: 12,
              cursor: 'pointer',
              boxShadow: '0 8px 25px rgba(25, 118, 210, 0.3)',
              transition: 'all 0.3s ease',
              textAlign: 'center',
              minWidth: '300px'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 12px 35px rgba(25, 118, 210, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 8px 25px rgba(25, 118, 210, 0.3)';
            }}
          >
            Select a patient
          </button>
        </div>
      ) : !patId && userRole === 'patient' ? (
        <div className="main-content" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '80vh' }}>
          <div style={{ textAlign: 'center', color: '#666', fontSize: 18 }}>
            Loading your data...
          </div>
        </div>
      ) : (
      <div className="main-content" style={{ display: 'flex', columnGap: 16 }}>
        {/* Left Column - Historical Data */}
        <div className="left-column" style={{ flex: isPhoneLayout ? '0 0 60%' : 1, width: isPhoneLayout ? '60%' : undefined, minWidth: 0 }}>
          {/* Critical Events Timeline */}
            <div 
              className="panel" 
              ref={timelinePanelRef}
              style={{ position: 'relative', cursor: timelineZoom.refAreaStart !== null ? 'crosshair' : 'default'  ,height: '300px',}}
              onMouseDown={handleTimelineMouseDown}
              onMouseMove={handleTimelineMouseMove}
              onMouseUp={handleTimelineMouseUp}
              onMouseLeave={handleTimelineMouseUp}
            >
            {/* Zoom selection area - spans full panel height */}
            {timelineZoom.refAreaStart !== null && timelineZoom.refAreaEnd !== null && (() => {
              const startPct = Math.min(timelineZoom.refAreaStart, timelineZoom.refAreaEnd);
              const endPct = Math.max(timelineZoom.refAreaStart, timelineZoom.refAreaEnd);
              const left = getTimelineLeftCss(startPct);
              const width = getTimelineWidthCss(startPct, endPct);
              return (
                <div
                  style={{
                    position: 'absolute',
                    left,
                    width,
                    top: 20,
                    bottom: 20,
                    background: 'rgba(136, 132, 216, 0.2)',
                    border: '1px solid rgba(136, 132, 216, 0.5)',
                    pointerEvents: 'none',
                    zIndex: 1,
                  }}
                />
              );
            })()}
            {/* Critical Events filter (top-right) */}
             <div style={{ position: 'absolute', top: '-10px', right: '5px', display: 'flex', gap: 8, alignItems: 'center', zIndex: 5 }}>
               <div style={{ position: 'relative' }} data-event-filter>
                 <button
                   onClick={() => setShowEventFilter(!showEventFilter)}
                   style={{
                     padding: '4px 8px',
                     fontSize: 12,
                     border: '1px solid #ccc',
                     borderRadius: '6px',
                     backgroundColor: selectedEventTypes.size > 0 ? '#e3f2fd' : 'white',
                     cursor: 'pointer',
                     display: 'flex',
                     alignItems: 'center',
                     gap: '4px',
                     transition: 'all 0.2s ease'
                   }}
                 >
                   Filter {selectedEventTypes.size > 0 && `(${selectedEventTypes.size})`}
                   <span style={{ fontSize: '8px' }}>▼</span>
                 </button>
                 
                 {showEventFilter && (
                   <div style={{
                     position: 'absolute',
                     top: '100%',
                     right: 0,
                     backgroundColor: 'white',
                     border: '1px solid #ddd',
                     borderRadius: '12px',
                     boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                     zIndex: 1000,
                     minWidth: '320px',
                     maxHeight: '190px',
                     overflowY: 'auto',
                     padding: '12px 0'
                   }}>
                     {EVENT_CATEGORIES.map(category => {
                       const categoryEvents = AVAILABLE_EVENT_TYPES.filter(type => 
                         getEventCategory(type) === category
                       );
                       
                       return (
                         <div key={category} style={{ marginBottom: '8px' }}>
                           <div style={{ 
                             padding: '4px 12px', 
                             fontSize: '13px', 
                             fontWeight: '600', 
                             color: '#333',
                             backgroundColor: '#f8f9fa',
                             borderBottom: '1px solid #eee'
                           }}>
                             {category}
                           </div>
                           {categoryEvents.map(eventType => (
                             <label key={eventType} style={{
                               display: 'flex',
                               alignItems: 'center',
                               padding: '4px 12px',
                               cursor: 'pointer',
                               fontSize: '12px',
                               backgroundColor: selectedEventTypes.has(eventType) ? '#e3f2fd' : 'transparent'
                             }}>
                               <input
                                 type="checkbox"
                                 checked={selectedEventTypes.has(eventType)}
                                 onChange={(e) => {
                                   const newSelected = new Set(selectedEventTypes);
                                   if (e.target.checked) {
                                     newSelected.add(eventType);
                                   } else {
                                     newSelected.delete(eventType);
                                   }
                                   setSelectedEventTypes(newSelected);
                                 }}
                                 style={{ marginRight: '8px' }}
                               />
<span style={{ textTransform: 'capitalize' }}>
                                {getEventTypeDisplayLabel(eventType)}
                               </span>
                             </label>
                           ))}
                         </div>
                       );
                     })}
                     
                     <div style={{ padding: '12px 16px', borderTop: '1px solid #eee', marginTop: '8px' }}>
                       <button
                         onClick={() => {
                           setSelectedEventTypes(new Set(AVAILABLE_EVENT_TYPES));
                         }}
                         style={{
                           padding: '6px 12px',
                           fontSize: '12px',
                           border: '1px solid #007bff',
                           borderRadius: '6px',
                           backgroundColor: '#007bff',
                           color: 'white',
                           cursor: 'pointer',
                           marginRight: '8px',
                           transition: 'all 0.2s ease'
                         }}
                       >
                         Select All
                       </button>
                       <button
                         onClick={() => {
                           setSelectedEventTypes(new Set());
                         }}
                         style={{
                           padding: '6px 12px',
                           fontSize: '12px',
                           border: '1px solid #dc3545',
                           borderRadius: '6px',
                           backgroundColor: '#dc3545',
                           color: 'white',
                           cursor: 'pointer',
                           transition: 'all 0.2s ease'
                         }}
                       >
                         Clear All
                       </button>
                     </div>
                   </div>
                 )}
               </div>
             </div>
          <button 
              className="nav-button" 
              style={{ 
                position: 'absolute',
                bottom: '-20px',
                left: '50%',
                transform: 'translateX(-50%)',
                backgroundColor: '#1976D2', 
                color: 'white', 
                padding: '12px 24px',
                fontSize: '16px',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                boxShadow: '0 4px 8px rgba(0,0,0,0.3)',
                fontWeight: 'bold',
                outline: 'none'
              }}
              onClick={() => setShowgraphs(prev => !prev)} 
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#1565C0';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#1976D2';
              }}
            >
              {!showgraphs ? 'Hide' : 'Show'} Raw Signals
            </button>
            <div className="panel-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>Critical Events</span>
              {timelineZoom.startTime !== null && timelineZoom.endTime !== null && (
                <button 
                  onClick={resetTimelineZoom}
                  style={{ position: 'relative', padding: '5px 10px', background: '#f44336', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: 12 }}
                >
                  Reset Zoom
                </button>
              )}
            </div>
            <div 
              className="timeline" 
              style={{ position: 'relative', padding: '0 40px' }}
            >
              <div className="timeline-line" style={{ position: 'absolute', left: 25, right: 25, top: '80%', height: 1, background: '#e2e2e2', transform: 'translateY(-0.5px)' }}></div>
              {(() => {
                // Use zoom range if available, otherwise use full date range
                const start = timelineZoom.startTime !== null ? timelineZoom.startTime : dateRange.startDate.getTime();
                const end = timelineZoom.endTime !== null ? timelineZoom.endTime : dateRange.endDate.getTime();
                const total = Math.max(1, end - start);
                const numTicks = 10; // match charts: 10 time ticks across range
                const ticks: number[] = Array.from({ length: numTicks }, (_, idx) => start + Math.round((idx * total) / (numTicks - 1)));
                return ticks.map((ts, idx) => {
                  const pct = ((ts - start) / total) * 100; // Use same calculation as events
                  // Use identical mapping as events for perfect alignment
                  const clampedPct = Math.max(0, Math.min(100, pct));
                  // Use full width to avoid cutting off events at the end
                  const scaledPct = Math.round(clampedPct * 10) / 10; // No scaling to preserve full range
                  const leftPx = getTimelineLeftCss(scaledPct);
                  const label = formatTimelineTime(ts);
                  const alignShift = scaledPct < 1.5 ? '0%' : (scaledPct > 98.5 ? '-100%' : '-50%');
                  return (
                    <div key={`timeline-tick-${idx}`} style={{ position: 'absolute', left: leftPx, transform: `translateX(${alignShift})`, zIndex: 0 }}>
                      <div style={{ position: 'absolute', top: 100  , fontSize: 9, color: '#666', background: 'white', padding: '0 1px' }}>{label}</div>
                    </div>
                  );
                });
              })()}
              {(() => {
                const filteredEvents = (() => {
                  // If no specific event types are selected, show no events
                  if (selectedEventTypes.size === 0) {
                    return [];
                  }
                  
                  // Filter events based on selected event types
                  const normalizedSelectedTypes = new Set(Array.from(selectedEventTypes).map((t) => normalizeEventTypeForUi(t)));
                  return visibleCriticalEvents.filter(ev => {
                    const normalizedEventType = normalizeEventTypeForUi(ev.type);
                    return normalizedSelectedTypes.has(normalizedEventType);
                  });
                })();

                // Group events by minute to show multiple events in same box
                const eventsByMinute = new Map<string, typeof filteredEvents>();
                
                for (const ev of filteredEvents) {
                  const eventTime = parseDbTimestamp(ev.timestamp);
                  if (!isValidDate(eventTime)) continue;
                  const minuteKey = `${eventTime.getFullYear()}-${eventTime.getMonth()}-${eventTime.getDate()}-${eventTime.getHours()}-${eventTime.getMinutes()}`;
                  
                  if (!eventsByMinute.has(minuteKey)) {
                    eventsByMinute.set(minuteKey, []);
                  }
                  eventsByMinute.get(minuteKey)!.push(ev);
                }

                // Convert grouped events to layout with alternating positioning
                type LaidOut = { 
                  events: typeof filteredEvents, 
                  timestamp: string, 
                  scaledLeftPct: number, 
                  placeBelow: boolean 
                };
                const laidOut: LaidOut[] = [];
                let lastPlacedBelow = true; // Start with true so first event goes above (placeBelow = false)

                for (const [, events] of Array.from(eventsByMinute.entries())) {
                  if (events.length === 0) continue;
                  
                  // Use the first event's timestamp for positioning
                  const firstEvent = events[0];
                  // Use zoom range if available, otherwise use full date range
                  const effectiveStart = timelineZoom.startTime !== null ? new Date(timelineZoom.startTime) : dateRange.startDate;
                  const effectiveEnd = timelineZoom.endTime !== null ? new Date(timelineZoom.endTime) : dateRange.endDate;
                  const leftPct = getTimePosition(firstEvent.timestamp, effectiveStart, effectiveEnd);
                  const clampedLeftPct = Math.max(0, Math.min(100, leftPct));
                  const scaledLeftPct = Math.round(clampedLeftPct * 10) / 10;
                  
                  // Always alternate between above and below for each new event group
                  let placeBelow: boolean = !lastPlacedBelow;
                  
                  laidOut.push({ 
                    events, 
                    timestamp: firstEvent.timestamp, 
                    scaledLeftPct, 
                    placeBelow 
                  });
                  
                  lastPlacedBelow = placeBelow;
                }

                // Show message when no events are selected
                if (filteredEvents.length === 0) {
                  return (
                    <div style={{
                      position: 'absolute',
                      top: '5%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      textAlign: 'center',
                      color: '#666',
                      fontSize: '14px',
                      padding: '20px'
                    }}>
                      {selectedEventTypes.size === 0 ? (
                        <div>
                          <div style={{ fontSize: '16px', marginBottom: '8px' }}>🔍</div>
                          <div>Select event types to display</div>
                          <div style={{ fontSize: '12px', marginTop: '10px' }}>Use the "Filter Events" button above</div>
                        </div>
                      ) : (
                        <div>
                          <div style={{ fontSize: '16px', marginBottom: '8px' }}>📊</div>
                          <div>No events match your filters</div>
                          <div style={{ fontSize: '12px', marginTop: '4px' }}>Try selecting different event types</div>
                        </div>
                      )}
                    </div>
                  );
                }

                return laidOut.map((eventGroup, index) => {
                  // Better positioning logic to prevent overflow
                  let labelShift = '-50%'; // Default center alignment

                  const isHovered = eventGroup.events.some(ev => hoveredEventId === ev.id);
                  const isSelected = eventGroup.events.some(ev => selectedEventId === String(ev.id));
                  const allAtrialFibrillation = eventGroup.events.every(ev => isAtrialFibrillationEvent(ev.type));
                  const guideStyleAbove = {
                    bottom: '20%',
                    height: 100
                  } as const;
                  const guideStyleBelow = {
                    top: '80%',
                    height: 90
                  } as const;
                  return (
                    <React.Fragment key={`group-${eventGroup.timestamp}-${index}`}>
                      {/* vertical guide connects the label to the center line */}
                      <div
                        style={{
                          position: 'absolute',
                          left: getTimelineLeftCss(eventGroup.scaledLeftPct),
                          transform: 'translateX(-50%)',
                          width: isSelected ? 3 : 1,
                          background: isSelected ? '#777' : '#444',
                          opacity: isSelected ? 0.7 : 0.45,
                          pointerEvents: 'none',
                          ...(eventGroup.placeBelow ? guideStyleBelow : guideStyleAbove)
                        }}
                      />
                      <div
                        className={`timeline-event ${eventGroup.placeBelow ? 'below' : 'above'}`}
                        style={{ position: 'absolute', top: eventGroup.placeBelow ? 110-(index%4)*10 : -10+(index%6 )*10, left: getTimelineLeftCss(eventGroup.scaledLeftPct), transform: `translateX(${labelShift})`, zIndex: isHovered ? 50 : 1 }}
                        onMouseEnter={() => setHoveredEventId(eventGroup.events[0].id)}
                        onMouseLeave={() => setHoveredEventId(null)}
                      >
                    <div
                      className={`timeline-dot ${index === 3 ? 'active' : ''}`}
                      style={{ display: 'none', width: 8, height: 8, borderRadius: 8, background: '#f44336', boxShadow: '0 0 0 2px #fff', position: 'absolute', transform: 'translate(-50%, 0)' }}
                    />
                      <button
                        type="button"
                        className="timeline-label"
                        onClick={async () => {
                        // If currently locked, ignore clicks to avoid spamming expensive loads
                        if (eventClickLocked) return;
                        // Toggle: if clicking the same event group again, clear snapshot and return to default
                        const firstEventId = String(eventGroup.events[0].id);
                        if (selectedEventId === firstEventId) {
                          setSelectedEventId(null);
                          setClickedEventSnapshot(null);
                          setCustomChartWindow(null);
                          setSelectedEventTimestamp(null);
                          setSharedZoomRange({ startTime: null, endTime: null });
                          return;
                        }
                        setSelectedEventId(firstEventId);
                        setEventClickLocked(true);
                        try {
                          const ts = parseDbTimestamp(eventGroup.timestamp);
                          // Precompute the ±12h window so it is always applied, even if later fetches fail
                          const start24 = new Date(ts.getTime() - 12 * 60 * 60 * 1000);
                          const end24 = new Date(ts.getTime() + 12 * 60 * 60 * 1000);
                          setCustomChartWindow({ startDate: start24, endDate: end24 });
                          setSharedZoomRange({ startTime: start24.toISOString(), endTime: end24.toISOString() });
                          // Store the critical event timestamp for title updates
                          setSelectedEventTimestamp(eventGroup.timestamp);
                          const startIso = new Date(ts.getTime() - 30 * 1000).toISOString();
                          const endIso = new Date(ts.getTime() + 30 * 1000).toISOString();
                          const rows = operationMode === 'demo'
                            ? await getDemoVitalsRange(startIso, endIso)
                            : await getVitalsRange(startIso, endIso, patId || undefined);
                          let best: any | null = null;
                          let bestDiff = Number.POSITIVE_INFINITY;
                          for (const r of rows) {
                            const diff = Math.abs(parseDbTimestamp(r.timestamp).getTime() - ts.getTime());
                            if (diff < bestDiff) { bestDiff = diff; best = r; }
                          }
                          if (best) {
                            const pickNum = (v: any): number | null => (typeof v === 'number' ? v : (v != null ? Number(v) : null));
                            const pick = (obj: any, keys: string[]): number | null => {
                              for (const k of keys) {
                                if (obj[k] != null) {
                                  const n = pickNum(obj[k]);
                                  if (typeof n === 'number' && !Number.isNaN(n) && n !== -1) return n;
                                }
                              }
                              return null;
                            };
                            const temperature = operationMode === 'demo' ? pickNum(best.temperature) : pick(best, ['temperature', 'temperature_value']);
                            const breathing = operationMode === 'demo' ? pickNum(best.breathing) : pick(best, ['respiratory_rate', 'breathing']);
                            const heartRate = operationMode === 'demo' ? pickNum(best.heart_rate) : pick(best, ['heart_rate', 'heart_rate_bpm', 'pulse_rate', 'pulse_rate_bpm']);
                            const hrv = normalizeHrvForDisplay(
                              operationMode === 'demo' ? pickNum(best.heart_rate_variability) : pick(best, ['hrv', 'hrv_ms', 'hrv_bpm', 'heart_rate_variability'])
                            );
                            const spo2 = operationMode === 'demo' ? pickNum(best.spo2) : pick(best, ['spo2', 'blood_oxygen_saturation']);
                            const systolic = operationMode === 'demo' ? pickNum(best.systolic) : pick(best, ['systolic_blood_pressure', 'systolic_blood_pressure_mmhg', 'systolic_blood_pressure_bpm', 'systolic_blood_pressure_bpm_mmhg', 'systolic']);
                            const diastolic = operationMode === 'demo' ? pickNum(best.diastolic) : pick(best, ['diastolic_blood_pressure', 'diastolic_blood_pressure_mmhg', 'diastolic_blood_pressure_bpm', 'diastolic_blood_pressure_bpm_mmhg', 'diastolic']);

                            // Prefer the exact value that triggered the critical event (if provided) over the median
                            const applyEventValues = (snapshot: DemoVitalsSnapshot): DemoVitalsSnapshot => {
                              const updated = { ...snapshot };
                              for (const ev of eventGroup.events) {
                                const v = parseFloat(ev.value);
                                if (Number.isNaN(v)) continue;
                                const t = ev.type.toLowerCase();
                                if (t.includes('hrv') || t.includes('variability')) updated.heart_rate_variability = v;
                                else if (t.includes('heart_rate')) updated.heart_rate = v;
                                else if (t.includes('spo2')) updated.spo2 = v;
                                else if (t.includes('resp') || t.includes('breath')) updated.breathing = v;
                                else if (t.includes('temp')) updated.temperature = v;
                                if (t.includes('systolic')) updated.systolic = v;
                                if (t.includes('diastolic')) updated.diastolic = v;
                              }
                              return updated;
                            };

                            const snapshot = applyEventValues({
                              timestamp: best.timestamp,
                              temperature: temperature ?? null,
                              breathing: breathing ?? null,
                              heart_rate: heartRate ?? null,
                              heart_rate_variability: hrv ?? null,
                              systolic: systolic ?? null,
                              diastolic: diastolic ?? null,
                              spo2: spo2 ?? null,
                            });
                            setClickedEventSnapshot(snapshot);
                          }

                          // Also load ±12h window around the event into the left charts
                          const rangeRows = operationMode === 'demo'
                            ? await getDemoVitalsRange(start24.toISOString(), end24.toISOString())
                            : await getVitalsRange(start24.toISOString(), end24.toISOString(), patId || undefined);
                          rawVitalsRowsRef.current = Array.isArray(rangeRows) ? rangeRows : [];
                          setActivityTimelineRows(Array.isArray(rangeRows) ? rangeRows : []);
                          
                          // Helper functions for aggregation
                          const calculateMedian = (values: number[]): number => {
                            if (values.length === 0) return 0;
                            const sorted = [...values].sort((a, b) => a - b);
                            const mid = Math.floor(sorted.length / 2);
                            return sorted.length % 2 === 0 
                              ? (sorted[mid - 1] + sorted[mid]) / 2 
                              : sorted[mid];
                          };
                          
                          const calculatePercentiles = (values: number[]): { q1: number; q3: number } => {
                            if (values.length === 0) {
                              return { q1: NaN, q3: NaN };
                            }
                            const sorted = [...values].sort((a, b) => a - b);
                            const percentile = (arr: number[], p: number): number => {
                              const idx = (arr.length - 1) * p;
                              const lower = Math.floor(idx);
                              const upper = Math.ceil(idx);
                              if (lower === upper) return arr[lower];
                              const weight = idx - lower;
                              return arr[lower] * (1 - weight) + arr[upper] * weight;
                            };
                            return {
                              q1: percentile(sorted, 0.25),
                              q3: percentile(sorted, 0.75),
                            };
                          };
                          
                          const calculateCustomWhiskers = (values: number[]) => {
                            if (values.length === 0) {
                              return { q_15: NaN, q_85: NaN };
                            }
                            const sorted = [...values].sort((a, b) => a - b);
                            const min = sorted[0];
                            const max = sorted[sorted.length - 1];
                            const { q1, q3 } = calculatePercentiles(values);
                            const x = (q3 - q1) * 1.5;
                            const upperLimit = q3 + x;
                            let q_85: number;
                            const EPS = 1e-9;
                            if (upperLimit >= max - EPS) {
                              q_85 = max;
                            } else {
                              const found = [...sorted].reverse().find(v => v <= upperLimit);
                              q_85 = found !== undefined ? found : max;
                            }
                            const lowerLimit = q1 - x;
                            let q_15: number;
                            if (lowerLimit <= min + EPS) {
                              q_15 = min;
                            } else {
                              const found = sorted.find(v => v >= lowerLimit);
                              q_15 = found !== undefined ? found : min;
                            }
                            return { q_15, q_85 };
                          };
                          
                          const calculateStats = (values: number[]) => {
                            if (values.length === 0) {
                              return { median: NaN, min: NaN, max: NaN, q1: NaN, q3: NaN, q_15: NaN, q_85: NaN };
                            }
                            const sorted = [...values].sort((a, b) => a - b);
                            const median = calculateMedian(values);
                            const { q1, q3 } = calculatePercentiles(values);
                            const { q_15, q_85 } = calculateCustomWhiskers(values);
                            return {
                              median,
                              min: sorted[0],
                              max: sorted[sorted.length - 1],
                              q1: q1,
                              q3: q3,
                              q_15: q_15,
                              q_85: q_85
                            };
                          };
                          
                          // Aggregate with 24-minute intervals for critical events
                          const intervalMinutes = 24;
                          const rangeStartDate = start24;
                          
                          // Group data by 24-minute intervals
                          const groupedTemp = new Map<number, number[]>();
                          const groupedBreath = new Map<number, number[]>();
                          const groupedHr = new Map<number, number[]>();
                          const groupedHrv = new Map<number, number[]>();
                          const groupedSpo2 = new Map<number, number[]>();
                          const groupedSys = new Map<number, number[]>();
                          const groupedDia = new Map<number, number[]>();
                          
                          const pickNum = (v: any): number | null => (typeof v === 'number' ? v : (v != null ? Number(v) : null));
                          const pick = (obj: any, keys: string[]): number | null => {
                            for (const k of keys) {
                              if (obj[k] != null) {
                                const n = pickNum(obj[k]);
                                if (typeof n === 'number' && !Number.isNaN(n) && n !== -1) return n;
                              }
                            }
                            return null;
                          };
                          
                          for (const r of rangeRows) {
                            const t = parseDbTimestamp(r.timestamp);
                            const tMs = t.getTime();
                            
                            // Skip points outside the 12-hour window
                            if (tMs < start24.getTime() || tMs > end24.getTime()) {
                              continue;
                            }
                            
                            const minutesFromStart = Math.floor((tMs - rangeStartDate.getTime()) / (1000 * 60));
                            const intervalIndex = Math.floor(minutesFromStart / intervalMinutes);
                            
                            const tempV = operationMode === 'demo' ? pickNum(r.temperature) : pick(r, ['temperature', 'temperature_value']);
                            const breathV = operationMode === 'demo' ? pickNum(r.breathing) : pick(r, ['respiratory_rate', 'breathing']);
                            const hrV = operationMode === 'demo' ? pickNum(r.heart_rate) : pick(r, ['heart_rate', 'heart_rate_bpm', 'pulse_rate', 'pulse_rate_bpm']);
                            const hrvV = operationMode === 'demo' ? pickNum(r.heart_rate_variability) : pick(r, ['hrv', 'hrv_ms', 'hrv_bpm', 'heart_rate_variability']);
                            const spo2V = operationMode === 'demo' ? pickNum(r.spo2) : pick(r, ['spo2', 'blood_oxygen_saturation']);
                            const sysV = operationMode === 'demo' ? pickNum(r.systolic) : pick(r, ['systolic_blood_pressure', 'systolic_blood_pressure_mmhg', 'systolic_blood_pressure_bpm', 'systolic_blood_pressure_bpm_mmhg', 'systolic']);
                            const diaV = operationMode === 'demo' ? pickNum(r.diastolic) : pick(r, ['diastolic_blood_pressure', 'diastolic_blood_pressure_mmhg', 'diastolic_blood_pressure_bpm', 'diastolic_blood_pressure_bpm_mmhg', 'diastolic']);
                            
                            if (tempV != null) {
                              if (!groupedTemp.has(intervalIndex)) groupedTemp.set(intervalIndex, []);
                              groupedTemp.get(intervalIndex)!.push(Number(tempV));
                            }
                            if (breathV != null) {
                              if (!groupedBreath.has(intervalIndex)) groupedBreath.set(intervalIndex, []);
                              groupedBreath.get(intervalIndex)!.push(Math.round(Number(breathV)));
                            }
                            if (hrV != null) {
                              if (!groupedHr.has(intervalIndex)) groupedHr.set(intervalIndex, []);
                              groupedHr.get(intervalIndex)!.push(Math.round(Number(hrV)));
                            }
                            if (hrvV != null) {
                              const hrvDisplay = normalizeHrvForDisplay(hrvV);
                              if (hrvDisplay != null) {
                                if (!groupedHrv.has(intervalIndex)) groupedHrv.set(intervalIndex, []);
                                groupedHrv.get(intervalIndex)!.push(Math.round(hrvDisplay));
                              }
                            }
                            if (spo2V != null) {
                              if (!groupedSpo2.has(intervalIndex)) groupedSpo2.set(intervalIndex, []);
                              groupedSpo2.get(intervalIndex)!.push(Math.round(Number(spo2V)));
                            }
                            if (sysV != null) {
                              if (!groupedSys.has(intervalIndex)) groupedSys.set(intervalIndex, []);
                              groupedSys.get(intervalIndex)!.push(Math.round(Number(sysV)));
                            }
                            if (diaV != null) {
                              if (!groupedDia.has(intervalIndex)) groupedDia.set(intervalIndex, []);
                              groupedDia.get(intervalIndex)!.push(Math.round(Number(diaV)));
                            }
                          }
                          
                          // Calculate aggregated values (plotboxes) for each interval
                          const aggregateGroup = <T extends { time: string; value: number; min: number; max: number; q1: number; q3: number; q_15: number; q_85: number; tsMs?: number }>(
                            grouped: Map<number, number[]>,
                            formatValue: (v: number) => number = (v) => v
                          ): T[] => {
                            const result: T[] = [];
                            const entries = Array.from(grouped.entries());
                            for (let i = 0; i < entries.length; i++) {
                              const [intervalIndex, values] = entries[i];
                              if (values.length === 0) continue;
                              const intervalStartMs = rangeStartDate.getTime() + (intervalIndex * intervalMinutes * 60 * 1000);
                              const intervalDate = new Date(intervalStartMs);
                              const { median, min, max, q1, q3, q_15, q_85 } = calculateStats(values);
                              result.push({
                                time: format(intervalDate, 'HH:mm'),
                                value: formatValue(median),
                                min: formatValue(min),
                                max: formatValue(max),
                                q1: formatValue(q1),
                                q3: formatValue(q3),
                                q_15: formatValue(q_15),
                                q_85: formatValue(q_85),
                                tsMs: intervalStartMs
                              } as T);
                            }
                            return result.sort((a, b) => (a.tsMs ?? 0) - (b.tsMs ?? 0));
                          };
                          
                          const demoTemperature = aggregateGroup<{ time: string; value: number; min: number; max: number; q1: number; q3: number; q_15: number; q_85: number; tsMs: number }>(groupedTemp, (v) => Number(v.toFixed(1)));
                          const demoBreathing = aggregateGroup<{ time: string; value: number; min: number; max: number; q1: number; q3: number; q_15: number; q_85: number }>(groupedBreath, (v) => Math.round(v));
                          const demoHeartRate = aggregateGroup<{ time: string; value: number; min: number; max: number; q1: number; q3: number; q_15: number; q_85: number }>(groupedHr, (v) => Math.round(v));
                          const demoHrv = aggregateGroup<{ time: string; value: number; min: number; max: number; q1: number; q3: number; q_15: number; q_85: number }>(groupedHrv, (v) => Math.round(v));
                          const demoSpo2 = aggregateGroup<{ time: string; value: number; min: number; max: number; q1: number; q3: number; q_15: number; q_85: number }>(groupedSpo2, (v) => Math.round(v));
                          const demoSys = aggregateGroup<{ time: string; value: number; min: number; max: number; q1: number; q3: number; q_15: number; q_85: number }>(groupedSys, (v) => Math.round(v));
                          const demoDia = aggregateGroup<{ time: string; value: number; min: number; max: number; q1: number; q3: number; q_15: number; q_85: number }>(groupedDia, (v) => Math.round(v));
                          setDbTemperature(demoTemperature);
                          setDbBreathing(demoBreathing);
                          setDbRespirationRate(demoBreathing);
                          setDbHeartRate(demoHeartRate);
                          setDbHeartRateVariability(demoHrv);
                          setDbSpo2(demoSpo2);
                          setDbSystolicBloodPressure(demoSys);
                          setDbDiastolicBloodPressure(demoDia);
                          
                          // Always display the full ±12h window so gaps remain visible to the user
                          setCustomChartWindow({ startDate: new Date(start24), endDate: new Date(end24) });
                        } catch (err) {
                          console.warn('Snapshot fetch failed', err);
                        } finally {
                          // release click lock after ~1s to throttle repeated clicks
                          setTimeout(() => setEventClickLocked(false), 900);
                        }
                      }}
                        style={{
                          position: 'absolute',
                          top: eventGroup.placeBelow ? 90 : -35, // place below or above the center line with more spacing
                          left: '50%',
                          transform: `translateX(${labelShift})${isSelected ? ' scale(1.15)' : ''}`,
                          background: allAtrialFibrillation
                            ? (isSelected ? '#d32f2f' : '#f44336')
                            : (isSelected ? 'linear-gradient(#f5f5f5, #e9e9e9)' : '#fff'),
                          border: `1px solid ${allAtrialFibrillation ? (isSelected ? '#b71c1c' : '#d32f2f') : (isSelected ? '#bdbdbd' : '#e0e0e0')}`,
                          borderRadius: 8,
                          padding: isSelected ? '9px 14px' : (isHovered ? '8px 13px' : '6px 10px'),
                          fontSize: allAtrialFibrillation ? 12 : 11,
                          color: allAtrialFibrillation ? '#fff' : (isSelected ? '#111' : '#333'),
                          boxShadow: isHovered
                            ? '0 4px 12px rgba(0,0,0,0.18)'
                            : (isSelected ? '0 3px 8px rgba(0,0,0,0.12)' : '0 2px 6px rgba(0,0,0,0.08)'),
                          whiteSpace: 'nowrap',
                          zIndex: isHovered || isSelected ? 999 : 2,
                          cursor: eventClickLocked ? 'wait' : 'pointer',
                          transition: 'box-shadow 0.15s ease, transform 0.12s ease, background-color 0.12s ease, border-color 0.12s ease',
                          minWidth: '160px',
                          maxWidth: isHovered || isSelected ? '220px' : '200px',
                          overflow: 'hidden',
                          textAlign: 'center'
                        }}
                    >
                      <div style={{ fontWeight: 600, marginBottom: '4px', color: '#111' }}>{formatDate(eventGroup.timestamp)}</div>
                      <div style={{ color: allAtrialFibrillation ? '#fff' : '#f44336' }}>
                        {eventGroup.events.map((ev, idx) => {
                          const isAf = isAtrialFibrillationEvent(ev.type);
                          const eventLabel = isAf
                            ? getEventTypeDisplayLabel(ev.type)
                            : (getEventCategoryShort(ev.type)
                              ? `${getEventCategoryShort(ev.type)} - ${getEventTypeDisplayLabel(ev.type)}`
                              : getEventTypeDisplayLabel(ev.type));
                          const eventValueLabel = resolveCriticalEventValueFromRows(
                            ev,
                            rawVitalsRowsRef.current,
                            rawTempRowsRef.current,
                          );
                          const shouldShowValue = eventValueLabel !== null;
                          return (
                          <div
                            key={idx}
                            style={{
                              fontSize: isAf ? '12px' : '10px',
                              marginBottom: '1px',
                              ...(!allAtrialFibrillation && isAf ? {
                                backgroundColor: '#f44336',
                                color: '#fff',
                                padding: '2px 6px',
                                borderRadius: 4,
                              } : {}),
                            }}
                          >
                            {shouldShowValue ? `${eventLabel} (${eventValueLabel})` : eventLabel}
                          </div>
                          );
                        })}
                      </div>
                    </button>
                      </div>
                    </React.Fragment>
                  );
                });
              })()}
            </div>
          </div>

          {/* Medications Timeline */}
          <div className="panel" style={{ position: 'relative', marginTop: '13px', minHeight: medicationCollapsed ? 72 : 200 }}>
          <div
              className="panel-title"
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              {/* LEFT */}
              <span>Medications Timeline</span>

              {/* RIGHT */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {userRole === 'doctor' && (
                  <button
                  onClick={() => onNavigate('medication')}
                  style={{
                    background: 'rgba(255, 255, 255, 0.85)', // clean white glass
                    border: '1.5px solid rgba(0, 0, 0, 0.25)',
                    borderRadius: '10px',
                    padding: '2px 10px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    color: '#000', 
                    boxShadow: '0 2px 6px rgba(0,0,0,0.12)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(245, 245, 245, 1)';
                    e.currentTarget.style.borderColor = 'rgba(0, 0, 0, 0.4)';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                    e.currentTarget.style.boxShadow = '0 6px 12px rgba(0,0,0,0.18)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.85)';
                    e.currentTarget.style.borderColor = 'rgba(0, 0, 0, 0.25)';
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.12)';
                  }}
                >
                  <span style={{ fontSize: '16px' }}>💊</span>
                  <span>Medications</span>
                </button>
                
                )}

                <button
                  className="refresh-button"
                  title={medicationCollapsed ? 'Expand Medications' : 'Collapse Medications'}
                  onClick={() => setMedicationCollapsed(prev => !prev)}
                  style={{
                    background: 'none',
                    border: '1px solid #bbb',
                    borderRadius: 6,
                    padding: '2px 6px',
                    fontSize: '1rem',
                    cursor: 'pointer'
                  }}
                >
                  {medicationCollapsed ? '▼' : '▶'}
                </button>
              </div>
            </div>

            {!medicationCollapsed && (
            <div style={{ height: '200px', padding: '0px' }}>
              {(() => {
                const zoomedRangeStart = timelineZoom.startTime !== null ? new Date(timelineZoom.startTime) : dateRange.startDate;
                const zoomedRangeEnd = timelineZoom.endTime !== null ? new Date(timelineZoom.endTime) : dateRange.endDate;
                const medicationData = processMedicationData(allMedications, { startDate: zoomedRangeStart, endDate: zoomedRangeEnd });
                
                if (medicationData.length === 0) {
                  return (
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center', 
                      height: '100%',
                      color: '#666',
                      fontSize: '14px',
                      fontStyle: 'italic'
                    }}>
                      No medications found for the selected time period
                    </div>
                  );
                }

                const startTime = zoomedRangeStart.getTime();
                const endTime = zoomedRangeEnd.getTime();
                const timeRange = Math.max(1, endTime - startTime);
                const barHeight = 30;

                return (
                  <div
                    ref={medicationsTimelineRef}
                    onMouseDown={handleMedicationsTimelineMouseDown}
                    onMouseMove={handleMedicationsTimelineMouseMove}
                    onMouseUp={handleTimelineMouseUp}
                    style={{ height: '100%', position: 'relative', cursor: timelineZoom.refAreaStart !== null ? 'crosshair' : 'default' }}
                  >
                    {timelineZoom.refAreaStart !== null && timelineZoom.refAreaEnd !== null && (() => {
                      const rawStart = Math.min(timelineZoom.refAreaStart, timelineZoom.refAreaEnd);
                      const rawEnd = Math.max(timelineZoom.refAreaStart, timelineZoom.refAreaEnd);
                      const clampedStart = Math.max(0, Math.min(100, rawStart));
                      const clampedEnd = Math.max(0, Math.min(100, rawEnd));
                      const width = Math.max(0, clampedEnd - clampedStart);
                      if (width === 0) return null;
                      return (
                        <div
                          style={{
                            position: 'absolute',
                            left: `${clampedStart}%`,
                            width: `${width}%`,
                            top: 0,
                            bottom: 40,
                            background: 'rgba(136, 132, 216, 0.2)',
                            border: '1px solid rgba(136, 132, 216, 0.35)',
                            pointerEvents: 'none',
                            zIndex: 1,
                          }}
                        />
                      );
                    })()}
                    {/* Medications area - scrollable */}
                    <div style={{ 
                      height: 'calc(100% - 45px)', 
                      overflow: 'auto',
                      paddingBottom: '5px'
                    }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                        {medicationData.map((med, index) => {
                          const rawStartPercent = ((med.startTime - startTime) / timeRange) * 100;
                          const rawEndPercent = ((med.endTime - startTime) / timeRange) * 100;
                          const startPercent = Math.max(0, Math.min(100, rawStartPercent));
                          const endPercent = Math.max(0, Math.min(100, rawEndPercent));
                          const widthPercent = Math.max(0.5, endPercent - startPercent);

                          return (
                            <div key={`${med.name}-${index}`} style={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              padding: '2px 0'
                            }}>
                              {/* Timeline bar - full width */}
                              <div style={{ 
                                width: '100%', 
                                height: barHeight, 
                                backgroundColor: '#f0f0f0', 
                                borderRadius: '4px',
                                position: 'relative',
                                border: '1px solid #ddd'
                              }}>
                                <div 
                                  data-medication-item
                                  style={{
                                    position: 'absolute',
                                    left: `${startPercent}%`,
                                    width: `${widthPercent}%`,
                                    height: '100%',
                                    backgroundColor: med.color,
                                    borderRadius: '3px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'flex-start',
                                    paddingLeft: '8px',
                                    paddingRight: '8px',
                                    color: med.color === '#FF6B6B' || med.color === '#4ECDC4' || med.color === '#45B7D1' || med.color === '#96CEB4' || med.color === '#DDA0DD' || med.color === '#BB8FCE' || med.color === '#85C1E9' ? 'white' : 'black',
                                    fontSize: '20px',
                                    fontWeight: '600',
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                                    minWidth: '20px',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.transform = 'scale(1.00)';
                                    e.currentTarget.style.boxShadow = '0 3px 8px rgba(0,0,0,0.3)';
                                    e.currentTarget.style.zIndex = '10';
                                    
                                    // Clean up any existing tooltips first
                                    const existingTooltips = document.querySelectorAll('[id^="medication-tooltip"]');
                                    existingTooltips.forEach(tooltip => tooltip.remove());
                                    
                                    // Create custom tooltip with unique ID
                                    const tooltipId = `medication-tooltip-${med.name}-${Date.now()}`;
                                    const tooltip = document.createElement('div');
                                    tooltip.id = tooltipId;
                                    tooltip.style.cssText = `
                                      position: fixed;
                                      background: white;
                                      border: 2px solid #333;
                                      border-radius: 8px;
                                      padding: 16px;
                                      box-shadow: 0 8px 24px rgba(0,0,0,0.3);
                                      z-index: 10000;
                                      font-family: Arial, sans-serif;
                                      max-width: 300px;
                                      pointer-events: none;
                                      opacity: 0;
                                      transition: opacity 0.2s ease;
                                    `;
                                    
                                    tooltip.innerHTML = `
                                      <div style="font-size: 18px; font-weight: 700; color: #000; margin-bottom: 8px;">
                                        Medication: ${med.name}
                                      </div>
                                      <div style="font-size: 16px; font-weight: 600; color: #000; margin-bottom: 4px;">
                                        Dosage: ${med.dosage}
                                      </div>
                                      <div style="font-size: 16px; font-weight: 600; color: #000; margin-bottom: 4px;">
                                        Frequency: ${med.frequency || 'Daily'}
                                      </div>
                                      <div style="font-size: 14px; font-weight: 500; color: #666; margin-bottom: 2px;">
                                        Start Date: ${new Date(med.startTime).toLocaleDateString('en-GB', { 
                                          day: '2-digit', 
                                          month: '2-digit', 
                                          year: 'numeric' 
                                        })}
                                      </div>
                                      <div style="font-size: 14px; font-weight: 500; color: #666; margin-bottom: 2px;">
                                        End Date: ${new Date(med.endTime).toLocaleDateString('en-GB', { 
                                          day: '2-digit', 
                                          month: '2-digit', 
                                          year: 'numeric' 
                                        })}
                                      </div>
                                      <div style="font-size: 14px; font-weight: 500; color: #666;">
                                        Notes: ${med.notes || 'No notes'}
                                      </div>
                                    `;
                                    
                                    document.body.appendChild(tooltip);
                                    
                                    // Position tooltip
                                    const updatePosition = (e: React.MouseEvent<HTMLDivElement>) => {
                                      const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                                      tooltip.style.left = `${rect.left + rect.width / 2 - 150}px`;
                                      tooltip.style.top = `${rect.top - 10}px`;
                                    };
                                    
                                    updatePosition(e);
                                    
                                    // Fade in tooltip
                                    setTimeout(() => {
                                      tooltip.style.opacity = '1';
                                    }, 10);
                                    
                                    // Auto-hide tooltip after 5 seconds
                                    const autoHideTimeout = setTimeout(() => {
                                      const currentTooltip = document.getElementById(tooltipId);
                                      if (currentTooltip) {
                                        currentTooltip.style.opacity = '0';
                                        setTimeout(() => currentTooltip.remove(), 200);
                                      }
                                    }, 5000);
                                    
                                    // Store timeout reference for cleanup
                                    (e.currentTarget as any).tooltipTimeout = autoHideTimeout;
                                    
                                    const mousemoveHandler = (e: Event) => {
                                      const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                                      tooltip.style.left = `${rect.left + rect.width / 2 - 150}px`;
                                      tooltip.style.top = `${rect.top - 10}px`;
                                    };
                                    
                                    e.currentTarget.addEventListener('mousemove', mousemoveHandler);
                                    (e.currentTarget as any).mousemoveHandler = mousemoveHandler;
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = 'scale(1)';
                                    e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.2)';
                                    e.currentTarget.style.zIndex = '1';
                                    
                                    // Clear auto-hide timeout
                                    if ((e.currentTarget as any).tooltipTimeout) {
                                      clearTimeout((e.currentTarget as any).tooltipTimeout);
                                      (e.currentTarget as any).tooltipTimeout = null;
                                    }
                                    
                                    // Remove mousemove event listener
                                    if ((e.currentTarget as any).mousemoveHandler) {
                                      e.currentTarget.removeEventListener('mousemove', (e.currentTarget as any).mousemoveHandler);
                                      (e.currentTarget as any).mousemoveHandler = null;
                                    }
                                    
                                    // Remove all medication tooltips
                                    const existingTooltips = document.querySelectorAll('[id^="medication-tooltip"]');
                                    existingTooltips.forEach(tooltip => {
                                      (tooltip as HTMLElement).style.opacity = '0';
                                      setTimeout(() => tooltip.remove(), 200);
                                    });
                                  }}
                                >
                                  <span>{med.name}</span>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                    
                    {/* Fixed time axis at bottom - thinner and lower */}
                    <div style={{ 
                      position: 'absolute',
                      bottom: '-5px',
                      left: '0',
                      right: '0',
                      borderTop: '1px solid #e0e0e0',
                      display: 'flex',
                      justifyContent: 'space-between',
                      fontSize: '10px',
                      color: '#555',
                      fontWeight: '500',
                      background: '#f8f9fa',
                      borderRadius: '3px',
                      padding: '6px 8px',
                      height: '35px',
                      alignItems: 'center'
                    }}>
                      {Array.from({ length: 10 }, (_, i) => {
                        const time = startTime + (i * timeRange / 9);
                        return (
                          <span key={i} style={{
                            background: 'white',
                            padding: '2px 6px',
                            borderRadius: '3px',
                            border: '1px solid #ddd',
                            fontSize: '10px'
                          }}>
                            {formatTimelineTime(time)}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}
            </div>
            )}
          </div>

          {/* Activity Timeline */}
          <div className="panel" style={{ position: 'relative', marginTop: '13px', minHeight: 170 }}>
            <div className="panel-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>Activity Timeline</span>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  fontSize: 12,
                  marginRight:
                    activityZoom.startTime !== null && activityZoom.endTime !== null
                      ? ACTIVITY_TIMELINE_LEGEND_SHIFT_WHEN_ZOOMED_PX
                      : 0,
                  transition: 'margin-right 0.15s ease',
                }}
              >
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <span style={{ width: 10, height: 10, borderRadius: 2, background: ACTIVITY_COLORS.still, display: 'inline-block' }} />
                  Sedentary
                </span>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <span style={{ width: 10, height: 10, borderRadius: 2, background: ACTIVITY_COLORS.walking, display: 'inline-block' }} />
                  Walking
                </span>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <span style={{ width: 10, height: 10, borderRadius: 2, background: ACTIVITY_COLORS.jogging, display: 'inline-block' }} />
                  Jogging
                </span>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <span style={{ width: 10, height: 10, borderRadius: 2, background: ACTIVITY_COLORS.sleep, display: 'inline-block' }} />
                  Sleep
                </span>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <span style={{ width: 10, height: 10, borderRadius: 2, background: ACTIVITY_NO_DATA_COLOR, display: 'inline-block' }} />
                  No data
                </span>
              </div>
            </div>

            <div
              ref={activityTimelineRef}
              onMouseDown={handleActivityTimelineMouseDown}
              onMouseMove={handleActivityTimelineMouseMove}
              onMouseUp={handleActivityTimelineMouseUp}
              onMouseLeave={() => {
                handleActivityTimelineMouseUp();
                setActivityTooltip(null);
              }}
              style={{ height: 120, position: 'relative', cursor: activityZoom.refAreaStart !== null ? 'crosshair' : 'default' }}
            >
              {activityZoom.refAreaStart !== null && activityZoom.refAreaEnd !== null && (() => {
                const rawStart = Math.min(activityZoom.refAreaStart, activityZoom.refAreaEnd);
                const rawEnd = Math.max(activityZoom.refAreaStart, activityZoom.refAreaEnd);
                const clampedStart = Math.max(0, Math.min(100, rawStart));
                const clampedEnd = Math.max(0, Math.min(100, rawEnd));
                const width = Math.max(0, clampedEnd - clampedStart);
                if (width === 0) return null;
                return (
                  <div
                    style={{
                      position: 'absolute',
                      left: getActivityTimelineLeftCss(clampedStart),
                      width: getActivityTimelineWidthCss(clampedStart, clampedEnd),
                      top: 18,
                      height: 44,
                      background: 'rgba(136, 132, 216, 0.2)',
                      border: '1px solid rgba(136, 132, 216, 0.35)',
                      pointerEvents: 'none',
                      zIndex: 2,
                    }}
                  />
                );
              })()}

              <div style={{ position: 'absolute', left: ACTIVITY_TIMELINE_LEFT_GUTTER_PX, right: ACTIVITY_TIMELINE_RIGHT_GUTTER_PX, top: 18, height: 44, backgroundColor: '#edf0f2', borderRadius: 6, border: '1px solid #d4d9de' }}>
                {activityTimelineData.segments.map((segment, index) => {
                  const fullRange = Math.max(1, activityTimelineData.endMs - activityTimelineData.startMs);
                  const leftPct = ((segment.startMs - activityTimelineData.startMs) / fullRange) * 100;
                  const widthPct = Math.max(0.35, ((segment.endMs - segment.startMs) / fullRange) * 100);
                  return (
                    <div
                      key={`${segment.activity ?? 'no-data'}-${segment.startMs}-${index}`}
                      style={{
                        position: 'absolute',
                        left: `${Math.max(0, Math.min(100, leftPct))}%`,
                        width: `${Math.max(0, Math.min(100, widthPct))}%`,
                        top: 0,
                        bottom: 0,
                        backgroundColor: getActivitySegmentColor(segment),
                        cursor: 'default',
                      }}
                      onMouseEnter={(e) => showActivitySegmentTooltip(e, segment)}
                      onMouseMove={(e) => showActivitySegmentTooltip(e, segment)}
                      onMouseLeave={() => {
                        setActivityTooltip(null);
                      }}
                    />
                  );
                })}
              </div>

              {!activityTimelineData.hasAnyActivityData ? (
                <div style={{ position: 'absolute', top: 32, left: ACTIVITY_TIMELINE_LEFT_GUTTER_PX, right: ACTIVITY_TIMELINE_RIGHT_GUTTER_PX, textAlign: 'center', color: '#666', fontSize: 13, fontStyle: 'italic' }}>
                  No activity data found for the selected time period
                </div>
              ) : null}

              {activityTooltip ? (
                <div
                  style={{
                    position: 'fixed',
                    left: activityTooltip.x,
                    top: activityTooltip.y,
                    transform: activityTooltip.placement === 'above' ? 'translate(-50%, -100%)' : 'translate(-50%, 0)',
                    background: 'rgba(255,255,255,0.95)',
                    color: '#000',
                    padding: '6px 8px',
                    borderRadius: 4,
                    fontSize: 12,
                    lineHeight: 1.35,
                    pointerEvents: 'none',
                    zIndex: 1500,
                    whiteSpace: 'nowrap',
                    boxShadow: '0 8px 20px rgba(0,0,0,0.18)',
                    border: '1px solid #ddd',
                  }}
                >
                  <div style={{ fontWeight: 700, marginBottom: 2 }}>
                    {formatActivityTooltipTitle(activityTooltip.segment)}
                  </div>
                  <div>{`time : ${formatActivitySegmentTimeRange(activityTooltip.segment.startMs, activityTooltip.segment.endMs)}`}</div>
                  <div>{`duration : ${activityTooltip.segment.activity
                    ? Math.max(1, Math.round((activityTooltip.segment.endMs - activityTooltip.segment.startMs) / 60000))
                    : Math.round((activityTooltip.segment.endMs - activityTooltip.segment.startMs) / 60000)} min`}</div>
                </div>
              ) : null}

              <div style={{
                position: 'absolute',
                bottom: 0,
                left: ACTIVITY_TIMELINE_LEFT_GUTTER_PX,
                right: ACTIVITY_TIMELINE_RIGHT_GUTTER_PX,
                borderTop: '1px solid #e0e0e0',
                fontSize: '10px',
                color: '#555',
                fontWeight: '500',
                background: '#f8f9fa',
                borderRadius: '3px',
                padding: '6px 0',
                height: '35px',
                alignItems: 'center',
              }}>
                {Array.from({ length: 10 }, (_, i) => {
                  const time = activityTimelineData.startMs + (i * Math.max(1, activityTimelineData.endMs - activityTimelineData.startMs) / 9);
                  const pct = (i / 9) * 100;
                  const alignShift = pct < 1.5 ? '0%' : (pct > 98.5 ? '-100%' : '-50%');
                  return (
                    <span key={i} style={{
                      background: 'white',
                      padding: '2px 6px',
                      borderRadius: '3px',
                      border: '1px solid #ddd',
                      fontSize: '10px',
                      position: 'absolute',
                      left: `${pct}%`,
                      transform: `translateX(${alignShift})`,
                    }}>
                      {formatTimelineTime(time)}
                    </span>
                  );
                })}
              </div>
            </div>
            {activityZoom.startTime !== null && activityZoom.endTime !== null ? (
              <div style={{ position: 'absolute', top: 8, right: 10 }}>
                <button
                  onClick={resetActivityTimelineZoom}
                  style={{ padding: '4px 8px', background: '#f44336', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12 }}
                >
                  Reset Zoom
                </button>
              </div>
            ) : null}
          </div>

          {/* Left Column Vitals - Current Status */}  

          {/* Heart Rate Graph */}
          {showgraphs && 
          <div className="panel" style={{ position: 'relative' }}>
            <div className="panel-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>{heartTitle}</span>
              <button className="refresh-button" title={hrCollapsed ? 'Expand HR pair' : 'Collapse HR pair'} onClick={() => setHrCollapsed(v => !v)} 
              style={{ background: 'none', border: '1px solid #bbb', borderRadius: 6, padding: '2px 6px', fontSize: '1rem', cursor: 'pointer' }}>{hrCollapsed ? '▼' : '▶'}
              </button>
            </div>
            {!hrCollapsed && (
            <>
            <div style={{ fontSize: '0.7rem', color: '#666', marginBottom: '5px', textAlign: 'center' }}>
              {useShortZoomHint ? 'Drag to zoom.' : 'Drag to zoom. Click empty space to start a new selection.'}
            </div>
        <div className="graph-container-outer">
        <div className="graph-container" ref={heartRateContainerRef} onDoubleClick={resetTimelineZoom} title="Double-click to reset zoom">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart 
              key={`heart-${operationMode}`}
              data={filteredDisplayedHeartRateData}
              onMouseDown={heartRateZoomCtl.onMouseDown}
              onMouseMove={handleHeartRateMouseMove}
              onMouseUp={heartRateZoomCtl.onMouseUp}
              onMouseLeave={handleHeartRateMouseLeave}
                >
                  <Customized component={zoomBoundsCapture(heartRatePlotBoundsRef)} />
                  {/* Grid removed per request (no dashed horizontals/verticals) */}
                  <XAxis 
               type="number"
               dataKey="t"
               allowDecimals={true}
               ticks={heartRateAxis.ticks}
               tickFormatter={(t: number) => formatDynamicTime(heartRateBaseData, t)}
               domain={[heartRateAxis.left, heartRateAxis.right]}
               allowDataOverflow={true}
                   label={{ value: 'Time', position: 'insideBottom', offset: -5 }}
                 />
              <YAxis domain={[30, 150]} allowDataOverflow={true} label={{ value: 'Heart Rate (BPM)', angle: -90, position: 'insideLeft', dy: 50 }} />
              <Tooltip content={<HeartRateTooltip />} cursor={showHeartRateTooltip ? { stroke: 'rgba(0,0,0,0.25)' } : false} />
              
              <Line 
                type="monotone" 
                dataKey="value" 
                stroke="#1565C0" 
                strokeWidth={0.5} 
                connectNulls={false}
                dot={(props: any) => {
                  const { cx, cy, payload, index } = props;

                  if (!payload || payload.value == null) {
                    return <circle key={`hr-dot-${index}`} cx={cx} cy={cy} r={0} fill="transparent" />;
                  }

                  // Opacity based on confidence (real mode only)
                  let opacity = 1.0;
                  if (!isDemo && payload.confidence != null && payload.confidence !== undefined) {
                    const conf = Number(payload.confidence);
                    if (!isNaN(conf) && conf === 1) {
                      opacity = 0.4;
                    }
                  }

                  // Color based on heart rate thresholds
                  let fillColor = '#1565C0'; // default blue

                  if (payload.value >= thresholds.heartRate.high) {
                    fillColor = '#f44336'; // red
                  } else if (payload.value >= thresholds.heartRate.medium) {
                    fillColor = '#f44336'; // red
                  } else if (payload.value >= thresholds.heartRate.low) {
                    fillColor = '#000000'; // black
                  } else {
                    fillColor = '#f44336'; // red
                  }

                  return (
                    <circle
                      key={`hr-dot-${index}`}
                      cx={cx}
                      cy={cy}
                      r={2}
                      fill={fillColor}
                      stroke={fillColor}
                      strokeWidth={0.5}
                      opacity={opacity}
                    />
                  );
                }}
              />

              <Customized component={MinMaxMarkers} />
              <Customized component={QuartileMarkers}/>
              <Scatter data={heartRateRawPoints} dataKey="value" fill="#000000" shape={<RawDot />} isAnimationActive={false} />
                  {/* Background Areas (HR) */}
                  <ReferenceArea y1={thresholds.heartRate.low} y2={thresholds.heartRate.medium} fill="rgba(60, 255, 73, 0.14)" fillOpacity={0.6} />
                  {/* <ReferenceArea y1={thresholds.heartRate.medium} y2={thresholds.heartRate.high} fill="rgba(244, 67, 54, 0.22)" fillOpacity={0.6} /> */}
                  {/* <ReferenceArea y1={thresholds.heartRate.high} y2={200} fill="rgba(244, 67, 54, 0.22)" fillOpacity={0.6} /> */}
                  {/* <ReferenceArea y1={40} y2={thresholds.heartRate.low} fill="rgba(244, 67, 54, 0.22)" fillOpacity={0.6} /> */}
                  {/* Thresholds (HR) */}
                  <ReferenceLine y={thresholds.heartRate.high} stroke="#f44336" strokeDasharray="6 6" label={{ value: formatThresholdValue(thresholds.heartRate.high), position: 'insideLeft', fill: '#f44336' }} />
                  <ReferenceLine y={thresholds.heartRate.medium} stroke="#ff9800" strokeDasharray="6 6" label={{ value: formatThresholdValue(thresholds.heartRate.medium), position: 'insideLeft', fill: '#ff9800' }} />
                  <ReferenceLine y={thresholds.heartRate.low} stroke="#f44336" strokeDasharray="6 6" label={{ value: formatThresholdValue(thresholds.heartRate.low), position: 'insideLeft', fill: '#f44336' }} />
              {heartRateZoomCtl.zoom.refAreaLeft != null && heartRateZoomCtl.zoom.refAreaRight != null && (
                    <ReferenceArea
                  x1={heartRateZoomCtl.zoom.refAreaLeft}
                  x2={heartRateZoomCtl.zoom.refAreaRight}
                      fill="#5c6bc0"
                      fillOpacity={0.25}
                      stroke="#5c6bc0"
                      strokeOpacity={0.6}
                    />
                  )}
                </LineChart>
              </ResponsiveContainer>
          </div>
              <button type="button" className="graph-reset-zoom-btn" onClick={resetHeartRateZoom}>
                Reset Zoom
              </button>
            </div>
          </>
          )}
          </div>
          }
        

          {/* Heart Rate Variability Graph */}

          {showgraphs && 
          <div className="panel">
            {/* Τίτλος + κουμπί expand/collapse στην ίδια μπάρα */}
            <div 
              className="panel-title" 
              style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
            >
              <span>{heartVariabilityTitle}</span>
              <button 
                className="refresh-button"
                title={hrvCollapsed ? 'Expand HRV pair' : 'Collapse HRV pair'}
                onClick={() => setHrvCollapsed(prev => !prev)}
                style={{ background: 'none', border: '1px solid #bbb', borderRadius: 6, padding: '2px 6px', fontSize: '1rem', cursor: 'pointer' }}
              >
                {hrvCollapsed ? '▼' : '▶'}
              </button>
            </div>

            {!hrvCollapsed && (
            <>
            {/* Υπότιτλος */}
            <div style={{ fontSize: '0.7rem', color: '#666', marginBottom: '5px', textAlign: 'center' }}>
              {useShortZoomHint ? 'Drag to zoom.' : 'Drag to zoom. Click empty space to start a new selection.'}
            </div>

            {/* Container διαγράμματος */}
            <div className="graph-container-outer">
            <div className="graph-container" ref={heartRateVariabilityContainerRef} onDoubleClick={resetTimelineZoom} title="Double-click to reset zoom">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart 
                  key={`hrv-${operationMode}`}
                  data={filteredDisplayedHrvData}
                  onMouseDown={heartRateVariabilityZoomCtl.onMouseDown}
                  onMouseMove={handleHeartRateVariabilityMouseMove}
                  onMouseUp={heartRateVariabilityZoomCtl.onMouseUp}
                  onMouseLeave={handleHeartRateVariabilityMouseLeave}
                >
                  <Customized component={zoomBoundsCapture(heartRateVariabilityPlotBoundsRef)} />
                  <XAxis 
                    type="number"
                    dataKey="t"
                    allowDecimals={true}
                    ticks={heartRateVariabilityAxis.ticks}
                    tickFormatter={(t: number) => formatDynamicTime(heartRateVariabilityBaseData, t)}
                    domain={[heartRateVariabilityAxis.left, heartRateVariabilityAxis.right]}
                    allowDataOverflow={true}
                    label={{ value: 'Time', position: 'insideBottom', offset: -5 }}
                  />
                  <YAxis domain={[5, 200]} allowDataOverflow={true} label={{ value: 'Heart Rate Variability (ms)', angle: -90, position: 'insideLeft', dy: 90 }} />
                  <Tooltip content={<HeartRateVariabilityTooltip />} cursor={showHeartRateVariabilityTooltip ? { stroke: 'rgba(0,0,0,0.25)' } : false} />
                  <Line 
                    type="monotone" 
                    dataKey="value" 
                    stroke="#1565C0" 
                    strokeWidth={0.5} 
                    connectNulls={false}
                    dot={(props: any) => {
                      const { cx, cy, payload, index } = props;
                      if (!payload || payload.value == null) {
                        return <circle key={`hrv-dot-${index}`} cx={cx} cy={cy} r={0} fill="transparent" />;
                      }
                      
                      // Determine opacity based on confidence (for real mode only)
                      // Confidence levels: -1=invalid, 0=<50%, 1=50-70%, 2=70-90%, 3=>=90%
                      // Note: Points with confidence <= 0 or -1 are already filtered out in filterByConfidence
                      let opacity = 1.0; // Default full opacity
                      if (!isDemo && payload.confidence != null && payload.confidence !== undefined) {
                        const conf = Number(payload.confidence);
                        if (!isNaN(conf) && conf === 1) {
                          opacity = 0.4; // 40% opacity if confidence level 1 (50-70%)
                        }
                        // Otherwise full opacity (confidence level 2 or 3)
                      }
                      
                      // Determine color based on value
                      let fillColor = '#1565C0'; // default blue
                      if (payload.value >= thresholds.hrv.high) {
                        fillColor = '#f44336'; // red
                      } else if (payload.value <= thresholds.hrv.medium) {
                        fillColor = '#f44336'; // red (was orange)
                      } else if (payload.value > thresholds.hrv.medium) {
                        fillColor = '#000000'; // black (was green)
                      } else {
                        fillColor = '#f44336'; // red
                      }
                      
                      return (
                        <circle
                          key={`hrv-dot-${index}`}
                          cx={cx}
                          cy={cy}
                          r={2}
                          fill={fillColor}
                          stroke={fillColor}
                          strokeWidth={0.5}
                          opacity={opacity}
                        />
                      );
                    }}
                  />
                  <Customized component={MinMaxMarkers} />
                
                  <Customized component={QuartileMarkers}/>
                  <Scatter data={hrvRawPoints} dataKey="value" fill="#000000" shape={<RawDot />} isAnimationActive={false} />
                  {/* Background Areas (HRV) */}
                  <ReferenceArea y1={thresholds.hrv.medium} y2={200} fill="rgba(60, 255, 73, 0.14)" fillOpacity={0.6} />
                  {/* <ReferenceArea y1={thresholds.hrv.low} y2={thresholds.hrv.medium} fill="rgba(244, 67, 54, 0.22)" fillOpacity={0.6} /> */}
                  {/* <ReferenceArea y1={thresholds.hrv.high} y2={200} fill="rgba(244, 67, 54, 0.22)" fillOpacity={0.6} /> */}
                  {/* <ReferenceArea y1={10} y2={thresholds.hrv.low} fill="rgba(244, 67, 54, 0.22)" fillOpacity={0.6} /> */}
                  {/* Thresholds (HRV) */}
                  <ReferenceLine y={thresholds.hrv.high} stroke="#f44336" strokeDasharray="6 6" label={{ value: formatThresholdValue(thresholds.hrv.high), position: 'insideLeft', fill: '#f44336' }} />
                  <ReferenceLine y={thresholds.hrv.medium} stroke="#ff9800" strokeDasharray="6 6" label={{ value: formatThresholdValue(thresholds.hrv.medium), position: 'insideLeft', fill: '#ff9800' }} />
                  <ReferenceLine y={thresholds.hrv.low} stroke="#f44336" strokeDasharray="6 6" label={{ value: formatThresholdValue(thresholds.hrv.low), position: 'insideLeft', fill: '#f44336' }} />
                  <ReferenceArea
                    x1={heartRateVariabilityZoomCtl.zoom.refAreaLeft as number}
                    x2={heartRateVariabilityZoomCtl.zoom.refAreaRight as number}
                    fill="#5c6bc0"
                    fillOpacity={0.25}
                    stroke="#5c6bc0"
                    strokeOpacity={0.6}
                  />
                </LineChart>
              </ResponsiveContainer>

            </div>
              <button type="button" className="graph-reset-zoom-btn" onClick={resetHeartRateVariabilityZoom}>
                Reset Zoom
              </button>
            </div>
            </>
            )}
          </div>
        }

          
        

          {/* Systolic/Diastolic Blood Pressure Graph */}
          {showgraphs && 
          <div className="panel">
            <div className="panel-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>{systolic_diastolicBloodPressureTitle}</span>
              <button 
                className="refresh-button"
                title={bpCollapsed ? 'Expand BP pair' : 'Collapse BP pair'}
                onClick={() => setBpCollapsed(prev => !prev)} 
                style={{ background: 'none', border: '1px solid #bbb', borderRadius: 6, padding: '2px 6px', fontSize: '1rem', cursor: 'pointer' }}
              >{bpCollapsed ? '▼' : '▶'}</button>
            </div>
            {!bpCollapsed && (
            <>
            <div style={{ fontSize: '0.7rem', color: '#666', marginBottom: '5px', textAlign: 'center' }}>
              {useShortZoomHint ? 'Drag to zoom.' : 'Drag to zoom. Click empty space to start a new selection.'}
            </div>
            <div className="graph-container-outer">
            <div className="graph-container" ref={systolic_diastolicBloodPressureContainerRef} onDoubleClick={resetTimelineZoom} title="Double-click to reset zoom">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart 
                  key={`bp-${operationMode}`}
                  data={filteredCombinedBloodPressureData}
                  onMouseDown={systolic_diastolicBloodPressureZoomCtl.onMouseDown}
                  onMouseMove={handleSystolicBloodPressureMouseMove}
                  onMouseUp={systolic_diastolicBloodPressureZoomCtl.onMouseUp}
                  onMouseLeave={handleSystolicBloodPressureMouseLeave}
                >
                  <Customized component={zoomBoundsCapture(systolic_diastolicBloodPressurePlotBoundsRef)} />
                  {/* Grid removed per request (no dashed horizontals/verticals) */}
                  <XAxis 
                    type="number"
                    dataKey="t"
                    allowDecimals={true}
                    ticks={systolic_diastolicBloodPressureAxis.ticks}
                    tickFormatter={(t: number) => formatDynamicTime(systolic_BloodPressureBaseData, t)}
                    domain={[systolic_diastolicBloodPressureAxis.left, systolic_diastolicBloodPressureAxis.right]}
                    allowDataOverflow={true}
                    label={{ value: 'Time', position: 'insideBottom', offset: -5 }}
                  />
                  <YAxis domain={[50, 190]} allowDataOverflow={true} label={{ value: 'Blood Pressure (mmHg)', angle: -90, position: 'insideLeft' , dy: 90 }} />
                  <Tooltip content={<SystolicDiastolicBloodPressureTooltip />} cursor={showSystolic_diastolicBloodPressureTooltip ? { stroke: 'rgba(0,0,0,0.25)' } : false} />
                  <Line 
                    name="Systolic" 
                    type="monotone" 
                    dataKey="systolic" 
                    stroke="#1565C0" 
                    strokeWidth={0.5} 
                    connectNulls={false}
                    dot={(props: any) => {
                      const { cx, cy, payload, index } = props;
                      if (!payload || payload.systolic == null) {
                        return <circle key={`systolic-dot-${index}`} cx={cx} cy={cy} r={0} fill="transparent" />;
                      }
                      
                      // Determine opacity based on confidence (for real mode only)
                      // Confidence levels: -1=invalid, 0=<50%, 1=50-70%, 2=70-90%, 3=>=90%
                      // Note: Points with confidence <= 0 or -1 are already filtered out in filterByConfidence
                      let opacity = 1.0; // Default full opacity
                      if (!isDemo && payload.systolic_confidence != null && payload.systolic_confidence !== undefined) {
                        const conf = Number(payload.systolic_confidence);
                        if (!isNaN(conf) && conf === 1) {
                          opacity = 0.4; // 40% opacity if confidence level 1 (50-70%)
                        }
                        // Otherwise full opacity (confidence level 2 or 3)
                      }
                      
                      // Determine color based on systolic value
                      let fillColor = '#1565C0'; // default blue
                      if (payload.systolic >= thresholds.bloodPressure.systolic.systolic_stage1) {
                        fillColor = '#f44336'; // red (was orange)
                      } else if (payload.systolic >= thresholds.bloodPressure.systolic.low) {
                        fillColor = '#000000'; // black (was green)
                      } else {
                        fillColor = '#f44336'; // red
                      }
                      
                      return (
                        <circle
                          key={`systolic-dot-${index}`}
                          cx={cx}
                          cy={cy}
                          r={2}
                          fill={fillColor}
                          stroke={fillColor}
                          strokeWidth={0.5}
                          opacity={opacity}
                        />
                      );
                    }}
                  />
                  <Line 
                    name="Diastolic" 
                    type="monotone" 
                    dataKey="diastolic" 
                    stroke="#1565C0" 
                    strokeWidth={0.5} 
                    connectNulls={false}
                    dot={(props: any) => {
                      const { cx, cy, payload, index } = props;
                      if (!payload || payload.diastolic == null) {
                        return <circle key={`diastolic-dot-${index}`} cx={cx} cy={cy} r={0} fill="transparent" />;
                      }
                      
                      // Determine opacity based on confidence (for real mode only)
                      // Confidence levels: -1=invalid, 0=<50%, 1=50-70%, 2=70-90%, 3=>=90%
                      // Note: Points with confidence <= 0 or -1 are already filtered out in filterByConfidence
                      let opacity = 1.0; // Default full opacity
                      if (!isDemo && payload.diastolic_confidence != null && payload.diastolic_confidence !== undefined) {
                        const conf = Number(payload.diastolic_confidence);
                        if (!isNaN(conf) && conf === 1) {
                          opacity = 0.4; // 40% opacity if confidence level 1 (50-70%)
                        }
                        // Otherwise full opacity (confidence level 2 or 3)
                      }
                      
                      // Determine color based on diastolic value
                      let fillColor = '#1565C0'; // default blue
                      if (payload.diastolic >= thresholds.bloodPressure.diastolic.diastolic_stage1) {
                        fillColor = '#f44336'; // red (was orange)
                      } else if (payload.diastolic >= thresholds.bloodPressure.diastolic.low) {
                        fillColor = '#000000'; // black (was green)
                      } else {
                        fillColor = '#f44336'; // red
                      }
                      
                      return (
                        <circle
                          key={`diastolic-dot-${index}`}
                          cx={cx}
                          cy={cy}
                          r={2}
                          fill={fillColor}
                          stroke={fillColor}
                          strokeWidth={0.5}
                          opacity={opacity}
                        />
                      );
                    }}
                  />
                  <Customized component={BloodPressureLines}/>
                  <Scatter data={systolicRawPoints} dataKey="value" fill="#000000" shape={<RawDot />} isAnimationActive={false} />
                  <Scatter data={diastolicRawPoints} dataKey="value" fill="#000000" shape={<RawDot />} isAnimationActive={false} />
                  {/* Thresholds */}
                  <ReferenceLine y={thresholds.bloodPressure.systolic.high} stroke="#f44336" strokeDasharray="6 6" label={{ value: `BPS ${formatThresholdValue(thresholds.bloodPressure.systolic.high)} High`, position: 'insideLeft', fill: '#f44336' }} />
                   <ReferenceLine y={thresholds.bloodPressure.systolic.systolic_stage1} stroke="#ff9800" strokeDasharray="6 6" label={{ value: `BPS ${formatThresholdValue(thresholds.bloodPressure.systolic.systolic_stage1)} Medium`, position: 'insideLeft', fill: '#ff9800' }} />
                  <ReferenceLine y={thresholds.bloodPressure.systolic.low} stroke="#f44336" strokeDasharray="6 6" label={{ value: `BPS ${formatThresholdValue(thresholds.bloodPressure.systolic.low)} Low`, position: 'insideLeft', fill: '#f44336' }} />
                  <ReferenceLine y={thresholds.bloodPressure.diastolic.high} stroke="#f44336" strokeDasharray="6 6" label={{ value: `BPD ${formatThresholdValue(thresholds.bloodPressure.diastolic.high)} High`, position: 'insideLeft', fill: '#f44336' }} />
                   <ReferenceLine y={thresholds.bloodPressure.diastolic.diastolic_stage1} stroke="#ff9800" strokeDasharray="6 6" label={{ value: `BPD ${formatThresholdValue(thresholds.bloodPressure.diastolic.diastolic_stage1)} Medium`, position: 'insideLeft', fill: '#ff9800' }} />
                  <ReferenceLine y={thresholds.bloodPressure.diastolic.low} stroke="#f44336" strokeDasharray="6 6" label={{ value: `BPD ${formatThresholdValue(thresholds.bloodPressure.diastolic.low)} Low`, position: 'insideLeft', fill: '#f44336' }} />
                  {/* Background Areas (Blood Pressure) - Same as Heart Rate */}
                  {/* Green zones: between low and medium thresholds */}
                  <ReferenceArea y1={thresholds.bloodPressure.systolic.low} y2={thresholds.bloodPressure.systolic.systolic_stage1} fill="rgba(60, 255, 73, 0.14)" fillOpacity={0.6} />
                  <ReferenceArea y1={thresholds.bloodPressure.diastolic.low} y2={thresholds.bloodPressure.diastolic.diastolic_stage1} fill="rgba(60, 255, 73, 0.14)" fillOpacity={0.6} />
                  {/* Red zones: all other areas */}
                  {/* <ReferenceArea y1={thresholds.bloodPressure.systolic.systolic_stage1} y2={170} fill="rgba(244, 67, 54, 0.22)" fillOpacity={0.6} /> */}
                  {/* <ReferenceArea y1={thresholds.bloodPressure.diastolic.diastolic_stage1} y2={thresholds.bloodPressure.systolic.low} fill="rgba(244, 67, 54, 0.22)" fillOpacity={0.6} /> */}
                  {/* <ReferenceArea y1={50} y2={thresholds.bloodPressure.diastolic.low} fill="rgba(244, 67, 54, 0.22)" fillOpacity={0.6} /> */}
                  {systolic_diastolicBloodPressureZoomCtl.zoom.refAreaLeft != null && systolic_diastolicBloodPressureZoomCtl.zoom.refAreaRight != null && (
                    <ReferenceArea
                      x1={systolic_diastolicBloodPressureZoomCtl.zoom.refAreaLeft}
                      x2={systolic_diastolicBloodPressureZoomCtl.zoom.refAreaRight}
                      fill="#5c6bc0"
                      fillOpacity={0.25}
                      stroke="#5c6bc0"
                      strokeOpacity={0.6}
                    />
                  )}
                </LineChart>
              </ResponsiveContainer>
              </div>
              <button type="button" className="graph-reset-zoom-btn" onClick={resetSystolic_diastolicBloodPressureZoom}>
                Reset Zoom
              </button>
            </div>
              </>
              )}
            </div>
          }

            {/* SpO2 Graph */}
            {showgraphs && 
            <div className="panel">
            <div className="panel-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>{spo2Title}</span>
              <button 
                className="refresh-button"
                title={spo2Collapsed ? 'Expand SpO2' : 'Collapse SpO2'}
                onClick={() => setSpo2Collapsed(prev => !prev)} 
                style={{ background: 'none', border: '1px solid #bbb', borderRadius: 6, padding: '2px 6px', fontSize: '1rem', cursor: 'pointer' }}
              >{spo2Collapsed ? '▼' : '▶'}</button>
            </div>
            {!spo2Collapsed && (
            <>
            <div style={{ fontSize: '0.7rem', color: '#666', marginBottom: '5px', textAlign: 'center' }}>
              {useShortZoomHint ? 'Drag to zoom.' : 'Drag to zoom. Click empty space to start a new selection.'}
            </div>
            <div className="graph-container-outer">
            <div className="graph-container" ref={spo2ContainerRef} onDoubleClick={resetTimelineZoom} title="Double-click to reset zoom">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart 
                  key={`spo2-${operationMode}`}
                  data={filteredDisplayedSpo2Data}
                  onMouseDown={spo2ZoomCtl.onMouseDown}
                  onMouseMove={handleSpo2MouseMove}
                  onMouseUp={spo2ZoomCtl.onMouseUp}
                  onMouseLeave={handleSpo2MouseLeave}
                >
                  <Customized component={zoomBoundsCapture(spo2PlotBoundsRef)} />
                  {/* Grid removed per request (no dashed horizontals/verticals) */}
                  <XAxis 
                    type="number"
                    dataKey="t"
                    allowDecimals={true}
                    ticks={spo2Axis.ticks}
                    tickFormatter={(t: number) => formatDynamicTime(spo2BaseData, t)}
                    domain={[spo2Axis.left, spo2Axis.right]}
                    allowDataOverflow={true}
                    label={{ value: 'Time', position: 'insideBottom', offset: -5 }}
                  />
                  <YAxis domain={[70, 100]} allowDataOverflow={true} label={{ value: 'SpO2 (%)', angle: -90, position: 'insideLeft' , dy: 40  }} />
                  <Tooltip content={<Spo2Tooltip />} cursor={showSpo2Tooltip ? { stroke: 'rgba(0,0,0,0.25)' } : false} />
                  <Line 
                    type="monotone" 
                    dataKey="value" 
                    stroke="#1565C0" 
                    strokeWidth={0.5} 
                    connectNulls={false}
                    dot={(props: any) => {
                      const { cx, cy, payload, index } = props;
                      if (!payload || payload.value == null) {
                        return <circle key={`spo2-dot-${index}`} cx={cx} cy={cy} r={0} fill="transparent" />;
                      }
                      
                      // Determine opacity based on confidence (for real mode only)
                      // Confidence levels: -1=invalid, 0=<50%, 1=50-70%, 2=70-90%, 3=>=90%
                      // Note: Points with confidence <= 0 or -1 are already filtered out in filterByConfidence
                      let opacity = 1.0; // Default full opacity
                      if (!isDemo && payload.confidence != null && payload.confidence !== undefined) {
                        const conf = Number(payload.confidence);
                        if (!isNaN(conf) && conf === 1) {
                          opacity = 0.4; // 40% opacity if confidence level 1 (50-70%)
                        }
                        // Otherwise full opacity (confidence level 2 or 3)
                      }
                      
                      // Determine color based on value
                      let fillColor = '#1565C0'; // default blue
                      if (payload.value <= thresholds.spo2.low) {
                        fillColor = '#f44336'; // red
                      } else if (payload.value <= thresholds.spo2.medium) {
                        fillColor = '#f44336'; // red (was orange)
                      } else {
                        fillColor = '#000000'; // black (was green)
                      }
                      
                      return (
                        <circle
                          key={`spo2-dot-${index}`}
                          cx={cx}
                          cy={cy}
                          r={2}
                          fill={fillColor}
                          stroke={fillColor}
                          strokeWidth={0.5}
                          opacity={opacity}
                        />
                      );
                    }}
                  />
                  
                  <Customized component={MinMaxMarkersforSPO2} />
                  <Scatter data={spo2RawPoints} dataKey="value" fill="#000000" shape={<RawDot />} isAnimationActive={false} />
                  {/* Background Areas (SpO2) */}
                  <ReferenceArea y1={thresholds.spo2.medium} y2={100} fill="rgba(60, 255, 73, 0.14)" fillOpacity={0.6} />
                  {/* <ReferenceArea y1={thresholds.spo2.low} y2={thresholds.spo2.medium} fill="rgba(244, 67, 54, 0.22)" fillOpacity={0.6} /> */}
                  {/* <ReferenceArea y1={70} y2={thresholds.spo2.low} fill="rgba(244, 67, 54, 0.22)" fillOpacity={0.6} /> */}
                  {/* Thresholds (SpO2) */}
                  <ReferenceLine y={thresholds.spo2.medium} stroke="#ff9800" strokeDasharray="6 6" label={{ value: formatThresholdValue(thresholds.spo2.medium), position: 'insideLeft', fill: '#ff9800' }} />
                  <ReferenceLine y={thresholds.spo2.low} stroke="#f44336" strokeDasharray="6 6" label={{ value: formatThresholdValue(thresholds.spo2.low), position: 'insideLeft', fill: '#f44336' }} />
                  {spo2ZoomCtl.zoom.refAreaLeft != null && spo2ZoomCtl.zoom.refAreaRight != null && (
                    <ReferenceArea
                      x1={spo2ZoomCtl.zoom.refAreaLeft}
                      x2={spo2ZoomCtl.zoom.refAreaRight}
                      fill="#5c6bc0"
                      fillOpacity={0.25}
                      stroke="#5c6bc0"
                      strokeOpacity={0.6}
                    />
                  )}
                </LineChart>
              </ResponsiveContainer>
            </div>
              <button type="button" className="graph-reset-zoom-btn" onClick={resetSpo2Zoom}>
                Reset Zoom
              </button>
            </div>
            </>
            )}
            </div>
            }

          {/* Breathing Rate Graph */}
          {showgraphs && 
          <div className="panel">
            <div className="panel-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>{breathingTitle}</span>
              <button 
                className="refresh-button"
                title={respCollapsed ? 'Expand Breathing' : 'Collapse Breathing'}
                onClick={() => setRespCollapsed(prev => !prev)} 
                style={{ background: 'none', border: '1px solid #bbb', borderRadius: 6, padding: '2px 6px', fontSize: '1rem', cursor: 'pointer' }}
              >{respCollapsed ? '▼' : '▶'}</button>
            </div>
            {!respCollapsed && (
            <>
            <div style={{ fontSize: '0.7rem', color: '#666', marginBottom: '5px', textAlign: 'center' }}>
              {useShortZoomHint ? 'Drag to zoom.' : 'Drag to zoom. Click empty space to start a new selection.'}
            </div>
            <div className="graph-container-outer">
            <div className="graph-container" ref={breathingContainerRef} onDoubleClick={resetTimelineZoom} title="Double-click to reset zoom">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart 
                  key={`breath-${operationMode}`}
                  data={filteredDisplayedRespirationData}
                  onMouseDown={breathingZoomCtl.onMouseDown}
                  onMouseMove={handleBreathingMouseMove}
                  onMouseUp={breathingZoomCtl.onMouseUp}
                  onMouseLeave={handleBreathingMouseLeave}
                >
                  <Customized component={zoomBoundsCapture(breathingPlotBoundsRef)} />
                  {/* Grid removed per request (no dashed horizontals/verticals) */}
                  <XAxis 
                    type="number"
                    dataKey="t"
                    allowDecimals={true}
                    ticks={breathingAxis.ticks}
                    tickFormatter={(t: number) => formatDynamicTime(breathingBaseData, t)}
                    domain={[breathingAxis.left, breathingAxis.right]}
                    allowDataOverflow={true}
                    label={{ value: 'Time', position: 'insideBottom', offset: -5 }}
                  />
                  <YAxis domain={[4, 30]} allowDataOverflow={true} label={{ value: 'Breathing Rate (BPM)', angle: -90, position: 'insideLeft' , dy: 60 }} />
                  <Tooltip content={<BreathingTooltip />} cursor={showBreathingTooltip ? { stroke: 'rgba(0,0,0,0.25)' } : false} />
                  <Line 
                    type="monotone" 
                    dataKey="value" 
                    stroke="#1565C0" 
                    strokeWidth={0.5} 
                    connectNulls={false}
                    dot={(props: any) => {
                      const { cx, cy, payload, index } = props;
                      if (!payload || payload.value == null) {
                        return <circle key={`breathing-dot-${index}`} cx={cx} cy={cy} r={0} fill="transparent" />;
                      }
                      
                      // Determine opacity based on confidence (for real mode only)
                      // Confidence levels: -1=invalid, 0=<50%, 1=50-70%, 2=70-90%, 3=>=90%
                      // Note: Points with confidence <= 0 or -1 are already filtered out in filterByConfidence
                      let opacity = 1.0; // Default full opacity
                      if (!isDemo && payload.confidence != null && payload.confidence !== undefined) {
                        const conf = Number(payload.confidence);
                        if (!isNaN(conf) && conf === 1) {
                          opacity = 0.4; // 40% opacity if confidence level 1 (50-70%)
                        }
                        // Otherwise full opacity (confidence level 2 or 3)
                      }
                      
                      // Determine color based on value
                      let fillColor = '#1565C0'; // default blue
                      if (payload.value >= thresholds.breathing.high) {
                        fillColor = '#f44336'; // red
                      } else if (payload.value >= thresholds.breathing.medium) {
                        fillColor = '#f44336'; // red (was orange)
                      } else if (payload.value >= thresholds.breathing.low) {
                        fillColor = '#000000'; // black (was green)
                      } else {
                        fillColor = '#f44336'; // red
                      }
                      
                      return (
                        <circle
                          key={`breathing-dot-${index}`}
                          cx={cx}
                          cy={cy}
                          r={2}
                          fill={fillColor}
                          stroke={fillColor}
                          strokeWidth={0.5}
                          opacity={opacity}
                        />
                      );
                    }}
                  />
                  <Customized component={MinMaxMarkers} />
              
                 <Customized component={QuartileMarkers}/>
                  <Scatter data={breathingRawPoints} dataKey="value" fill="#000000" shape={<RawDot />} isAnimationActive={false} />
                  {/* Background Areas (Breathing) */}
                  <ReferenceArea y1={thresholds.breathing.low} y2={thresholds.breathing.medium} fill="rgba(60, 255, 73, 0.14)" fillOpacity={0.6} />
                  {/* <ReferenceArea y1={thresholds.breathing.medium} y2={thresholds.breathing.high} fill="rgba(244, 67, 54, 0.22)" fillOpacity={0.6} /> */}
                  {/* <ReferenceArea y1={thresholds.breathing.high} y2={40} fill="rgba(244, 67, 54, 0.22)" fillOpacity={0.6} /> */}
                  {/* <ReferenceArea y1={8} y2={thresholds.breathing.low} fill="rgba(244, 67, 54, 0.22)" fillOpacity={0.6} /> */}
                  {/* Thresholds (Breathing) */}
                  <ReferenceLine y={thresholds.breathing.high} stroke="#f44336" strokeDasharray="6 6" label={{ value: formatThresholdValue(thresholds.breathing.high), position: 'insideLeft', fill: '#f44336' }} />
                  <ReferenceLine y={thresholds.breathing.medium} stroke="#ff9800" strokeDasharray="6 6" label={{ value: formatThresholdValue(thresholds.breathing.medium), position: 'insideLeft', fill: '#ff9800' }} />
                  <ReferenceLine y={thresholds.breathing.low} stroke="#f44336" strokeDasharray="6 6" label={{ value: formatThresholdValue(thresholds.breathing.low), position: 'insideLeft', fill: '#f44336' }} />
                  {breathingZoomCtl.zoom.refAreaLeft != null && breathingZoomCtl.zoom.refAreaRight != null && (
                    <ReferenceArea
                      x1={breathingZoomCtl.zoom.refAreaLeft}
                      x2={breathingZoomCtl.zoom.refAreaRight}
                      fill="#5c6bc0"
                      fillOpacity={0.25}
                      stroke="#5c6bc0"
                      strokeOpacity={0.6}
                    />
                  )}
                </LineChart>
              </ResponsiveContainer>
            </div>
              <button type="button" className="graph-reset-zoom-btn" onClick={resetBreathingZoom}>
                Reset Zoom
              </button>
            </div>
            </>
            )}
        </div>
        } 

          {/* Temperature Graph */}
          {showgraphs && 
          <div className="panel">
            <div className="panel-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>{tempTitle}</span>
              <button 
                className="refresh-button"
                title={tempCollapsed ? 'Expand Skin Temperature' : 'Collapse Skin Temperature'}
                onClick={() => setTempCollapsed(prev => !prev)} 
                style={{ background: 'none', border: '1px solid #bbb', borderRadius: 6, padding: '2px 6px', fontSize: '1rem', cursor: 'pointer' }}
              >{tempCollapsed ? '▼' : '▶'}</button>
            </div>
            {!tempCollapsed && (
            <>
            <div style={{ fontSize: '0.7rem', color: '#666', marginBottom: '5px', textAlign: 'center' }}>
              {useShortZoomHint ? 'Drag to zoom.' : 'Drag to zoom. Click empty space to start a new selection.'}
            </div>
            <div className="graph-container-outer">
            <div className="graph-container" ref={tempContainerRef} onDoubleClick={resetTimelineZoom} title="Double-click to reset zoom">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart 
                  key={`temp-${operationMode}`}
                  data={filteredDisplayedTemperatureData}
                  onMouseDown={tempZoomCtl.onMouseDown}
                  onMouseMove={handleTempMouseMove}
                  onMouseUp={tempZoomCtl.onMouseUp}
                  onMouseLeave={handleTempMouseLeave}
                >
                  <Customized component={zoomBoundsCapture(tempPlotBoundsRef)} />
                  {/* Grid removed per request (no dashed horizontals/verticals) */}
                  <XAxis 
                    type="number"
                    dataKey="t"
                    allowDecimals={true}
                    ticks={tempAxis.ticks}
                    tickFormatter={(t: number) => formatDynamicTime(temperatureBaseData, t)}
                    domain={[tempAxis.left, tempAxis.right]}
                    allowDataOverflow={true}
                    label={{ value: 'Time', position: 'insideBottom', offset: -5 }}
                  />
                  <YAxis domain={[22, 40]} allowDataOverflow={true} tickFormatter={(v) => Number(v).toFixed(1)} label={{ value: 'Skin Temperature (°C)', angle: -90, position: 'insideLeft' , dy: 50 }} />
                  <Tooltip content={<TemperatureTooltip />} cursor={showTempTooltip ? { stroke: 'rgba(0,0,0,0.25)' } : false} />
                  <Line 
                    type="monotone" 
                    dataKey="value" 
                    stroke="#1565C0" 
                    strokeWidth={0.5} 
                    connectNulls={false}
                    dot={(props: any) => {
                      const { cx, cy, payload } = props;
                      if (!payload || payload.value == null) {
                        return <circle cx={cx} cy={cy} r={0} fill="transparent" />;
                      }
                      
                      // Temperature: always full opacity (no confidence field)
                      const opacity = 1.0;
                      
                      // Determine color based on value
                      let fillColor = '#1565C0'; // default blue
                      if (payload.value >= thresholds.temperature.high) {
                        fillColor = '#f44336'; // red
                      } else if (payload.value >= thresholds.temperature.medium) {
                        fillColor = '#f44336'; // red (was orange)
                      } else if (payload.value >= thresholds.temperature.low) {
                        fillColor = '#000000'; // black (was green)
                      } else {
                        fillColor = '#f44336'; // red
                      }
                      
                      return (
                        <circle
                          cx={cx}
                          cy={cy}
                          r={2}
                          fill={fillColor}
                          stroke={fillColor}
                          strokeWidth={0.5}
                          opacity={opacity}
                        />
                      );
                    }}
                  />
                  <Customized component={MinMaxMarkersforTEMP} />
              
                  <Scatter data={temperatureRawPoints} dataKey="value" fill="#000000" shape={<RawDot />} isAnimationActive={false} />
                  {/* Background Areas (Temp) */}
                  <ReferenceArea y1={thresholds.temperature.low} y2={thresholds.temperature.medium} fill="rgba(60, 255, 73, 0.14)" fillOpacity={0.6} />
                  {/* <ReferenceArea y1={thresholds.temperature.medium} y2={thresholds.temperature.high} fill="rgba(244, 67, 54, 0.22)" fillOpacity={0.6} /> */}
                  {/* <ReferenceArea y1={thresholds.temperature.high} y2={41} fill="rgba(244, 67, 54, 0.22)" fillOpacity={0.6} /> */}
                  {/* <ReferenceArea y1={25} y2={thresholds.temperature.low} fill="rgba(244, 67, 54, 0.22)" fillOpacity={0.6} /> */}
                  {/* Thresholds (Temp) */}
                  <ReferenceLine y={thresholds.temperature.high} stroke="#f44336" strokeDasharray="6 6" label={{ value: formatThresholdValue(thresholds.temperature.high), position: 'insideLeft', fill: '#f44336' }} />
                  <ReferenceLine y={thresholds.temperature.medium} stroke="#ff9800" strokeDasharray="6 6" label={{ value: formatThresholdValue(thresholds.temperature.medium), position: 'insideLeft', fill: '#ff9800' }} />
                  <ReferenceLine y={thresholds.temperature.low} stroke="#f44336" strokeDasharray="6 6" label={{ value: formatThresholdValue(thresholds.temperature.low), position: 'insideLeft', fill: '#f44336' }} />
                  {tempZoomCtl.zoom.refAreaLeft != null && tempZoomCtl.zoom.refAreaRight != null && (
                    <ReferenceArea
                      x1={tempZoomCtl.zoom.refAreaLeft}
                      x2={tempZoomCtl.zoom.refAreaRight}
                      fill="#5c6bc0"
                      fillOpacity={0.25}
                      stroke="#5c6bc0"
                      strokeOpacity={0.6}
                    />
                  )}
                </LineChart>
              </ResponsiveContainer>
          </div>
              <button type="button" className="graph-reset-zoom-btn" onClick={resetTempZoom}>
                Reset Zoom
              </button>
            </div>
          </>
          )}
        </div>
        }


          {/* Sleep Stage Graph */}
          {showgraphs && 
          <div className="panel">
            <div className="panel-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>Sleep Stage</span>
              <button 
                className="refresh-button"
                title={sleepCollapsed ? 'Expand Sleep pair' : 'Collapse Sleep pair'}
                onClick={() => setSleepCollapsed(prev => !prev)}
                style={{ background: 'none', border: '1px solid #bbb', borderRadius: 6, padding: '2px 6px', fontSize: '1rem', cursor: 'pointer' }}
              >{sleepCollapsed ? '▼' : '▶'}</button>
            </div>
            {!sleepCollapsed && (
            <div style={{ fontSize: '0.7rem', color: '#666', marginBottom: '5px', textAlign: 'center' }}>
              {sleepWideView
                ? 'Drag on a day column to open that night’s sleep graph (23:00–12:00).'
                : useShortZoomHint
                  ? 'Drag to zoom.'
                  : 'Drag to zoom. Click empty space to start a new selection.'}
            </div>
            )}
            {!sleepCollapsed && (
            <div className="graph-container-outer">
            <div className="graph-container" ref={sleepingContainerRef} onDoubleClick={resetTimelineZoom} title="Double-click to reset zoom" style={{ position: 'relative' }}>
              {renderSleepRunTooltip()}
              <ResponsiveContainer width="100%" height="100%">
                <LineChart 
                  key={`sleep-${operationMode}`}
                  data={filteredDisplayedSleepData}
                  onMouseDown={sleepZoomCtl.onMouseDown}
                  onMouseMove={handleSleepMouseMove}
                  onMouseUp={sleepZoomCtl.onMouseUp}
                  onMouseLeave={handleSleepMouseLeave}
                >
                  <Customized component={zoomBoundsCapture(sleepingPlotBoundsRef)} />
                  <XAxis 
                    type="number"
                    dataKey="t"
                    allowDecimals={true}
                    ticks={sleepChartXAxisTicks}
                    tickFormatter={(t: number) =>
                      sleepWideView
                        ? format(new Date(rangeStartDate.getTime() + t * 60 * 1000), 'dd/MM')
                        : formatDynamicTime(sleepingBaseData, t)
                    }
                    domain={[sleepAxis.left, sleepAxis.right]}
                    allowDataOverflow={true}
                    label={{ value: sleepWideView ? 'Day' : 'Time', position: 'insideBottom', offset: -5 }}
                  />
                  <YAxis
                    type="number"
                    domain={sleepWideView ? [0, SLEEP_DAILY_Y_SPAN] : [0, SLEEP_STAGE_Y_MAX]}
                    ticks={sleepWideView ? sleepDailyYTicks : [0, 1, 2, 3, 4]}
                    allowDataOverflow={true}
                    tickFormatter={(s: number) =>
                      sleepWideView
                        ? formatSleepYTime(sleepWindowYToMinutesOfDay(s))
                        : (SLEEP_STAGE_LABELS[s] ?? String(s))
                    }
                    label={
                      sleepWideView
                        ? { value: 'Time', angle: -90, position: 'insideLeft', offset: 10 }
                        : undefined
                    }
                  />
                  {sleepZoomCtl.zoom.refAreaLeft != null && sleepZoomCtl.zoom.refAreaRight != null && (
                    <ReferenceArea
                      x1={sleepZoomCtl.zoom.refAreaLeft}
                      x2={sleepZoomCtl.zoom.refAreaRight}
                      fill="#5c6bc0"
                      fillOpacity={0.25}
                      stroke="#5c6bc0"
                      strokeOpacity={0.6}
                    />
                  )}
                  <Customized
                    component={(props: any) => (
                      <SleepStageChart
                        {...props}
                        layoutMode={sleepWideView ? 'daily' : 'stage'}
                        rangeStartDate={rangeStartDate}
                        visibleMinT={sleepAxis.left}
                        visibleMaxT={sleepAxis.right}
                        showConnectors={!sleepWideView && sleepAxis.right - sleepAxis.left <= 24 * 60}
                        onRunHover={handleSleepRunHover}
                      />
                    )}
                  />
                  {!sleepWideView && (
                  <Scatter
                    dataKey="value"
                    fill="transparent"
                    line={false}
                    isAnimationActive={false}
                    shape={(dotProps: any) => {
                      const { cx, cy, payload } = dotProps;
                      const radius = cx != null && cy != null && payload?.value != null ? 10 : 0;
                      return <circle cx={cx ?? 0} cy={cy ?? 0} r={radius} fill="transparent" stroke="none" pointerEvents="none" />;
                    }}
                  />
                  )}
                </LineChart>
              </ResponsiveContainer>
            </div>
              <button type="button" className="graph-reset-zoom-btn" onClick={resetSleepZoom}>
                Reset Zoom
              </button>
            </div>
            )}
          </div>
          }

        {/* Left Column RAW - Current Status */}  

        {/* ECG1 RAW Graph */}
        {!showgraphs && 
          <div className="panel" style={{ position: 'relative' }}>
        
        <div className="panel-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>ECG1 Raw</span>
          <button className="refresh-button" title={ecg1RawCollapsed ? 'Expand ECG1 raw' : 'Collapse ECG1 raw'} onClick={() => setEcg1RawCollapsed(v => !v)} style={{ background: 'none', border: '1px solid #bbb', borderRadius: 6, padding: '2px 6px', fontSize: '1rem', cursor: 'pointer' }}>{ecg1RawCollapsed ? '▼' : '▶'}</button>
        </div>
            {!ecg1RawCollapsed && (<div style={{ fontSize: '0.7rem', color: '#666', marginBottom: '5px', textAlign: 'center' }}>
              {selectedEventTimestamp ? '1 minute of the selected critical event' : 'Select a critical event to view raw data'}
            </div>)}
        {!ecg1RawCollapsed && (<div className="graph-container" style={{ position: 'relative' }}>
              {!selectedEventTimestamp ? (
                <div style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  textAlign: 'center',
                  color: '#666',
                  fontSize: '16px',
                  zIndex: 10
                }}>
                  No critical event selected
                </div>
              ) : null}
              <ResponsiveContainer width="100%" height="85%">
                <LineChart data={filteredEcgRawChartData}>
                  <XAxis 
               type="number"
               dataKey="t"
               allowDecimals={false}
               ticks={selectedEventTimestamp ? (() => {
                 const start = ecgRawZoom.start;
                 const end = ecgRawZoom.end;
                 const step = (end - start) / 5;
                 return Array.from({ length: 6 }, (_, i) => Math.round((start + i * step) * 10) / 10);
               })() : [0,2,4,6,8,10,12,14]}
               tickFormatter={(t: number) => selectedEventTimestamp ? `${t.toFixed(1)}s` : `${t}.0s`.replace('.00','')}
               domain={selectedEventTimestamp ? [ecgRawZoom.start, ecgRawZoom.end] : [0, 14.16]}
               allowDataOverflow={false}
                   label={{ value: selectedEventTimestamp ? 'Time (s)' : 'Time (s)', position: 'insideBottom', offset: -5 }}
                 />
              <YAxis 
                domain={['auto', 'auto']}
                allowDataOverflow={false}
                label={{ value: 'ECG (mV)', angle: -90, position: 'insideLeft' }} 
              />
              <Tooltip 
                formatter={(v: any) => Math.round(Number(v))}
                labelFormatter={(l: any, payload: any) => {
                  const p = (payload && payload[0] && payload[0].payload) || {};
                  return `sample_index: ${p.sampleIndex ?? ''}`;
                }}
                cursor={false}
                isAnimationActive={false}
              />
              {selectedEventTimestamp && ecgRawZoom.start <= 0 && ecgRawZoom.end >= 0 && (
                <ReferenceLine x={0} stroke="#f44336" strokeDasharray="3 3" label={{ value: 'Event', position: 'top' }} />
              )}
              <Line type="linear" dataKey="value" stroke="#1565C0" strokeWidth={2.5} dot={false} isAnimationActive={false} animationDuration={0} />
                </LineChart>
              </ResponsiveContainer>
              {selectedEventTimestamp && ecgRawSamples.length > 0 && (
                  <div style={{ padding: '15px', borderTop: '2px solid #ddd', background: '#f9f9f9', width: '100%' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                      <span style={{ fontSize: '12px', color: '#666', minWidth: '30px' }}>0s</span>
                      <input
                        type="range"
                        min={0}
                        max={rawSignalMaxSliderValue}
                        step={0.1}
                        value={ecgRawZoom.start}
                        onInput={(e) => handleRawSignalSlider(parseFloat((e.target as HTMLInputElement).value))}
                        onChange={(e) => handleRawSignalSlider(parseFloat(e.target.value))}
                        style={{
                          flex: 1,
                          height: '8px',
                          background: '#ddd',
                          borderRadius: '4px',
                          outline: 'none',
                          cursor: 'pointer',
                          WebkitAppearance: 'none',
                        }}
                      />
                      <span style={{ fontSize: '12px', color: '#666', minWidth: '40px' }}>{rawSignalMaxTime.toFixed(0)}s</span>
                      <div style={{ fontSize: '12px', color: '#1976D2', fontWeight: '600', minWidth: '80px', textAlign: 'center' }}>
                        {ecgRawZoom.start.toFixed(1)}s - {(ecgRawZoom.start + 10).toFixed(1)}s
                      </div>
                    </div>
                  </div>
              )}
          </div>)}
          </div>
          }

           {/* ECG2 RAW Graph */}
        {!showgraphs && 
          <div className="panel" style={{ position: 'relative' }}>
        
        <div className="panel-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>ECG2 Raw</span>
          <button className="refresh-button" title={ecg2RawCollapsed ? 'Expand ECG2 raw' : 'Collapse ECG2 raw'} onClick={() => setEcg2RawCollapsed(v => !v)} style={{ background: 'none', border: '1px solid #bbb', borderRadius: 6, padding: '2px 6px', fontSize: '1rem', cursor: 'pointer' }}>{ecg2RawCollapsed ? '▼' : '▶'}</button>
        </div>
            {!ecg2RawCollapsed && (<div style={{ fontSize: '0.7rem', color: '#666', marginBottom: '5px', textAlign: 'center' }}>
              {selectedEventTimestamp ? '1 minute of the selected critical event' : 'Select a critical event to view raw data'}
            </div>)}
            {/* {currentEcgBatchTs && (
              <div style={{ position: 'absolute', top: '10px', left: '10px', background: 'rgba(25,118,210,0.1)', color: '#1976d2', padding: '4px 8px', borderRadius: 4, fontSize: 12 }}>
                Showing batch: {new Date(currentEcgBatchTs).toLocaleString()} • size: {currentEcgBatchSize}
              </div>
            )} */}
        {!ecg2RawCollapsed && (<div className="graph-container" style={{ position: 'relative' }}>
              {!selectedEventTimestamp ? (
                <div style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  textAlign: 'center',
                  color: '#666',
                  fontSize: '16px',
                  zIndex: 10
                }}>
                  No critical event selected
                </div>
              ) : null}
              <ResponsiveContainer width="100%" height="85%">
                <LineChart data={filteredEcg2RawChartData}>
                  <XAxis 
               type="number"
               dataKey="t"
               allowDecimals={false}
               ticks={selectedEventTimestamp ? (() => {
                 const start = ecg2RawZoom.start;
                 const end = ecg2RawZoom.end;
                 const step = (end - start) / 5;
                 return Array.from({ length: 6 }, (_, i) => Math.round((start + i * step) * 10) / 10);
               })() : [0,2,4,6,8,10,12,14]}
               tickFormatter={(t: number) => selectedEventTimestamp ? `${t.toFixed(1)}s` : `${t}.0s`.replace('.00','')}
               domain={selectedEventTimestamp ? [ecg2RawZoom.start, ecg2RawZoom.end] : [0, 14.16]}
               allowDataOverflow={false}
                   label={{ value: selectedEventTimestamp ? 'Time (s)' : 'Time (s)', position: 'insideBottom', offset: -5 }}
                 />
              <YAxis 
                domain={['auto', 'auto']}
                allowDataOverflow={false}
                label={{ value: 'ECG (mV)', angle: -90, position: 'insideLeft' }} 
              />
              <Tooltip 
                formatter={(v: any) => Math.round(Number(v))}
                labelFormatter={(l: any, payload: any) => {
                  const p = (payload && payload[0] && payload[0].payload) || {};
                  return `sample_index: ${p.sampleIndex ?? ''}`;
                }}
                cursor={false}
                isAnimationActive={false}
              />
              {selectedEventTimestamp && ecg2RawZoom.start <= 0 && ecg2RawZoom.end >= 0 && (
                <ReferenceLine x={0} stroke="#f44336" strokeDasharray="3 3" label={{ value: 'Event', position: 'top' }} />
              )}
              <Line type="linear" dataKey="value" stroke="#1565C0" strokeWidth={2.5} dot={false} isAnimationActive={false} animationDuration={0} />
                </LineChart>
              </ResponsiveContainer>
              {selectedEventTimestamp && ecg2RawSamples.length > 0 && (
                  <div style={{ padding: '15px', borderTop: '2px solid #ddd', background: '#f9f9f9', width: '100%' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                      <span style={{ fontSize: '12px', color: '#666', minWidth: '30px' }}>0s</span>
                      <input
                        type="range"
                        min={0}
                        max={rawSignalMaxSliderValue}
                        step={0.1}
                        value={ecg2RawZoom.start}
                        onInput={(e) => handleRawSignalSlider(parseFloat((e.target as HTMLInputElement).value))}
                        onChange={(e) => handleRawSignalSlider(parseFloat(e.target.value))}
                        style={{
                          flex: 1,
                          height: '8px',
                          background: '#ddd',
                          borderRadius: '4px',
                          outline: 'none',
                          cursor: 'pointer',
                          WebkitAppearance: 'none',
                        }}
                      />
                      <span style={{ fontSize: '12px', color: '#666', minWidth: '40px' }}>{rawSignalMaxTime.toFixed(0)}s</span>
                      <div style={{ fontSize: '12px', color: '#1976D2', fontWeight: '600', minWidth: '80px', textAlign: 'center' }}>
                        {ecg2RawZoom.start.toFixed(1)}s - {(ecg2RawZoom.start + 10).toFixed(1)}s
                      </div>
                    </div>
                  </div>
              )}
          </div>)}
          </div>
          }


          {/* PPG Green RAW Graph */}
          {!showgraphs && 
          <div className="panel" style={{ position: 'relative' }}>
        
        <div className="panel-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>PPG Green Raw</span>
          <button className="refresh-button" title={ppgGreenRawCollapsed ? 'Expand PPG green raw' : 'Collapse PPG green raw'} onClick={() => setPpgGreenRawCollapsed(v => !v)} style={{ background: 'none', border: '1px solid #bbb', borderRadius: 6, padding: '2px 6px', fontSize: '1rem', cursor: 'pointer' }}>{ppgGreenRawCollapsed ? '▼' : '▶'}</button>
        </div>
            {!ppgGreenRawCollapsed && (<div style={{ fontSize: '0.7rem', color: '#666', marginBottom: '5px', textAlign: 'center' }}>
              {selectedEventTimestamp ? '1 minute of the selected critical event' : 'Select a critical event to view raw data'}
            </div>)}
            {/* {currentPpgBatchTs && (
              <div style={{ position: 'absolute', top: '10px', left: '10px', background: 'rgba(25,118,210,0.1)', color: '#1976d2', padding: '4px 8px', borderRadius: 4, fontSize: 12 }}>
                Showing batch: {new Date(currentPpgBatchTs).toLocaleString()} • size: {currentPpgBatchSize}
              </div>
            )} */}
        {!ppgGreenRawCollapsed && (<div className="graph-container" style={{ position: 'relative' }}>
              {!selectedEventTimestamp ? (
                <div style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  textAlign: 'center',
                  color: '#666',
                  fontSize: '16px',
                  zIndex: 10
                }}>
                  No critical event selected
                </div>
              ) : null}
              <ResponsiveContainer width="100%" height="85%">
                <LineChart data={filteredPpgGreenRawChartData}>
                  <XAxis 
               type="number"
               dataKey="t"
               allowDecimals={false}
               ticks={selectedEventTimestamp ? (() => {
                 const start = ppgGreenRawZoom.start;
                 const end = ppgGreenRawZoom.end;
                 const step = (end - start) / 5;
                 return Array.from({ length: 6 }, (_, i) => Math.round((start + i * step) * 10) / 10);
               })() : [0,2,4,6,8,10,12,14]}
               tickFormatter={(t: number) => selectedEventTimestamp ? `${t.toFixed(1)}s` : `${t}.0s`.replace('.00','')}
               domain={selectedEventTimestamp ? [ppgGreenRawZoom.start, ppgGreenRawZoom.end] : [0, 14.16]}
               allowDataOverflow={false}
                   label={{ value: selectedEventTimestamp ? 'Time (s)' : 'Time (s)', position: 'insideBottom', offset: -5 }}
                 />
              <YAxis 
                domain={['auto', 'auto']}
                allowDataOverflow={false}
                label={{ value: 'PPG (a.u.)', angle: -90, position: 'insideLeft' }} 
              />
              <Tooltip 
                formatter={(v: any) => Math.round(Number(v))}
                labelFormatter={(l: any, payload: any) => {
                  const p = (payload && payload[0] && payload[0].payload) || {};
                  const timestamp = p.timestamp;
                  let timestampStr = '';
                  
                  if (timestamp) {
                    try {
                      const date = parseDbTimestamp(timestamp);
                      timestampStr = date.toLocaleString('el-GR', {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit',
                        hour12: false
                      });
                    } catch (e) {
                      timestampStr = 'Invalid timestamp';
                    }
                  } else {
                    timestampStr = 'No timestamp';
                  }
                  
                  return [
                    `sample_index: ${p.sampleIndex ?? ''}`,
                    `timestamp: ${timestampStr}`
                  ];
                }}
                cursor={false}
                isAnimationActive={false}
              />
              {selectedEventTimestamp && ppgGreenRawZoom.start <= 0 && ppgGreenRawZoom.end >= 0 && (
                <ReferenceLine x={0} stroke="#f44336" strokeDasharray="3 3" label={{ value: 'Event', position: 'top' }} />
              )}
              <Line type="linear" dataKey="value" stroke="#1565C0" strokeWidth={2.5} dot={false} isAnimationActive={false} animationDuration={0} />
                </LineChart>
              </ResponsiveContainer>
              {selectedEventTimestamp && ppgGreenRawSamples.length > 0 && (
                  <div style={{ padding: '15px', borderTop: '2px solid #ddd', background: '#f9f9f9', width: '100%' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                      <span style={{ fontSize: '12px', color: '#666', minWidth: '30px' }}>0s</span>
                      <input
                        type="range"
                        min={0}
                        max={rawSignalMaxSliderValue}
                        step={0.1}
                        value={ppgGreenRawZoom.start}
                        onInput={(e) => handleRawSignalSlider(parseFloat((e.target as HTMLInputElement).value))}
                        onChange={(e) => handleRawSignalSlider(parseFloat(e.target.value))}
                        style={{
                          flex: 1,
                          height: '8px',
                          background: '#ddd',
                          borderRadius: '4px',
                          outline: 'none',
                          cursor: 'pointer',
                          WebkitAppearance: 'none',
                        }}
                      />
                      <span style={{ fontSize: '12px', color: '#666', minWidth: '40px' }}>{rawSignalMaxTime.toFixed(0)}s</span>
                      <div style={{ fontSize: '12px', color: '#1976D2', fontWeight: '600', minWidth: '80px', textAlign: 'center' }}>
                        {ppgGreenRawZoom.start.toFixed(1)}s - {(ppgGreenRawZoom.start + 10).toFixed(1)}s
                      </div>
                    </div>
                  </div>
              )}
          </div>)}
          </div>
          }

          {/* PPG Yellow RAW Graph */}
          {!showgraphs && 
          <div className="panel" style={{ position: 'relative' }}>
        
        <div className="panel-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>PPG Yellow Raw</span>
          <button className="refresh-button" title={ppgYellowRawCollapsed ? 'Expand PPG yellow raw' : 'Collapse PPG yellow raw'} onClick={() => setPpgYellowRawCollapsed(v => !v)} style={{ background: 'none', border: '1px solid #bbb', borderRadius: 6, padding: '2px 6px', fontSize: '1rem', cursor: 'pointer' }}>{ppgYellowRawCollapsed ? '▼' : '▶'}</button>
        </div>
            {!ppgYellowRawCollapsed && (<div style={{ fontSize: '0.7rem', color: '#666', marginBottom: '5px', textAlign: 'center' }}>
              {selectedEventTimestamp ? '1 minute of the selected critical event' : 'Select a critical event to view raw data'}
            </div>)}
            {/* {currentPpgBatchTs && (
              <div style={{ position: 'absolute', top: '10px', left: '10px', background: 'rgba(25,118,210,0.1)', color: '#1976d2', padding: '4px 8px', borderRadius: 4, fontSize: 12 }}>
                Showing batch: {new Date(currentPpgBatchTs).toLocaleString()} • size: {currentPpgBatchSize}
              </div>
            )} */}
        {!ppgYellowRawCollapsed && (<div className="graph-container" style={{ position: 'relative' }}>
              {!selectedEventTimestamp ? (
                <div style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  textAlign: 'center',
                  color: '#666',
                  fontSize: '16px',
                  zIndex: 10
                }}>
                  No critical event selected
                </div>
              ) : null}
              <ResponsiveContainer width="100%" height="85%">
                <LineChart data={filteredPpgYellowRawChartData}>
                  <XAxis 
               type="number"
               dataKey="t"
               allowDecimals={false}
               ticks={selectedEventTimestamp ? (() => {
                 const start = ppgYellowRawZoom.start;
                 const end = ppgYellowRawZoom.end;
                 const step = (end - start) / 5;
                 return Array.from({ length: 6 }, (_, i) => Math.round((start + i * step) * 10) / 10);
               })() : [0,2,4,6,8,10,12,14]}
               tickFormatter={(t: number) => selectedEventTimestamp ? `${t.toFixed(1)}s` : `${t}.0s`.replace('.00','')}
               domain={selectedEventTimestamp ? [ppgYellowRawZoom.start, ppgYellowRawZoom.end] : [0, 14.16]}
               allowDataOverflow={false}
                   label={{ value: selectedEventTimestamp ? 'Time (s)' : 'Time (s)', position: 'insideBottom', offset: -5 }}
                 />
              <YAxis 
                domain={['auto', 'auto']}
                allowDataOverflow={false}
                label={{ value: 'PPG (a.u.)', angle: -90, position: 'insideLeft' }} 
              />
              <Tooltip 
                formatter={(v: any) => Math.round(Number(v))}
                labelFormatter={(l: any, payload: any) => {
                  const p = (payload && payload[0] && payload[0].payload) || {};
                  const timestamp = p.timestamp;
                  let timestampStr = '';
                  
                  if (timestamp) {
                    try {
                      const date = parseDbTimestamp(timestamp);
                      timestampStr = date.toLocaleString('el-GR', {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit'
                      });
                    } catch (e) {
                      timestampStr = timestamp;
                    }
                  }
                  
                  return `sample_index: ${p.sampleIndex ?? ''}${timestampStr ? `\ntimestamp: ${timestampStr}` : ''}`;
                }}
                cursor={false}
                isAnimationActive={false}
              />
              {selectedEventTimestamp && ppgYellowRawZoom.start <= 0 && ppgYellowRawZoom.end >= 0 && (
                <ReferenceLine x={0} stroke="#f44336" strokeDasharray="3 3" label={{ value: 'Event', position: 'top' }} />
              )}
              <Line 
                type="linear" 
                dataKey="value" 
                stroke="#1565C0" 
                strokeWidth={2.5} 
                dot={false}
                isAnimationActive={false}
                animationDuration={0}
              />
                </LineChart>
              </ResponsiveContainer>
              {selectedEventTimestamp && ppgYellowRawSamples.length > 0 && (
                  <div style={{ padding: '15px', borderTop: '2px solid #ddd', background: '#f9f9f9', width: '100%' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                      <span style={{ fontSize: '12px', color: '#666', minWidth: '30px' }}>0s</span>
                      <input
                        type="range"
                        min={0}
                        max={rawSignalMaxSliderValue}
                        step={0.1}
                        value={ppgYellowRawZoom.start}
                        onInput={(e) => handleRawSignalSlider(parseFloat((e.target as HTMLInputElement).value))}
                        onChange={(e) => handleRawSignalSlider(parseFloat(e.target.value))}
                        style={{
                          flex: 1,
                          height: '8px',
                          background: '#ddd',
                          borderRadius: '4px',
                          outline: 'none',
                          cursor: 'pointer',
                          WebkitAppearance: 'none',
                        }}
                      />
                      <span style={{ fontSize: '12px', color: '#666', minWidth: '40px' }}>{rawSignalMaxTime.toFixed(0)}s</span>
                      <div style={{ fontSize: '12px', color: '#1976D2', fontWeight: '600', minWidth: '80px', textAlign: 'center' }}>
                        {ppgYellowRawZoom.start.toFixed(1)}s - {(ppgYellowRawZoom.start + 10).toFixed(1)}s
                      </div>
                    </div>
                  </div>
              )}
          </div>)}
          </div>
          }

    </div>
              

        {/* Right Column - Current Status */} 
        <div className="right-column" style={{ width: isPhoneLayout ? '40%' : '20%', flex: isPhoneLayout ? '0 0 40%' : undefined, display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>

          {/* Vital Signs Panel */}
          <div className="current-panel" style={{ width: '100%', padding: '3px 3px', minHeight:300, overflowX: 'hidden' }}>
            <div className="current-panel-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
              <div className="current-panel-title">{patientName || 'Patient History'}</div>
              {userRole === 'doctor' && !isDemo && patId && (
                <button
                  type="button"
                  onClick={() => onNavigate('history', { keepPatient: true })}
                  title="Edit intake"
                  aria-label="Edit intake"
                  style={{
                    background: 'none',
                    border: 'none',
                    padding: '4px 8px',
                    cursor: 'pointer',
                    color: '#495057',
                    fontSize: '1.1rem',
                  }}
                >
                  ✏️
                </button>
              )}
            </div>
            <div className="current-panel-content" style={{ width: '100%' }}>
              <PatientHistoryPanel patId={patId} onNavigateToHistory={userRole === 'doctor' && !isDemo && patId ? () => onNavigate('history', { keepPatient: true }) : undefined} />
            </div>
          </div>

            {/* Patient Notes Current */}
            <div className="current-panel bg-notes" style={{ padding: '10px 10px', minHeight: medicationCollapsed ? 74 : 272, backgroundColor: '#f8f9fa',}}>
            <div className="current-panel-header" style={{ textAlign: 'center', display: 'flex', justifyContent: 'center', alignItems: 'center', position: 'relative' }}>
              <div className="current-panel-title" style={{ textAlign: 'center' }}>Notes about patient</div>
              {(!medicationCollapsed && <button
                id="patient-notes-edit-btn"
                onClick={() => {
                  const textarea = document.querySelector('textarea[data-patient-notes]') as HTMLTextAreaElement;
                  const button = document.getElementById('patient-notes-edit-btn') as HTMLButtonElement;
                  if (textarea && button) {
                    textarea.readOnly = !textarea.readOnly;
                    if (!textarea.readOnly) {
                      textarea.style.backgroundColor = 'white';
                      textarea.style.cursor = 'text';
                      textarea.focus();
                      button.textContent = 'Save';
                      button.style.background = '#28a745';
                    } else {
                      textarea.style.backgroundColor = '#e9ecef';
                      textarea.style.cursor = 'not-allowed';
                      button.textContent = 'Edit';
                      button.style.background = '#007bff';
                    }
                  }
                }}
                style={{
                  background: '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  padding: '6px 12px',
                  fontSize: '12px',
                  cursor: 'pointer',
                  position: 'absolute',
                  right: '0px',
                  top: '50%',
                  transform: 'translateY(-50%)'
                }}
              >
                Edit
              </button>
              )}
            </div>
            {(!medicationCollapsed && <div className="current-panel-content" style={{ padding: '5px 5px', height: 'calc(100% - 60px)' }}>
              <div style={{ 
                width: '100%', 
                height: '100%',
                display: 'flex',
                flexDirection: 'column'
              }}>
                <textarea
                  data-patient-notes
                  readOnly
                  placeholder={notesLoading ? "Loading patient notes..." : "Click Edit to modify patient notes..."}
                  value={patientNotes}
                  style={{
                    width: '100%',
                    height: '100%',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    padding: '15px',
                    fontSize: '18px',
                    fontFamily: 'Arial, sans-serif',
                    resize: 'none',
                    outline: 'none',
                    backgroundColor: '#e9ecef',
                    boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.1)',
                    cursor: 'not-allowed'
                  }}
                  onChange={async (e) => {
                    const newValue = e.target.value;
                    setPatientNotes(newValue);
                    
                    // Auto-save to database
                    if (patId) {
                      try {
                        await updatePatientNotes(patId, newValue);
                        console.log('[FE] Patient notes saved to database');
                      } catch (error) {
                        console.error('[FE] Error saving patient notes:', error);
                        // Optionally show error message to user
                      }
                    }
                  }}
                />
              </div>
            </div>)}
          </div>
          
          {/* Activity Summary */}
          <div className="current-panel" style={{ padding: '10px 10px', minHeight: 185, backgroundColor: '#f8fafc' }}>
            <div className="current-panel-header" style={{ textAlign: 'center' }}>
              <div className="current-panel-title">{activityTitleCurrent}</div>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#4b5563', marginTop: 2 }}>{summaryRangeLabel}</div>
            </div>
            <div className="current-panel-content" style={{ marginTop: 8, justifyContent: 'center', alignItems: 'center' }}>
              {(() => {
                const stillMinutes = activityTimelineData.summaryMinutes.still;
                const walkingMinutes = activityTimelineData.summaryMinutes.walking;
                const joggingMinutes = activityTimelineData.summaryMinutes.jogging;
                const sleepMinutes = activityTimelineData.summaryMinutes.sleep;
                const totalMinutes = stillMinutes + walkingMinutes + joggingMinutes + sleepMinutes;
                const pct = (v: number) => (totalMinutes > 0 ? Math.round((v / totalMinutes) * 100) : 0);
                const rows = [
                  { key: 'still', label: 'Sedentary', minutes: stillMinutes, color: ACTIVITY_COLORS.still },
                  { key: 'walking', label: 'Walking', minutes: walkingMinutes, color: ACTIVITY_COLORS.walking },
                  { key: 'jogging', label: 'Jogging', minutes: joggingMinutes, color: ACTIVITY_COLORS.jogging },
                  { key: 'sleep', label: 'Sleep', minutes: sleepMinutes, color: ACTIVITY_COLORS.sleep },
                ].filter((row) => row.minutes > 0);

                if (totalMinutes <= 0) {
                  return (
                    <div style={{ textAlign: 'center', color: '#6b7280', fontStyle: 'italic', fontSize: 14 }}>
                      No activity data for this range
                    </div>
                  );
                }

                return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 15, width: '100%', maxWidth: 260, margin: '0 auto' }}>
                    {rows.map((row) => (
                      <div key={row.key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ width: 10, height: 10, borderRadius: 2, background: row.color, display: 'inline-block' }} />
                          {row.label}
                        </span>
                        <span style={{ fontWeight: 600 }}>
                          {Math.round(row.minutes)} min ({pct(row.minutes)}%)
                        </span>
                      </div>
                    ))}
                    <div style={{ borderTop: '1px solid #dbe2ea', paddingTop: 8, marginTop: 2, display: 'flex', justifyContent: 'space-between', fontWeight: 700 }}>
                      <span>Total tracked</span>
                      <span>{Math.round(totalMinutes)} min</span>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>

          {/* Heart Rate Current — Summary: Median, Night Median, min, max, % invalid, Warnings, Critical events; demo: only Median, Night Median */}
          <div className={`current-panel bg-heart ${(hrDisplay.median == null && hrDisplay.min === 0 && hrDisplay.max === 0) ? 'risk-grey' : getEventRiskClass(criticalCountsByCategory['Heart Rate'].warnings, criticalCountsByCategory['Heart Rate'].critical)}`} style={{ padding: hrCollapsed ? '2.7px 5px' : '4.5px 0px', minHeight: hrCollapsed ? 68 : 300 }}>
            <div className="panel-bg" style={{ backgroundImage: "url('/heartrate.png')" }}></div>
            <div className="current-panel-header" style={{ textAlign: 'center' }}>
              <div className="current-panel-title">{heartTitleCurrent}</div>
              {!hrCollapsed && <div style={{ fontSize: 20, fontWeight: 700, color: '#222', marginTop: 2 }}>{summaryRangeLabel}</div>}
            </div>
            {!hrCollapsed && (
            <div className="current-panel-content">
              <div className="current-panel-left" style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 15, fontSize: 20 }}>
                {renderSummaryValueWithWristMode(hrDisplay.median != null ? `${Math.round(hrDisplay.median)} BPM` : '-')}
                <div>Night: {hrDisplay.nightMedian != null ? `${Math.round(hrDisplay.nightMedian)} BPM` : '-'}</div>
                <div>{hrDisplay.min === 0 && hrDisplay.max === 0 ? '↓-↑-' : <>↓ {hrDisplay.min} ↑ {hrDisplay.max}</>}</div>
                <div>{displayHeartRateInvalidPct != null ? `${Number(displayHeartRateInvalidPct).toFixed(1)}% out of range` : '-% out of range'}</div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, marginTop: 8 }}>
                  <span>⚠️ Warnings: {criticalCountsByCategory['Heart Rate'].warnings}</span>
                  <span>🔴 Critical events: {criticalCountsByCategory['Heart Rate'].critical}</span>
                </div>
              </div>
            </div>
            )}
          </div>


          {/* Heart Rate Variability Current — Summary */}
          <div className={`current-panel bg-hrv ${(hrvDisplay.median == null && hrvDisplay.min === 0 && hrvDisplay.max === 0) ? 'risk-grey' : getEventRiskClass(criticalCountsByCategory['HRV'].warnings, criticalCountsByCategory['HRV'].critical)}`} style={{ padding: hrvCollapsed ? '2.7px 5px' : '4.5px 0px', minHeight: hrvCollapsed ? 68 : 260 }}>
            <div className="panel-bg" style={{ backgroundImage: "url('/heartratevariability.png')" }}></div>
            <div className="current-panel-header" style={{ textAlign: 'center' }}>
              <div className="current-panel-title">{heartVariabilityTitleCurrent}</div>
              {!hrvCollapsed && <div style={{ fontSize: 20, fontWeight: 700, color: '#222', marginTop: 2 }}>{summaryRangeLabel}</div>}
            </div>
            {!hrvCollapsed && (
            <div className="current-panel-content">
              <div className="current-panel-left" style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, fontSize: 20 }}>
                {renderSummaryValueWithWristMode(hrvDisplay.median != null ? `${Math.round(hrvDisplay.median)} ms` : '-')}
                <div>Night: {hrvDisplay.nightMedian != null ? `${Math.round(hrvDisplay.nightMedian)} ms` : '-'}</div>
                <div>{hrvDisplay.min === 0 && hrvDisplay.max === 0 ? '↓-↑-' : <>↓ {hrvDisplay.min} ↑ {hrvDisplay.max}</>}</div>
                <div>{displayHrvInvalidPct != null ? `${Number(displayHrvInvalidPct).toFixed(1)}% out of range` : '-% out of range'}</div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, marginTop: 4 }}>
                  <span>⚠️ Warnings: {criticalCountsByCategory['HRV'].warnings}</span>
                  <span>🔴 Critical events: {criticalCountsByCategory['HRV'].critical}</span>
                </div>
              </div>
            </div>
            )}
          </div>

          {/* Systolic/Diastolic Blood Pressure Current — Summary */}
          {(() => {
            const bpNoData = (systolicDisplay.median == null && systolicDisplay.min === 0 && systolicDisplay.max === 0) && (diastolicDisplay.median == null && diastolicDisplay.min === 0 && diastolicDisplay.max === 0);
            const bpEventRisk = bpNoData ? 'risk-grey' : getEventRiskClass(criticalCountsByCategory['Blood Pressure'].warnings, criticalCountsByCategory['Blood Pressure'].critical);
            const bpOutOfRangeStr = (displaySystolicBpInvalidPct != null || displayDiastolicBpInvalidPct != null)
              ? `${displaySystolicBpInvalidPct != null ? Number(displaySystolicBpInvalidPct).toFixed(1) : '-'}% / ${displayDiastolicBpInvalidPct != null ? Number(displayDiastolicBpInvalidPct).toFixed(1) : '-'}%`
              : '-';
            return (
              <div className={`current-panel bg-bp ${bpEventRisk}`} style={{  padding: bpCollapsed ? '3.5px 16px' : '4.5px 0px', minHeight: bpCollapsed ? 68 : 260 }}>
                <div className="panel-bg" style={{ backgroundImage: "url('/presure.jpg')" }}></div>
                <div className="current-panel-header" style={{ textAlign: 'center' }}>
                  <div className="current-panel-title">{systolic_diastolicBloodPressureTitleCurrent}</div>
                  {!bpCollapsed && <div style={{ fontSize: 20, fontWeight: 700, color: '#222', marginTop: 2 }}>{summaryRangeLabel}</div>}
                </div>
                {!bpCollapsed && (
                <div className="current-panel-content">
                  <div className="current-panel-left" style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, fontSize: 20 }}>
                    {renderSummaryValueWithWristMode(systolicDisplay.median != null && diastolicDisplay.median != null ? `${Math.round(systolicDisplay.median)} / ${Math.round(diastolicDisplay.median)} mmHg` : '-')}
                    <div>Night: {systolicDisplay.nightMedian != null && diastolicDisplay.nightMedian != null ? `${Math.round(systolicDisplay.nightMedian)} / ${Math.round(diastolicDisplay.nightMedian)} mmHg` : '-'}</div>
                    <div>Systolic: {systolicDisplay.min === 0 && systolicDisplay.max === 0 ? '↓-↑-' : <>↓ {systolicDisplay.min} ↑ {systolicDisplay.max} mmHg</>}</div>
                    <div>Diastolic: {diastolicDisplay.min === 0 && diastolicDisplay.max === 0 ? '↓-↑-' : <>↓ {diastolicDisplay.min} ↑ {diastolicDisplay.max} mmHg</>}</div>
                    <div>{bpOutOfRangeStr != null ? `${bpOutOfRangeStr} out of range` : '-% out of range'}</div>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, marginTop: 4 }}>
                      <span>⚠️ Warnings: {criticalCountsByCategory['Blood Pressure'].warnings}</span>
                      <span>🔴 Critical events: {criticalCountsByCategory['Blood Pressure'].critical}</span>
                    </div>
                  </div>
                </div>
                )}
              </div>
            );
          })()}

          {/* SpO2 Current — Summary */}
          <div className={`current-panel bg-spo2 ${(spo2Display.median == null && spo2Display.min === 0 && spo2Display.max === 0) ? 'risk-grey' : getEventRiskClass(criticalCountsByCategory['SpO2'].warnings, criticalCountsByCategory['SpO2'].critical)}`} style={{  padding: spo2Collapsed ? '2.7px 5px' : '4.5px 0px', minHeight: spo2Collapsed ? 68 : 260 }}>
            <div className="panel-bg" style={{ backgroundImage: "url('/spo2.png')" }}></div>
            <div className="current-panel-header" style={{ textAlign: 'center' }}>
              <div className="current-panel-title">{spo2TitleCurrent}</div>
              {!spo2Collapsed && <div style={{ fontSize: 20, fontWeight: 700, color: '#222', marginTop: 2 }}>{summaryRangeLabel}</div>}
            </div>
            {!spo2Collapsed && (
            <div className="current-panel-content">
              <div className="current-panel-left" style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, fontSize: 20 }}>
                {renderSummaryValueWithWristMode(spo2Display.median != null ? `${Number(spo2Display.median).toFixed(1)}%` : '-')}
                <div>Night: {spo2Display.nightMedian != null ? `${Number(spo2Display.nightMedian).toFixed(1)}%` : '-'}</div>
                <div>{spo2Display.min === 0 && spo2Display.max === 0 ? '↓-↑-' : <>↓ {Number(spo2Display.min).toFixed(1)} ↑ {Number(spo2Display.max).toFixed(1)}</>}</div>
                <div>{displaySpo2InvalidPct != null ? `${Number(displaySpo2InvalidPct).toFixed(1)}% out of range` : '-% out of range'}</div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, marginTop: 4 }}>
                  <span>⚠️ Warnings: {criticalCountsByCategory['SpO2'].warnings}</span>
                  <span>🔴 Critical events: {criticalCountsByCategory['SpO2'].critical}</span>
                </div>
              </div>
            </div>
            )}
          </div>
          

          {/* Breathing Rate Current — Summary */}
          <div className={`current-panel ${(breathingDisplay.median == null && breathingDisplay.min === 0 && breathingDisplay.max === 0) ? 'risk-grey' : getEventRiskClass(criticalCountsByCategory['RESP'].warnings, criticalCountsByCategory['RESP'].critical)}`} style={{padding: respCollapsed ? '2.7px 5px' : '4.5px 0px', minHeight: respCollapsed ? 68 : 260 }}>
            <div className="panel-bg" style={{ backgroundImage: "url('/resp.jpg')" }}></div>
            <div className="current-panel-header" style={{ textAlign: 'center' }}>
              <div className="current-panel-title">{breathingTitleCurrent}</div>
              {!respCollapsed && <div style={{ fontSize: 20, fontWeight: 700, color: '#222', marginTop: 2 }}>{summaryRangeLabel}</div>}
            </div>
            {!respCollapsed && (
            <div className="current-panel-content">
              <div className="current-panel-left" style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, fontSize: 20 }}>
                {renderSummaryValueWithWristMode(breathingDisplay.median != null ? `${Math.round(breathingDisplay.median)} /min` : '-')}
                <div>Night: {breathingDisplay.nightMedian != null ? `${Math.round(breathingDisplay.nightMedian)} /min` : '-'}</div>
                <div>{breathingDisplay.min === 0 && breathingDisplay.max === 0 ? '↓-↑-' : <>↓ {breathingDisplay.min} ↑ {breathingDisplay.max}</>}</div>
                <div>{displayRespiratoryInvalidPct != null ? `${Number(displayRespiratoryInvalidPct).toFixed(1)}% out of range` : '-% out of range'}</div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, marginTop: 4 }}>
                  <span>⚠️ Warnings: {criticalCountsByCategory['RESP'].warnings}</span>
                  <span>🔴 Critical events: {criticalCountsByCategory['RESP'].critical}</span>
                </div>
              </div>
            </div>
            )}
          </div>

            {/* Temperature Current — Summary */}
            <div className={`current-panel bg-temp ${(tempDisplay.median == null && tempDisplay.min === 0 && tempDisplay.max === 0) ? 'risk-grey' : getEventRiskClass(criticalCountsByCategory['Skin Temperature'].warnings, criticalCountsByCategory['Skin Temperature'].critical)}`} style={{padding: tempCollapsed ? '2.7px 5px' : '4.5px 0px',  minHeight: tempCollapsed ? 68 : 260 }}>
              <div className="panel-bg" style={{ backgroundImage: "url('/temperature.png')" }}></div>
            <div className="current-panel-header" style={{ textAlign: 'center' }}>
              <div className="current-panel-title">Skin Temperature Summary</div>
              {!tempCollapsed && <div style={{ fontSize: 20, fontWeight: 700, color: '#222', marginTop: 2 }}>{summaryRangeLabel}</div>}
            </div>
            {!tempCollapsed && (
            <div className="current-panel-content">
              <div className="current-panel-left" style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, fontSize: 20 }}>
                {renderSummaryValueWithWristMode(tempDisplay.median != null ? `${Number(tempDisplay.median).toFixed(1)}°C` : '-')}
                <div>Night: {tempDisplay.nightMedian != null ? `${Number(tempDisplay.nightMedian).toFixed(1)}°C` : '-'}</div>
                <div>{tempDisplay.min === 0 && tempDisplay.max === 0 ? '↓-↑-' : <>↓ {Number(tempDisplay.min).toFixed(1)} ↑ {Number(tempDisplay.max).toFixed(1)}</>}</div>
                <div>{displayTempInvalidPct != null ? `${Number(displayTempInvalidPct).toFixed(1)}% out of range` : '-% out of range'}</div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, marginTop: 4 }}>
                  <span>⚠️ Warnings: {criticalCountsByCategory['Skin Temperature'].warnings}</span>
                  <span>🔴 Critical events: {criticalCountsByCategory['Skin Temperature'].critical}</span>
                </div>
              </div>
            </div>
            )}
          </div>

          {/* Sleep Stage Summary */}
          <div className={`current-panel sleep-summary-panel ${sleepScoreResult ? getSleepScoreColorClass(sleepScoreResult.score.sleepScore) : 'risk-grey'}`} style={{  padding: sleepCollapsed ? '2.7px 5px' : '4.5px 0px', minHeight: sleepCollapsed ? 74 : 360 }}>
            <div className="current-panel-header" style={{ textAlign: 'center' }}>
              <div className="current-panel-title">Sleep Stage Summary</div>
              {!sleepCollapsed && (
                <div style={{ fontSize: 20, fontWeight: 700, color: '#222', marginTop: 2 }}>{sleepNightLabel}</div>
              )}
            </div>
            {!sleepCollapsed && (
            <div className="current-panel-content sleep-summary-content">
              <div className="sleep-summary-body" key={sleepSummaryView}>
                {sleepSummaryView === 'score' ? (
                  sleepScoreResult ? (
                    <div className="sleep-summary-score-view" style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, fontSize: 20 }}>
                      <div className="sleep-summary-heading">
                        Sleep Score: {sleepScoreResult.score.classification}
                      </div>
                      <div>
                        <span style={{ fontSize: 28, fontWeight: 700, color: '#222' }}>
                          {sleepScoreResult.score.sleepScore}
                          <span style={{ fontSize: 18, fontWeight: 600 }}> / 100</span>
                        </span>
                      </div>
                      <div className="sleep-summary-metrics">
                        <SleepScoreMetric
                          label={`Duration: ${sleepScoreResult.score.durationScore}/${DURATION_MAX_POINTS}`}
                          helpText={sleepScoreResult.metricDetails.duration}
                        />
                        <SleepScoreMetric
                          label={`Consistency: ${sleepScoreResult.score.consistencyScore}/${CONSISTENCY_MAX_POINTS}`}
                          helpText={sleepScoreResult.metricDetails.consistency}
                        />
                        <SleepScoreMetric
                          label={`Interruptions: ${sleepScoreResult.score.interruptionScore}/${INTERRUPTION_MAX_POINTS}`}
                          helpText={sleepScoreResult.metricDetails.interruptions}
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="sleep-summary-empty">No sleep score for this night</div>
                  )
                ) : (
                  sleepStageSummary ? (
                    <div className="sleep-summary-breakdown-view" style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, fontSize: 20 }}>
                      <div className="sleep-summary-breakdown-title">Stage Breakdown</div>
                      <div className="sleep-summary-breakdown-item">Awake: {formatSleepDuration(sleepStageSummary.awake.minutes)} ({sleepStageSummary.awake.percent}%)</div>
                      <div className="sleep-summary-breakdown-item">REM: {formatSleepDuration(sleepStageSummary.rem.minutes)} ({sleepStageSummary.rem.percent}%)</div>
                      <div className="sleep-summary-breakdown-item">Light: {formatSleepDuration(sleepStageSummary.light.minutes)} ({sleepStageSummary.light.percent}%)</div>
                      <div className="sleep-summary-breakdown-item">Deep: {formatSleepDuration(sleepStageSummary.deep.minutes)} ({sleepStageSummary.deep.percent}%)</div>
                    </div>
                  ) : (
                    <div className="sleep-summary-empty">No sleep stage data for this night</div>
                  )
                )}
              </div>
              <button
                type="button"
                className="sleep-summary-swap-btn"
                onClick={() => setSleepSummaryView((view) => (view === 'score' ? 'breakdown' : 'score'))}
                title={sleepSummaryView === 'score' ? 'Show stage breakdown' : 'Show sleep score'}
              >
                <span className="sleep-summary-swap-icon" aria-hidden="true">⇄</span>
                <span>{sleepSummaryView === 'score' ? 'Stage Breakdown' : 'Sleep Score'}</span>
              </button>
            </div>
            )}
          </div>
        </div>
      </div>
      )}
    </div>
  );
};

// HistoryPage component moved to src/pages/HistoryPage.tsx

/**
 * MAIN APP COMPONENT - X(EPI)BLEPSIS Medical Monitoring System
 * 
 * This is the root component of the entire medical monitoring application. It manages
 * the complete application state, user authentication, navigation, and real-time
 * medical data monitoring. The component orchestrates all major application features
 * including patient management, vital signs monitoring, medical history collection,
 * and critical event alerting.
 * 
 * Key Responsibilities:
 * - User authentication and role-based access control (Doctor, Patient, Parent)
 * - Application navigation and page routing
 * - Real-time vital signs data fetching and visualization
 * - Critical medical event detection and alerting
 * - Patient management and medical history collection
 * - Demo data generation for testing and development
 * - Backend health monitoring and connection management
 * 
 * State Management:
 * - currentPage: Controls which page/component is currently displayed
 * - user: Firebase user profile and authentication state
 * - currentPatId: Currently selected patient ID for data filtering
 * - userRole: User's role (doctor/patient/parent) for access control
 * 
 * Data Flow:
 * 1. User authentication via Firebase
 * 2. Role determination and navigation setup
 * 3. Real-time data fetching from backend API
 * 4. Critical event detection and alerting
 * 5. Data visualization using interactive medical charts
 * 6. Form submission and data persistence
 */
const App: React.FC = () => {
  // Application state management for navigation and user context
  const [currentPage, setCurrentPage] = useState<Page>(() => determineInitialPage());           // Current page/route in the application
  const [previousPage, setPreviousPage] = useState<Page | null | undefined>(null);     // Previous page for back navigation
  const [user, setUser] = useState<UserProfile | null>(null);            // Firebase user profile and authentication state
  const [currentPatId, setCurrentPatId] = useState<string>('');           // Currently selected patient ID for data filtering
  const [userRole, setUserRole] = useState<string>('');            // User's role for access control (doctor/patient/parent)
  const [thresholdsPresetMode, setThresholdsPresetMode] = useState<boolean>(false);  // true when doctor edits their preset thresholds from Settings
  const [showSessionInvalidated, setShowSessionInvalidated] = useState(false);

  useEffect(() => {
    return onSessionInvalidated(() => {
      setShowSessionInvalidated(true);
    });
  }, []);

  useEffect(() => {
    if (currentPage !== 'dashboard') {
      void lockPortraitForPhone();
    }
  }, [currentPage]);

  const persistLastPage = useCallback((page: Page | null) => {
    try {
      if (page && !AUTH_ONLY_PAGES.has(page)) {
        window.localStorage.setItem('lastPage', page);
      } else {
        window.localStorage.removeItem('lastPage');
      }
    } catch (_err) {
      // ignore storage errors
    }
  }, []);

  const applyPageChange = useCallback((page: Page, method: 'push' | 'replace' = 'push') => {
    setCurrentPage(page);
    persistLastPage(page);
    const targetPath = getPathForPage(page);

    if (method === 'replace') {
      if (window.location.pathname !== targetPath) {
        window.history.replaceState({ page }, '', targetPath);
      }
    } else {
      if (window.location.pathname !== targetPath) {
        window.history.pushState({ page }, '', targetPath);
      } else {
        // Ensure history state is updated even if path is the same
        window.history.replaceState({ page }, '', targetPath);
      }
    }
  }, [persistLastPage]);

  useEffect(() => {
    if (currentPage !== 'admin-errors' && currentPage !== 'admin-fetch-raw' && currentPage !== 'admin-login-attempts') {
      return;
    }
    if (!user) {
      applyPageChange('login', 'replace');
      return;
    }
    if (isSecurityAdminEmail(user.email)) {
      if (currentPage !== 'admin-login-attempts') {
        applyPageChange('admin-login-attempts', 'replace');
      }
      return;
    }
    if (!isAdminWebEmail(user.email)) {
      applyPageChange(selectPageForUser(userRole, user.email, null), 'replace');
      return;
    }
    if (currentPage === 'admin-fetch-raw' && !isFetchRawAdminEmail(user.email)) {
      applyPageChange('admin-errors', 'replace');
      return;
    }
    if (currentPage === 'admin-login-attempts') {
      applyPageChange(getFallbackPageForUser(userRole, user.email), 'replace');
    }
  }, [applyPageChange, currentPage, user, userRole]);

  const clearStoredSession = useCallback(() => {
    setUser(null);
    try {
      localStorage.removeItem('xv_session');
      localStorage.removeItem('currentUserEmail');
      localStorage.removeItem('selectedPatId');
      localStorage.removeItem('xv_auth_token');
      localStorage.removeItem('xv_pat_id');
      localStorage.removeItem('xv_doc_id');
    } catch (_err) {
      // ignore storage errors
    }
  }, []);

  const forceRedirectToLogin = useCallback(() => {
    clearStoredSession();
    if (typeof window === 'undefined') return;
    const loginPath = getPathForPage('login');
    try {
      window.history.replaceState({ page: 'login' }, '', loginPath);
    } catch (_err) {
      // ignore state errors
    }
    window.location.replace(loginPath);
  }, [clearStoredSession]);

  // Handle browser back button to navigate between in-app pages instead of logging out
  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      const statePage = isPageValue(event?.state?.page) ? (event.state.page as Page) : getPageFromPath(window.location.pathname);
      if (!statePage) {
        applyPageChange('login', 'replace');
        return;
      }
      const requiresAuth = !AUTH_ONLY_PAGES.has(statePage);
      if (requiresAuth && !user) {
        applyPageChange('login', 'replace');
        return;
      }
      applyPageChange(statePage, 'replace');
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [applyPageChange, user]);

  // Prevent forward navigation (BFCache) from restoring protected pages after logout
  useEffect(() => {
    const handlePageShow = (event: PageTransitionEvent) => {
      const restoredFromCache = event.persisted || (window.performance && window.performance.getEntriesByType
        ? window.performance.getEntriesByType('navigation').some((entry: any) => entry.type === 'back_forward')
        : false);
      if (!restoredFromCache) return;
      const currentPathPage = getPageFromPath(window.location.pathname);
      if (currentPathPage && !AUTH_ONLY_PAGES.has(currentPathPage) && !user) {
        applyPageChange('login', 'replace');
        forceRedirectToLogin();
      }
    };

    window.addEventListener('pageshow', handlePageShow);
    return () => window.removeEventListener('pageshow', handlePageShow);
  }, [applyPageChange, forceRedirectToLogin, user]);

  // Clear stored session on the login screen only when nobody is signed in (avoid wiping mid-login).
  useEffect(() => {
    if (currentPage === 'login' && !user) {
      clearStoredSession();
    }
  }, [clearStoredSession, currentPage, user]);

  useEffect(() => {
    // Check if this is a verification URL with email parameter
    if (window.location.pathname.includes('/verify') && window.location.search.includes('email=')) {
      applyPageChange('verify', 'replace');
      return;
    }
    
    // Check if this is a reset password URL with email parameter
    if (window.location.pathname.includes('/reset-password') && window.location.search.includes('email=')) {
      applyPageChange('reset-password', 'replace');
      return;
    }
    
    // Check if this is a patient verification URL with email parameter
    if ((window.location.pathname.includes('/verify-patient') || window.location.search.includes('verify-patient')) && window.location.search.includes('email=')) {
      applyPageChange('verify-patient', 'replace');
      return;
    }
    
    // Check if this is a patient reset password URL with email parameter
    if ((window.location.pathname.includes('/reset-password') || window.location.search.includes('reset-password')) && window.location.search.includes('email=')) {
      applyPageChange('reset-password-patient', 'replace');
      return;
    }
    
    // Set current page based on pathname for auth pages (login, register, forgot-password)
    const pathname = window.location.pathname;
    if (pathname === '/' || pathname === '/login') {
      applyPageChange('login', 'replace');
    } else if (pathname === '/register') {
      applyPageChange('register', 'replace');
    } else if (pathname === '/forgot-password') {
      applyPageChange('forgot-password', 'replace');
    }
    
    if (!auth) {
      // Don't check session if user is on login, register, or forgot-password pages
      const isAuthPage = pathname === '/' || pathname === '/login' || pathname === '/register' || pathname === '/forgot-password';
      
      if (!isAuthPage) {
        const stored = localStorage.getItem('xv_session');
        if (stored) {
          try {
            const sess = JSON.parse(stored);
            if (sess?.email) {
              setUser({ name: sess.email.split('@')[0], email: sess.email, language: 'English' });
              if (sess.role) setUserRole(sess.role);
              if (sess.email === 'demo@example.com') {
                setUserRole('doctor');
                applyPageChange('dashboard', 'push');
              } else if (isAdminWebEmail(sess.email)) {
                setUserRole('doctor');
                applyPageChange(getFallbackPageForUser('doctor', sess.email), 'push');
              } else {
                applyPageChange('dashboard', 'push');
              }
            }
          } catch {}
        }
      }
      return;
    }
    const unsub = onAuthStateChanged(auth, async (fbUser) => {
      if (fbUser) {
        const profile: UserProfile = {
          name: fbUser.displayName || (fbUser.email ? fbUser.email.split('@')[0] : 'User'),
          email: fbUser.email || '',
          language: 'English',
        };
        setUser(profile);
        try { localStorage.setItem('currentUserEmail', profile.email || ''); } catch {}
        
        // Load user role and navigate accordingly
        try {
          if (profile.email) {
            const isDemoUser = profile.email === 'demo@example.com';
            // For demo users, always go to dashboard
            if (isDemoUser) {
              setUserRole('doctor');
              applyPageChange('dashboard', 'replace');
              return;
            }

            if (isAdminWebEmail(profile.email)) {
              setUserRole('doctor');
              applyPageChange(getFallbackPageForUser('doctor', profile.email), 'replace');
              return;
            }
            
            console.log('[FE] Loading user role for email:', profile.email);
            const roleData = await getUserRole(profile.email);
            setUserRole(roleData.role);
            console.log('[FE] User role loaded:', roleData.role);
            
            const requestedPage = getPageFromPath(window.location.pathname);
            const targetPage = selectPageForUser(roleData.role, profile.email, requestedPage);
            applyPageChange(targetPage, 'replace');
          } else {
            applyPageChange('login', 'push');
          }
        } catch (error) {
          console.error('[FE] Error loading user role:', error);
          // Default to dashboard if role check fails
          applyPageChange('login', 'push');
        }
        return;
      }
      // Don't check session if user is on login, register, or forgot-password pages
      const pathname = window.location.pathname;
      const isAuthPage = pathname === '/' || pathname === '/login' || pathname === '/register' || pathname === '/forgot-password';
      
      if (!isAuthPage) {
        const stored = localStorage.getItem('xv_session');
        if (stored) {
          try {
            const sess = JSON.parse(stored);
            if (sess?.email) {
              const profile: UserProfile = {
                name: sess.email.split('@')[0],
                email: sess.email,
                language: 'English',
              };
              setUser(profile);
              try { localStorage.setItem('currentUserEmail', profile.email || ''); } catch {}
              if (sess.role) setUserRole(sess.role);
              
              // Load user role and navigate accordingly
              try {
                if (profile.email) {
                  const isDemoUser = profile.email === 'demo@example.com';
                  // For demo users, always go to dashboard
                  if (isDemoUser) {
                    setUserRole('doctor');
                    applyPageChange('dashboard', 'replace');
                    return;
                  }

                  if (isAdminWebEmail(profile.email)) {
                    setUserRole('doctor');
                    applyPageChange(getFallbackPageForUser('doctor', profile.email), 'replace');
                    return;
                  }
                  
                  console.log('[FE] Loading user role for email:', profile.email);
                  const roleData = await getUserRole(profile.email);
                  setUserRole(roleData.role);
                  console.log('[FE] User role loaded:', roleData.role);
                  
                  const requestedPage = getPageFromPath(window.location.pathname);
                  const targetPage = selectPageForUser(roleData.role, profile.email, requestedPage);
                  applyPageChange(targetPage, 'replace');
                } else {
                  applyPageChange('dashboard', 'push');
                }
              } catch (error) {
                console.error('[FE] Error loading user role:', error);
                // Default to dashboard if role check fails
                applyPageChange('dashboard', 'push');
              }
              return;
            }
          } catch {}
        }
      }
      // Database login stores xv_session without a Firebase user — do not wipe that session.
      if (localStorage.getItem('xv_session')) {
        return;
      }
      setUser(null);
      try { localStorage.removeItem('currentUserEmail'); } catch {}
      persistLastPage(null);
      applyPageChange('login', 'push');
    });
    return () => unsub && unsub();
  }, [applyPageChange, persistLastPage]);

  const handleLogin = async (u: UserProfile) => {
    setUser(u);
    try { localStorage.setItem('currentUserEmail', u.email || ''); } catch {}
    
    // Check if this is a demo user
    const isDemoUser = u.email === 'demo@example.com';
    
    // Load user role
    let userRole = 'patient'; // Default role
    try {
      if (u.email && !isDemoUser) {
        if (u.role) {
          userRole = u.role;
          setUserRole(u.role);
          console.log('[FE] User role from login:', u.role);
        } else {
          console.log('[FE] Loading user role for email:', u.email);
          const roleData = await getUserRole(u.email);
          userRole = roleData.role;
          setUserRole(roleData.role);
          console.log('[FE] User role loaded:', roleData.role);
        }
      } else if (isDemoUser) {
        // Demo user gets doctor role
        userRole = 'doctor';
        setUserRole('doctor');
        console.log('[FE] Demo user - setting role to doctor');
      }
    } catch (error) {
      console.error('[FE] Error loading user role:', error);
      // Default to doctor if role check fails
      userRole = 'doctor';
      setUserRole('doctor');
    }
    
  // Admin users land on their dedicated admin dashboard.
    if (isAdminWebEmail(u.email)) {
      setUserRole('doctor');
    applyPageChange(getFallbackPageForUser('doctor', u.email), 'push');
      return;
    }

    // For demo users, always go to dashboard instead of patients page
    if (isDemoUser) {
      applyPageChange('dashboard', 'push');
      return;
    }
    
    const requestedPage = getPageFromPath(window.location.pathname);
    const targetPage = selectPageForUser(userRole, u.email, requestedPage);
    applyPageChange(targetPage, 'push');
  };

  const handleGoToRegister = () => {
    applyPageChange('register', 'push');
  };

  const handleGoToForgotPassword = () => {
    applyPageChange('forgot-password', 'push');
  };

  const handleGoToLogin = () => {
    applyPageChange('login', 'push');
  };

  const handleRegisterSuccess = () => {
    applyPageChange('dashboard', 'push');
  };

  const handleLogout = async () => {
    resetSessionInvalidation();
    setShowSessionInvalidated(false);
    clearStoredSession();
    try { if (auth) await signOut(auth); } catch {}
    persistLastPage(null);
    applyPageChange('login', 'replace');
    if (!Capacitor.isNativePlatform()) {
      forceRedirectToLogin();
    }
  };

  // Custom navigation function that remembers previous page
  // options.keepPatient: when true and navigating to history, keep current patient (e.g. from pencil); otherwise clear so user must select
  const handleNavigate = (page: Page, options?: { keepPatient?: boolean }) => {
    // Store current page as previous before navigating
    if (currentPage !== 'login' && currentPage !== 'register') {
      setPreviousPage(currentPage);
    }
    if (currentPage === 'thresholds' && page !== 'thresholds') {
      setThresholdsPresetMode(false);
    }
    if ((page === 'history' || page === 'timeline' || page === 'history-debug') && !options?.keepPatient) {
      setCurrentPatId('');
    }
    applyPageChange(page, 'push');
  };
  const compatiblePreviousPage =
    !previousPage
    || previousPage === 'admin-errors'
    || previousPage === 'admin-fetch-raw'
    || previousPage === 'admin-login-attempts'
      ? undefined
      : previousPage === 'history-debug'
        ? 'history'
        : (previousPage as Exclude<Page, 'history-debug' | 'admin-errors' | 'admin-fetch-raw' | 'admin-login-attempts'>);

  return (
    <>
      {currentPage === 'login' && (
        <LoginPage onLogin={handleLogin} onGoToRegister={handleGoToRegister} onGoToForgotPassword={handleGoToForgotPassword} />
      )}
      {currentPage === 'register' && (
        <RegisterPage onGoToLogin={handleGoToLogin} onRegisterSuccess={handleRegisterSuccess} />
      )}
      {currentPage === 'verify' && (
        <VerificationPage onGoToLogin={handleGoToLogin} />
      )}
      {currentPage === 'forgot-password' && (
        <ForgotPasswordPage onGoToLogin={handleGoToLogin} />
      )}
      {currentPage === 'reset-password' && (
        <ResetPasswordPage onGoToLogin={handleGoToLogin} />
      )}
      {currentPage === 'verify-patient' && (
        <PatientVerificationPage onGoToLogin={handleGoToLogin} />
      )}
      {currentPage === 'reset-password-patient' && (
        <PatientResetPasswordPage onGoToLogin={handleGoToLogin} />
      )}
      {currentPage === 'dashboard' && (
        <Dashboard onLogout={handleLogout} onNavigate={handleNavigate} onPatIdChange={setCurrentPatId} userRole={userRole} initialDemoMode={user?.email === 'demo@example.com'} />
      )}
      {currentPage === 'patients' && userRole === 'doctor' && (
        <PatientsPage onNavigate={handleNavigate} onLogout={handleLogout} isDemo={user?.email === 'demo@example.com'} />
      )}
      {currentPage === 'history' && (
        <HistoryPage
          onLogout={handleLogout}
          onNavigate={handleNavigate}
          userRole={userRole}
          isDemo={user?.email === 'demo@example.com'}
          patId={currentPatId}
          onPatIdChange={setCurrentPatId}
          previousPage={compatiblePreviousPage || undefined}
        />
      )}
      {currentPage === 'timeline' && (
        <TimelinePage
          onLogout={handleLogout}
          onNavigate={handleNavigate}
          userRole={userRole}
          isDemo={user?.email === 'demo@example.com'}
          patId={currentPatId}
          onPatIdChange={setCurrentPatId}
        />
      )}
      {currentPage === 'settings' && (
        <SettingsPage
          onLogout={handleLogout}
          onNavigate={handleNavigate}
          onPatIdChange={setCurrentPatId}
          user={user}
          onUpdateUser={(updates) => setUser((prev) => ({ ...(prev as any), ...updates }))}
          userRole={userRole}
          isDemo={user?.email === 'demo@example.com'}
          onOpenPresetThresholds={userRole === 'doctor' ? () => { setThresholdsPresetMode(true); handleNavigate('thresholds'); } : undefined}
        />
      )}
      {currentPage === 'consent' && (
        <ConsentPage onLogout={handleLogout} onNavigate={handleNavigate} userRole={userRole} isDemo={user?.email === 'demo@example.com'} />
      )}
      {currentPage === 'thresholds' && (
        <ThresholdsPage
          onNavigate={handleNavigate}
          patId={currentPatId}
          previousPage={compatiblePreviousPage || undefined}
          isDemo={user?.email === 'demo@example.com'}
          presetMode={thresholdsPresetMode}
          userRole={userRole}
        />
      )}
      {currentPage === 'medication' && (
        <MedicationPage onNavigate={handleNavigate} patId={currentPatId} previousPage={compatiblePreviousPage || undefined} isDemo={user?.email === 'demo@example.com'} />
      )}
      {currentPage === 'admin-errors' && (
        <AdminPhoneErrorsPage userEmail={user?.email} onLogout={handleLogout} />
      )}
      {currentPage === 'admin-fetch-raw' && (
        <AdminFetchRawPage userEmail={user?.email} onLogout={handleLogout} />
      )}
      {currentPage === 'admin-login-attempts' && (
        <AdminLoginAttemptsPage userEmail={user?.email} onLogout={handleLogout} />
      )}
      {showSessionInvalidated && (
        <SessionInvalidatedModal onLogout={handleLogout} />
      )}
    </>
  );
};

export default App;
